import { type HelpContent, type ScreenId } from '@/types/help-support';

export const helpContentMap: Record<ScreenId, HelpContent> = {
  dashboard: {
    pageTitle: 'Credit Control Dashboard',
    description:
      'Your daily command centre for collection priorities, ageing debt, and team performance.',
    functionality: {
      overview:
        'The Credit Control Dashboard gives you an immediate operational picture of your accounts receivable function. At the top, four gateway scorecards surface the most urgent actions: accounts in the collection queue, unallocated payments waiting to be matched, promises to pay due today (with a count of broken promises), and open disputes. A Today\'s Progress bar beneath them summarises what the team has already achieved today. The lower half of the dashboard shows two side-by-side panels: Ageing Analysis (a breakdown of outstanding debt by age band, each band linking through to the full ageing report) and Performance Snapshot (key metrics -- customer count, DSO, promise success rate, average payment days -- plus a collection rate donut chart). Use the DashboardFilter in the top-right to switch between the manager view (whole portfolio) and a named individual\'s portfolio. Use the Refresh button in the top-left to reload all widget data on demand.',
      keyFeatures: [
        {
          label: 'Today\'s Priorities -- Four Gateway Scorecards',
          detail:
            'Collection Queue, Unallocated Payments, Promises Due Today, and Disputes each show a count and a currency total. Every card is clickable and navigates directly to the relevant module pre-filtered to the items requiring action.',
        },
        {
          label: 'Today\'s Progress Bar',
          detail:
            'A summary line beneath the scorecards that shows how many payments have been allocated, how many promises have been kept, and how many collection cases have been resolved so far today.',
        },
        {
          label: 'Ageing Analysis Panel',
          detail:
            'Displays outstanding debt split into age bands (labels and colours are configured server-side). Each band shows the outstanding amount, its percentage share of total debt, and a proportional bar. Click any band to go to the Ageing Analysis report pre-filtered to that band. A total outstanding figure and a "See your aging debt" link at the bottom navigate to the full report.',
        },
        {
          label: 'Performance Snapshot Panel',
          detail:
            'Shows four key metrics -- Customers (total active customer count), DSO (Days Sales Outstanding vs target), Promise Success (percentage of promises kept with a kept/total breakdown), and Avg Payment Days -- followed by a Collection Rate donut chart showing the percentage of cash collected this month against the configured target.',
        },
        {
          label: 'Dashboard Filter (Manager / Individual view)',
          detail:
            'A dropdown in the top-right of the header switches all widget data between the full portfolio view ("Manager view") and a named individual\'s portfolio. Selecting an individual shows a blue banner confirming whose data is displayed; clicking the X on the banner returns to the manager view.',
        },
        {
          label: 'Dashboard Selector',
          detail:
            'Toggle buttons in the top-left of the header allow switching to alternative dashboard views. Use these to move between different operational perspectives without leaving the dashboard area.',
        },
        {
          label: 'Manual Refresh',
          detail:
            'The Refresh button in the top-left header row triggers an on-demand reload of all widget data (collection queue, promises, unallocated payments, disputes, today\'s progress, ageing summary, and performance snapshot).',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Header Controls row',
          description:
            'The top row of the page contains (left to right): DashboardSelector toggle buttons, Refresh button. On the right: DashboardFilter dropdown. The page title, date, feedback collector, and Help button appear in the row beneath.',
        },
        {
          name: 'DashboardSelector',
          description:
            'Toggle buttons that switch between available dashboard views. Located on the left of the header controls row.',
        },
        {
          name: 'Refresh button',
          description:
            'Located to the right of the DashboardSelector. Invalidates and reloads all widget queries. Shows a spinning icon and "Refreshing..." label while data is loading.',
        },
        {
          name: 'DashboardFilter dropdown',
          description:
            'Located on the right of the header controls row. Options are "Manager view" (full portfolio, all users) and individual named users. Switching filter updates all four scorecards, the progress bar, ageing analysis, and performance snapshot simultaneously.',
        },
        {
          name: 'Global Filter Banner',
          description:
            'A blue banner that appears beneath the header controls row when an individual\'s portfolio is selected. Shows the person\'s initials, their name ("Viewing: [Name]\'s Portfolio"), and an X button to clear the filter and return to Manager view.',
        },
        {
          name: 'Today\'s Priorities -- Collection Queue card',
          description:
            'Primary value: count of accounts in the collection queue requiring action. Secondary value: total outstanding amount across those accounts. Clicking navigates to /collections/queue.',
        },
        {
          name: 'Today\'s Priorities -- Unallocated Payments card',
          description:
            'Primary value: count of receipts pending allocation. Secondary value: total value of those unallocated receipts. Clicking navigates to /payments/receipts filtered to unallocated.',
        },
        {
          name: 'Today\'s Priorities -- Promises Due Today card',
          description:
            'Primary value: count of promises to pay due today. Secondary value: count and value of broken promises (shown as "N | £X.XX"). Clicking navigates to /collections/promises filtered to due today.',
        },
        {
          name: 'Today\'s Priorities -- Disputes card',
          description:
            'Primary value: count of unresolved disputes. Secondary value: total value of unresolved disputes. Clicking navigates to /disputes filtered to active.',
        },
        {
          name: 'Today\'s Progress bar',
          description:
            'A single line beneath the four scorecards summarising today\'s completed activity: "X payments allocated (£Y) • Z promises kept • W cases resolved". Updates live as activity is recorded.',
        },
        {
          name: 'Ageing Analysis card -- band rows',
          description:
            'Each row shows the band label (e.g. "0-30 days"), the outstanding amount, its percentage share of total debt, and a proportional colour bar. Band labels, colours, and values come from the server. Each row is a clickable link to /analytics-reporting/ageing-analysis filtered to that band.',
        },
        {
          name: 'Ageing Analysis card -- Total outstanding',
          description:
            'A summary row at the bottom of the Ageing Analysis card showing the combined outstanding amount across all bands.',
        },
        {
          name: 'Ageing Analysis card -- "See your aging debt" button',
          description:
            'A ghost button at the bottom of the Ageing Analysis card that navigates to the full Ageing Analysis report at /analytics-reporting/ageing-analysis.',
        },
        {
          name: 'Performance Snapshot -- Customers metric',
          description:
            'The count of total active customers in the portfolio. Shown as a large number with the label "Total customers" beneath.',
        },
        {
          name: 'Performance Snapshot -- DSO metric',
          description:
            'Days Sales Outstanding: the average number of days it takes to collect payment after an invoice is raised. Shown alongside its configured target (e.g. "Target: 30 days").',
        },
        {
          name: 'Performance Snapshot -- Promise Success metric',
          description:
            'The percentage of promises to pay that have been kept, shown as a percentage with a "X of Y kept" breakdown beneath.',
        },
        {
          name: 'Performance Snapshot -- Avg Payment Days metric',
          description:
            'The average number of days customers are taking to pay invoices. Shown alongside the DSO target for comparison.',
        },
        {
          name: 'Performance Snapshot -- Collection Rate donut chart',
          description:
            'A circular donut chart showing the collection rate percentage for the current month (cash collected ÷ total cash due). The centre of the chart shows the percentage figure. Below the chart: the configured target percentage and how many percentage points above or below target the current rate sits. Hover over the information icon (ⓘ) beside the "Collection Rate" label to see the full calculation breakdown.',
        },
      ],
      useCases: [
        'Starting the working day by scanning the four Today\'s Priorities scorecards to understand the team\'s workload before assigning tasks.',
        'Switching the DashboardFilter to a specific credit controller\'s name to review their individual portfolio before a one-to-one performance review.',
        'Checking the Today\'s Progress bar mid-morning to confirm payments have been allocated and promises are being kept without opening each module.',
        'Clicking a band in the Ageing Analysis panel to go directly to the Ageing Analysis report filtered to the most heavily aged debt.',
        'Using the Performance Snapshot to check whether the Collection Rate is on track to hit the monthly target before a management update meeting.',
        'Clicking the Unallocated Payments card to jump straight to the receipts queue and clear outstanding allocations at the start of the day.',
        'Clicking the Promises Due Today card to see which customers are due to pay today and checking broken promise counts before making follow-up calls.',
        'Refreshing the dashboard after completing a batch of allocation work to confirm the Unallocated Payments count has reduced.',
      ],
    },
    userGuide: {
      gettingStarted:
        'The dashboard is the default landing page -- click the logo or the home icon in the left navigation to return to it at any time. No configuration is needed. Data loads automatically when the page opens. If figures look outdated, click the Refresh button on the left of the header controls row to force a data reload.',
      steps: [
        {
          title: 'How do I access the dashboard?',
          items: [
            {
              text: 'Click the logo or the home icon at the top of the left navigation panel.',
              note: 'The dashboard is the default page when you log in. If you have navigated away, click the home icon to return.',
            },
            {
              text: 'The page loads with live data automatically. All four scorecards, the progress bar, and both side-by-side panels load immediately.',
              tip: 'If you see figures that look stale, click the Refresh button on the left of the header controls row to force a data reload.',
            },
          ],
        },
        {
          title: 'How do I read the Today\'s Priorities scorecards?',
          items: [
            {
              text: 'Look at the four cards under the "Today\'s Priorities" heading.',
            },
            {
              text: 'Collection Queue shows how many customer accounts are in the active collection queue and the total outstanding value of those accounts. Click the card to open the Collection Queue.',
            },
            {
              text: 'Unallocated Payments shows how many receipts are waiting to be matched to invoices and the total value sitting unallocated. Click the card to open the Receipts page filtered to unallocated.',
            },
            {
              text: 'Promises Due Today shows how many promises to pay fall due today, and beneath that a count and value of broken promises. Click the card to open the Promises page filtered to due today.',
              tip: 'Check the broken promises figure first thing each morning -- broken promises need immediate follow-up calls to recover the payment.',
            },
            {
              text: 'Disputes shows the count and total value of unresolved disputes. Click the card to open the Disputes page filtered to active.',
            },
          ],
        },
        {
          title: 'How do I check today\'s progress?',
          items: [
            {
              text: 'Look at the summary line beneath the four scorecards.',
            },
            {
              text: 'It shows three figures: payments allocated (count and total amount), promises kept (count), and cases resolved (count).',
            },
            {
              text: 'These figures update each time you refresh the dashboard. Use them in team stand-ups to track daily throughput without opening each module.',
            },
          ],
        },
        {
          title: 'How do I use the Ageing Analysis panel?',
          items: [
            {
              text: 'Find the Ageing Analysis card on the left of the lower section.',
            },
            {
              text: 'Each row shows an age band label, the outstanding amount for that band, its percentage share of total debt, and a proportional colour bar.',
              note: 'Band labels and colours are configured by your system administrator -- the names and groupings may differ from the examples above. The bar width is proportional to the largest band, not the total, so you can compare bands against each other.',
            },
            {
              text: 'Click any band row to open the Ageing Analysis report pre-filtered to that band.',
              tip: 'Click the band with the largest amount to immediately see which customers are contributing most to that age bucket.',
            },
            {
              text: 'Click "See your aging debt" at the bottom of the card to open the full Ageing Analysis report without any band filter applied.',
            },
          ],
        },
        {
          title: 'How do I read the Performance Snapshot?',
          items: [
            {
              text: 'Find the Performance Snapshot card on the right of the lower section.',
            },
            {
              text: 'The Key Metrics row at the top shows four figures: Customers (total active accounts), DSO (Days Sales Outstanding vs your target), Promise Success (percentage of promises kept with a kept/total breakdown), and Avg Payment Days.',
              note: 'DSO and Avg Payment Days both use the same configured target for comparison. If the target looks wrong, ask your system administrator to check the settings.',
            },
            {
              text: 'The Collection Rate donut chart below the metrics shows the percentage of cash collected this month against the total cash due.',
            },
            {
              text: 'Hover over the ⓘ icon next to "Collection Rate" to see the exact calculation: the collected amount, the total due, and the target percentage.',
              tip: 'If Collection Rate is below target, check whether a large payment is sitting in Unallocated Payments -- unmatched receipts are not counted as collected until they are allocated.',
            },
          ],
        },
        {
          title: 'How do I view a specific person\'s portfolio?',
          items: [
            {
              text: 'Click the filter dropdown in the top-right of the header controls row (it defaults to "Manager view").',
            },
            {
              text: 'Select a named individual from the list.',
            },
            {
              text: 'A blue banner appears beneath the header controls confirming whose portfolio you are viewing.',
            },
            {
              text: 'All four scorecards, the progress bar, the Ageing Analysis panel, and the Performance Snapshot update to show data for that individual\'s assigned accounts only.',
            },
            {
              text: 'To return to the full portfolio view, click the X on the blue banner, or open the filter dropdown and select "Manager view".',
            },
          ],
        },
        {
          title: 'How do I refresh the dashboard data?',
          items: [
            {
              text: 'Click the Refresh button on the left side of the header controls row (beside the DashboardSelector).',
              tip: 'The page does not auto-refresh while you are viewing it. Click Refresh after completing a collection action to confirm the figures have updated.',
            },
          ],
        },
      ],
      proTips: [
        'Open the dashboard on a second monitor during team stand-ups -- the Today\'s Priorities scorecards and Today\'s Progress bar give you a live view of workload and throughput without preparing a separate report.',
        'If the Unallocated Payments count is high first thing in the morning, allocate payments before making collection calls -- customers may already have paid and unallocated receipts will make your outstanding figures look worse than they are.',
        'Use the DashboardFilter to switch between individual credit controllers during a review session. You can check each person\'s queue, promise performance, and collection rate without leaving the dashboard.',
        'A rising DSO figure in the Performance Snapshot over consecutive weeks usually means a small number of large accounts are paying late -- cross-reference with the highest Ageing Analysis band to identify them quickly.',
        'If Collection Rate is below target, hover over the ⓘ icon on the donut chart to see the exact amounts. A large gap between collected and total due often traces back to a batch of unallocated receipts.',
      ],
    },
    faq: [
      {
        question: 'How often does the dashboard data update?',
        answer:
          'Data loads fresh when you open or navigate to the dashboard. It does not auto-refresh while the page is open. Click the Refresh button on the left of the header controls row to reload all widget figures at any time.',
      },
      {
        question: 'Can I click through from a scorecard to the underlying records?',
        answer:
          'Yes -- all four Today\'s Priorities scorecards are clickable. Collection Queue navigates to /collections/queue. Unallocated Payments navigates to /payments/receipts filtered to unallocated. Promises Due Today navigates to /collections/promises filtered to due today. Disputes navigates to /disputes filtered to active.',
      },
      {
        question: 'What does DSO mean?',
        answer:
          'DSO (Days Sales Outstanding) is the average number of days it takes to collect payment after an invoice is raised, calculated as (Accounts Receivable ÷ Total Revenue) × Number of Days. The Performance Snapshot shows your current DSO alongside your configured target. A rising DSO means customers are taking longer to pay.',
      },
      {
        question: 'What does the Collection Rate donut chart show?',
        answer:
          'The Collection Rate is the percentage of cash collected this month relative to the total cash due this month (cash collected ÷ total cash due). The donut chart visualises that percentage. The target line and gap figure below the chart show how far above or below your configured target you currently sit. Hover over the ⓘ icon to see the exact amounts used in the calculation.',
      },
      {
        question: 'Why are the ageing band names different from what I expected?',
        answer:
          'Ageing band labels (e.g. "0-30 days", "31-60 days") and their colours are configured server-side by your system administrator. The bands shown on the dashboard reflect your organisation\'s specific ageing configuration.',
      },
      {
        question: 'What is the difference between "Manager view" and selecting an individual?',
        answer:
          '"Manager view" shows data aggregated across the whole portfolio -- all accounts, all credit controllers. Selecting a named individual filters every widget to show only the accounts assigned to that person. Use this to assess an individual\'s workload or to conduct a one-to-one performance review without running a separate report.',
      },
      {
        question: 'Why does the Promises Due Today card show broken promises?',
        answer:
          'The secondary value on the Promises Due Today card shows both the count and value of broken promises -- promises where the customer committed to paying by today but no matching payment has been received. This figure is shown alongside the total promises due so you can immediately see how many need a follow-up call.',
      },
      {
        question: 'What does Today\'s Progress show?',
        answer:
          'Today\'s Progress is a live summary of work completed since midnight: the number of payments allocated and their total value, the number of promises kept, and the number of collection cases resolved. It updates each time you refresh the dashboard.',
      },
      {
        question: 'How do I navigate to a specific module from the dashboard?',
        answer:
          'Click any of the four Today\'s Priorities scorecards to jump directly to the relevant module. Alternatively, click a band row in the Ageing Analysis panel to open the Ageing Analysis report filtered to that band, or use the left navigation panel to open any module manually.',
      },
    ],
    relatedPages: [
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Promises', path: '/collections/promises' },
      { label: 'Receipts (Unallocated)', path: '/payments/receipts' },
      { label: 'Disputes', path: '/disputes' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
    ],
  },

  customers: {
    pageTitle: 'Customer Accounts',
    description:
      'A complete list of all customer accounts showing outstanding balances, overdue amounts, credit limits, risk levels, and DSO -- used to monitor the ledger and take collection action.',
    functionality: {
      overview:
        'Customer Accounts shows every customer in your accounts receivable ledger in a single table. Four KPI cards at the top give you an immediate picture of total customers, total outstanding balance, total overdue balance, and the count of high-risk accounts. Use the search bar, risk filter, and More Filters panel to narrow the list. Select one or more rows to send emails, log calls, assign collection strategies, reassign credit controllers or account managers, send statements, or export. Click a customer name to open their full detail record.',
      keyFeatures: [
        {
          label: 'KPI Summary Cards',
          detail:
            'Four cards at the top of the page show Total Customers, Outstanding Balance, Overdue Balance, and High Risk Customers -- giving an at-a-glance picture of the ledger before working the list.',
        },
        {
          label: 'Customer Table with Financial Metrics',
          detail:
            'Each row shows account number, customer name, outstanding balance, overdue amount, credit limit, available credit, credit utilisation, DSO, risk level, last payment date, payment terms, collection strategy, credit controller, and account manager -- configurable via column settings.',
        },
        {
          label: 'Risk Level Filter',
          detail:
            'Quick dropdown in the toolbar filters the list to High, Medium, or Low risk customers immediately, without opening the advanced filter panel.',
        },
        {
          label: 'More Filters Panel',
          detail:
            'A slide-out panel with multi-select filters for currencies, payment terms, collection strategies, credit controllers, account managers, and four configurable user sort keys, plus numeric range filters for outstanding balance, overdue amount, credit limit, and DSO.',
        },
        {
          label: 'Currency Toggle',
          detail:
            'Switch balance and credit columns between customer currency (each account\'s native currency) and base currency (your organisation\'s home currency) for consistent cross-currency comparison.',
        },
        {
          label: 'Bulk Actions',
          detail:
            'Select one or more customers with the row checkboxes to access a floating action toolbar with Send Email, Log Call (single customer), Send Statements, Assign Strategy, Assign Credit Controller, Assign Account Manager, Assign Risk Level, Add Note (single customer), and Export.',
        },
        {
          label: 'Column Configuration & Reordering',
          detail:
            'Click the Settings icon to show or hide individual columns. Drag column headers to reorder them. Right-click a header to pin a column to the left or right, resize it, or reset column widths and order.',
        },
        {
          label: 'Export',
          detail:
            'Export the current filtered view (visible columns only) or all columns to Excel. Export is also available from the bulk selection toolbar to export only the selected rows.',
        },
        {
          label: 'Sortable Columns',
          detail:
            'Click any sortable column header to sort ascending or descending. Sortable columns include: account number, name, contact, currency, outstanding, overdue, credit limit, DSO, risk, last payment, payment terms, collection strategy, available credit, and credit utilisation.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Total Customers card',
          description: 'Count of all active customer accounts in the ledger.',
        },
        {
          name: 'Outstanding Balance card',
          description:
            'Sum of all open invoice balances across all customers, shown in base currency. If a single currency is selected in More Filters, shows that currency total.',
        },
        {
          name: 'Overdue Balance card',
          description:
            'Sum of all invoice balances past their due date, shown in base currency. Hover the info icon to see how this figure is scoped.',
        },
        {
          name: 'High Risk Customers card',
          description: 'Count of customers currently rated High risk.',
        },
        {
          name: 'Search bar',
          description:
            'Free-text input that filters the list in real time by customer name or account number.',
        },
        {
          name: 'Risk filter dropdown',
          description:
            'Filters the list to a single risk level -- All Risk Levels (default), or any configured level such as High, Medium, or Low.',
        },
        {
          name: 'Currency Toggle',
          description:
            'Switches the Outstanding, Overdue, and Credit Limit columns between customer currency and base currency.',
        },
        {
          name: 'Filters button',
          description:
            'Opens the More Filters slide-out panel for advanced multi-select and range filtering.',
        },
        {
          name: 'Export button (toolbar)',
          description:
            'Dropdown with two options: Export Current View (visible columns only) or Export All Columns -- both export to Excel.',
        },
        {
          name: 'Column Settings button',
          description: 'Opens the Column Configuration dialog to show or hide individual columns.',
        },
        {
          name: 'Row checkbox',
          description:
            'Selects a customer row for bulk actions. The header checkbox selects all rows on the current page.',
        },
        {
          name: 'Customer Name (link)',
          description:
            'Click to navigate to the Customer Detail page for that account. This is the only clickable cell that navigates.',
        },
        {
          name: 'Account Number',
          description: 'The unique identifier for this customer in the source accounting system.',
        },
        {
          name: 'Outstanding',
          description:
            'Total of all open invoice balances for this customer. Displayed in customer or base currency depending on the Currency Toggle.',
        },
        {
          name: 'Overdue',
          description:
            'Portion of the outstanding balance that is past the invoice due date.',
        },
        {
          name: 'Credit Limit',
          description:
            'The maximum credit approved for this customer. Shown in the selected currency mode.',
        },
        {
          name: 'Available Credit',
          description:
            'Credit Limit minus Outstanding Balance. Negative when the balance exceeds the limit.',
        },
        {
          name: 'Credit Utilisation',
          description:
            'Outstanding balance as a percentage of the credit limit. Values over 100% mean the customer has exceeded their limit.',
        },
        {
          name: 'DSO',
          description:
            'Days Sales Outstanding -- the average number of days this customer takes to pay their invoices, calculated from paid invoice history.',
        },
        {
          name: 'Risk Level badge',
          description:
            'Colour-coded rating for this customer. Calculated nightly from payment history, overdue ratio, and credit utilisation.',
        },
        {
          name: 'Last Payment',
          description: 'The date of the most recent receipt applied to this account.',
        },
        {
          name: 'Payment Terms',
          description:
            'The agreed payment terms for this account -- for example, Net 30 or Net 45.',
        },
        {
          name: 'Collection Strategy',
          description:
            'The collection strategy currently assigned to this customer.',
        },
        {
          name: 'Credit Controller',
          description:
            'The team member responsible for this account\'s collection activity.',
        },
        {
          name: 'Account Manager',
          description:
            'The relationship manager linked to this account.',
        },
        {
          name: 'Column header context menu',
          description:
            'Three-dot menu on each column header. Options: Pin to Left, Pin to Right, Reset Column Widths, Reset Column Order.',
        },
        {
          name: 'More Filters panel',
          description:
            'Slide-out panel with multi-select filters for: Currencies, Payment Terms, Collection Strategies, Credit Controllers, Account Managers, and four configurable user sort key fields (labels set by your administrator). Also includes paired Min / Max numeric range inputs for Outstanding, Overdue, Credit Limit, and DSO.',
        },
        {
          name: 'Bulk Action toolbar',
          description:
            'Floating bar that appears at the bottom of the screen when one or more rows are selected. Shows the selected count, combined outstanding and overdue totals, and action buttons: Export, and an Actions dropdown with Send Email, Log Call, Send Statements, Assign Strategy, Assign Credit Controller, Assign Account Manager, Assign Risk Level, and Add Note.',
        },
        {
          name: 'Pagination controls',
          description:
            'Fixed at the bottom of the table. Shows current page and total pages. Row count per page is selectable: 10, 25, 50, or 100.',
        },
      ],
      useCases: [
        'Morning triage: filtering by High risk and sorting by Overdue descending to identify the highest-value overdue accounts before starting calls.',
        'Bulk-assigning a more aggressive collection strategy to all customers on Net 30 terms whose overdue balance exceeds a threshold, using the More Filters panel to isolate them first.',
        'Preparing for a credit review meeting by exporting all customers with credit utilisation above 100% using the range filter for credit utilisation.',
        'Reassigning a credit controller\'s accounts to a colleague when someone leaves the team -- filter by the departing controller and bulk-reassign in one action.',
        'Sending monthly statements to all customers with an outstanding balance, using the bulk Send Statements action.',
        'Finding a specific account in seconds by typing the account number or customer name in the search bar when a customer calls in with a query.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Select Customer Accounts from the left navigation. The page loads with all customers visible and no filters applied. Customers are populated automatically from your accounting system integration -- if the list is empty, contact your administrator to confirm the integration is running. No additional setup is required before you can search, filter, or navigate to customer records.',
      steps: [
        {
          title: 'How do I find a specific customer?',
          items: [
            {
              text: 'Click the Search bar in the toolbar above the table.',
            },
            {
              text: 'Type the first few characters of the customer name or account number.',
            },
            {
              text: 'The list filters in real time. Click the customer\'s name (underlined link) to open their detail page.',
              note: 'Only the customer name cell is a navigation link. Clicking the account number, balance, or any other cell does not navigate to the detail page.',
            },
          ],
        },
        {
          title: 'How do I filter by risk level?',
          items: [
            {
              text: 'Click the Risk filter dropdown in the toolbar.',
            },
            {
              text: 'Select a risk level -- for example, High Risk.',
            },
            {
              text: 'The table updates immediately to show only customers at that risk level.',
              tip: 'Combine the risk filter with the search bar or the More Filters panel to narrow the list further -- for example, High Risk accounts on Net 30 terms.',
            },
          ],
        },
        {
          title: 'How do I use the advanced filters?',
          items: [
            {
              text: 'Click the Filters button in the toolbar to open the More Filters panel.',
            },
            {
              text: 'Select one or more values in any multi-select filter -- Currencies, Payment Terms, Collection Strategies, Credit Controllers, Account Managers, or the configurable user sort key fields. User sort key labels are named by your administrator and may vary.',
            },
            {
              text: 'Enter minimum and/or maximum values in the numeric range fields -- Outstanding (Min / Max), Overdue (Min / Max), Credit Limit (Min / Max), or DSO (Min / Max) -- to restrict the list to accounts within those bounds.',
              tip: 'To find customers over their credit limit, enter a value in Credit Limit Max that is less than your typical outstanding balances, then check the Credit Utilisation column in the results.',
            },
            {
              text: 'Click Apply Filters. The table updates and the Filters button shows a count badge for how many advanced filters are active.',
            },
            {
              text: 'To remove advanced filters, open the More Filters panel and click Clear Filters, then Apply Filters.',
              note: 'Closing the More Filters panel without clicking Apply discards any draft changes and keeps the previously applied filters.',
            },
          ],
        },
        {
          title: 'How do I switch between customer currency and base currency?',
          items: [
            {
              text: 'Click the Currency Toggle in the toolbar.',
            },
            {
              text: 'Select Customer Currency to show each account\'s Outstanding, Overdue, and Credit Limit in the currency of that account.',
            },
            {
              text: 'Select Base Currency to show all amounts converted to your organisation\'s home currency for consistent comparison.',
              note: 'The KPI cards at the top of the page always show base currency totals regardless of the Currency Toggle setting.',
            },
          ],
        },
        {
          title: 'How do I perform a bulk action on multiple customers?',
          items: [
            {
              text: 'Tick the checkbox on the left of each customer row you want to include.',
              tip: 'Tick the header checkbox at the top of the checkbox column to select all rows on the current page at once.',
            },
            {
              text: 'A floating action toolbar appears at the bottom of the screen showing the count of selected customers and their combined outstanding and overdue totals.',
            },
            {
              text: 'Click the Actions dropdown and choose the action you need:',
              subSteps: [
                'Send Email -- sends a templated or custom email to all selected customers at once.',
                'Log Call -- records a call note (available for single-customer selection only).',
                'Send Statements -- generates and sends account statements to all selected customers.',
                'Assign Strategy -- applies a collection strategy to all selected customers.',
                'Assign Credit Controller -- reassigns the credit controller for all selected accounts.',
                'Assign Account Manager -- reassigns the account manager for all selected accounts.',
                'Assign Risk Level -- manually overrides the risk level for all selected accounts.',
                'Add Note -- adds an internal note (available for single-customer selection only).',
              ],
            },
            {
              text: 'Click the X in the toolbar or deselect all rows to dismiss the toolbar.',
              note: 'Bulk actions apply only to the customers you have selected -- not to all filtered rows. If you want to act on all filtered results, use the header checkbox to select the current page first, then repeat for subsequent pages.',
            },
          ],
        },
        {
          title: 'How do I configure which columns are visible?',
          items: [
            {
              text: 'Click the Settings icon (cog) in the toolbar to open the Column Configuration dialog.',
            },
            {
              text: 'Toggle the switch next to each column name to show or hide it.',
            },
            {
              text: 'Click Save to apply your preferences. The column selection is saved per user and persists across sessions.',
              tip: 'To reorder columns, close the dialog and drag a column header left or right to the position you want.',
            },
          ],
        },
        {
          title: 'How do I pin a column so it stays visible when scrolling?',
          items: [
            {
              text: 'Hover over the column header you want to pin.',
            },
            {
              text: 'Click the three-dot menu (...) that appears on the right side of the header.',
            },
            {
              text: 'Select Pin to Left or Pin to Right. The column freezes in place while the rest of the table scrolls.',
            },
            {
              text: 'To unpin, click the three-dot menu again and select Unpin from Left or Unpin from Right.',
            },
          ],
        },
        {
          title: 'How do I export the customer list?',
          items: [
            {
              text: 'Apply any filters you want the export to reflect.',
            },
            {
              text: 'Click the Export icon (down-arrow) button in the toolbar.',
            },
            {
              text: 'Select Export Current View to export only the visible columns, or Export All Columns to include every available field.',
              note: 'The export reflects the current filter state. If no filters are active, the full customer list is exported.',
            },
            {
              text: 'The file downloads immediately as an Excel spreadsheet.',
            },
          ],
        },
      ],
      proTips: [
        'Sort by Overdue descending at the start of each week to build a call list prioritised by value. Pair this with the High risk filter to focus on the accounts most likely to become bad debt.',
        'Use the More Filters Outstanding Min and Outstanding Max fields to isolate a specific value band -- for example, accounts with outstanding balances between GBP10,000 and GBP50,000 -- before bulk-assigning a targeted collection strategy.',
        'Pin the Customer Name column to the left so it stays visible when scrolling right through the financial columns. Pin the Overdue column next to it for quick reference during calls.',
        'When a credit controller leaves, filter by their name in More Filters and use bulk Assign Credit Controller to reassign their entire portfolio in one step rather than updating each account individually.',
      ],
    },
    faq: [
      {
        question: 'How does the system calculate the Risk Level for each customer?',
        answer:
          'In Release 1, Risk Level is assigned manually -- there is no automated calculation. Use the Assign Risk Level bulk action from the selection toolbar, or open the Customer Detail page to set the risk level for an individual customer.',
      },
      {
        question: 'How do I find a customer that should be on the list but is not showing?',
        answer:
          'Customers appear here only after they have been synchronised from your accounting system. If a recently created customer is missing, check the integration run schedule with your administrator. Also confirm that no active filters are excluding the customer -- click the risk filter to All Risk Levels and clear any More Filters selections.',
      },
      {
        question: 'Can I add a new customer from this page?',
        answer:
          'No. Customers are created in your accounting system and imported into Credit Control via the integration. To add a new customer, create them in the source system first and wait for the next sync.',
      },
      {
        question: 'What does Available Credit mean?',
        answer:
          'Available Credit is the credit limit minus the current outstanding balance. A negative value means the customer has exceeded their approved credit limit. This figure does not automatically block new orders -- that behaviour depends on your ERP or accounting system configuration.',
      },
      {
        question: 'What does Credit Utilisation mean?',
        answer:
          'Credit Utilisation is the outstanding balance expressed as a percentage of the credit limit. A value of 100% means the customer has used their full limit. Values above 100% indicate the balance exceeds the limit.',
      },
      {
        question: 'Can I change a customer\'s credit limit from this page?',
        answer:
          'No. Credit limits are managed inside the Customer Detail page, not from the list view. Click the customer\'s name to open their detail page, then edit the credit limit from there.',
      },
      {
        question: 'What does DSO mean for an individual customer?',
        answer:
          'DSO for a customer is the average number of days between invoice date and payment receipt across their paid invoices. A customer DSO of 65 means they typically pay 65 days after being invoiced -- regardless of what payment terms they are on.',
      },
      {
        question: 'How do I see all customers assigned to a specific credit controller?',
        answer:
          'Click the Filters button to open More Filters. In the Credit Controllers multi-select, type or select the controller\'s name. Click Apply Filters. The table shows only their accounts.',
      },
      {
        question: 'How do I send a statement to a customer from this page?',
        answer:
          'Select the customer using the row checkbox, then click Actions -> Send Statements in the bulk action toolbar at the bottom of the screen. You can also select multiple customers and send statements to all of them at once.',
      },
      {
        question: 'What does it mean when the Outstanding amount here looks different from the Dashboard total?',
        answer:
          'The Dashboard KPI cards always show base currency totals across all customers. This page respects the Currency Toggle setting -- if you have selected Customer Currency, each row shows amounts in the customer\'s own currency, which changes the column totals. Switch to Base Currency in the toggle to see comparable figures.',
      },
      {
        question: 'How do I filter customers by a specific balance range?',
        answer:
          'Open More Filters by clicking the Filters button. Use the Outstanding Min / Max, Overdue Min / Max, or Credit Limit Min / Max fields to set a lower and/or upper bound. You can set only a minimum, only a maximum, or both. Click Apply Filters to update the list.',
      },
      {
        question: 'What are the User Sort Key columns in More Filters?',
        answer:
          'User Sort Keys are four configurable classification fields set up by your administrator for your organisation\'s specific needs -- for example, "Region", "Industry", or "Sales Rep". The labels shown in the More Filters panel reflect whatever names your administrator has configured in General Settings.',
      },
      {
        question: 'Can I sort by multiple columns at the same time?',
        answer:
          'No. Only single-column sorting is supported. Click a column header to sort by that column. Clicking the same header again toggles between ascending and descending. Clicking a third time removes the sort.',
      },
    ],
    relatedPages: [
      { label: 'Customer Detail', path: '/customers/:id' },
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Credit Scoring', path: '/credit-scoring' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
      { label: 'Dashboard', path: '/' },
      { label: 'My Tasks', path: '/tasks' },
    ],
  },

  'customer-detail': {
    pageTitle: 'Customer Detail',
    description:
      'Your primary workspace for managing an individual customer account — bringing together financial metrics, transaction history, collection activity, promises, disputes, communications, notes, contacts, addresses, and a full audit log in a single tabbed view.',
    functionality: {
      overview:
        'The Customer Detail page opens whenever you click a customer\'s name from the Customers list, a collection case, or a transaction record. It shows live financial metrics at the top (outstanding balance, overdue amount, credit position, and payment performance), then exposes every aspect of the account through a series of tabs. Use it to prepare for a collection call, record the outcome of a conversation, review ageing debt, manage collection settings, or check the full history of what has happened on the account. For customers that belong to a multi-company group you can toggle Consolidated View to see the combined position across all group members.',
      keyFeatures: [
        {
          label: 'Metric Cards',
          detail: 'Four cards at the top of the page show Total Outstanding, Overdue, Credit Limit / Available Credit, and Payment Performance (DSO). Each card is clickable and navigates directly to the relevant tab or sub-tab.',
        },
        {
          label: 'Currency Select',
          detail: 'Switch all amounts between Customer Currency and Base Currency using the toggle in the page header. For Any Currency accounts a dropdown lets you choose a specific transaction currency; selecting the base currency shows all transactions.',
        },
        {
          label: 'Consolidated View toggle',
          detail: 'Available only for customers that belong to a multi-company group. When switched on, the metric cards, Transactions, Collections, Promises, and Disputes tabs all show the combined position across every company in the group.',
        },
        {
          label: 'Overview tab',
          detail: 'Shows Recent Activity (the latest notes, communications, and system events), Account Information (account number, name, currency, On Stop toggle, status), Contact Information (email, phone, address), Collection Settings (strategy, risk level, credit controller, account manager), and Category Keys.',
        },
        {
          label: 'Financial tab',
          detail: 'Shows Credit Limit, Available Credit, and Credit Utilisation (with a progress bar), plus Payment Terms, Due Days, Due Days Type, Payment Performance (DSO), Risk Level, and both the base-currency and customer-currency credit limits.',
        },
        {
          label: 'Ageing tab',
          detail: 'Displays a visual ageing bar chart breaking the customer\'s outstanding balance into age bands (configured server-side). Amounts are shown in the currently selected currency.',
        },
        {
          label: 'Transactions tab',
          detail: 'Lists all outstanding and historical transactions for this customer with search, sorting, pagination, column configuration, and a full suite of bulk actions: Send Reminders, Create Collection Case, Create Promise to Pay, Flag as Disputed, Add Note, Send Copy Invoice, Allocate Receipt, View Allocation History, and Export.',
        },
        {
          label: 'Collections tab',
          detail: 'Shows all collection cases for this customer. Each case includes the case status, assigned user, and a link to the full case record.',
        },
        {
          label: 'Promises tab',
          detail: 'Lists every payment promise recorded for this customer with the promise amount, due date, and status (Pending / Kept / Broken).',
        },
        {
          label: 'Disputes tab',
          detail: 'Shows all disputes raised against this customer\'s invoices, including the dispute type, date raised, current status, and assigned owner.',
        },
        {
          label: 'Communications tab',
          detail: 'A log of every email and letter sent to this customer from the system, with the date, template or subject used, and the sending user.',
        },
        {
          label: 'Notes tab',
          detail: 'Internal-only notes recorded against this customer. Each note shows the text, the author, and a timestamp. Notes are never sent to the customer.',
        },
        {
          label: 'Contacts tab',
          detail: 'All contact records associated with this customer account, with name, role, email, and phone. Contacts can be used when sending communications.',
        },
        {
          label: 'Addresses tab',
          detail: 'All address records for this customer (billing, delivery, and other address types synced from the accounting system).',
        },
        {
          label: 'Audit Log tab',
          detail: 'A chronological record of every change made to this customer\'s settings in Credit Control — including On Stop changes, collection strategy changes, risk level changes, and credit controller / account manager reassignments — showing the old value, new value, changed by, and timestamp.',
        },
        {
          label: 'On Stop toggle',
          detail: 'Located in the Account Information section of the Overview tab. Switching this on places the customer on credit hold. You are prompted to record a note explaining the reason when enabling On Stop.',
        },
        {
          label: 'Collection Settings',
          detail: 'On the Overview tab, set or change the Assigned Strategy, Risk Level, Assigned Credit Controller, and Account Manager for this customer. Click Save to apply changes — these take effect on the next nightly strategy run. Note: a Collection Strategy will only run for this customer if a Credit Controller is assigned to the account.',
        },
      ],
      sectionsAndFields: [
        { name: 'Total Outstanding metric card', description: 'The total value of all open invoices for this customer. Clicking the card navigates to the Transactions tab filtered to outstanding transactions.' },
        { name: 'Overdue metric card', description: 'The portion of the outstanding balance that is past the invoice due date. Clicking navigates to the Transactions tab filtered to overdue.' },
        { name: 'Credit Limit / Available Credit metric card', description: 'Shows both the approved credit limit and the remaining available credit (limit minus outstanding). Clicking navigates to the Financial tab.' },
        { name: 'Payment Performance metric card', description: 'Shows the customer\'s DSO (Days Sales Outstanding) — how long they typically take to pay. Clicking navigates to the Transactions tab (payment history sub-tab).' },
        { name: 'Currency Select (header)', description: 'Dropdown in the top-right of the header. Switches all amount columns and metric cards between Customer Currency and Base Currency. For Any Currency accounts, shows a list of all currencies the account has transacted in.' },
        { name: 'Consolidated View toggle (header)', description: 'Switch visible only when the customer belongs to a multi-company group. When on, metrics and the Transactions, Collections, Promises, and Disputes tabs show combined group data.' },
        { name: 'Back to Customers button', description: 'Returns to the Customers list without losing any filter state.' },
        { name: 'Overview -- Account Information', description: 'Account Number, Customer Name, Currency, On Stop toggle (with note prompt on enable), and Status badge (Active / Inactive).' },
        { name: 'Overview -- Contact Information', description: 'Primary email address, phone number, and address for this customer.' },
        { name: 'Overview -- Collection Settings', description: 'Assigned Strategy dropdown, Risk Level dropdown, Assigned Credit Controller user picker, Account Manager user picker, and a Save button. The Save button is disabled until a value changes.' },
        { name: 'Overview -- Category Keys', description: 'Read-only display of any category keys assigned to this customer in the accounting system (e.g. industry, region, or segment codes).' },
        { name: 'Overview -- Recent Activity panel', description: 'A feed of the most recent notes, communications, and system events for this customer, shown in reverse chronological order.' },
        { name: 'Financial -- Credit Limit card', description: 'The approved credit limit in the currently selected currency.' },
        { name: 'Financial -- Available Credit card', description: 'Credit Limit minus current outstanding balance. Shows "No Credit Limit Set" when the limit is zero.' },
        { name: 'Financial -- Credit Utilisation card', description: 'A percentage showing how much of the credit limit is currently consumed, with a visual progress bar.' },
        { name: 'Financial -- Payment Terms & Performance', description: 'Payment Terms name, Due Days, Due Days Type, Payment Performance (DSO in days), Risk Level badge, Credit Limit (base currency), and Currency Credit Limit (if the customer currency differs from base).' },
        { name: 'Ageing -- Ageing Summary chart', description: 'Bar chart showing the outstanding balance split by age band. Band labels and colours are configured in Settings. Amounts shown in the selected currency.' },
        { name: 'Transactions -- Outstanding sub-tab', description: 'Table of all open and overdue invoices, credit notes, and unallocated receipts. Supports search, column configuration, sorting, pagination, and bulk actions.' },
        { name: 'Transactions -- Payment History sub-tab', description: 'Table of all completed (fully paid or allocated) transactions for this customer.' },
        { name: 'Transactions -- Search', description: 'Free-text search across reference number and description. Debounced — the list updates as you type.' },
        { name: 'Transactions -- Column Settings', description: 'Opens the Column Configuration dialog to show, hide, or reorder the visible columns in the transaction table.' },
        { name: 'Transactions -- Bulk Actions (Selection Toolbar)', description: 'Appears at the bottom of the screen when rows are selected. Shows the count, total gross, and total outstanding for the selection. Available actions: Send Reminders, Allocate Receipt (receipts only), Send Copy Invoice (invoices only), Create Collection Case, Create Promise to Pay, Flag as Disputed, Add Note (single row), View Allocation History, Export.' },
        { name: 'Collections tab', description: 'Lists all collection cases linked to this customer. Each row shows the case reference, status, assigned user, and creation date.' },
        { name: 'Promises tab', description: 'Lists all promises to pay for this customer with promise amount, commitment date, and status: Pending (not yet due), Kept (payment received), or Broken (due date passed without payment).' },
        { name: 'Disputes tab', description: 'Lists all disputes raised against this customer\'s invoices. Each row shows the invoice reference, dispute type, date raised, current status, and assigned owner.' },
        { name: 'Communications tab', description: 'Log of every system-sent communication for this customer. Includes the date, subject/template name, sending user, and delivery status.' },
        { name: 'Notes tab', description: 'Internal notes recorded for this customer. Each note shows the note text, author name, and created timestamp. Notes are never visible to the customer.' },
        { name: 'Contacts tab', description: 'All contact records for this customer with name, role/title, email address, and phone number.' },
        { name: 'Addresses tab', description: 'All address records for this customer synced from the accounting system, labelled by address type (e.g. Billing, Delivery).' },
        { name: 'Audit Log tab', description: 'Change history for this customer\'s Credit Control settings. Columns: Action type, previous value, new value, changed by (user), and date/time of change.' },
      ],
      useCases: [
        'Preparing for a collection call — review recent activity, open invoices, broken promises, and ageing before picking up the phone.',
        'Recording the outcome of a call — add a note detailing what was discussed and create a new promise to pay in the same workflow.',
        'Placing a customer on credit hold — toggle On Stop on the Overview tab and record the reason as a note.',
        'Adjusting collection settings — change the assigned strategy, risk level, credit controller, or account manager without leaving the page.',
        'Reviewing the full ageing position — use the Ageing tab to see exactly which age bands the debt falls into before escalating.',
        'Checking whether a prior promise was kept before deciding whether to open a collection case.',
        'Viewing the consolidated position for a multi-company group customer by enabling Consolidated View.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to a Customer Detail page by clicking any customer name on the Customers list, from a collection case record, or by clicking a customer name in the Transactions page. The page opens on the Overview tab. Use the tab bar to move between sections — all data loads on demand when you click a tab.',
      steps: [
        {
          title: 'How do I check a customer\'s outstanding invoices?',
          items: [
            { text: 'Click the Transactions tab.' },
            { text: 'The Outstanding sub-tab is selected by default and shows all open and overdue invoices.' },
            { text: 'Use the search bar to find a specific invoice by reference number.' },
            { text: '', tip: 'Click a reference number to open the Transaction Detail Sheet and see line items, allocation history, linked promises, and linked disputes.' },
          ],
        },
        {
          title: 'How do I place a customer on credit hold (On Stop)?',
          items: [
            { text: 'Click the Overview tab.' },
            { text: 'In the Account Information card, toggle the On Stop switch to Yes.' },
            { text: 'An "On Stop Note" dialog will appear — enter the reason for placing the account on hold.' },
            { text: 'Click Confirm. The account is immediately placed on stop and the reason is saved as an internal note.' },
            { text: '', note: 'To remove On Stop, toggle the switch back to No. You will be prompted to record a note explaining the reason for removal.' },
          ],
        },
        {
          title: 'How do I change the collection strategy or credit controller for this customer?',
          items: [
            { text: 'Click the Overview tab.' },
            { text: 'Scroll to the Collection Settings card.' },
            { text: 'Select the new Assigned Strategy, Risk Level, Credit Controller, or Account Manager from the relevant dropdowns.' },
            { text: 'Click Save. The change is applied immediately and recorded in the Audit Log.' },
            { text: '', tip: 'Strategy changes take effect from the next nightly strategy run. The risk level and user assignments update immediately. A Credit Controller must be assigned to this account for the Collection Strategy to run — if no Credit Controller is set, the strategy will not execute.' },
          ],
        },
        {
          title: 'How do I view the customer\'s ageing breakdown?',
          items: [
            { text: 'Click the Ageing tab.' },
            { text: 'The bar chart shows outstanding balance split into age bands (e.g. Current, 1–30 days, 31–60 days, 61–90 days, 90+ days).' },
            { text: 'Use the Currency Select in the page header to switch between customer and base currency amounts.' },
            { text: '', tip: 'For group customers, enable Consolidated View first to see the combined ageing across all group members.' },
          ],
        },
        {
          title: 'How do I create a promise to pay from the transactions table?',
          items: [
            { text: 'Click the Transactions tab.' },
            { text: 'Select one or more invoice rows using the row checkboxes.' },
            { text: 'In the Selection Toolbar at the bottom of the screen, click Actions then choose Create Promise to Pay.' },
            { text: 'Fill in the commitment date and amount in the drawer that opens, then click Save.' },
            { text: '', note: 'The promise is now visible on the Promises tab. It will automatically be marked Kept when a matching payment is allocated, or Broken if the due date passes without payment.' },
          ],
        },
        {
          title: 'How do I send a reminder from the transactions table?',
          items: [
            { text: 'Click the Transactions tab.' },
            { text: 'Select the invoice rows you want to chase using the row checkboxes.' },
            { text: 'In the Selection Toolbar, click Actions then choose Send Reminders.' },
            { text: 'Select a communication template (or write a custom message), confirm the recipient, and click Send.' },
            { text: '', note: 'The communication is automatically logged in the Communications tab. You do not need to add a separate note.' },
          ],
        },
        {
          title: 'How do I add an internal note?',
          items: [
            { text: 'On the Notes tab, click the Add Note button.' },
            { text: 'Type your note — for example, "Called accounts payable, they confirm payment will be made by Friday."' },
            { text: 'Click Save. The note appears in the Notes tab and in the Recent Activity panel on the Overview tab.' },
            { text: '', note: 'Notes are internal only and are never sent or visible to the customer.' },
          ],
        },
        {
          title: 'How do I view what has changed on this account over time?',
          items: [
            { text: 'Click the Audit Log tab.' },
            { text: 'Each row shows the type of change (e.g. On Stop Change, Strategy Change), the previous value, the new value, who made the change, and when.' },
            { text: '', tip: 'Use the Audit Log when a colleague queries why a setting changed — it provides a full, tamper-proof history.' },
          ],
        },
      ],
      proTips: [
        'Before calling a customer, open the Overview tab first. The Recent Activity panel shows the last interactions at a glance — this prevents you from unknowingly repeating a promise or commitment a colleague already made.',
        'If a customer claims they have sent payment, check the Transactions tab for any unallocated receipts. The money may have arrived but not yet been matched to an invoice — allocate it before chasing again.',
        'Use the Consolidated View toggle when dealing with a group customer who pays across multiple entities. The combined outstanding and ageing figures give you a much clearer picture of total exposure.',
        'The Currency Select in the header applies consistently across all tabs — set it to Base Currency before a management review so all figures are in a single comparable currency.',
        'When you change the On Stop status, always record a detailed reason in the note prompt. This note becomes part of the Audit Log and will be invaluable if the account is queried later.',
        'Use the Promises tab as a daily call list — sort by commitment date and work through promises that are due or overdue. A broken promise is a clear signal to escalate to a collection case.',
      ],
    },
    faq: [
      {
        question: 'Why can\'t I see the Overview, Financial, Ageing, Communications, Notes, Contacts, Addresses, or Audit Log tabs?',
        answer: 'These tabs are hidden when Consolidated View is switched on. Consolidated View only shows tabs that aggregate data across the group — Transactions, Collections, Promises, and Disputes. Switch Consolidated View off to access the full set of tabs for the individual entity.',
      },
      {
        question: 'What is the difference between Customer Currency and Base Currency in the Currency Select?',
        answer: 'Customer Currency shows all amounts in the currency the customer transacts in (e.g. USD). Base Currency converts all amounts to your company\'s home currency (e.g. GBP) using the exchange rates held in the system. For Any Currency accounts, you choose a specific currency from the dropdown — selecting the base currency displays all transactions regardless of their original currency.',
      },
      {
        question: 'What does "On Stop" mean?',
        answer: 'On Stop (also called a credit hold) means the customer\'s account is flagged to prevent new orders or deliveries being processed on credit terms. In Credit Control it is a manual flag you set and release — enforcement in your fulfilment or ERP system depends on how your integration is configured.',
      },
      {
        question: 'What does a "Broken" promise status mean?',
        answer: 'A promise is automatically marked Broken when the commitment date passes without a matching receipt being allocated to the invoice(s) the promise was created for. It does not necessarily mean the customer refused to pay — they may simply be late. Use a broken promise as a trigger to follow up.',
      },
      {
        question: 'Can I change a customer\'s credit limit from this page?',
        answer: 'The credit limit shown on the Financial tab and in the metric cards is read-only in Credit Control — it is synchronised from your accounting or ERP system. To change the credit limit you need to update it in the source system and wait for the next integration sync.',
      },
      {
        question: 'Why do some columns show different values when I switch currencies?',
        answer: 'When you switch to Base Currency, all amounts are converted to your company\'s home currency using the exchange rates held in the system. Small differences may appear due to rate rounding. The original transaction amounts are always preserved and visible when you switch back to Customer Currency.',
      },
      {
        question: 'How do I open a collection case for this customer?',
        answer: 'Go to the Transactions tab, select the invoice(s) you want to escalate, then click Actions > Create Collection Case in the Selection Toolbar. The new case will appear immediately in the Collections tab.',
      },
      {
        question: 'Are notes visible to the customer?',
        answer: 'No. Notes added via the Notes tab or via Actions > Add Note on a transaction are internal only and are never included in any communication sent to the customer.',
      },
      {
        question: 'Why is my Collection Strategy not running for this customer?',
        answer: 'Collection Strategies will only execute for a customer if a Credit Controller is assigned to their account. If no Credit Controller has been set, the strategy will not run regardless of which strategy is assigned. Go to the Overview tab, scroll to the Collection Settings card, assign a Credit Controller, and click Save. The strategy will then run from the next scheduled execution.',
      },
    ],
    relatedPages: [
      { label: 'Customers', path: '/customers' },
      { label: 'Transactions', path: '/transactions' },
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Promises to Pay', path: '/collections/promises' },
      { label: 'Disputes', path: '/disputes/all' },
      { label: 'Receipts', path: '/payments/receipts' },
    ],
  },

  transactions: {
    pageTitle: 'Transactions',
    description:
      'A cross-customer ledger view of all invoices, credit notes, receipts, and adjustments, with filtering, bulk actions, and export for daily collection and reconciliation work.',
    functionality: {
      overview:
        'The Transactions page shows every financial transaction across all customer accounts in one place. Use it to find a specific invoice, review overdue balances, check on unallocated receipts, flag disputed transactions, create collection cases, or run exports for period-end reporting. The page loads by default filtered to outstanding transactions. You can refine the list further using the metric cards, the search bar, and the advanced Filter Sheet.',
      keyFeatures: [
        {
          label: 'Metric Summary Cards',
          detail: 'Four clickable cards at the top -- Total Outstanding, Overdue, At Risk, and Unallocated Receipts -- filter the list to that subset when clicked. Clicking a second time clears the filter.',
        },
        {
          label: 'Advanced Filter Sheet',
          detail: 'Open the Filters button to apply multi-select filters by customer, transaction type, status, currency, date range, amount range, outstanding balance range, and four flag checkboxes (External Dispute, Has Dispute, Has Promise to Pay, Has Collection Case).',
        },
        {
          label: 'Saved Filter Presets',
          detail: 'Save any combination of filters as a named preset from inside the Filter Sheet. Presets appear at the top of the sheet and can be starred to show which one is active. Delete custom presets at any time; the default Outstanding preset cannot be deleted.',
        },
        {
          label: 'Currency Toggle',
          detail: 'Switch between Customer Currency (amounts shown in each transaction\'s own currency) and Base Currency (all amounts converted to the company base currency) using the toggle in the toolbar.',
        },
        {
          label: 'Column Configuration',
          detail: 'Click the column settings icon to show, hide, or reorder columns. Visible columns include Reference Number, Transaction Date, Due Date, Customer Name, Type, Currency, Gross Amount, Outstanding Amount, Status, Description, Created By, and Flags. Hidden-by-default columns include Net Amount, VAT Amount, Sub Ledger, Order Number, Account Code, and Modified By.',
        },
        {
          label: 'Bulk Actions',
          detail: 'Select one or more transactions using the row checkboxes to reveal a floating Selection Toolbar. Available actions depend on the selection: Export (Current View or All Columns), and -- when all selected rows belong to the same customer -- Create Collection Case, Create Promise to Pay, Flag as Disputed, Add Note (single row only), Send Copy Invoice (invoices only), Allocate Receipt (unallocated receipts only), and View Allocation History.',
        },
        {
          label: 'Transaction Detail Sheet',
          detail: 'Click any Reference Number to open a side sheet showing full transaction details: line items, allocation history, linked disputes, linked promises, and available actions.',
        },
        {
          label: 'Server-Side Export',
          detail: 'Export the filtered transaction list to Excel. Choose Export Current View (visible columns only) or Export All Columns. The export runs server-side and processes large datasets without freezing the browser.',
        },
      ],
      sectionsAndFields: [
        { name: 'Total Outstanding card', description: 'Clickable KPI showing the total unpaid invoice value. Click to filter the list to outstanding/pending invoices only. Shows base-currency total unless a single currency filter is active.' },
        { name: 'Overdue card', description: 'Clickable KPI showing the total value of invoices past their due date. Click to filter to overdue transactions only.' },
        { name: 'At Risk card', description: 'Clickable KPI showing invoices that are overdue or due within the next 7 days. Click to filter to at-risk transactions.' },
        { name: 'Unallocated Receipts card', description: 'Clickable KPI showing the total value of receipts that have not yet been fully matched to an invoice. Click to filter to unallocated receipts.' },
        { name: 'Search bar', description: 'Free-text search across reference number, customer name, and description. The list updates as you type (debounced).' },
        { name: 'Currency Toggle', description: 'Switches amount columns between the customer\'s own currency and the company base currency. Also affects which KPI totals are shown.' },
        { name: 'Filters button', description: 'Opens the Filter Sheet where all advanced filters are configured. Shows a badge with the count of active filters when any non-default filters are applied.' },
        { name: 'Column Settings button', description: 'Opens the Column Configuration dialog to show, hide, or reorder table columns.' },
        { name: 'Clear Filters button', description: 'Resets all filters back to the default Outstanding preset and clears any active metric card selection. Only shown when non-default filters are active.' },
        { name: 'Reference Number column', description: 'The unique transaction identifier from your accounting system. Click to open the Transaction Detail Sheet.' },
        { name: 'Transaction Date column', description: 'The date the transaction was posted in the accounting system.' },
        { name: 'Due Date column', description: 'The date payment was due. Blank for receipt and adjustment transactions.' },
        { name: 'Customer Name column', description: 'The account the transaction belongs to. Click to navigate to the Customer Detail page.' },
        { name: 'Type column', description: 'Transaction type badge: Invoice, Credit Note, Receipt, Debit Adjustment, or Credit Adjustment.' },
        { name: 'Currency column', description: 'The currency in which the transaction was originally recorded.' },
        { name: 'Gross Amount column', description: 'The full face value of the transaction. Shown in customer currency or base currency depending on the Currency Toggle.' },
        { name: 'Outstanding Amount column', description: 'The unpaid balance remaining after part-payments or credit allocations. Zero for fully paid or fully allocated transactions.' },
        { name: 'Status column', description: 'Current state of the transaction: Outstanding (unpaid within terms), Overdue (past due date), or Completed (fully paid or allocated).' },
        { name: 'Description column', description: 'Free-text description imported from the accounting system.' },
        { name: 'Flags column', description: 'Icon badges showing whether the transaction has an active Dispute (red triangle), a Promise to Pay (blue coin), or a Collection Case (orange briefcase).' },
        { name: 'External Dispute column', description: 'Shows Yes / No to indicate whether the transaction is flagged as disputed in your external accounting system.' },
        { name: 'Filter Sheet -- Customer', description: 'Multi-select search to filter to one or more specific customer accounts.' },
        { name: 'Filter Sheet -- Transaction Type', description: 'Multi-select: Invoice, Credit Note, Receipt, Debit Adjustment, Credit Adjustment.' },
        { name: 'Filter Sheet -- Status', description: 'Multi-select: Outstanding, Overdue, Completed.' },
        { name: 'Filter Sheet -- Currency', description: 'Multi-select to restrict results to transactions in one or more specific currencies.' },
        { name: 'Filter Sheet -- Date Range', description: 'From and To date pickers to restrict results to transactions within a specific transaction-date window.' },
        { name: 'Filter Sheet -- Amount Range', description: 'Min and Max fields to filter by gross transaction amount.' },
        { name: 'Filter Sheet -- Outstanding Balance', description: 'Min and Max fields to filter by remaining outstanding balance. The default preset sets Min to 0.01 to exclude fully paid transactions.' },
        { name: 'Filter Sheet -- Flags', description: 'Four checkboxes: External Dispute, Has Dispute, Has Promise to Pay, Has Collection Case. Check one or more to show only transactions matching those flags.' },
        { name: 'Filter Sheet -- Saved Presets', description: 'Named filter combinations you have previously saved. Click a preset to load it into the draft filter state, then click Apply. The star icon shows which preset is currently active.' },
        { name: 'Selection Toolbar', description: 'Floating bar at the bottom of the screen that appears when rows are selected. Shows count, total gross amount, and total outstanding. Provides Export and Actions buttons.' },
      ],
      useCases: [
        'Checking the total overdue balance across all customers at the start of the day by clicking the Overdue KPI card.',
        'Finding a specific invoice by reference number when a customer calls to query it.',
        'Identifying all unallocated receipts so they can be matched to invoices in the Allocation Workspace.',
        'Selecting all overdue invoices for a single customer and creating a collection case in one action.',
        'Exporting all outstanding transactions filtered to a specific currency for a monthly foreign-currency report.',
        'Flagging a batch of invoices as disputed after a customer raises a billing query by selecting the rows and using the Flag as Disputed action.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Transactions from the left navigation. The page loads pre-filtered to outstanding transactions (Outstanding Balance > 0). If the list appears empty, check that your accounting system integration is active under Settings -- no transactions will appear until at least one successful sync has run.',
      steps: [
        {
          title: 'How do I find a specific invoice?',
          items: [
            {
              text: 'Type the invoice reference number, customer name, or a keyword from the description into the Search bar.',
              tip: 'The search is debounced -- wait a moment after typing before the results update.',
            },
            {
              text: 'Click the Reference Number link in the results to open the Transaction Detail Sheet.',
              note: 'If you cannot find the transaction, check whether the Filters button shows active filters (a count badge). The default Outstanding preset excludes fully paid transactions.',
            },
          ],
        },
        {
          title: 'How do I use the metric cards to filter transactions?',
          items: [
            {
              text: 'Click one of the four cards at the top: Total Outstanding, Overdue, At Risk, or Unallocated Receipts.',
            },
            {
              text: 'The list immediately updates to show only transactions matching that metric. The selected card shows a ring highlight.',
              note: 'The metric card filter and the Filter Sheet filters are applied together -- both are active at the same time.',
            },
            {
              text: 'Click the same card again to deselect it and return to the unfiltered view.',
              tip: 'Use the At Risk card to build a same-day contact list -- it shows invoices due today or within the next 7 days alongside already-overdue ones.',
            },
          ],
        },
        {
          title: 'How do I apply advanced filters?',
          items: [
            {
              text: 'Click the Filters button in the toolbar to open the Filter Sheet.',
            },
            {
              text: 'Select any combination of Customer, Transaction Type, Status, Currency, Date Range, Amount Range, Outstanding Balance Range, and Flags.',
              note: 'You can set only a Min or only a Max in any range field -- you do not need to fill in both.',
            },
            {
              text: 'Click Apply to apply the filters and close the sheet. A count badge on the Filters button shows how many filters are active.',
              tip: 'To save the current filter combination for reuse, click the Save icon (star) inside the Filter Sheet before clicking Apply.',
            },
          ],
        },
        {
          title: 'How do I save and reuse a filter preset?',
          items: [
            {
              text: 'Configure the filters you want inside the Filter Sheet.',
            },
            {
              text: 'Click the Save Preset button (star icon) at the top of the Filter Sheet.',
            },
            {
              text: 'Type a name for the preset and confirm.',
            },
            {
              text: 'The preset now appears at the top of the Filter Sheet. Click it to reload those filters in future sessions.',
              note: 'The default "Outstanding Transactions" preset cannot be deleted. Any custom preset can be deleted using the trash icon next to its name.',
            },
          ],
        },
        {
          title: 'How do I perform bulk actions on selected transactions?',
          items: [
            {
              text: 'Tick the checkbox on each row you want to include. Use the header checkbox to select all visible rows.',
            },
            {
              text: 'The Selection Toolbar appears at the bottom of the screen showing the count and totals.',
            },
            {
              text: 'Click Export to download the selected rows, or click Actions to see what is available for the current selection.',
              note: 'The Actions menu only appears when all selected rows belong to the same customer. Actions available depend on transaction types in the selection:',
              subSteps: [
                'Create Collection Case -- available when at least one invoice is selected.',
                'Create Promise to Pay -- available when at least one invoice is selected.',
                'Flag as Disputed -- available when at least one invoice is selected.',
                'Add Note -- available for a single row only.',
                'Send Copy Invoice -- available when at least one invoice is selected from a single customer.',
                'Allocate Receipt -- available when all selected rows are unallocated receipts.',
                'View Allocation History -- available when selected rows share the same allocation reference.',
              ],
            },
          ],
        },
        {
          title: 'How do I export transactions?',
          items: [
            {
              text: 'Apply any filters and metric card selections you want the export to reflect.',
              tip: 'To export a specific customer\'s transactions, open the Filter Sheet, select the customer, and apply before exporting.',
            },
            {
              text: 'To export without selecting rows: select at least one row to reveal the Selection Toolbar, then click Export.',
              subSteps: [
                'Export Current View -- exports only the columns currently visible in the table.',
                'Export All Columns -- exports every available column regardless of visibility settings.',
              ],
            },
            {
              text: 'The export runs server-side. A notification appears when the file is ready to download.',
              note: 'The export reflects the current filter state. Apply all required filters before starting the export.',
            },
          ],
        },
        {
          title: 'How do I flag a transaction as disputed?',
          items: [
            {
              text: 'Select the invoice row(s) you want to dispute using the row checkboxes.',
              note: 'All selected rows must belong to the same customer account for the Actions menu to appear.',
            },
            {
              text: 'Click Actions in the Selection Toolbar, then select Flag as Disputed.',
            },
            {
              text: 'Fill in the dispute type and reason in the dialog that appears, then click Save.',
            },
          ],
        },
        {
          title: 'How do I allocate an unallocated receipt?',
          items: [
            {
              text: 'Click the Unallocated Receipts KPI card to filter the list to receipts with an outstanding balance.',
            },
            {
              text: 'Select one or more unallocated receipts using the row checkboxes.',
            },
            {
              text: 'Click Actions in the Selection Toolbar, then select Allocate Receipt.',
              note: 'The Allocate Receipt option only appears when every selected row is an unallocated receipt (Outstanding Amount > 0). Mixed selections of invoices and receipts will not show this option.',
            },
            {
              text: 'You will be taken to the Allocation Workspace for the selected receipt where you can match it to open invoices.',
            },
          ],
        },
      ],
      proTips: [
        'Pin the Overdue filter preset and use it at the start of each day. Sort by Outstanding Amount descending to see the highest-value overdue invoices first and prioritise your outreach.',
        'Use the Currency filter in the Filter Sheet alongside the Currency Toggle to isolate a foreign-currency ledger -- set the Currency filter to a single currency, then toggle to Customer Currency so all amounts display in that currency for easy review.',
        'The Outstanding Balance Min/Max filter in the Filter Sheet is separate from the Gross Amount filter. Use the Outstanding Balance range to find invoices with a specific remaining balance -- useful for identifying part-paid invoices where the remainder is above a threshold.',
        'When a customer calls about a statement discrepancy, use the Customer filter in the Filter Sheet to show only their transactions, then sort by Transaction Date descending to walk through the ledger chronologically with them on the call.',
      ],
    },
    faq: [
      {
        question: 'What does the Outstanding Amount column show?',
        answer:
          'It shows the unpaid balance remaining on the transaction after any part-payments, credit note allocations, or receipt allocations have been applied. A value of zero means the transaction has been fully settled. For receipts, it shows how much of the receipt is still unallocated to an invoice.',
      },
      {
        question: 'What does the At Risk KPI card include?',
        answer:
          'It includes all invoices that are already overdue plus invoices with a due date falling within the next 7 days. Use it as a forward-looking collection list to contact customers before invoices become overdue.',
      },
      {
        question: 'What do the flag icons in the Flags column mean?',
        answer:
          'Three icons can appear: a red triangle means an active dispute has been raised against the transaction; a blue coin means the customer has given a promise to pay; an orange briefcase means the transaction is part of an active collection case. Hover over any icon to see a tooltip.',
      },
      {
        question: 'What does the External Dispute column show?',
        answer:
          'It shows whether the transaction is marked as disputed in your external accounting system (Yes or No). This is separate from disputes created inside this application -- both can be true at the same time.',
      },
      {
        question: 'How do I see all transactions for one customer?',
        answer:
          'Open the Filter Sheet, use the Customer multi-select to pick the customer, and click Apply. Alternatively, navigate to the Customer Detail page and open the Transactions tab there -- it is pre-filtered to that customer.',
      },
      {
        question: 'Why is the transaction I am looking for not in the list?',
        answer:
          'The page loads with the Outstanding Transactions preset active, which excludes completed (fully paid) transactions. To find a paid transaction:',
        steps: [
          'Click the Filters button to open the Filter Sheet.',
          'In the Status field, add "Completed".',
          'Clear the Outstanding Balance Min field (remove the 0.01 default).',
          'Click Apply.',
        ],
      },
      {
        question: 'Can I delete a transaction?',
        answer:
          'No. Transactions cannot be deleted from within this application. Deletions, reversals, or corrections must be made in your source accounting system. The change will appear here on the next integration sync.',
      },
      {
        question: 'How do I create a collection case from a transaction?',
        answer:
          'Select one or more invoice rows that belong to the same customer using the row checkboxes. Click Actions in the Selection Toolbar, then select Create Collection Case. Complete the case details in the drawer and save.',
      },
      {
        question: 'How do I record a promise to pay against a transaction?',
        answer:
          'Select one or more invoice rows from the same customer. Click Actions in the Selection Toolbar, then select Create Promise to Pay. Enter the promised date and amount in the drawer and save. The transaction will show the blue promise flag afterwards.',
      },
      {
        question: 'Can I see which invoices a receipt has been allocated to?',
        answer:
          'Yes. Select the receipt row, click Actions in the Selection Toolbar, and select View Allocation History -- this option appears when the selected row has at least one allocation recorded.',
      },
      {
        question: 'How do I export only the columns I can see on screen?',
        answer:
          'Select at least one row to reveal the Selection Toolbar. Click Export, then choose Export Current View. Only the columns currently visible in the table (as configured via the Column Settings button) will be included.',
      },
      {
        question: 'What does the default Outstanding Transactions preset do?',
        answer:
          'It sets the Outstanding Balance Min filter to 0.01, which excludes any transaction with a zero or negative outstanding balance (i.e., fully paid invoices and fully allocated receipts). This is the default state when you first open the page. You can modify the filters without overwriting the preset -- it will be restored when you click Clear Filters.',
      },
    ],
    relatedPages: [
      { label: 'Customers', path: '/customers' },
      { label: 'Disputes', path: '/disputes/all' },
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Customer Detail', path: '/customers/:id' },
    ],
  },

  payments: {
    pageTitle: 'Payments Dashboard',
    description:
      'The Payments Dashboard is the entry point for all payment-related tasks including receipts, allocation, reconciliation, and exception handling.',
    functionality: {
      overview:
        'This page provides navigation to each area of the payments module. Use the sub-menu to move between Receipts, OCR Remittance, Direct Debits, Card Payments, Allocation Workspace, Record Payment, Payment History, Unapplied Payments, Payment Exceptions, and Reconciliation.',
      keyFeatures: [
        { label: 'Receipts Management', detail: 'View and process incoming payments before they are allocated to invoices.' },
        { label: 'Allocation Workspace', detail: 'Match receipts to open invoices using a visual drag-and-drop or table-based interface.' },
        { label: 'Unapplied Payments Tracking', detail: 'Monitor receipts that have arrived but have not yet been matched to any invoice.' },
        { label: 'Payment Exceptions', detail: 'Review and resolve payments where the amount does not match any invoice or where matching rules failed.' },
        { label: 'Reconciliation', detail: 'Match bank statement entries against recorded receipts to confirm the ledger matches the bank.' },
      ],
      sectionsAndFields: [
        { name: 'Payments Sub-Menu', description: 'Navigation links to each payment sub-module: Receipts, OCR Remittance, Direct Debits, Card Payments, Allocation Workspace, Record Payment, Payment History, Unapplied Payments, Payment Exceptions, and Reconciliation.' },
      ],
      useCases: [
        'Starting the daily payment allocation run by navigating to the Allocation Workspace.',
        'Uploading a bank statement for reconciliation at month end.',
        'Reviewing payment exceptions that were flagged overnight.',
        'Recording a manual cash or cheque payment that arrived outside the automated feed.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Payments from the left navigation. The sub-menu on the left (or top tabs) takes you to each payment function. Start with Receipts to see what has come in today.',
      steps: [
        {
          title: 'How do I navigate to a specific payment function?',
          items: [
            { text: 'Click Payments in the left navigation.' },
            { text: 'Select the sub-page you need from the payments sub-menu -- for example, Receipts or Allocation Workspace.' },
          ],
        },
      ],
      proTips: [
        'Start each morning in Unapplied Payments to see overnight receipts before moving to the Allocation Workspace.',
        'If a customer disputes a payment, check Payment History first to confirm whether the receipt was received and allocated.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between a receipt and a payment?',
        answer: 'A receipt is the money that arrives from a customer. A payment is the act of allocating that receipt to one or more invoices. A receipt can sit unapplied (received but not yet allocated) until you match it in the Allocation Workspace.',
      },
      {
        question: 'How do I record a manual payment?',
        answer: 'Go to Payments > Record Payment and fill in the payment details including customer, amount, date, and method.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'Unapplied Payments', path: '/payments/unapplied' },
      { label: 'Reconciliation', path: '/payments/reconciliation' },
    ],
  },

  receipts: {
    pageTitle: 'Receipts',
    description:
      'A full list of customer payments received, showing how much of each receipt has been allocated to invoices and providing a direct route into the Allocation Workspace for unmatched amounts.',
    functionality: {
      overview:
        'The Receipts page shows every payment receipt across all customer accounts. Each row tells you the receipt number, the customer it came from, the currency, when it was received, the gross amount, how much is still unallocated, and the current allocation status. Use the four KPI cards at the top to quickly focus on receipts that need action -- Unallocated, Partially Allocated, Fully Allocated, or the full list. From any row with an outstanding balance you can go directly into the Allocation Workspace to match the receipt to open invoices.',
      keyFeatures: [
        {
          label: 'KPI Summary Cards',
          detail: 'Four cards -- Total Receipts, Fully Allocated, Partially Allocated, and Unallocated -- each show the total value and count for that allocation state. Click a card to filter the table to that subset; click again to clear the filter.',
        },
        {
          label: 'Allocation Status Filter',
          detail: 'A Status dropdown in the toolbar lets you restrict the list to Fully Allocated, Partially Allocated, or Unallocated without using the KPI cards. Selecting a value in the dropdown also updates the active KPI card highlight.',
        },
        {
          label: 'Currency Filter',
          detail: 'A Currency dropdown filters the list to receipts recorded in a specific currency. The available options are drawn from currencies present in the receipt data.',
        },
        {
          label: 'Search Bar',
          detail: 'Free-text search across receipt number and customer name. The list updates as you type.',
        },
        {
          label: 'Column Configuration',
          detail: 'Click the column settings icon to show or hide columns. Visible by default: Receipt #, Account Number, Customer, Currency, Receipt Date, Gross Amount, Unallocated Amount, Status, Actions. Hidden by default: Allocated Amount, Created By, Created At, Modified By, Modified At.',
        },
        {
          label: 'Column Pinning & Reordering',
          detail: 'Right-click (or use the three-dot menu) on any column header to pin it to the left or right side of the table so it stays visible when scrolling. Drag column headers to reorder them.',
        },
        {
          label: 'Column Resizing',
          detail: 'Drag the right edge of any column header to resize that column. Use Reset Column Widths from the column header menu to restore defaults.',
        },
        {
          label: 'Quick Allocate Button',
          detail: 'Every row with a non-zero Unallocated Amount shows an Allocate button. Clicking it navigates directly to the Allocation Workspace pre-loaded with that receipt.',
        },
        {
          label: 'Export',
          detail: 'Click the download icon in the toolbar to export the current filtered view to Excel. The export runs server-side and reflects the active search, status, and currency filters. A second export option (triggered from the same icon area) exports all available columns regardless of visibility settings.',
        },
        {
          label: 'Server-Side Filtering, Sorting & Pagination',
          detail: 'Filtering, sorting, and pagination are all handled server-side. Large receipt datasets do not require loading all records into the browser. Use the Rows per Page selector (10, 25, 50, 100) and the page controls in the footer to navigate.',
        },
      ],
      sectionsAndFields: [
        { name: 'Total Receipts card', description: 'Shows the total value and count of all receipts matching the current search and currency filters. Clicking it clears the KPI allocation filter and shows all receipts.' },
        { name: 'Fully Allocated card', description: 'Clickable KPI showing the total value and count of receipts where the full gross amount has been matched to invoices. Click to filter the table to fully allocated receipts only.' },
        { name: 'Partially Allocated card', description: 'Clickable KPI showing the total value and count of receipts where some -- but not all -- of the gross amount has been matched. Click to filter to partially allocated receipts.' },
        { name: 'Unallocated card', description: 'Clickable KPI showing the total value and count of receipts where no amount has been matched to any invoice. Click to filter to unallocated receipts.' },
        { name: 'Search bar', description: 'Free-text search across receipt number and customer name. Results update as you type.' },
        { name: 'Status dropdown', description: 'Filters the list to Fully Allocated, Partially Allocated, or Unallocated receipts. Selecting a status also highlights the corresponding KPI card.' },
        { name: 'Currency dropdown', description: 'Restricts the list to receipts in a specific currency. Populated from currencies present in the data.' },
        { name: 'Export icon', description: 'Triggers a server-side Excel export of the currently filtered receipt list using the visible columns.' },
        { name: 'Column Settings icon', description: 'Opens the Column Configuration dialog to show, hide, or reorder columns.' },
        { name: 'Receipt # column', description: 'The unique reference number for the receipt from the accounting system.' },
        { name: 'Account Number column', description: 'The customer account number associated with this receipt.' },
        { name: 'Customer column', description: 'The name of the customer account the receipt belongs to.' },
        { name: 'Currency column', description: 'The currency in which the receipt was recorded.' },
        { name: 'Receipt Date column', description: 'The date the payment was received.' },
        { name: 'Gross Amount column', description: 'The full value of the receipt as received. Shown in the receipt\'s own currency.' },
        { name: 'Allocated Amount column (hidden by default)', description: 'The portion of the gross amount that has been matched to one or more invoices.' },
        { name: 'Unallocated Amount column', description: 'The remaining unmatched portion of the receipt. Shown in red when greater than zero.' },
        { name: 'Status column', description: 'Allocation state badge: Fully Allocated (teal), Partially Allocated (amber), or Unallocated (red).' },
        { name: 'Actions column', description: 'Shows an Allocate button on any row where the Unallocated Amount is greater than zero. Clicking it opens the Allocation Workspace for that receipt.' },
        { name: 'Column header three-dot menu', description: 'Per-column context menu offering: Pin to Left, Pin to Right, Unpin Column, Reset Column Widths, Reset Column Order.' },
        { name: 'Pagination footer', description: 'Shows current page and total pages. Includes Rows per Page selector (10 / 25 / 50 / 100) and page navigation buttons.' },
      ],
      useCases: [
        'Reviewing all unallocated receipts at the start of the day to process yesterday\'s bank payments before the morning team meeting.',
        'Finding a specific customer payment by receipt number when the customer emails to confirm they have paid.',
        'Identifying partially allocated receipts where a customer underpaid an invoice and deciding whether to chase the balance or write it off.',
        'Exporting all unallocated receipts filtered to a specific currency for a foreign-currency reconciliation report.',
        'Using the Currency filter to isolate receipts in a non-base currency before end-of-month foreign-exchange revaluation.',
        'Checking whether a receipt from a specific customer has been fully allocated before issuing a new statement.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Payments -> Receipts in the left navigation. Receipts are synced automatically from your accounting system -- the integration is configured in Settings -> Company when a new company is added, and data begins syncing automatically from that point. If the list is empty, confirm that the company setup has been completed and the initial sync has run.',
      steps: [
        {
          title: 'How do I find all receipts that need allocating?',
          items: [
            {
              text: 'Click the Unallocated KPI card at the top of the page.',
              tip: 'The card shows a count next to the value -- use this as your daily workload indicator.',
            },
            {
              text: 'The table updates immediately to show only receipts with a zero allocation. The card shows a ring highlight to confirm the filter is active.',
              note: 'If you also want partially allocated receipts, use the Status dropdown in the toolbar and select Partially Allocated -- or click the Partially Allocated KPI card separately.',
            },
            {
              text: 'Sort by Receipt Date ascending to process the oldest receipts first.',
            },
          ],
        },
        {
          title: 'How do I allocate a receipt to invoices?',
          items: [
            {
              text: 'Find the receipt row in the list. Unallocated and partially allocated receipts show an Allocate button in the Actions column.',
            },
            {
              text: 'Click the Allocate button on the row.',
              note: 'The Allocate button only appears when the Unallocated Amount is greater than zero. Fully allocated receipts do not show this button.',
            },
            {
              text: 'You are taken to the Allocation Workspace with this receipt pre-loaded. Select the invoices to match and confirm the allocation.',
            },
            {
              text: 'When you return to the Receipts page, the receipt\'s status and Unallocated Amount will reflect the allocation you just made.',
            },
          ],
        },
        {
          title: 'How do I search for a specific receipt?',
          items: [
            {
              text: 'Type the receipt number or a few characters of the customer name into the Search bar in the toolbar.',
            },
            {
              text: 'The list filters as you type. Clear the search field to return to the full list.',
              note: 'The search works alongside any active KPI card filter or Status dropdown selection -- both are applied at the same time. If you are not finding the receipt, clear the KPI filter by clicking Total Receipts.',
            },
          ],
        },
        {
          title: 'How do I filter receipts by currency?',
          items: [
            {
              text: 'Click the Currency dropdown in the toolbar.',
            },
            {
              text: 'Select the currency code you want to isolate (e.g., USD, EUR).',
            },
            {
              text: 'The list and the KPI card totals both update to reflect only receipts in that currency.',
              note: 'The KPI card values are always shown in the base currency of your company. When a Currency filter is active, the KPI totals reflect only the filtered receipts, but amounts are still converted to base currency for display.',
            },
          ],
        },
        {
          title: 'How do I configure which columns are visible?',
          items: [
            {
              text: 'Click the column settings icon (grid icon) at the right end of the toolbar.',
            },
            {
              text: 'In the Column Configuration dialog, tick or untick columns to show or hide them.',
              tip: 'Enable the Allocated Amount column if you want to see how much of a partial receipt has already been matched, alongside the remaining Unallocated Amount.',
            },
            {
              text: 'Click Apply to save your preferences. Column visibility is saved per user and persists across sessions.',
            },
          ],
        },
        {
          title: 'How do I pin a column so it stays visible when I scroll?',
          items: [
            {
              text: 'Hover over the column header to reveal the three-dot menu icon on the right side of the header cell.',
            },
            {
              text: 'Click the three-dot menu and select Pin to Left or Pin to Right.',
            },
            {
              text: 'The column moves to the fixed side of the table and a shadow indicates it is pinned. Select Unpin Column from the same menu to release it.',
              tip: 'Pin the Receipt # and Customer columns to the left if you need to cross-reference values in the right-most amount columns on a wide table.',
            },
          ],
        },
        {
          title: 'How do I export receipts to Excel?',
          items: [
            {
              text: 'Apply any filters (KPI card, Status, Currency, Search) that you want the export to reflect.',
            },
            {
              text: 'Click the download icon (arrow pointing down) in the toolbar.',
              subSteps: [
                'The default export uses visible columns only -- as configured in the Column Settings dialog.',
                'To export all available columns, use the Export All Columns option if shown in the export dropdown.',
              ],
            },
            {
              text: 'A notification appears when the file is ready to download. The export runs server-side and handles large datasets without freezing the page.',
              note: 'The export reflects the current filter state. Apply all required filters before starting the export.',
            },
          ],
        },
      ],
      proTips: [
        'Click the Unallocated KPI card every morning and sort by Receipt Date ascending -- oldest unallocated receipts first. Work through them in order to keep your unapplied balance as low as possible.',
        'Use the Currency filter combined with the Unallocated card to isolate foreign-currency receipts. Process these before end-of-month revaluation to avoid artificial exchange gains or losses on the unallocated balance.',
        'If a receipt appears as Partially Allocated and you cannot see what it was matched against, navigate to the Allocation Workspace for that receipt.',
        'Pin the Receipt # column to the left and the Actions column to the right when working on a large multi-currency receipt list -- this keeps the row identifier and the Allocate button visible regardless of horizontal scrolling.',
      ],
    },
    faq: [
      {
        question: 'What does Unallocated mean?',
        answer:
          'A receipt is Unallocated when none of its gross amount has been matched to any invoice. The full value sits as an open credit on the customer account until it is allocated in the Allocation Workspace.',
      },
      {
        question: 'What does Partially Allocated mean?',
        answer:
          'A receipt is Partially Allocated when some -- but not all -- of its gross amount has been matched to invoices. The remaining unmatched amount shows in the Unallocated Amount column (in red) and still needs to be matched.',
      },
      {
        question: 'What does Fully Allocated mean?',
        answer:
          'A receipt is Fully Allocated when its entire gross amount has been matched to one or more invoices. The Unallocated Amount column shows zero and the Allocate button no longer appears on that row.',
      },
      {
        question: 'Why is the Allocate button not showing on a row?',
        answer:
          'The Allocate button only appears when the Unallocated Amount is greater than zero. If the receipt is Fully Allocated, the button is hidden because there is nothing left to match.',
      },
      {
        question: 'Can I delete a receipt?',
        answer:
          'No. Receipts cannot be deleted from within this application. Deletions, reversals, or corrections must be made in your source accounting system. The change will appear here on the next integration sync.',
      },
      {
        question: 'Why is a receipt I am looking for not in the list?',
        answer:
          'The most common reasons are: (1) the receipt was imported in a different currency -- try clearing the Currency filter; (2) an active KPI card filter is hiding it -- click the Total Receipts card to show all receipts; (3) the integration sync has not run yet -- check Settings -> Company for the last sync time.',
      },
      {
        question: 'What do the KPI card totals represent when a Currency filter is active?',
        answer:
          'When a Currency filter is active, the KPI card values show only receipts in the selected currency, but the amounts are converted to your company base currency for display. This means the KPI totals change when you switch currencies -- they always reflect the filtered subset.',
      },
      {
        question: 'Can I sort the receipts list by any column?',
        answer:
          'Yes. Click any column header that shows an arrow icon (Receipt #, Customer, Receipt Date, Gross Amount, Unallocated Amount, Status) to sort ascending. Click again to sort descending. Click a third time to clear the sort and return to the default order.',
      },
      {
        question: 'How do I see receipts for just one customer?',
        answer:
          'Type the customer name into the Search bar. The list filters to receipts matching that customer. For a full ledger view of a specific customer including their receipts, navigate to the Customer Detail page and open the Transactions tab.',
      },
    ],
    relatedPages: [
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'OCR Remittance', path: '/payments/ocr-remittance' },
      { label: 'Transactions', path: '/transactions' },
      { label: 'Customers', path: '/customers' },
    ],
  },

  'ocr-remittance': {
    pageTitle: 'OCR Remittance',
    description:
      'The OCR Remittance page lets you upload remittance advice documents and automatically extract the payment details using optical character recognition.',
    functionality: {
      overview:
        'When a customer emails a remittance advice PDF or image, upload it here. The system uses OCR to read the document and extract the invoice numbers and amounts the customer intended to pay. You then review the extracted data, correct any errors, and confirm the allocation.',
      keyFeatures: [
        { label: 'Document Upload', detail: 'Upload PDFs, images, or scanned remittance documents for automatic data extraction.' },
        { label: 'OCR Extraction', detail: 'The system reads the document and identifies invoice references, amounts, and customer details.' },
        { label: 'Review and Correct', detail: 'See the extracted data alongside the original document and correct any misread values before confirming.' },
        { label: 'Auto-Allocation', detail: 'After confirming the extracted data, allocate the matching receipt directly from this screen.' },
      ],
      sectionsAndFields: [
        { name: 'Upload Area', description: 'Drag-and-drop zone or file selector for uploading remittance documents.' },
        { name: 'Document Preview', description: 'Shows the uploaded document so you can compare it against the extracted data.' },
        { name: 'Extracted Data Table', description: 'Displays invoice references and amounts the OCR identified in the document.' },
        { name: 'Confidence Score', description: 'Indicates how confident the OCR engine is in each extracted value. Low scores need manual verification.' },
        { name: 'Confirm Button', description: 'Accepts the extracted data and proceeds to allocation.' },
      ],
      useCases: [
        'Processing a remittance advice emailed by a customer who pays multiple invoices in one bank transfer.',
        'Handling a scanned paper remittance that was posted by the customer.',
        'Speeding up allocation when a customer sends a detailed remittance but the bank transfer reference gives no invoice detail.',
      ],
    },
    userGuide: {
      gettingStarted:
        'You need a remittance advice document from the customer and an unallocated receipt in the system that corresponds to the payment. Upload the document here to extract the allocation detail.',
      steps: [
        {
          title: 'How do I upload a remittance document?',
          items: [
            { text: 'Click the upload area or drag and drop the document file onto it.' },
            { text: 'Supported formats: PDF, JPG, PNG, TIFF.' },
            { text: '', note: 'Very small or low-resolution scans may produce poor extraction results. Use a minimum 150 DPI scan if possible.' },
          ],
        },
        {
          title: 'How do I review and correct extracted data?',
          items: [
            { text: 'After upload, the extracted data table appears next to the document preview.' },
            { text: 'Compare each extracted invoice number and amount against the document.' },
            { text: 'Click any cell in the extracted data table to edit it manually.' },
            { text: '', tip: 'Low confidence scores (shown in amber or red) flag values the system is uncertain about -- prioritise checking these first.' },
          ],
        },
        {
          title: 'How do I confirm and allocate?',
          items: [
            { text: 'Once all extracted values look correct, click Confirm.' },
            { text: 'Select the corresponding receipt from the dropdown.' },
            { text: 'Click Allocate to match the receipt to the extracted invoice references.' },
          ],
        },
      ],
      proTips: [
        'Save the customer\'s remittance email as a PDF before uploading -- PDF extraction is generally more accurate than JPG or PNG from email screenshots.',
        'If OCR regularly misreads a specific customer\'s remittance format, raise this with your system administrator -- custom extraction templates can be configured for recurring formats.',
      ],
    },
    faq: [
      {
        question: 'What file formats are supported for OCR upload?',
        answer: 'PDF, JPG, PNG, and TIFF are supported. PDF typically gives the best results.',
      },
      {
        question: 'What happens if the OCR extracts the wrong invoice numbers?',
        answer: 'Edit the incorrect values directly in the extracted data table before confirming. Do not confirm until all values are correct -- corrections after confirmation require reversing the allocation.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
    ],
  },

  'direct-debits': {
    pageTitle: 'Direct Debits',
    description:
      'The Direct Debits page manages direct debit mandates that authorise automated payment collection from customer bank accounts.',
    functionality: {
      overview:
        'Set up and manage direct debit mandates for customers who have agreed to pay by automated bank debit. This page shows all active, pending, and cancelled mandates and allows you to schedule collection runs and track failures.',
      keyFeatures: [
        { label: 'Mandate Management', detail: 'Create, view, and cancel direct debit mandates for customer bank accounts.' },
        { label: 'Collection Scheduling', detail: 'Schedule when direct debits are collected -- on invoice due date or on a fixed day each month.' },
        { label: 'Failed Payment Tracking', detail: 'See which direct debits were returned by the bank so you can follow up with the customer.' },
        { label: 'Mandate Status Monitoring', detail: 'Track whether mandates are Active, Pending, Failed, or Cancelled.' },
      ],
      sectionsAndFields: [
        { name: 'Customer', description: 'The customer account the mandate belongs to.' },
        { name: 'Bank Account Name', description: 'The name on the bank account to be debited.' },
        { name: 'Sort Code / Account Number', description: 'The bank account details for the mandate.' },
        { name: 'Mandate Status', description: 'Active: ready to collect. Pending: awaiting bank confirmation. Failed: bank rejected the collection. Cancelled: no longer active.' },
        { name: 'Collection Schedule', description: 'When the direct debit will be collected -- on invoice due date or fixed date.' },
        { name: 'Last Collection', description: 'Date and amount of the most recent collection.' },
        { name: 'Cancel Button', description: 'Cancels an active mandate. This action cannot be undone.' },
      ],
      useCases: [
        'Setting up a new direct debit after a customer agrees to pay by automated debit during a collection call.',
        'Investigating a failed collection before calling the customer to request an alternative payment.',
        'Cancelling a mandate when a customer closes their account or switches payment method.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Before creating a mandate, you need the customer\'s written authorisation and their bank account details. Your business must also have a Direct Debit service user number (SUN) configured in Settings > Integrations.',
      steps: [
        {
          title: 'How do I create a new mandate?',
          items: [
            { text: 'Click Create Mandate.' },
            { text: 'Select the customer.' },
            { text: 'Enter the bank account name, sort code, and account number.' },
            { text: 'Select the collection schedule.' },
            { text: 'Click Save.' },
            { text: '', note: 'Mandates take 3-5 business days to activate after the bank confirms them. The mandate will show as Pending until then.' },
          ],
        },
        {
          title: 'How do I handle a failed direct debit?',
          items: [
            { text: 'Filter the mandate list by Status = Failed.' },
            { text: 'Click the failed mandate row to see the failure reason from the bank.' },
            { text: 'Contact the customer to arrange an alternative payment or to confirm their bank details are correct.' },
            {
              text: 'Once the customer confirms, cancel the failed mandate and create a new one with corrected details if needed.',
              subSteps: [
                'If the customer wants to retry with the same account: click Retry Collection on the mandate.',
                'If the account details were wrong: cancel the mandate and create a new one with the correct details.',
              ],
            },
          ],
        },
      ],
      proTips: [
        'Run a weekly check on mandates with Pending status -- if a mandate has been pending for more than 7 days, contact the bank to confirm receipt of the setup instruction.',
        'Failed direct debits often indicate a customer account has been closed or has insufficient funds -- flag these customers in Collections for proactive follow-up.',
      ],
    },
    faq: [
      {
        question: 'How long does it take to set up a direct debit mandate?',
        answer: 'Mandates take 3-5 business days to become active after the customer authorisation is submitted to the bank.',
      },
      {
        question: 'Can I retry a failed collection without creating a new mandate?',
        answer: 'Yes, click Retry Collection on the failed mandate record. Note that repeated failed retries may result in the bank suspending the mandate.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Payment History', path: '/payments/history' },
      { label: 'Integrations', path: '/integrations' },
    ],
  },

  'card-payments': {
    pageTitle: 'Card Payments',
    description:
      'The Card Payments page allows you to process customer card payments directly or send a secure payment link for the customer to pay online.',
    functionality: {
      overview:
        'Use this page to take card payments over the phone or to generate a payment link to send to the customer by email. All transactions are processed through a PCI-DSS compliant payment gateway and logged automatically.',
      keyFeatures: [
        { label: 'Phone Card Payment', detail: 'Enter card details provided verbally by the customer to take immediate payment.' },
        { label: 'Payment Link', detail: 'Generate a secure URL to email to the customer so they can pay online at their convenience.' },
        { label: 'Transaction History', detail: 'View a log of all card transactions processed through this page.' },
        { label: 'Refund Management', detail: 'Initiate refunds for card transactions where applicable.' },
      ],
      sectionsAndFields: [
        { name: 'Customer', description: 'The customer account this payment is being taken for.' },
        { name: 'Amount', description: 'The amount to charge to the card.' },
        { name: 'Invoice Reference', description: 'Optional reference to link the card payment to one or more invoices.' },
        { name: 'Card Details Form', description: 'Card number, expiry date, and CVV fields -- only shown in manual entry mode and handled securely.' },
        { name: 'Send Link Button', description: 'Generates a payment link for the specified amount and opens the email composer.' },
        { name: 'Transaction History Table', description: 'List of all card transactions with date, amount, status, and authorisation code.' },
      ],
      useCases: [
        'Taking a card payment over the phone when a customer calls to pay an overdue invoice immediately.',
        'Sending a payment link to a customer who cannot pay by bank transfer.',
        'Processing a partial card payment to reduce an overdue balance while the customer arranges the remainder.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Card payment processing requires a payment gateway to be configured in Settings > Integrations. Contact your system administrator if the card payment option is not available.',
      steps: [
        {
          title: 'How do I take a card payment over the phone?',
          items: [
            { text: 'Select the customer from the dropdown.' },
            { text: 'Enter the payment amount.' },
            { text: 'Optionally link to an invoice reference.' },
            { text: 'Click Process Payment and enter the card details as the customer reads them to you.' },
            { text: 'Click Charge to process.' },
            { text: '', note: 'Card details entered here are sent directly to the payment gateway -- they are never stored in the credit control system.' },
          ],
        },
        {
          title: 'How do I send a payment link?',
          items: [
            { text: 'Select the customer and enter the amount.' },
            { text: 'Click Send Payment Link.' },
            { text: 'Review the pre-populated email to the customer\'s email address.' },
            { text: 'Click Send.' },
            { text: '', tip: 'Payment links expire after 72 hours by default. If a customer needs a new link after expiry, generate a new one from this page.' },
          ],
        },
      ],
      proTips: [
        'Use payment links instead of phone card payments where possible -- it removes the risk of handling card data verbally and reduces PCI compliance scope.',
        'Check the Transaction History table before calling a customer about a payment link -- they may have already paid but the allocation has not been processed yet.',
      ],
    },
    faq: [
      {
        question: 'Are card payments secure?',
        answer: 'Yes. All card payments are processed via a PCI-DSS compliant gateway. Card numbers are never stored in the credit control system.',
      },
      {
        question: 'How long does a payment link remain valid?',
        answer: 'Payment links expire after 72 hours by default. Generate a new link if the customer needs one after expiry.',
      },
      {
        question: 'Can I process a refund from this page?',
        answer: 'Yes. Find the original transaction in the Transaction History table, click on it, and select Refund. Refunds may take 3-5 business days to appear on the customer\'s statement.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Payment History', path: '/payments/history' },
      { label: 'Integrations', path: '/integrations' },
    ],
  },

  'allocation-workspace': {
    pageTitle: 'Allocate Receipt',
    description:
      'The Allocate page lets you match an individual receipt against one or more open invoices, with full control over the amount applied to each transaction.',
    functionality: {
      overview:
        'Opened from the Receipts list, this page is dedicated to a single receipt. An Allocation Summary card shows the receipt amount, how much has been allocated so far, and how much remains. Below it, a full transaction table lists all open invoices and credit notes for the same customer. Tick the checkbox next to any transaction to include it in the allocation, type a precise amount into the Allocated Amount column if you want a partial match, and click Allocate & Confirm when ready. Automated tools — Auto-Allocate Promises and Auto-Allocate Oldest First — are available to apply the receipt in one click.',
      keyFeatures: [
        {
          label: 'Allocation Summary Card',
          detail: 'Always-visible panel showing Receipt Amount, Allocated Amount (with a colour-coded progress bar), and Remaining Amount so you always know where you stand.',
        },
        {
          label: 'Receipt Details Card',
          detail: 'Displays the receipt reference, customer name, payment date, and currency so you can confirm you are allocating against the correct payment before making any changes.',
        },
        {
          label: 'Inline Editable Allocated Amount',
          detail: 'Each invoice row has a currency input in the Allocated Amount column. Selecting the checkbox auto-fills the full invoice balance; you can override this with any partial amount up to the current balance.',
        },
        {
          label: 'Auto-Allocate Promises',
          detail: 'Automatically selects invoices that have an active Promise-to-Pay (shown with a P2P badge) and allocates the receipt against them first.',
        },
        {
          label: 'Auto-Allocate Oldest First',
          detail: 'Automatically fills allocation amounts starting from the oldest overdue invoice and working forward until the receipt is fully used.',
        },
        {
          label: 'Clear Allocation',
          detail: 'Removes all current allocation amounts and checkbox selections in one click without saving anything, letting you start over.',
        },
        {
          label: 'Currency View Toggle',
          detail: 'Switch the table between Receipt Currency (the currency the payment was made in) and Home Currency (your company base currency) to check the allocation in either denomination.',
        },
        {
          label: 'Column Controls',
          detail: 'Sort, resize, pin, and reorder table columns to suit your workflow. Column state is preserved within the session.',
        },
        {
          label: 'Search & Filter',
          detail: 'Filter the invoice list by transaction number, description, or type to locate specific items quickly in long transaction lists.',
        },
        {
          label: 'Export',
          detail: 'Download the current invoice table as a CSV or Excel file for offline reference or reconciliation.',
        },
        {
          label: 'Pagination',
          detail: 'Navigate through long invoice lists with configurable rows-per-page (10, 25, 50, 100) and page controls at the bottom of the table.',
        },
        {
          label: 'Confirm Allocation Dialog',
          detail: 'Before finalising, a summary dialog shows the Allocation Date (editable), Receipt Amount, Total Allocated, and Remaining amount so you can review the split before committing.',
        },
      ],
      sectionsAndFields: [
        { name: 'Back to Receipts', description: 'Returns you to the Receipts list without saving. Any checkbox selections or amount entries are discarded.' },
        { name: 'Receipt Amount (Summary)', description: 'The total value of the receipt being allocated, shown in the receipt\'s original currency.' },
        { name: 'Allocated Amount (Summary)', description: 'Running total of the amounts entered across all selected invoice rows. The progress bar fills as you allocate.' },
        { name: 'Remaining Amount (Summary)', description: 'Receipt Amount minus Allocated Amount. Turns amber when positive (unallocated balance remains) and green when fully allocated.' },
        { name: 'Receipt Reference', description: 'The bank or payment reference associated with this receipt, shown in the Receipt Details card.' },
        { name: 'Customer Name', description: 'The customer account the receipt belongs to. Only invoices for this customer appear in the table.' },
        { name: 'Transaction Number', description: 'The invoice or credit note identifier. Rows marked P2P have an active Promise-to-Pay against them.' },
        { name: 'Transaction Type', description: 'Badge showing the document type — Invoice, Credit Note, Debit Note, etc.' },
        { name: 'Due Date', description: 'Dates in red indicate overdue invoices. Sorting by this column is the quickest way to find the oldest debt.' },
        { name: 'Gross', description: 'The full face value of the invoice or credit note.' },
        { name: 'Outstanding', description: 'The outstanding balance remaining on the invoice after any previous part-payments.' },
        { name: 'Allocated Amount (Column)', description: 'Editable currency field. Ticking the checkbox fills this automatically; type a different value for a partial allocation.' },
        { name: 'Currency Toggle', description: 'Dropdown at the top-right of the table: Receipt Currency or Home Currency.' },
        { name: 'Search Box', description: 'Filters rows in real-time by transaction number, description, or type.' },
        { name: 'Allocation Date (Dialog)', description: 'The date that will be stamped on the allocation record. Defaults to today; change it if back-dating is required.' },
      ],
      useCases: [
        'Matching a bank receipt to the exact invoice it was paid against after a customer quotes a single invoice reference.',
        'Splitting a lump-sum payment across several overdue invoices using Auto-Allocate Oldest First.',
        'Partially allocating a short payment to the oldest invoice and leaving the remainder unallocated for investigation.',
        'Clearing invoices that have a Promise-to-Pay in one click using Auto-Allocate Promises.',
        'Allocating a multi-currency receipt and verifying both the foreign-currency and home-currency amounts before confirming.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Payments > Receipts, locate the receipt you want to allocate, and click the Allocate action. The Allocate page opens pre-loaded with that receipt\'s details and the customer\'s open transactions.',
      steps: [
        {
          title: 'How do I allocate a receipt to one invoice?',
          items: [
            { text: 'Check the Allocation Summary card at the top — confirm the Receipt Amount and customer are correct.' },
            { text: 'In the transaction table, find the invoice you want to allocate against. Use the search box to filter by invoice number if the list is long.' },
            { text: 'Tick the checkbox on the invoice row. The Allocated Amount field fills automatically with the full invoice balance.' },
            {
              text: 'Review the Remaining Amount in the summary card.',
              note: 'If the receipt is smaller than the invoice balance, the Allocated Amount field will be capped at the receipt amount — the invoice will remain partially open.',
            },
            { text: 'Click Allocate & Confirm. A summary dialog appears — verify the Allocation Date and amounts, then click Confirm Allocation.' },
          ],
        },
        {
          title: 'How do I split a receipt across multiple invoices?',
          items: [
            { text: 'Tick the checkboxes for each invoice you want to include. Allocated Amount fills automatically for each.' },
            { text: 'Watch the Allocated Amount and Remaining Amount in the summary card update as you tick each row.' },
            {
              text: 'If the combined invoice balances exceed the receipt amount, the last-selected invoice\'s Allocated Amount will be reduced to the available remainder.',
              subSteps: [
                'You can manually edit any Allocated Amount field by clicking into it and typing a different value.',
                'The maximum you can enter on any row is the invoice\'s Current Balance.',
              ],
            },
            { text: 'Once Remaining Amount shows zero (or the amount you intend to leave unallocated), click Allocate & Confirm.' },
          ],
        },
        {
          title: 'How do I use Auto-Allocate?',
          items: [
            { text: 'Click Auto-Allocate Promises to automatically fill allocation amounts for all invoices with a Promise-to-Pay (P2P badge) first, then remaining balance to older invoices.' },
            { text: 'Alternatively, click Auto-Allocate Oldest First to fill amounts starting from the oldest due date and working forward until the receipt is fully used.' },
            { text: 'Review the filled amounts in the table — you can still manually adjust any Allocated Amount field before confirming.' },
            { text: 'Click Allocate & Confirm to proceed to the confirmation dialog.' },
            { text: '', tip: 'If the auto-allocation does not suit your needs, click Clear Allocation to reset all fields and start again manually.' },
          ],
        },
        {
          title: 'How do I do a partial allocation?',
          items: [
            { text: 'Tick the invoice checkbox — the Allocated Amount field fills to the full invoice balance.' },
            { text: 'Click the Allocated Amount field on that row and type a lower value.' },
            { text: 'The invoice will be partially settled — it will remain open with a reduced outstanding balance.' },
            {
              text: 'The Remaining Amount in the summary card will reflect the unapplied portion of the receipt.',
              note: 'Unallocated receipt balances will appear in the Unapplied Payments list until fully allocated.',
            },
            { text: 'Click Allocate & Confirm to save the partial allocation.' },
          ],
        },
      ],
      proTips: [
        'Use Auto-Allocate Oldest First for routine daily cash application — it clears the most overdue debt first and completes in a single click for straightforward receipts.',
        'The P2P badge on a transaction row indicates a customer has promised to pay that invoice. Use Auto-Allocate Promises to prioritise these and keep Promise-to-Pay tracking accurate.',
        'Switch the Currency Toggle to Home Currency before confirming if you need to verify the sterling equivalent of a foreign-currency allocation for your records.',
        'Sort by Due Date ascending to quickly spot the oldest overdue invoices if you prefer to allocate manually rather than using the auto tools.',
        'If a credit note appears in the table with a negative balance, ticking it will reduce your Allocated Amount — this is correct behaviour for applying credit against the receipt.',
        'The Allocation Date in the confirmation dialog defaults to today. If you are posting a backdated receipt, change this date before clicking Confirm Allocation.',
      ],
    },
    faq: [
      {
        question: 'Can I allocate one receipt to multiple invoices?',
        answer: 'Yes. Tick the checkbox on each invoice row you want to include. The Allocation Summary card updates in real time to show the running total and remaining balance. You can allocate to as many invoices as needed, provided the total allocated amount does not exceed the receipt amount.',
      },
      {
        question: 'What is the difference between Auto-Allocate Promises and Auto-Allocate Oldest First?',
        answer: 'Auto-Allocate Promises prioritises invoices that have an active Promise-to-Pay recorded against them, ensuring those commitments are honoured first. Auto-Allocate Oldest First ignores promise status and simply works through invoices from the oldest due date to the most recent until the receipt is used up. Both approaches fully allocate the receipt if there is enough invoice balance to absorb it.',
      },
      {
        question: 'Why can\'t I allocate more than the Current Balance of an invoice?',
        answer: 'The Allocated Amount field is capped at the invoice\'s Current Balance to prevent over-allocation. If you need to apply a surplus to a different invoice, the remaining amount will be visible in the Remaining Amount field and can be ticked against another open transaction.',
      },
      {
        question: 'What happens to any amount I leave unallocated?',
        answer: 'The unallocated portion of the receipt remains as an unapplied payment and will appear in the Unapplied Payments list. You can return to allocate it at any time by opening the receipt from the Receipts list and clicking Allocate again.',
      },
      {
        question: 'Can I change the allocation after confirming it?',
        answer: 'Once an allocation has been confirmed it cannot be changed within this system. Reversals must be processed in the Finance system. Please contact your finance team to action any reversal before re-allocating the receipt.',
      },
      {
        question: 'Why is a credit note appearing in the invoice table?',
        answer: 'Credit notes are included because they represent money owed back to the customer and can offset the receipt during allocation. Ticking a credit note row will reduce the Allocated Amount by the credit note value — this is the correct way to net off a credit when matching a payment.',
      },
      {
        question: 'The receipt currency is different from my invoices — is that a problem?',
        answer: 'Multi-currency allocations are supported. Use the Currency Toggle at the top of the table to switch between Receipt Currency and Home Currency views. The system handles the conversion automatically using the exchange rates configured in the system; you do not need to convert amounts manually.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Unapplied Payments', path: '/payments/unapplied' },
      { label: 'Payment History', path: '/payments/history' },
      { label: 'Transactions', path: '/transactions' },
    ],
  },

  'record-payment': {
    pageTitle: 'Record Payment',
    description:
      'The Record Payment page lets you manually enter a payment received outside the automated bank feed -- such as a cash or cheque payment.',
    functionality: {
      overview:
        'Use this form to log payments that did not arrive via the bank feed integration -- for example, a cheque deposited at the bank, a cash payment collected in person, or a payment received through another channel. The recorded payment appears as a receipt in the system and can then be allocated to invoices.',
      keyFeatures: [
        { label: 'Manual Payment Entry', detail: 'Enter amount, date, payment method, and reference for any payment not captured automatically.' },
        { label: 'Multiple Payment Methods', detail: 'Supports Cash, Cheque, Bank Transfer, Card, and Other.' },
        { label: 'Immediate Allocation Option', detail: 'Optionally allocate the payment to specific invoices as part of the same recording step.' },
        { label: 'Payment Reference Tracking', detail: 'Record a reference or cheque number for audit and traceability.' },
      ],
      sectionsAndFields: [
        { name: 'Customer', description: 'The customer account this payment was received from.' },
        { name: 'Payment Date', description: 'The date the payment was received -- not the date you are entering it.' },
        { name: 'Amount', description: 'The total value of the payment received.' },
        { name: 'Payment Method', description: 'How the payment was made: Cash, Cheque, Bank Transfer, Card, or Other.' },
        { name: 'Reference', description: 'A cheque number, bank reference, or other identifier for audit purposes.' },
        { name: 'Notes', description: 'Optional free-text field for additional context -- for example, where a cash payment was received.' },
        { name: 'Allocate Now Checkbox', description: 'When ticked, opens the invoice selection panel so you can allocate the payment immediately after recording.' },
      ],
      useCases: [
        'Recording a cheque received by post before it has cleared at the bank.',
        'Logging a cash payment collected from a customer at a branch or trade counter.',
        'Entering a payment received through an external payment portal that does not feed automatically into the system.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Payments > Record Payment. You need the customer\'s name, the payment amount, the date it was received, and the payment method.',
      steps: [
        {
          title: 'How do I record a manual payment?',
          items: [
            { text: 'Select the customer from the dropdown -- type the first few characters to search.' },
            { text: 'Enter the Payment Date (the date money was received, not today\'s date).' },
            { text: 'Enter the Amount.' },
            { text: 'Select the Payment Method.' },
            { text: 'Enter a Reference if available (e.g., cheque number).' },
            { text: 'Click Save to record without allocating, or tick Allocate Now to allocate immediately.' },
            { text: '', note: 'If you save without allocating, the payment appears in Unapplied Payments and can be allocated later in the Allocation Workspace.' },
          ],
        },
      ],
      proTips: [
        'Always enter the date the payment was physically received -- not the date you are entering it into the system. This keeps the aging analysis accurate.',
        'Use the Notes field to record where you received a cash payment and who handed it over -- this creates an audit trail if the payment is ever queried.',
      ],
    },
    faq: [
      {
        question: 'What payment methods can I record?',
        answer: 'Cash, Cheque, Bank Transfer, Card, and Other.',
      },
      {
        question: 'What happens after I record a payment?',
        answer: 'The payment appears as a receipt in the Receipts page and in Unapplied Payments. You can allocate it to invoices immediately or later in the Allocation Workspace.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'Unapplied Payments', path: '/payments/unapplied' },
    ],
  },

  'payment-history': {
    pageTitle: 'Payment History',
    description:
      'The Payment History page shows a complete record of all payments received and how they were allocated to invoices.',
    functionality: {
      overview:
        'This is a read-only audit log of all receipts and their allocation details. Use it to answer customer payment queries, to confirm a specific payment was received, or to investigate how a past allocation was made.',
      keyFeatures: [
        { label: 'Complete Payment Records', detail: 'Shows every receipt with amount, date, method, and full allocation detail.' },
        { label: 'Payment Method Breakdown', detail: 'Filter by payment method to analyse volumes by channel.' },
        { label: 'Allocation History', detail: 'See exactly which invoices each payment was allocated to and when.' },
        { label: 'Export', detail: 'Download payment history as CSV for external reconciliation or audit.' },
      ],
      sectionsAndFields: [
        { name: 'Date Filter', description: 'Restrict the list to payments within a specific date range.' },
        { name: 'Customer Filter', description: 'Show payments for one customer only.' },
        { name: 'Payment Method Filter', description: 'Filter by Cash, Cheque, Bank Transfer, Card, or all methods.' },
        { name: 'Receipt Date', description: 'When the payment was received.' },
        { name: 'Amount', description: 'Total value of the receipt.' },
        { name: 'Method', description: 'How the payment was made.' },
        { name: 'Allocated To', description: 'Invoice numbers the payment was matched to.' },
        { name: 'Allocation Date', description: 'When the payment was allocated to invoices.' },
        { name: 'Allocated By', description: 'The user who performed the allocation.' },
      ],
      useCases: [
        'Confirming to a customer that their payment was received and showing which invoices it was applied to.',
        'Investigating a historical allocation that may have been applied incorrectly.',
        'Running an end-of-month audit of all payments received during the period.',
        'Verifying payment volumes by method for management reporting.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Payment History from the Payments sub-menu. Use the date range and customer filters to narrow the list to the period or account you need.',
      steps: [
        {
          title: 'How do I find a specific payment?',
          items: [
            { text: 'Use the Customer filter to select the relevant customer.' },
            { text: 'Set a date range covering the period the payment was received.' },
            { text: 'Click the payment row to see full allocation details.' },
          ],
        },
        {
          title: 'How do I export payment history?',
          items: [
            { text: 'Apply the filters you want to export.' },
            { text: 'Click Export.' },
            { text: 'Select CSV or Excel and click Download.' },
          ],
        },
      ],
      proTips: [
        'When a customer disputes a payment, pull the history for their account and export it as a PDF to include with your response email.',
        'Filter by a single payment method (e.g., Cheque) and date range to produce a bank deposit summary for reconciliation.',
      ],
    },
    faq: [
      {
        question: 'How far back does payment history go?',
        answer: 'Payment history is retained as long as your data retention policy requires, typically at least 7 years for financial compliance.',
      },
      {
        question: 'Can I change a historical allocation from this page?',
        answer: 'No. Payment History is read-only. To reverse or change an allocation, contact your system administrator.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'Transactions', path: '/transactions' },
    ],
  },

  'unapplied-payments': {
    pageTitle: 'Unapplied Payments',
    description:
      'The Unapplied Payments page lists all receipts that have been received but not yet allocated to any invoice.',
    functionality: {
      overview:
        'Any receipt sitting in this list is money that has arrived but has not been matched to an outstanding invoice. Unapplied amounts distort your receivables position and can cause customers to receive incorrect chasers. Clear this list regularly to maintain an accurate ledger.',
      keyFeatures: [
        { label: 'Unapplied Receipt List', detail: 'Shows every receipt with no or partial allocation, grouped by customer.' },
        { label: 'Ageing of Unapplied Amounts', detail: 'Shows how long each receipt has been sitting unapplied so you can prioritise older items.' },
        { label: 'Quick Allocate', detail: 'One-click navigation to the Allocation Workspace pre-loaded with a specific receipt.' },
        { label: 'Customer-wise Grouping', detail: 'Receipts are grouped by customer so you can see total unapplied cash per account.' },
      ],
      sectionsAndFields: [
        { name: 'Customer', description: 'The customer the unapplied receipt belongs to.' },
        { name: 'Receipt Date', description: 'When the payment was received.' },
        { name: 'Amount', description: 'Total receipt value.' },
        { name: 'Unallocated Amount', description: 'The portion of the receipt not yet matched to any invoice.' },
        { name: 'Days Unapplied', description: 'How many days since the receipt arrived without being allocated.' },
        { name: 'Allocate Button', description: 'Opens the Allocation Workspace with this receipt pre-selected.' },
      ],
      useCases: [
        'Daily review of unallocated receipts before sending collection chasers -- to avoid chasing customers who have already paid.',
        'Month-end tidy-up to clear old unapplied amounts and ensure the closing receivables balance is accurate.',
        'Investigating a customer query about a payment they say they sent but which has not reduced their outstanding balance.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Payments > Unapplied Payments. Review the list and allocate each receipt to the correct invoices.',
      steps: [
        {
          title: 'How do I allocate an unapplied receipt?',
          items: [
            { text: 'Find the receipt in the list.' },
            { text: 'Click Allocate.' },
            { text: 'The Allocation Workspace opens with this receipt pre-loaded.' },
            { text: 'Select the matching invoices and confirm the allocation.' },
          ],
        },
        {
          title: 'How do I prioritise which receipts to allocate first?',
          items: [
            { text: 'Sort the list by Days Unapplied descending to see the oldest unmatched receipts first.' },
            {
              text: 'Alternatively, sort by Unallocated Amount descending to tackle the highest-value items first.',
              tip: 'Clear receipts from customers who are currently in the collection queue first -- unallocated cash can mean chasers are sent in error.',
            },
          ],
        },
      ],
      proTips: [
        'Run through the Unapplied Payments list every morning before the collection team starts making calls -- it prevents wasted calls to customers who have already paid.',
        'If a receipt has been unapplied for more than 30 days and you cannot match it to an invoice, contact the customer to confirm which invoices they intended to pay.',
      ],
    },
    faq: [
      {
        question: 'Why would a payment be unapplied?',
        answer: 'Payments arrive as unapplied when they come via the bank feed without a clear invoice reference, when automatic matching fails, or when a manual payment is recorded without immediate allocation.',
      },
      {
        question: 'Can I auto-allocate all unapplied payments at once?',
        answer: 'There is no single bulk auto-allocate button. However, the Allocation Workspace shows automatic match suggestions for each receipt to speed up the process.',
      },
    ],
    relatedPages: [
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Payment History', path: '/payments/history' },
    ],
  },

  'payment-exceptions': {
    pageTitle: 'Payment Exceptions',
    description:
      'The Payment Exceptions page lists receipts that could not be automatically processed -- such as overpayments, underpayments, or receipts with no matching customer.',
    functionality: {
      overview:
        'This page captures receipts that triggered an exception during automatic processing. Each exception includes a reason code explaining why it was flagged. Resolve exceptions by editing the receipt, matching it manually, writing off small differences, or escalating for investigation.',
      keyFeatures: [
        { label: 'Exception List with Reason Codes', detail: 'Each exception shows why it was flagged: overpayment, underpayment, unknown customer, or duplicate.' },
        { label: 'Resolution Workflows', detail: 'Different resolution options are offered depending on the exception type.' },
        { label: 'Audit Trail', detail: 'Every action taken on an exception is logged with user and timestamp.' },
        { label: 'Write-Off Option', detail: 'For small differences within the write-off tolerance, approve a write-off directly from this page.' },
      ],
      sectionsAndFields: [
        { name: 'Exception Type', description: 'The reason the receipt was flagged: Overpayment, Underpayment, Unknown Customer, Duplicate, or Unmatched Reference.' },
        { name: 'Receipt Date', description: 'When the payment arrived.' },
        { name: 'Amount', description: 'The amount of the receipt.' },
        { name: 'Expected Amount', description: 'The invoice amount the system expected (for over/underpayment exceptions).' },
        { name: 'Difference', description: 'The gap between the receipt amount and the expected amount.' },
        { name: 'Customer', description: 'The customer identified for this receipt, if one was matched.' },
        { name: 'Resolution Options', description: 'Actions available: Allocate Manually, Write Off Difference, Escalate, or Mark as Duplicate.' },
      ],
      useCases: [
        'Handling a customer who consistently underpays invoices by a small amount -- reviewing whether to write off the difference or chase.',
        'Resolving a receipt where the bank narrative contains no useful reference to identify the customer.',
        'Investigating a suspected duplicate payment before returning funds to the customer.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Payments > Payment Exceptions. Each row represents a receipt that needs manual review. Work through them in order of value or age.',
      steps: [
        {
          title: 'How do I resolve a payment exception?',
          items: [
            { text: 'Click on the exception row to open its details.' },
            { text: 'Review the exception type and the information provided.' },
            {
              text: 'Select the appropriate resolution action:',
              subSteps: [
                'Allocate Manually: opens the Allocation Workspace with this receipt pre-loaded.',
                'Write Off Difference: available for small differences within the configured tolerance. Requires approval if over the threshold.',
                'Escalate: flags the exception for a senior user or finance team to investigate.',
                'Mark as Duplicate: marks the receipt as a duplicate of a previously processed payment.',
              ],
            },
            { text: 'Click Confirm to apply the resolution.' },
          ],
        },
      ],
      proTips: [
        'Set a daily review of Payment Exceptions -- letting them accumulate makes reconciliation harder and risks customers receiving incorrect statements.',
        'For recurring underpayment exceptions from the same customer, consider updating the invoice amount or raising a credit note to clear the difference permanently.',
      ],
    },
    faq: [
      {
        question: 'What causes payment exceptions?',
        answer: 'Exceptions occur when a receipt amount does not match any open invoice exactly, when no customer can be identified from the bank narrative, when a receipt appears to be a duplicate, or when automatic matching rules fail.',
      },
      {
        question: 'Can I write off any difference from this page?',
        answer: 'Write-offs up to a configured tolerance can be approved here. Differences above the tolerance require escalation to a senior user or finance manager.',
      },
    ],
    relatedPages: [
      { label: 'Allocation Workspace', path: '/payments/allocation-workspace' },
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Transactions', path: '/transactions' },
    ],
  },

  reconciliation: {
    pageTitle: 'Reconciliation',
    description:
      'The Reconciliation page lets you import a bank statement and match it against recorded receipts to confirm the ledger matches the bank.',
    functionality: {
      overview:
        'Upload a bank statement file and the system will attempt to match each bank line to a receipt in the system. Matched items clear automatically; unmatched items require manual review. Use this at month end to confirm that every receipt in the system corresponds to a real bank entry and vice versa.',
      keyFeatures: [
        { label: 'Bank Statement Import', detail: 'Upload CSV, OFX, or QIF format bank statement files.' },
        { label: 'Automatic Matching', detail: 'The system matches bank lines to receipts by amount, date, and reference.' },
        { label: 'Discrepancy Identification', detail: 'Items that cannot be matched are highlighted for manual review.' },
        { label: 'Reconciliation Report', detail: 'Download a reconciliation report showing matched, unmatched, and discrepant items.' },
      ],
      sectionsAndFields: [
        { name: 'Statement Upload Area', description: 'Drag-and-drop or file selector for the bank statement file.' },
        { name: 'Statement Lines Table', description: 'Each row from the bank statement listed with date, description, and amount.' },
        { name: 'Match Status', description: 'Matched, Unmatched, or Discrepant for each statement line.' },
        { name: 'Corresponding Receipt', description: 'The receipt in the system the bank line has been matched to.' },
        { name: 'Unmatched Items Panel', description: 'Rows from the statement that have not been matched to any receipt.' },
        { name: 'Reconciliation Summary', description: 'Totals of matched, unmatched, and discrepant amounts at the top of the page.' },
      ],
      useCases: [
        'Month-end reconciliation to confirm the accounts receivable ledger matches the bank statement.',
        'Identifying receipts in the bank that have not been recorded in the system.',
        'Finding receipts recorded in the system that do not appear on the bank statement (e.g., timing differences).',
      ],
    },
    userGuide: {
      gettingStarted:
        'Download your bank statement in CSV, OFX, or QIF format from your bank\'s online portal. Then open Payments > Reconciliation and upload the file.',
      steps: [
        {
          title: 'How do I import a bank statement?',
          items: [
            { text: 'Click the upload area or drag the statement file onto it.' },
            { text: 'Select the bank account the statement belongs to.' },
            { text: 'Click Import.' },
            { text: '', note: 'Supported formats: CSV, OFX, QIF. Contact your system administrator if your bank uses a different format.' },
          ],
        },
        {
          title: 'How do I review and resolve unmatched items?',
          items: [
            { text: 'After import, the Unmatched Items panel shows bank lines with no corresponding receipt.' },
            {
              text: 'Click each unmatched item and select one of the resolution options:',
              subSteps: [
                'Match to Existing Receipt: search for the receipt and link it manually.',
                'Create New Receipt: record the payment if it was not previously entered in the system.',
                'Mark as Not Applicable: for bank charges or interest entries that do not relate to customer receipts.',
              ],
            },
          ],
        },
        {
          title: 'How do I generate a reconciliation report?',
          items: [
            { text: 'After resolving all discrepancies, click Download Report.' },
            { text: 'The report includes matched, unmatched, and discrepant items with totals.' },
            { text: '', tip: 'Run the report before resolving all items to capture the initial unreconciled position for audit purposes.' },
          ],
        },
      ],
      proTips: [
        'Reconcile weekly rather than monthly -- smaller, frequent reconciliations are faster and catch issues before they compound.',
        'If a receipt appears in the system but not on the bank statement, check whether it was recorded with the wrong date or is a timing difference that will appear on the following month\'s statement.',
      ],
    },
    faq: [
      {
        question: 'What file formats are supported for bank statements?',
        answer: 'CSV, OFX, and QIF are supported. For CSV files, the columns must match the expected format -- download the template from the import screen if unsure.',
      },
      {
        question: 'What happens if I import the same statement twice?',
        answer: 'The system checks for duplicate imports by statement date range and will warn you before proceeding. Confirm carefully to avoid creating duplicate matched entries.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Payment History', path: '/payments/history' },
      { label: 'Unapplied Payments', path: '/payments/unapplied' },
    ],
  },

  'collections-dashboard': {
    pageTitle: 'Collections Dashboard',
    description:
      'A team-level view of collection case status, weekly collection trends, promise reliability, and individual collector performance -- all filterable by team member, date range, and currency.',
    functionality: {
      overview:
        'The Collections Dashboard is designed for collections managers and team leads. It shows how many cases are in each status (Active, Promised, Escalated, On Hold, Closed this month), how much has been collected week by week over a chosen period, how reliably the team\'s promises to pay are being honoured, and how each credit controller is performing against their caseload. Use it to identify workload imbalances, spot promise reliability problems, and track whether collection activity is trending up or down over time.',
      keyFeatures: [
        {
          label: 'Case Status Cards',
          detail: 'Five clickable cards -- Active, Promised, Escalated, On Hold, Closed this month -- each showing the count and total value of cases in that state. Click any card to navigate directly to the Collections Queue filtered to that status.',
        },
        {
          label: 'Team Member Filter',
          detail: 'A dropdown in the header lets you scope all KPIs, charts, and tables to a single credit controller or to the whole team (Everyone). The selection is saved to local storage so it persists between sessions.',
        },
        {
          label: 'Date Range Filter',
          detail: 'A calendar date-range picker scopes the Weekly Collections Trend chart, Promise Breakdown donut, and Promises to Pay table to a specific period. Defaults to the last 90 days.',
        },
        {
          label: 'Currency Filter',
          detail: 'A dropdown in the header lets you select the currency for all monetary values across the dashboard -- KPI card values, the Weekly Collections Trend chart, the Promise Breakdown donut, the Promises to Pay table, and the Team Overview. Defaults to the base currency of the selected company.',
        },
        {
          label: 'Weekly Collections Trend Chart',
          detail: 'A line chart showing the amount collected per calendar week within the selected date range, with summary metrics for Total Collected, Average per Week, and a week-on-week Trend percentage.',
        },
        {
          label: 'Promise Breakdown Donut',
          detail: 'A donut chart showing the distribution of promises by status (Active, Closed, Broken) as a percentage within the selected period. Hover segments for invoice count and total value.',
        },
        {
          label: 'Promises to Pay Table',
          detail: 'A scrollable table of active and broken promises within the selected period showing customer, credit controller, status badge, days overdue, and promised amount. Includes a View all link to the full Promises page with the same filters pre-applied.',
        },
        {
          label: 'Team Overview',
          detail: 'One card per credit controller showing their active case count, amount collected, outstanding balance, and cases closed within the selected period. A totals bar at the bottom sums all four metrics across the team.',
        },
        {
          label: 'Clickable Case Status Navigation',
          detail: 'Each of the five status cards navigates to the Collections Queue pre-filtered to that status. If a team member filter is active, the queue is also filtered to that person.',
        },
      ],
      sectionsAndFields: [
        { name: 'Active card', description: 'Count and total value of collection cases currently in Active status. Click to open the Collections Queue filtered to Active cases.' },
        { name: 'Promised card', description: 'Count and total value of cases where the customer has made a promise to pay. Click to open the Collections Queue filtered to Promised cases.' },
        { name: 'Escalated card', description: 'Count and total value of cases that have been escalated for senior review. Click to open the Collections Queue filtered to Escalated cases.' },
        { name: 'On Hold card', description: 'Count and total value of cases currently paused -- for example, while a dispute is being resolved. Click to open the Collections Queue filtered to On Hold cases.' },
        { name: 'Closed this month card', description: 'Count and total value recovered from cases closed in the current calendar month. Click to open the Collections Queue filtered to Closed cases.' },
        { name: 'Date Range picker', description: 'Sets the period for the trend chart, promise donut, and promises table. Select any custom start and end date using the two-month calendar popover.' },
        { name: 'Team Member dropdown', description: 'Scopes all dashboard data to a single credit controller or Everyone. Saved to local storage between sessions.' },
        { name: 'Currency dropdown', description: 'Selects the currency used for all monetary amounts on the dashboard. Affects KPI card values, the Weekly Collections Trend chart, the Promise Breakdown donut, the Promises to Pay table, and the Team Overview. Defaults to the base currency of the selected company.' },
        { name: 'Weekly Collections Trend -- Total Collected', description: 'The sum of all amounts collected within the selected date range, for the selected team member or everyone.' },
        { name: 'Weekly Collections Trend -- Average per Week', description: 'Total collected divided by the number of weeks in the selected period.' },
        { name: 'Weekly Collections Trend -- Trend', description: 'The percentage change between the last two data points in the chart. Green when positive, red when negative.' },
        { name: 'Weekly Collections Trend -- Line chart', description: 'Each data point is one calendar week. The Y-axis shows the amount collected that week in thousands. Hover a point for the exact figure.' },
        { name: 'Promise Breakdown -- donut chart', description: 'Each coloured segment represents a promise status. The percentage shown is the proportion of all promises in that status within the selected period.' },
        { name: 'Promise Breakdown -- legend', description: 'Lists each status name with its colour swatch and percentage. Hover the donut to see invoice count and total value for each segment.' },
        { name: 'Promises to Pay -- table', description: 'Rows represent individual promises. Columns: Customer (with account number), Credit Controller, Status badge, Days Overdue (red when overdue), Amount.' },
        { name: 'Promises to Pay -- View all link', description: 'Navigates to the Promises page filtered to Active and Broken statuses, scoped to the selected team member and date range.' },
        { name: 'Team Overview -- member card', description: 'One row per credit controller. Shows name, Active cases count, Collected amount, Outstanding amount, and Closed count within the selected period.' },
        { name: 'Team Overview -- View cases link', description: 'On each member card. Navigates to the Collections Queue filtered to Active cases for that credit controller.' },
        { name: 'Team Overview -- totals bar', description: 'Aggregated row at the bottom of Team Overview. Sums Active cases, Total collected, Total outstanding, and Closed this period across all team members.' },
      ],
      useCases: [
        'A collections manager checking the team\'s position first thing in the morning -- reviewing case status counts, last week\'s collection trend, and which promises are overdue.',
        'Identifying a credit controller with a high active case count but low collected amount within the period to have a performance conversation or reassign cases.',
        'Reviewing promise reliability after a month-end push -- checking the Promise Breakdown donut to see what proportion of promises were Broken versus Closed.',
        'Using the Date Range picker to compare this quarter\'s collection trend against last quarter by adjusting the period.',
        'Drilling into Escalated cases by clicking the Escalated card to see which high-value accounts need immediate manager attention.',
        'Preparing a weekly management report by reviewing the Team Overview totals bar for a single-line summary of the team\'s performance.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Collections -> Dashboard in the left navigation. The page defaults to Everyone and the last 90 days. If you manage a specific team, use the Team Member dropdown to scope the view, or leave it on Everyone to see the full team position. Data is live from the collections system -- no manual refresh is needed.',
      steps: [
        {
          title: 'How do I check the team\'s current case position?',
          items: [
            {
              text: 'Review the five status cards at the top of the page: Active, Promised, Escalated, On Hold, and Closed this month.',
            },
            {
              text: 'Each card shows a count (number of cases) and a total value. The value uses your company\'s base currency.',
              note: 'The Closed this month card always shows cases closed in the current calendar month regardless of the Date Range filter -- it is not affected by the date range picker.',
            },
            {
              text: 'Click any card to navigate to the Collections Queue pre-filtered to that status.',
              tip: 'If a Team Member filter is active, the queue navigation carries that filter through -- you land on the queue already scoped to that person\'s cases.',
            },
          ],
        },
        {
          title: 'How do I scope the dashboard to a single credit controller?',
          items: [
            {
              text: 'Click the Team Member dropdown in the top-right area of the header.',
            },
            {
              text: 'Select a credit controller\'s name from the list, or select Everyone to return to the full-team view.',
              note: 'Your selection is saved to local storage. The next time you open this page, it will default to the same person you last selected.',
            },
            {
              text: 'All five status cards, the trend chart, the promise donut, and the promises table update immediately to reflect only that person\'s data. The Team Overview section always shows all team members regardless of this filter.',
            },
          ],
        },
        {
          title: 'How do I change the date range for trend and promise data?',
          items: [
            {
              text: 'Click the date range button in the header. It shows the current period (e.g. "Jan 01, 2025 - Mar 31, 2025").',
            },
            {
              text: 'A two-month calendar popover opens. Click a start date, then click an end date to define the range.',
              tip: 'For a month-by-month comparison, set the range to exactly one calendar month -- the weekly chart will then show only that month\'s weeks, making it easy to compare peak and quiet periods.',
            },
            {
              text: 'Click outside the popover to close it. The trend chart, promise donut, and promises table all update to the new period.',
              note: 'The five status cards at the top always show the live current position and are not affected by the date range filter.',
            },
          ],
        },
        {
          title: 'How do I change the currency for dashboard amounts?',
          items: [
            {
              text: 'Click the Currency dropdown in the header (it shows the currently selected currency code and name, e.g. "GBP - British Pound Sterling").',
            },
            {
              text: 'Type to search or scroll the list, then click a currency to select it.',
            },
            {
              text: 'All monetary values across the dashboard update immediately -- KPI card values, the Weekly Collections Trend chart amounts, the Promise Breakdown donut totals, the Promises to Pay table amounts, and the Team Overview collected and outstanding figures.',
              note: 'The Currency filter affects all five data areas including Team Overview, unlike the Team Member filter which does not affect Team Overview.',
            },
          ],
        },
        {
          title: 'How do I read the Weekly Collections Trend chart?',
          items: [
            {
              text: 'Scroll to the Weekly Collections Trend card. The three summary metrics above the chart -- Total Collected, Average per Week, and Trend -- summarise the selected period.',
            },
            {
              text: 'The Trend figure shows the percentage change between the last two data points. A green value means the most recent week collected more than the previous week; red means less.',
              note: 'Trend shows -- (a dash) when there are fewer than two data points in the selected range, or when the previous week\'s value is zero.',
            },
            {
              text: 'Hover any data point on the line chart to see the exact week number and amount collected for that week.',
            },
            {
              text: 'The Y-axis values are in thousands (e.g., "50K" means GBP50,000). The exact currency is shown in the hover tooltip.',
            },
          ],
        },
        {
          title: 'How do I understand the Promise Breakdown donut?',
          items: [
            {
              text: 'Scroll to the Promise Overview section. The donut chart on the left shows promise status distribution as percentages within the selected date range.',
            },
            {
              text: 'Each segment colour corresponds to a promise status shown in the legend below the chart: Active, Closed, Broken.',
              note: 'A high Broken percentage relative to Closed indicates that customers are not honouring their commitments -- review the Promises to Pay table for the specific accounts.',
            },
            {
              text: 'Hover a donut segment to see the invoice count and total value for that status.',
            },
          ],
        },
        {
          title: 'How do I review the Promises to Pay table?',
          items: [
            {
              text: 'The Promises to Pay table sits to the right of the donut chart and shows individual promises with their current status.',
            },
            {
              text: 'Rows with a Broken badge indicate the payment date has passed without the promised payment being received. The Days Overdue column shows in red for these rows.',
              tip: 'To see only Broken promises, click View all -> to open the full Promises page where you can filter by status -- the dashboard table cannot be sorted or filtered independently.',
            },
            {
              text: 'Click View all -> at the top right of the card to open the full Promises page pre-filtered to Active and Broken statuses within the selected date range and team member.',
            },
          ],
        },
        {
          title: 'How do I review individual credit controller performance?',
          items: [
            {
              text: 'Scroll to the Team Overview section. Each credit controller appears as a card showing: Active cases, Collected, Outstanding, and Closed counts for the selected period.',
            },
            {
              text: 'Compare the Collected and Outstanding columns across team members to identify where the most value is sitting unrecovered.',
              note: 'The Team Member filter at the top of the page does not affect the Team Overview section -- it always shows all team members. Use the Team Member filter to scope the charts, then scroll to Team Overview to compare that person against the rest of the team.',
            },
            {
              text: 'Click View cases -> on any member\'s card to open the Collections Queue filtered to that person\'s Active cases.',
            },
            {
              text: 'Review the totals bar at the bottom of Team Overview for a team-wide summary: total active cases, total collected, total outstanding, and total closed this period.',
            },
          ],
        },
      ],
      proTips: [
        'Keep the Team Member filter on Everyone and scan the Team Overview section first each morning. The member with the highest Outstanding and lowest Collected ratio in the period needs the most attention.',
        'Set the Date Range to the current week on Monday morning. The Weekly Trend chart will build day by day through the week, giving you a live view of whether the team is on track against last week\'s average.',
        'A rising Broken percentage in the Promise Breakdown donut is an early warning sign. Drill into the Promises to Pay table, filter by Broken, and contact those customers the same day the promise date passes -- response rates drop sharply after 48 hours.',
        'Use the View cases -> link on a credit controller\'s Team Overview card immediately after spotting a high active-case count. The Collections Queue opens pre-filtered to their cases so you can see whether any are overdue for contact.',
      ],
    },
    faq: [
      {
        question: 'What does the Active case count on the status cards include?',
        answer:
          'Active cases are collection cases with a status of Active in the Collections Queue -- cases that are currently being worked and are not On Hold, Escalated, or Closed. The value shown is the sum of all overdue balances across those cases.',
      },
      {
        question: 'What does the Closed this month card show?',
        answer:
          'It shows the count and total recovered value of cases that were moved to Closed status during the current calendar month. This card is not affected by the Date Range filter -- it always reflects the current month.',
      },
      {
        question: 'How does the Trend percentage in the Weekly Collections Trend section get calculated?',
        answer:
          'The Trend is the percentage change between the last two weekly data points in the chart. If the most recent week collected GBP120,000 and the previous week collected GBP100,000, the trend shows +20%. It shows -- when there are fewer than two data points or the previous week\'s value is zero.',
      },
      {
        question: 'Why does the donut chart show no data?',
        answer:
          'The Promise Breakdown donut is empty when there are no promises recorded within the selected date range for the selected team member. Try widening the date range or switching the Team Member filter to Everyone.',
      },
      {
        question: 'What does a high Broken percentage in the Promise Breakdown mean?',
        answer:
          'It means a significant proportion of customers who committed to a payment date did not pay by that date. Use the Promises to Pay table to identify the specific accounts and follow up immediately. A persistently high Broken rate may indicate the team is accepting promises too easily or not following up when they are broken.',
      },
      {
        question: 'Can I drill into the data behind the Weekly Collections Trend chart?',
        answer:
          'Not directly from the chart. To see the individual cases and payments contributing to a week\'s collection figure, navigate to the Collections Queue and filter by date range and status to match the period you are investigating.',
      },
      {
        question: 'How do I filter the Promises to Pay table to show only Broken promises?',
        answer:
          'The table on this page shows both Active and Broken promises and cannot be filtered independently. Click View all -> at the top right of the Promises to Pay card -- this opens the full Promises page where you can filter by status, date range, and credit controller.',
        steps: [
          'Click View all -> in the Promises to Pay card header.',
          'On the Promises page, open the Status filter.',
          'Select Broken.',
          'The table updates to show only broken promises.',
        ],
      },
      {
        question: 'Why does the Team Overview show different figures from the status cards?',
        answer:
          'The five status cards show the live current count of cases in each status -- they are not date-range filtered. The Team Overview Collected and Closed figures are scoped to the selected date range. If you are looking at a short date range, a collector may show zero Closed in the period even though their Active case count is high.',
      },
      {
        question: 'Can I export the Collections Dashboard data?',
        answer:
          'There is no export button on this page. To export case data, navigate to the Collections Queue and use the Export function there. To export promise data, go to Collections -> Promises and export from that page.',
      },
      {
        question: 'What does the Team Member filter affect?',
        answer:
          'Selecting a team member scopes the five status cards, the Weekly Collections Trend chart, the Promise Breakdown donut, and the Promises to Pay table to that person\'s data. The Team Overview section always shows all team members regardless of the Team Member filter.',
      },
      {
        question: 'What does the Currency filter affect?',
        answer:
          'Selecting a currency updates all monetary values across the entire dashboard: the KPI card values (Active, Promised, Escalated, On Hold, Closed this month), the Weekly Collections Trend chart amounts, the Promise Breakdown donut totals, the Promises to Pay table amounts, and the Team Overview collected and outstanding figures. Unlike the Team Member filter, the Currency filter affects Team Overview as well. It defaults to the base currency of the selected company.',
      },
    ],
    relatedPages: [
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Promises to Pay', path: '/collections/promises' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
      { label: 'Dashboard', path: '/' },
    ],
  },

  'collections-queue': {
    pageTitle: 'Collections Queue',
    description:
      'A prioritised list of your collection cases showing each account that needs a contact action, the next scheduled step, and the outstanding balance -- used to work through daily collection activity.',
    functionality: {
      overview:
        'The Collections Queue is the primary workspace for day-to-day collection activity. Each row is a collection case assigned to you or your team, ordered by priority, amount, and days in collection. You can search, filter, and sort the list, then act on individual cases -- logging calls, recording promises, sending emails, or opening the full Case Details page. Bulk actions let you reassign or update the status of multiple cases at once. Five KPI cards at the top show a live count of cases across key categories based on your current filters.',
      keyFeatures: [
        { label: 'KPI Summary Cards', detail: 'Five cards show My Cases, Promised, High Priority, Due Today, and Total Outstanding -- each reflecting the current filter state so you know your workload at a glance.' },
        { label: 'Search Bar', detail: 'Search across case ID, customer name, and account number to locate a specific case without changing your active filters.' },
        { label: 'Currency Toggle', detail: 'Switch between Case Currency (each case shown in its own currency) and Base Currency (all amounts converted to your company currency) to compare values across multi-currency portfolios.' },
        { label: 'Filters Panel', detail: 'Open the Filters sheet to scope the queue by Status (Active, Promised, Escalated, On Hold, Closed), Priority (High, Medium, Low), Assigned User, and Currency -- independently or in combination.' },
        { label: 'Column Configuration', detail: 'Show or hide any of the 18 available columns, reorder them by drag-and-drop, resize column widths, and pin columns to the left or right edge of the table.' },
        { label: 'Sortable Columns', detail: 'Click any column header to sort ascending, click again for descending, click a third time to remove the sort. Sort by priority, amount, days in collection, action due date, status, strategy, stage, or any other visible column.' },
        { label: 'Single-Case Action Toolbar', detail: 'Select exactly one case using its checkbox and the floating toolbar shows Log Call, Send Email, and Record Promise -- for completing actions without leaving the queue.' },
        { label: 'Bulk Actions Toolbar', detail: 'Select one or more cases using the checkboxes to reveal the floating toolbar. With a single case selected you also get Log Call, Send Email, and Record Promise. With multiple cases selected, use Reassign User or Update Status to act across all selected cases at once.' },
        { label: 'Export', detail: 'Export visible columns or all columns to Excel. The export applies your active search and filter state so it reflects exactly what is on screen.' },
        { label: 'Create New Case', detail: 'Open the case creation form directly from the queue header when a new case needs to be raised manually.' },
      ],
      sectionsAndFields: [
        { name: 'My Cases KPI card', description: 'Total count of cases matching your current filters.' },
        { name: 'Promised KPI card', description: 'Count of cases with status Promised -- customer has committed to a payment date.' },
        { name: 'High Priority KPI card', description: 'Count of cases with Priority set to High -- these require immediate attention.' },
        { name: 'Due Today KPI card', description: 'Count of cases where the next action is scheduled for today.' },
        { name: 'Total Outstanding KPI card', description: 'Sum of outstanding amounts across all cases matching your filters. Shown in base currency unless a single currency filter is active.' },
        { name: 'Search field', description: 'Free-text search across case ID, customer name, and account number.' },
        { name: 'Currency Toggle', description: 'Switches the Amount column and Total Outstanding KPI between Case Currency and Base Currency.' },
        { name: 'Filters button', description: 'Opens the Filters sheet. Displays a badge with the count of active filters when any are applied.' },
        { name: 'Export button', description: 'Dropdown offering Export Current View (visible columns only) or Export All Columns.' },
        { name: 'Column Settings button', description: 'Opens the column configuration dialog to show/hide, reorder, and lock columns.' },
        { name: 'Case Number column', description: 'Unique case identifier. Click to open the full Case Details page for that case.' },
        { name: 'Priority column', description: 'Badge showing High (red), Medium (blue), or Low (grey) -- set by the collection strategy or manually overridden.' },
        { name: 'Customer column', description: 'The customer account name this case covers.' },
        { name: 'Currency column', description: 'The trading currency of the case. Shows base currency label when Currency Toggle is set to Base Currency.' },
        { name: 'Amount column', description: 'Outstanding balance for the case. Respects the Currency Toggle setting.' },
        { name: 'Days column', description: 'Number of days the case has been in the collections workflow.' },
        { name: 'Last Action column', description: 'Hover the info icon to see the type, description, and date of the most recent action logged against the case.' },
        { name: 'Next Action column', description: 'The next step defined by the collection strategy for this case.' },
        { name: 'Action Due Date column', description: 'Date the next action is due. Shown in red if overdue, amber if due today.' },
        { name: 'Status column', description: 'Current case status badge: Active, Promised, Escalated, On Hold, or Closed. If Promised, shows the promise due date below the badge.' },
        { name: 'Collection Strategy column', description: 'Name of the strategy driving the case workflow. Visible by default.' },
        { name: 'Stage column', description: 'Progress bar showing how far through the strategy phases the case has reached (e.g. 2/5). Hover for the phase name.' },
        { name: 'Filters sheet -- Status', description: 'Dropdown to filter by Active, Promised, Escalated, On Hold, or Closed.' },
        { name: 'Filters sheet -- Priority', description: 'Dropdown to filter by High, Medium, or Low priority.' },
        { name: 'Filters sheet -- Assigned User', description: 'Dropdown to scope the queue to cases assigned to a specific collector.' },
        { name: 'Filters sheet -- Currency', description: 'Multi-select to show only cases in one or more specific currencies.' },
        { name: 'Bulk Action toolbar', description: 'Appears at the bottom of the screen when one or more cases are selected. Shows selection count and total outstanding value. When exactly one case is selected: Log Call, Send Email, Record Promise, Reassign User, Update Status. When multiple cases are selected: Reassign User and Update Status only.' },
        { name: 'Pagination footer', description: 'Shows current page and total pages. Select rows per page (10, 25, 50, or 100) and navigate with Previous / Next / page number buttons.' },
      ],
      useCases: [
        'Starting the day by filtering the queue to your own name and sorting by Action Due Date ascending to work through overdue contacts first.',
        'A manager scoping the queue to all Escalated cases to decide which need senior intervention today.',
        'Filtering by High priority and Due Today to build a call list for the morning session.',
        'Selecting all cases assigned to an absent colleague and bulk reassigning them to another collector.',
        'Filtering by a specific currency to review a foreign-currency portfolio ahead of a revaluation run.',
        'Sorting by Days in Collection descending to identify cases that have been open the longest without resolution.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Collections > Queue. Cases must have been created either manually or automatically by a collection strategy before they appear here. If the queue is empty and you expect cases, check that collection strategies are active and that customer accounts have overdue balances assigned.',
      steps: [
        {
          title: 'How do I filter the queue to my own cases?',
          items: [
            { text: 'Click the Filters button in the toolbar.' },
            { text: 'Open the Assigned User dropdown and select your name.' },
            { text: 'Click Apply Filters.' },
            { text: '', note: 'The My Cases KPI card updates to reflect your personal count after the filter is applied.' },
            { text: '', tip: 'If you always work from your own assigned cases, apply this filter at the start of each session before doing anything else.' },
          ],
        },
        {
          title: 'How do I work through the queue?',
          items: [
            { text: 'Sort by Action Due Date ascending so overdue and today\'s cases appear at the top.' },
            { text: 'Click the Case Number of the first case to open the full Case Details page.' },
            { text: 'Review the customer\'s outstanding balance, last actions, and any open promises.' },
            { text: 'Contact the customer by phone or email.' },
            {
              text: 'Return to the queue and log the outcome by selecting the case checkbox to reveal the action toolbar at the bottom of the screen:',
              subSteps: [
                'Click Log Call to record what was said and the outcome.',
                'Click Record Promise if the customer committed to a payment date and amount.',
                'Click Send Email to send a follow-up communication directly from the queue.',
              ],
            },
            { text: 'Proceed to the next case.', tip: 'Use the browser Back button or the breadcrumb on Case Details to return to the queue with your filters still in place.' },
          ],
        },
        {
          title: 'How do I log a call against a case?',
          items: [
            { text: 'Select the checkbox on the case row to reveal the action toolbar at the bottom of the screen.' },
            { text: 'Click Log Call.' },
            { text: 'Enter the call outcome, any notes, and the date.' },
            { text: 'Click Save.' },
            { text: '', note: 'The Last Action column updates immediately to reflect the new call log.' },
          ],
        },
        {
          title: 'How do I record a promise to pay?',
          items: [
            { text: 'Select the checkbox on the case row to reveal the action toolbar at the bottom of the screen.' },
            { text: 'Click Record Promise.' },
            { text: 'Enter the promised amount, the date the customer expects to pay, and the confirmation method (verbal, email, etc.).' },
            { text: 'Click Save.' },
            { text: '', note: 'The case status changes to Promised and the promise due date appears below the status badge in the Status column.' },
            { text: '', tip: 'If the customer promises a partial amount, enter just that amount -- you can record a second promise for the remainder later.' },
          ],
        },
        {
          title: 'How do I reassign a case to another collector?',
          items: [
            { text: 'Select the checkbox on the case row (or multiple rows for a bulk reassignment).' },
            { text: 'Click Reassign User in the floating action toolbar at the bottom of the screen.' },
            { text: 'Select the new collector from the dropdown.' },
            { text: 'Add an optional note explaining the reason for reassignment.' },
            { text: 'Click Save.' },
            { text: '', note: 'If you have filtered the queue to your own name, the reassigned case will disappear from your view after reassignment.' },
          ],
        },
        {
          title: 'How do I update the status of multiple cases at once?',
          items: [
            { text: 'Select the checkboxes on all the case rows you want to update.' },
            { text: 'Click Update Status in the floating action toolbar at the bottom of the screen.' },
            { text: 'Select the new status from the dropdown: Active, Promised, Escalated, On Hold, or Closed.' },
            { text: 'Add an optional note.' },
            { text: 'Click Save.' },
            { text: '', note: 'Closing a case sets it to Closed status. The case will only disappear from the default queue view if you have filtered by Active or a non-Closed status.' },
          ],
        },
        {
          title: 'How do I configure which columns are visible?',
          items: [
            { text: 'Click the Settings icon () on the right side of the toolbar.' },
            { text: 'In the column configuration dialog, toggle any column on or off.' },
            { text: 'Click Save to apply.', tip: 'To reorder columns, drag any column header left or right directly in the table after closing the dialog.' },
            { text: '', note: 'Column visibility preferences are saved per user so your layout persists across sessions.' },
          ],
        },
        {
          title: 'How do I export the queue to Excel?',
          items: [
            { text: 'Apply any filters and search terms you want reflected in the export.' },
            { text: 'Click the Export icon () in the toolbar.' },
            { text: 'Select Export Current View to export only visible columns, or Export All Columns to include hidden columns.' },
            { text: '', note: 'The export runs server-side and applies your active filters -- it is not limited to the current page.' },
          ],
        },
      ],
      proTips: [
        'Log a call note immediately after each contact -- if you are unavailable the next day, a colleague picking up the case needs to know what was last said.',
        'Sort by Days in Collection descending once a week to surface cases that have been open the longest. Long-running cases often indicate a dispute or a communication breakdown that needs escalation.',
        'Pin the Action Due Date column to the left edge of the table so it stays visible when scrolling right through wide column configurations.',
        'Use the Promised filter at the start of each day to check whether any promises from yesterday were kept -- broken promises should be re-prioritised immediately.',
      ],
    },
    faq: [
      {
        question: 'How does the queue decide which cases appear at the top?',
        answer: 'The default order is by the case\'s internal priority score, which is calculated from the collection strategy rules, the overdue amount, and the days in collection. You can override this by clicking any sortable column header to apply your own sort order.',
      },
      {
        question: 'What does the Due Today KPI card count?',
        answer: 'It counts cases where the Next Action Due Date is today\'s date. It does not include cases where the next action date is in the past (those are already overdue and shown in red in the Action Due Date column).',
      },
      {
        question: 'What does the Stage progress bar mean?',
        answer: 'The progress bar shows how far through the collection strategy\'s defined phases this case has reached. For example, 2/5 means the case is on phase 2 of a 5-phase strategy. Hover the bar to see the name of the current phase.',
      },
      {
        question: 'Why is a case showing a red Action Due Date?',
        answer: 'The next action for that case was due on a past date and has not been completed. Work the case as soon as possible -- overdue actions increase the case\'s priority score.',
      },
      {
        question: 'Can I see all cases for the whole team, not just mine?',
        answer: 'Yes. Open the Filters sheet, set Assigned User to All Users (the default), and click Apply Filters. You will see every case in the system regardless of who it is assigned to.',
      },
      {
        question: 'Why does the Total Outstanding KPI card not match the Dashboard figure?',
        answer: 'The Total Outstanding card reflects only cases matching your current filters. The Dashboard figure covers all active cases. Remove all filters (click Clear All in the Filters sheet) to see the unfiltered total.',
      },
      {
        question: 'Can I filter by more than one status at the same time?',
        answer: 'No -- the Status filter in the Filters sheet is a single-select dropdown. To see cases across multiple statuses, remove the status filter and use the search bar or sort by Status to group them visually.',
      },
      {
        question: 'How do I find a specific case if I know the case number?',
        answer: 'Type the case number into the Search field in the toolbar. The list filters as you type. You do not need to remove other active filters first -- search applies on top of any existing filter state.',
      },
      {
        question: 'What happens to a case when I mark it as Closed?',
        answer: 'The case status is set to Closed and it counts toward the Closed this month metric on the Collections Dashboard. The case remains visible in the queue if you do not have a status filter active. It is not deleted.',
      },
      {
        question: 'Can I create a collection case from this page?',
        answer: 'Yes. Click Create New Case in the top-right header. This opens the case creation form where you can select the customer, assign a collector, and choose a collection strategy.',
      },
      {
        question: 'Why can I not see the Email, Phone, or Customer ID columns?',
        answer: 'Those columns are hidden by default. Click the Settings icon () in the toolbar, enable Email, Phone, Customer ID, Assigned To, Created Date, or Last Updated, then click Save. Note that Collection Strategy is visible by default -- if you cannot see it, check your column settings to confirm it has not been hidden.',
      },
    ],
    relatedPages: [
      { label: 'Collections Dashboard', path: '/collections/dashboard' },
      { label: 'Promises to Pay', path: '/collections/promises' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
      { label: 'Customers', path: '/customers' },
    ],
  },

  promises: {
    pageTitle: 'Promise to Pay Management',
    description:
      'A list of all customer payment commitments across your collection cases, showing each promise status, due date, and the amount the customer agreed to pay.',
    functionality: {
      overview:
        'The Promises to Pay page gives a consolidated view of every payment commitment recorded against your collection cases. Each row is a single promise -- created either from the Collections Queue, a Case Details page, or directly from this page. Use the KPI cards to assess whether promises are being kept or broken, then filter and sort the list to focus on promises that are overdue, due this week, or at risk. Click the Promise ID to open the promise detail record, or click the Case Number to navigate directly to the linked case. Select one or more rows to access bulk actions including Log Call (single row only), Send Email (single row only), Reassign, and Update Status.',
      keyFeatures: [
        { label: 'KPI Summary Cards', detail: 'Three cards show Active Promises (count and total value), Broken Promises (count and amount at risk), and Due This Week (count and expected receipts) -- all updating with your current filter state.' },
        { label: 'Search Bar', detail: 'Search across customer name, account number, and case number to locate a specific promise without clearing other filters.' },
        { label: 'Currency Toggle', detail: 'Switch between Promise Currency (each row in its own currency) and Base Currency (all amounts converted to your company currency) for cross-currency comparisons.' },
        { label: 'Filters Panel', detail: 'Open the Filters sheet to scope the list by Status (Active, Broken, Paid, Partially Paid, Closed), Assigned User, Currency (multi-select), and Created At date range. A separate Quick Filters section in the sheet contains the Due Today checkbox.' },
        { label: 'Column Configuration', detail: 'Show or hide any of the 17 available columns, reorder by drag-and-drop, resize widths, and pin columns to the left or right edge of the table.' },
        { label: 'Sortable Columns', detail: 'Click any column header to sort ascending or descending. Default sort is Promise Date ascending so the nearest-due promises appear first.' },
        { label: 'Row Navigation', detail: 'Click the Promise ID to open the Promise Detail page. Click the Case Number to navigate directly to the linked Case Details page. There is no per-row action menu -- use the Bulk Actions Toolbar by selecting a row.' },
        { label: 'Bulk Actions Toolbar', detail: 'Select one or more rows to reveal the floating action toolbar. With a single row selected: Log Call, Send Email, Reassign, and Update Status are all available. With two or more rows selected: only Reassign and Update Status are available (Log Call and Send Email require a single selection).' },
        { label: 'Export', detail: 'Export current view (visible columns) or all columns to Excel. Export applies your active filters and is not limited to the current page.' },
        { label: 'Create New Promise', detail: 'Raise a new promise directly from this page without navigating to a case first.' },
      ],
      sectionsAndFields: [
        { name: 'Active Promises KPI card', description: 'Count of promises with status Active, and their combined promised value. Reflects current filter state.' },
        { name: 'Broken Promises KPI card', description: 'Count of promises with status Broken, and the total amount at risk. Reflects current filter state.' },
        { name: 'Due This Week KPI card', description: 'Count of promises with a Promise Date within the current calendar week, and the total expected value.' },
        { name: 'Search field', description: 'Free-text search across customer name, account number, and case number.' },
        { name: 'Currency Toggle', description: 'Switches the Amount column and KPI card values between Promise Currency and Base Currency.' },
        { name: 'Filters button', description: 'Opens the Filters sheet. Displays a badge with the count of active filters when any are applied.' },
        { name: 'Export button', description: 'Dropdown with Export Current View (visible columns) and Export All Columns.' },
        { name: 'Column Settings button', description: 'Opens the column configuration dialog to toggle, reorder, and lock columns.' },
        { name: 'Promise ID column', description: 'Unique identifier for the promise. Click to open the Promise Detail page.' },
        { name: 'Customer column', description: 'Name of the customer account this promise belongs to.' },
        { name: 'Case Number column', description: 'The collection case this promise is linked to. Click to navigate to the Case Details page.' },
        { name: 'Last Action column', description: 'Hover the info icon to see the type, description, date, and logged-by name of the most recent action on this promise.' },
        { name: 'Promise Date column', description: 'The date the customer committed to paying by. Check the Days Overdue column (shown in red when > 0) to identify promises that have passed their due date without payment.' },
        { name: 'Days Overdue column', description: 'Number of days since the Promise Date passed without a matching receipt. Blank if the promise is not yet due or has been paid.' },
        { name: 'Currency column', description: 'The trading currency of the promise.' },
        { name: 'Amount column', description: 'The amount the customer agreed to pay. Respects the Currency Toggle setting.' },
        { name: 'Transactions column', description: 'Count of invoices this promise covers. Used to understand whether the promise relates to one invoice or a balance across multiple.' },
        { name: 'Status column', description: 'Current status badge: Active (green), Broken (red), Paid (blue), Partially Paid (amber), Closed (grey).' },
        { name: 'Method column', description: 'How the customer confirmed the commitment: Phone, Email, or Portal.' },
        { name: 'Filters sheet -- Status', description: 'Multi-select to filter by one or more statuses: Active, Broken, Paid, Partially Paid, Closed.' },
        { name: 'Filters sheet -- Assigned User', description: 'Dropdown to scope the list to promises assigned to a specific collector.' },
        { name: 'Filters sheet -- Currency', description: 'Multi-select to show only promises in one or more specific currencies.' },
        { name: 'Filters sheet -- Quick Filters -- Due Today', description: 'Checkbox in the Quick Filters section of the Filters sheet. When checked, shows only promises where the Promise Date is today.' },
        { name: 'Filters sheet -- Created At', description: 'Date-range pair (From / To) in the Filters sheet to filter by when the promise record was originally entered into the system.' },
        { name: 'Bulk Action toolbar', description: 'Appears at the bottom of the screen when one or more rows are selected. Shows selection count. With one row selected: Log Call, Send Email, Reassign, and Update Status buttons are shown. With two or more rows selected: only Reassign and Update Status are shown.' },
        { name: 'Pagination footer', description: 'Shows current page and total pages. Select rows per page (10, 25, 50, 100) and navigate with Previous / Next / page number buttons.' },
      ],
      useCases: [
        'Checking at the start of the day whether any promises that were due yesterday are still showing as Active (indicating they were not paid) so they can be marked Broken and followed up.',
        'Filtering by status Broken and sorting by Amount descending to prioritise the highest-value broken promises for immediate re-contact.',
        'Using the Due This Week KPI card to forecast expected cash receipts for the week and compare against the target.',
        'Filtering by a specific Assigned User to review a team member\'s promise portfolio during a performance discussion.',
        'Applying a Currency filter to review all GBP promises ahead of a cash flow report, without including foreign-currency commitments.',
        'Bulk-updating the status of several Paid promises to Closed after receipts have been matched and allocated.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Collections > Promises to Pay. Promises are created from the Collections Queue or Case Details pages when a customer commits to a payment. If the list is empty, check that collection cases exist and that promise records have been created against them.',
      steps: [
        {
          title: 'How do I find promises that are overdue?',
          items: [
            { text: 'Open the Filters sheet by clicking Filters in the toolbar.' },
            { text: 'Under Status, select Active and Broken.' },
            { text: 'Click Apply Filters.' },
            { text: 'Sort by Promise Date ascending -- promises with the oldest due dates appear at the top.' },
            { text: '', note: 'A promise is Broken when the Promise Date has passed and no matching receipt has been recorded. An Active promise that is past its date has not yet been reclassified -- check with your system administrator if reclassification rules apply in your organisation.' },
          ],
        },
        {
          title: 'How do I log a follow-up call on a broken promise?',
          items: [
            { text: 'Find the broken promise in the list.' },
            { text: 'Tick the checkbox on the left of the row to select it.' },
            { text: 'Click Log Call in the bulk action toolbar that appears at the bottom of the screen.' },
            { text: 'Enter the call outcome and any notes about what the customer said.' },
            { text: 'Click Save.' },
            { text: '', note: 'Log Call is only available when exactly one row is selected. The Last Action column updates immediately. If the customer gives a new payment date, record a new promise rather than editing the existing one.' },
            { text: '', tip: 'Navigate to the linked case (click the Case Number) to see the full history of all previous contacts for this customer.' },
          ],
        },
        {
          title: 'How do I update the status of a promise?',
          items: [
            { text: 'Tick the checkbox on the left of the promise row to select it.' },
            { text: 'Click Update Status in the bulk action toolbar at the bottom of the screen.' },
            { text: 'Choose the new status: Active, Broken, Paid, Partially Paid, or Closed.' },
            { text: 'Add an optional note explaining the reason for the change.' },
            { text: 'Click Save.' },
            {
              text: '',
              note: 'Marking a promise as Paid does not automatically allocate the receipt in the system -- go to the Receipts page to match the payment to the relevant invoices.',
            },
          ],
        },
        {
          title: 'How do I bulk-update statuses across multiple promises?',
          items: [
            { text: 'Select the checkboxes on all the rows you want to update.' },
            { text: 'Click Update Status in the floating bulk action toolbar at the bottom of the screen.' },
            { text: 'Select the new status.' },
            { text: 'Add an optional note.' },
            { text: 'Click Save.' },
            { text: '', tip: 'Use a Status filter first to show only Active promises before selecting all -- this prevents accidentally updating Paid or Closed records.' },
          ],
        },
        {
          title: 'How do I filter promises by due date?',
          items: [
            { text: 'To see only today\'s promises: Click Filters, check the Due Today box under Quick Filters, and click Apply Filters.' },
            {
              text: 'To see promises within a custom date range:',
              subSteps: [
                'Click Filters to open the Filters sheet.',
                'Enter a From date and a To date in the Created At fields.',
                'Click Apply Filters.',
              ],
            },
            { text: '', note: 'The Due Today checkbox (under Quick Filters) filters by Promise Date (the date the customer agreed to pay). The Created At date range filters by when the promise record was entered into the system -- these are different fields.' },
          ],
        },
        {
          title: 'How do I configure which columns are visible?',
          items: [
            { text: 'Click the Settings icon () in the toolbar.' },
            { text: 'Toggle any column on or off in the configuration dialog.' },
            { text: 'Click Save to apply.' },
            { text: '', note: 'Account Number, Email, Phone, Created Date, Created By, and Notes are hidden by default. Enable them if you need to see contact details or audit information.' },
            { text: '', tip: 'To reorder columns, drag any column header left or right directly in the table after closing the dialog.' },
          ],
        },
        {
          title: 'How do I export the promises list to Excel?',
          items: [
            { text: 'Apply any filters you want reflected in the export.' },
            { text: 'Click the Export icon () in the toolbar.' },
            { text: 'Select Export Current View to export only visible columns, or Export All Columns to include hidden columns.' },
            { text: '', note: 'The export runs server-side and applies your active filters -- it is not limited to the current page.' },
          ],
        },
        {
          title: 'How do I create a new promise from this page?',
          items: [
            { text: 'Click Create New Promise in the top-right header.' },
            { text: 'Select the customer account.' },
            { text: 'Select the collection case this promise belongs to.' },
            { text: 'Enter the promised amount, the promise date, and the confirmation method (Phone, Email, or Portal).' },
            { text: 'Add any supporting notes.' },
            { text: 'Click Save.' },
            { text: '', note: 'A promise must be linked to a collection case. If no case exists for the customer, create the case first via the Collections Queue page.' },
          ],
        },
      ],
      proTips: [
        'Run a Broken + Active filter every morning sorted by Promise Date ascending. Any Active promise past its due date should be reviewed and either marked Broken or confirmed as Paid -- a growing pile of overdue Active promises distorts your cash forecasting.',
        'Use the Due This Week KPI card on Mondays to set a cash collection target for the week. If the expected value is lower than your target, escalate the highest-value Active promises immediately rather than waiting for them to break.',
        'Pin the Promise Date and Amount columns to the left of the table so they stay visible when scrolling across wide column configurations.',
        'When a customer partially pays, mark the promise as Partially Paid and record a new promise for the remaining balance with a new agreed date -- this keeps the audit trail clean and ensures the remaining amount stays in the forecast.',
      ],
    },
    faq: [
      {
        question: 'What does the Broken status mean?',
        answer: 'Broken means the Promise Date passed without a matching receipt being recorded and the status was manually or automatically set to Broken. A Broken promise signals that the customer did not pay as agreed and requires a follow-up contact.',
      },
      {
        question: 'What does Partially Paid mean?',
        answer: 'The customer paid some of the promised amount but not the full committed value. The outstanding portion should be re-promised with a new payment date.',
      },
      {
        question: 'How does the system decide when to mark a promise as Broken?',
        answer: 'Promise status is not changed automatically by the system unless your organisation has configured automated reclassification rules. In most cases, a collector reviews Active promises past their due date and manually updates the status to Broken.',
      },
      {
        question: 'What does the Transactions column show?',
        answer: 'It shows the number of invoices this promise covers. A value of 3 means the customer agreed to pay an amount that spans three specific invoices. Click the Promise ID to open the detail record and see the individual invoice breakdown.',
      },
      {
        question: 'Can I edit a promise after it has been created?',
        answer: 'Open the Promise Detail page by clicking the Promise ID. From there you can update the promised amount, date, confirmation method, and notes. You cannot change the linked customer or case after creation.',
      },
      {
        question: 'How do I see which invoices a promise covers?',
        answer: 'Click the Promise ID to open the Promise Detail page. The linked invoices are listed in the Transactions section with their individual amounts and due dates.',
      },
      {
        question: 'Why does the Amount on the KPI cards not match what I expect?',
        answer: 'The KPI cards reflect the current filter state. If you have a Currency or Status filter active, the totals will only include promises that match those filters. Remove all filters to see the unfiltered totals.',
      },
      {
        question: 'Can I filter by multiple statuses at the same time?',
        answer: 'Yes -- the Status filter in the Filters sheet is a multi-select. You can select any combination of Active, Broken, Paid, Partially Paid, and Closed at the same time.',
      },
      {
        question: 'How do I navigate from a promise to the linked collection case?',
        answer: 'Click the Case Number in the Case Number column. This opens the Case Details page for that case in the Collections Queue, preserving the back-navigation path to Promises.',
      },
      {
        question: 'Can I delete a promise I recorded by mistake?',
        answer: 'Promise records cannot be deleted -- they are part of the collection audit trail. If you recorded a promise in error, update the status to Closed and add a note explaining the reason.',
      },
      {
        question: 'Why is a promise I just created not appearing in the list?',
        answer: 'Check whether a Status or Assigned User filter is active -- a new promise defaults to Active status and the creating user, so it may be filtered out if your current view excludes those values. Click Filters and check for active filters, then click Clear All and Apply to reset.',
      },
    ],
    relatedPages: [
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Collections Dashboard', path: '/collections/dashboard' },
      { label: 'Receipts', path: '/receipts' },
      { label: 'Customers', path: '/customers' },
    ],
  },

  'case-details': {
    pageTitle: 'Case Details',
    description:
      'The complete workspace for a single collection case — view and edit case information, manage linked transactions, record actions, and review the full activity history and audit log.',
    functionality: {
      overview:
        'Case Details is the central workspace for managing an individual collection case. The page has three main tabs: Overview (customer and case information, next action, case notes, and linked transactions), Activity (a full timeline of calls, emails, notes, and communications with filter controls and the ability to add new notes), and Audit Log (a chronological record of every field change, transaction link, status update, and assignment change ever made to the case). The left-hand sidebar lists all cases and lets you navigate between them without returning to the Queue. In view mode you can record a promise to pay, log a call, or send an email directly from the header. Click Edit Case to enter edit mode where all case fields become editable and you can add or remove linked transactions.',
      keyFeatures: [
        {
          label: 'Cases Sidebar',
          detail: 'A collapsible panel on the left lists all collection cases. Click any case to navigate to its details instantly. Collapse the sidebar to maximise the working area; a CASES handle on the left edge lets you reopen it.',
        },
        {
          label: 'Overview Tab — Customer Information Card',
          detail: 'Displays the customer account name and ID, the selected contact name (with a dropdown in edit mode to switch contacts), and the contact email and phone. In view mode, the email field has a teal Send Email button and the phone field has a teal Log Call button for one-click actions.',
        },
        {
          label: 'Overview Tab — Case Management Card',
          detail: 'Shows Case ID, Total Outstanding amount, Days in Collection, Case Status badge, Priority Level badge, Collection Strategy name, Assigned User name, Collection Stage progress bar (with phase tooltip), and Risk Level badge. All fields except Case ID become editable in edit mode.',
        },
        {
          label: 'Overview Tab — Next Action Card',
          detail: 'Displays the next scheduled action and its due date. For manually created cases in edit mode, the action type and due date can be changed via a dropdown and date picker.',
        },
        {
          label: 'Overview Tab — Case Notes Card',
          detail: 'Shows the most recent case notes. In edit mode, a text area allows you to write or update the case notes directly.',
        },
        {
          label: 'Overview Tab — Transactions Card',
          detail: 'Lists all transactions linked to this case with a searchable, sortable, paginated table. Export the transaction list to Excel (current view or all columns). Configure visible columns with the column settings button. In edit mode, select which transactions are linked and add new ones via Add Transaction.',
        },
        {
          label: 'Activity Tab — Timeline',
          detail: 'A chronological feed of every activity, note, and communication associated with the case. Filter by type (Activity, Notes, Communications) using the toggle buttons. In edit mode an Add New Note panel appears above the timeline so notes can be posted directly from this tab.',
        },
        {
          label: 'Audit Log Tab',
          detail: 'An immutable chronological log of every change made to the case: status changes, priority changes, field updates, transaction additions and removals, assignment changes, notes added, and case creation — each entry showing the old value, new value, user, and timestamp.',
        },
        {
          label: 'Record Promise',
          detail: 'Opens a drawer to create a Promise to Pay linked to this case. Enter the promised amount, promise date, confirmation method, and optional notes. The case status updates automatically once saved.',
        },
        {
          label: 'Log Call',
          detail: 'Opens the Log Call modal to record the outcome of a phone conversation: call type, outcome, any notes, and the date. The entry appears in the Activity Timeline immediately.',
        },
        {
          label: 'Send Email',
          detail: 'Opens the Send Email modal to compose and send an email to the case contact directly from the case. The email is logged as a Communication entry in the Activity Timeline.',
        },
        {
          label: 'Edit Case Mode',
          detail: 'Click Edit Case to unlock all case fields for editing. Changes are not saved until you click Save Changes. Click Cancel to discard all changes and return to view mode.',
        },
      ],
      sectionsAndFields: [
        { name: 'Back button', description: 'Returns to the Collections Queue, preserving any filters that were active when you navigated to this case.' },
        { name: 'Cases sidebar', description: 'Left-hand panel listing all collection cases. Click a case to navigate to it. Use the collapse/expand control at the top to show or hide the panel. When collapsed, a teal CASES handle appears on the left edge to reopen it.' },
        { name: 'Record Promise button', description: 'Opens the Create Promise drawer. Available in view mode only (hidden when editing). Visible in the page header.' },
        { name: 'Edit Case button', description: 'Switches the page into edit mode, making all case fields editable. Located in the page header in view mode.' },
        { name: 'Save Changes button', description: 'Saves all edits including field changes and transaction links. Visible in the header when edit mode is active.' },
        { name: 'Cancel button', description: 'Discards all unsaved edits and returns to view mode. Visible in the header when edit mode is active.' },
        { name: 'Overview tab', description: 'Default tab showing Customer Information, Case Management, Next Action, Case Notes, and Transactions cards.' },
        { name: 'Activity tab', description: 'Shows the full activity timeline with filter controls and (in edit mode) an Add New Note form. The badge on the tab shows the total activity count.' },
        { name: 'Audit Log tab', description: 'Shows the immutable change history for the case.' },
        { name: 'Customer Information — Customer', description: 'The customer account name and internal ID.' },
        { name: 'Customer Information — Contact Name', description: 'The contact person on the case. In edit mode, a dropdown lists all contacts for the customer; selecting one auto-populates the email and phone fields.' },
        { name: 'Customer Information — Email Address', description: 'The contact\'s email address. Read-only — populated automatically from the selected contact. In view mode, a teal Send Email button opens the email modal.' },
        { name: 'Customer Information — Phone Number', description: 'The contact\'s phone number. Read-only — populated automatically from the selected contact. In view mode, a teal Log Call button opens the call logging modal.' },
        { name: 'Case Management — Case ID', description: 'The unique identifier for this collection case. Read-only.' },
        { name: 'Case Management — Total Outstanding', description: 'The sum of the outstanding amounts on all transactions linked to this case, shown in the case currency. In edit mode, recalculates as transactions are added or removed.' },
        { name: 'Case Management — Days in Collection', description: 'How many days this case has been open in the collections workflow.' },
        { name: 'Case Management — Case Status', description: 'Current status badge: Active, Promised, Escalated, On Hold, or Closed. Editable in edit mode via a dropdown.' },
        { name: 'Case Management — Priority Level', description: 'Priority badge: High (red), Medium (blue), or Low (grey). Editable in edit mode.' },
        { name: 'Case Management — Collection Strategy', description: 'The strategy driving the case workflow. Read-only.' },
        { name: 'Case Management — Assigned User', description: 'The collector the case is assigned to. Editable in edit mode via a dropdown of active users.' },
        { name: 'Case Management — Collection Stage', description: 'A progress bar showing which phase of the collection strategy the case is on (e.g. 2/5). Hover to see the phase name. Read-only.' },
        { name: 'Case Management — Risk Level', description: 'The customer\'s current risk classification (Critical, High, Medium, or Low). Editable in edit mode.' },
        { name: 'Next Action card', description: 'Shows the next scheduled action and its due date. For manual cases, the action type and date are editable in edit mode.' },
        { name: 'Case Notes card', description: 'A text summary of the most recent case notes. Editable as free text in edit mode.' },
        { name: 'Transactions card — search', description: 'A quick-search field that filters the transaction table by reference number, description, amount, date, or customer name.' },
        { name: 'Transactions card — Export button', description: 'Downloads the transaction list as an Excel file. Options: Export Current View (visible columns only) or Export All Columns. The export reflects the current search and filter state.' },
        { name: 'Transactions card — Column Settings', description: 'Opens the column configuration dialog to show, hide, or reorder transaction table columns. Preferences are saved per user.' },
        { name: 'Transactions card — Add Transaction button', description: 'Only visible in edit mode. Opens the Select Transactions modal to search for and add new transactions to the case.' },
        { name: 'Transactions card — selection checkboxes', description: 'Only visible in edit mode. Uncheck a transaction to remove it from the case when Save Changes is clicked.' },
        { name: 'Transactions card — Total Outstanding footer', description: 'Displays the running sum of outstanding amounts across all linked transactions. Updates dynamically as transactions are added or deselected in edit mode.' },
        { name: 'Activity Timeline — filter buttons', description: 'Three toggle buttons (Activity, Notes, Communications) to show only items of the selected type. Multiple filters can be active at once. A Clear Filters button appears when any filter is active.' },
        { name: 'Activity Timeline — Add New Note panel', description: 'Visible only in edit mode. A text area and Add Note button that post a new note directly to the timeline without leaving the activity tab.' },
        { name: 'Activity Timeline — Load more', description: 'A button at the bottom of the timeline that loads additional historical entries when the list has been truncated.' },
        { name: 'Audit Log — change entries', description: 'Each entry shows an icon indicating the change type, a description, old and new values (for status/priority/field changes), and the user and timestamp. Entries are shown newest first.' },
      ],
      useCases: [
        'Opening a case just before calling the customer to review the last action taken, any open promises, and all prior communications.',
        'Entering edit mode to reassign the case to a colleague and update the status to On Hold while waiting for a customer response.',
        'Using the Activity tab to post a detailed call note immediately after a conversation so the record is complete before moving to the next case.',
        'Adding a new transaction to the case in edit mode when the customer\'s overdue balance has grown since the case was created.',
        'Using the Audit Log tab to produce a chronological paper trail of every action taken before escalating a case to legal.',
        'Recording a Promise to Pay after the customer commits to settling the balance by a specific date, which automatically updates the case status to Promised.',
        'Sending a follow-up email directly from the case so the communication is logged in the Activity Timeline without switching to a separate email client.',
        'Exporting the linked transactions to Excel to share an itemised list of overdue invoices with the customer.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Collections > Queue and click any Case Number to open Case Details. Alternatively, use the Cases sidebar on the left of the page to jump directly between cases. The page opens in view mode by default — all information is visible but no fields are editable until you click Edit Case.',
      steps: [
        {
          title: 'How do I navigate between cases?',
          items: [
            {
              text: 'Use the Cases sidebar on the left edge of the page. It lists all collection cases; click any row to load that case in the main panel.',
              tip: 'Click the collapse icon at the top of the sidebar to hide it and give more room to the case content. A teal CASES handle appears on the left edge — click it to reopen the sidebar.',
            },
            {
              text: 'To return to the full Collections Queue, click the Back button in the page header.',
              note: 'The Back button preserves any filters you had active on the Queue, so you return to exactly the same filtered view.',
            },
          ],
        },
        {
          title: 'How do I log a call against this case?',
          items: [
            {
              text: 'In view mode, click the teal phone icon button next to the phone number in the Customer Information card, or use the Log Call button in the header area.',
            },
            {
              text: 'In the Log Call modal, select the call type and outcome, add your notes, and confirm the date.',
            },
            {
              text: 'Click Save. The call is posted to the Activity Timeline immediately as a Communication entry.',
              tip: 'Log the call note as soon as you hang up — even a brief summary ensures colleagues can pick up the case if you are unavailable later.',
            },
          ],
        },
        {
          title: 'How do I send an email from this case?',
          items: [
            {
              text: 'In view mode, click the teal envelope icon button next to the email address in the Customer Information card.',
            },
            {
              text: 'In the Send Email modal, compose your message (or select a template) and click Send.',
            },
            {
              text: 'The email is logged in the Activity Timeline as a Communication entry so there is a full record of all outbound contact.',
            },
          ],
        },
        {
          title: 'How do I record a Promise to Pay?',
          items: [
            {
              text: 'Click Record Promise in the page header (view mode only).',
            },
            {
              text: 'In the drawer, enter the promised amount, the date the customer agreed to pay, the confirmation method (verbal, email, written), and any notes.',
            },
            {
              text: 'Click Save. The case status updates to Promised and the promise is recorded in the Activity Timeline.',
              note: 'If the customer promises only a partial amount, enter just that amount. You can record a second promise for the remainder after the first payment is received.',
            },
          ],
        },
        {
          title: 'How do I edit case details?',
          items: [
            {
              text: 'Click Edit Case in the page header.',
            },
            {
              text: 'All editable fields on the Overview tab unlock: Case Status, Priority Level, Assigned User, Risk Level, Next Action (manual cases only), and Case Notes.',
            },
            {
              text: 'Make your changes. The Total Outstanding figure recalculates automatically if you change which transactions are linked.',
            },
            {
              text: 'Click Save Changes to commit all edits at once.',
              note: 'All changes — field updates and transaction links — are saved in a single operation. Click Cancel at any time to discard all pending changes and return to view mode.',
            },
          ],
        },
        {
          title: 'How do I change the contact whose email and phone are shown?',
          items: [
            {
              text: 'Click Edit Case to enter edit mode.',
            },
            {
              text: 'In the Customer Information card, open the Contact Name dropdown and select the contact you want to use.',
            },
            {
              text: 'The Email Address and Phone Number fields update automatically to show the selected contact\'s details.',
              tip: 'Contacts are fetched from the customer account. If the person you need is missing, update the customer\'s contact list in Customer Detail first.',
            },
            {
              text: 'Click Save Changes to link the new contact to this case.',
            },
          ],
        },
        {
          title: 'How do I add or remove transactions linked to this case?',
          items: [
            {
              text: 'Click Edit Case to enter edit mode.',
            },
            {
              text: 'To remove a transaction: uncheck its checkbox in the Transactions table. The Total Outstanding footer updates immediately.',
              note: 'All transactions on the case are pre-selected when you enter edit mode. Unchecking one marks it for removal — it is not removed until you click Save Changes.',
            },
            {
              text: 'To add a transaction: click Add Transaction above the Transactions table. Search for the invoice in the modal and select it.',
            },
            {
              text: 'Newly added transactions appear in the table with a green "pending" indicator until saved.',
            },
            {
              text: 'Click Save Changes. All selected transactions are linked to the case and the case amount is recalculated.',
              note: 'All transactions linked to the same case must share the same currency. Attempting to save with mixed currencies will show a validation error.',
            },
          ],
        },
        {
          title: 'How do I add a note to the Activity Timeline?',
          items: [
            {
              text: 'Click Edit Case to enter edit mode, then switch to the Activity tab.',
            },
            {
              text: 'An Add New Note panel appears above the timeline. Type your note in the text area.',
            },
            {
              text: 'Click Add Note to post it. The note appears in the timeline immediately tagged as a Note.',
              tip: 'Notes added via this panel are separate from the Case Notes field on the Overview tab. Use the Case Notes field for a short status summary and the Activity Timeline for detailed running commentary.',
            },
          ],
        },
        {
          title: 'How do I filter the Activity Timeline?',
          items: [
            {
              text: 'Switch to the Activity tab.',
            },
            {
              text: 'Use the Activity, Notes, and Communications toggle buttons in the filter bar to show only items of those types. Multiple types can be active simultaneously.',
            },
            {
              text: 'To clear all filters, click Clear Filters. The timeline returns to showing all entries.',
              note: 'Filters only apply to the current view — they do not affect what is saved or exported.',
            },
          ],
        },
        {
          title: 'How do I review the full change history for a case?',
          items: [
            {
              text: 'Switch to the Audit Log tab.',
            },
            {
              text: 'Each entry shows: the type of change (colour-coded icon), a description, old and new values where applicable, the user who made the change, and the exact timestamp.',
            },
            {
              text: 'Scroll down to see older entries. The log is immutable — entries cannot be edited or deleted.',
              tip: 'Use the Audit Log when preparing an escalation or legal referral to demonstrate the full history of contact attempts, status changes, and broken promises.',
            },
          ],
        },
        {
          title: 'How do I export the linked transactions to Excel?',
          items: [
            {
              text: 'On the Overview tab, locate the Transactions card.',
            },
            {
              text: 'Click the download icon button on the right of the Transactions card header.',
            },
            {
              text: 'Select Export Current View to export only the visible columns, or Export All Columns to include all available columns.',
              note: 'The export runs server-side and includes all transactions linked to the case regardless of the current page in the table.',
            },
          ],
        },
      ],
      proTips: [
        'Log calls and emails immediately after each customer contact — even a single line ensures the timeline is complete if another collector needs to pick up the case unexpectedly.',
        'Check the Audit Log before making any changes to a case that has been touched by another user — it shows exactly what was changed and when, preventing duplicate work.',
        'Use the Cases sidebar instead of navigating back to the Queue between each case — it is significantly faster when working through a batch of cases in a single session.',
        'When entering edit mode, the Total Outstanding figure will recalculate as you add or remove transactions — watch this value to ensure the case amount is correct before saving.',
        'If you need to record both a call note and a promise from the same customer conversation, record the promise first (Record Promise button) as it updates the case status, then log the call note via the Activity tab to add the full narrative.',
        'The Activity and Audit Log tabs both have Load More buttons at the bottom — for long-running cases with extensive history, scroll down and load more before concluding that a particular action was never taken.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between the Case Notes field and the Activity Timeline notes?',
        answer: 'The Case Notes field (on the Overview tab) is a single free-text field that holds the current summary note for the case — it is overwritten each time you save in edit mode. The Activity Timeline (Activity tab) is a running log of individual notes, calls, and emails that is never overwritten. Use Case Notes for a short current-status summary and the Activity Timeline for a detailed chronological record.',
      },
      {
        question: 'Why are the email and phone fields read-only in edit mode?',
        answer: 'Email and phone details are taken directly from the contact record linked to the case. To use different contact details, change the selected contact in the Contact Name dropdown — the fields will auto-populate from the new contact. To update the underlying contact details, edit the customer\'s contact list in Customer Detail.',
      },
      {
        question: 'Can I change the Collection Strategy on a case?',
        answer: 'The Collection Strategy field is read-only on the Case Details page. To change the strategy, the case must be reassigned through the Collections Queue or via a bulk strategy update from the Ageing Analysis page.',
      },
      {
        question: 'Why can\'t I change the Next Action on this case?',
        answer: 'For cases driven by a collection strategy, the Next Action is determined automatically by the strategy rules and is read-only. Only manually created cases (those with no assigned strategy) allow the Next Action and due date to be edited freely.',
      },
      {
        question: 'What happens if I add a transaction in a different currency to the case?',
        answer: 'All transactions linked to a case must share the same currency. If you attempt to save with transactions in mixed currencies, a validation error will prevent the save. Remove the conflicting transaction and link it to a separate case in the correct currency.',
      },
      {
        question: 'Can I undo changes after clicking Save Changes?',
        answer: 'There is no undo function. Once Save Changes is clicked, all edits are committed to the server. The Audit Log records what changed and who changed it, providing a full history, but fields cannot be automatically reverted. Make corrections by re-entering edit mode and updating the affected fields.',
      },
      {
        question: 'Why does the Total Outstanding amount in the Case Management card differ from the sum I calculate manually?',
        answer: 'The Total Outstanding figure is derived from the outstanding amounts on each linked transaction (not the gross invoice amounts). It reflects the net amount still owed after any partial payments or credit notes have been applied to those transactions.',
      },
      {
        question: 'How do I close a case when the customer has paid?',
        answer: 'Click Edit Case, change Case Status to Closed, and click Save Changes. The case will remain visible in the queue unless you have an active status filter excluding Closed cases. Closing a case does not remove it from the system — it is retained for audit purposes.',
      },
      {
        question: 'What does the Collection Stage progress bar show?',
        answer: 'The progress bar shows how far through the phases of the assigned collection strategy this case has reached (e.g. 2 of 5 phases). Hover over the bar to see the name of the current phase. It is read-only and advances automatically as the strategy rules are executed.',
      },
      {
        question: 'Can I view all cases for a different customer from this page?',
        answer: 'The Cases sidebar on the left lists all collection cases. You can navigate to any case from here. To see all cases for a specific customer, use the Collections Queue and filter or search by customer name.',
      },
    ],
    relatedPages: [
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Promises to Pay', path: '/collections/promises' },
      { label: 'Customer Detail', path: '/customers/:id' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
    ],
  },

  'ageing-analysis': {
    pageTitle: 'Ageing Analysis',
    description:
      'A customer-level receivables report that breaks outstanding balances into configurable aging bands so you can see exactly how much is overdue and by how long, for each account in your portfolio.',
    functionality: {
      overview:
        'The Ageing Analysis page loads every customer account with an outstanding balance and distributes each balance across your configured aging bands -- for example Current, 1-30 days, 31-60 days, 61-90 days, 91-120 days, and 120+ days. The metric cards at the top reflect the current search and filter state, showing the total value in each band for the customers currently displayed. The table below lists every customer with their band-by-band breakdown. Use the search bar to find a specific account, the Filters panel to narrow the list by risk level, credit controller, collection strategy, currency, or value ranges, and the Currency Toggle to switch between viewing table amounts in each customer\'s own currency or in your base currency. Note: the Currency Toggle affects table row amounts only -- the metric cards always display values in base currency.',
      keyFeatures: [
        {
          label: 'Dynamic Ageing Band Metric Cards',
          detail: 'One card per configured aging band shows the total outstanding value across all customers in that band, helping you see at a glance where the portfolio risk is concentrated.',
        },
        {
          label: 'Customer-Level Ageing Table',
          detail: 'Lists every customer with an outstanding balance, showing individual band amounts so you can compare each account\'s aging profile side by side.',
        },
        {
          label: 'Currency Toggle',
          detail: 'Switch between Customer Currency (each table row shown in the customer\'s own currency) and Base Currency (all table amounts converted to your company\'s home currency) to get a consistent portfolio view. This toggle affects table row amounts only -- the metric cards at the top always display totals in base currency regardless of this setting.',
        },
        {
          label: 'Search Bar',
          detail: 'Searches across customer name, account number, and contact name in real time -- narrows both the table and the metric card totals to matching customers.',
        },
        {
          label: 'Advanced Filters Panel',
          detail: 'Filter by Customer, Risk Level, Currency, Payment Terms, Credit Controller, Account Manager, Credit Limit range, Last Payment date range, Collection Strategy, Days Overdue range, Outstanding range, and four User Sort Key fields.',
        },
        {
          label: 'Active Filter Badges',
          detail: 'Each applied filter appears as a dismissible badge below the toolbar so you can see exactly what is active and remove individual filters without opening the panel.',
        },
        {
          label: 'Sortable Columns',
          detail: 'Click any column header to sort the table ascending or descending. Useful for surfacing the highest outstanding balances or longest-overdue accounts instantly.',
        },
        {
          label: 'Column Configuration',
          detail: 'Show or hide columns using the settings icon. Hidden by default: Last Payment, Contact Email, Contact Phone, Credit Controller, Account Manager, and four User Sort Key columns.',
        },
        {
          label: 'Customer Details Drawer',
          detail: 'Click any customer name to open a side drawer with the full account summary, aging breakdown, and available actions -- Log Call, Send Email, Create Promise, and Assign Credit Controller.',
        },
        {
          label: 'Export to CSV',
          detail: 'Download all filtered customers and their visible column data as a CSV file. The export reflects the current search, filter, and currency toggle state.',
        },
      ],
      sectionsAndFields: [
        { name: 'Ageing band metric cards', description: 'A row of cards at the top of the page -- one per configured aging band. Each card shows the band label (e.g. "1-30 Days") and the total outstanding across the customers currently displayed (reflecting any active search or filters), always in your base currency. The tooltip on each card confirms the currency used and indicates when the figures reflect active filters.' },
        { name: 'All Customers counter', description: 'Shows the total number of customer rows currently displayed. Changes to "Filtered Customers (N)" when a search or filter is active.' },
        { name: 'Search bar', description: 'Type a customer name, account number, or contact name to filter the table in real time. A clear (x) button appears when text is entered.' },
        { name: 'Filters button', description: 'Opens the Advanced Filters panel. A badge on the button shows the number of active filter groups.' },
        { name: 'Export button (download icon)', description: 'Opens a dropdown with "Export as CSV". The export includes all filtered rows and currently visible columns.' },
        { name: 'Column Settings button (gear icon)', description: 'Opens the Column Configuration dialog. Drag rows to reorder; toggle checkboxes to show or hide columns. Account # and Name are locked visible.' },
        { name: 'Currency Toggle', description: 'Two-state toggle in the page header: Customer Currency (each table row shown in the customer\'s invoicing currency) or Base Currency (all table amounts converted to your company\'s home currency). This toggle affects table row amounts only -- the metric cards at the top always show totals in base currency regardless of this setting.' },
        { name: 'Active filter badges', description: 'Appear below the toolbar when filters are applied. Each badge names the filter and has a x to remove it individually. A "Clear All" link removes all filters at once.' },
        { name: 'Account # column', description: 'The customer\'s account number as imported from your accounting system. Locked -- always visible.' },
        { name: 'Name column', description: 'The customer\'s trading name. Click to open the Customer Details drawer. Locked -- always visible.' },
        { name: 'Currency column', description: 'The currency the customer is invoiced in.' },
        { name: 'Credit Limit column', description: 'The customer\'s approved credit limit. Follows the Currency Toggle -- shown in the customer\'s own currency when Customer Currency is selected, or converted to your base currency when Base Currency is selected.' },
        { name: 'Outstanding column', description: 'Total open balance across all aging bands. Switches between customer currency and base currency when the Currency Toggle changes.' },
        { name: 'Ageing band columns (dynamic)', description: 'One column per configured band (e.g. Current, 1-30, 31-60, 61-90, 91-120, 120+). Each cell shows the portion of the customer\'s outstanding balance that falls into that band. Band labels and intervals are configured in Settings > Company Settings.' },
        { name: 'Terms column', description: 'The customer\'s standard payment terms (e.g. Net 30).' },
        { name: 'Risk Level column', description: 'Colour-coded badge: Critical, High, Medium, or Low.' },
        { name: 'Collection Strategy column', description: 'The collection strategy currently assigned to the customer.' },
        { name: 'Last Payment column (hidden by default)', description: 'The date of the customer\'s most recent payment. Enable in column settings.' },
        { name: 'Contact Email column (hidden by default)', description: 'Primary contact email address. Enable in column settings.' },
        { name: 'Contact Phone column (hidden by default)', description: 'Primary contact phone number. Enable in column settings.' },
        { name: 'Credit Controller column (hidden by default)', description: 'The credit controller assigned to the account. Enable in column settings.' },
        { name: 'Account Manager column (hidden by default)', description: 'The account manager assigned to the account. Enable in column settings.' },
        { name: 'User Sort Key 1-4 columns (hidden by default)', description: 'Four configurable classification fields imported from your accounting system. Labels are set by your administrator. Enable in column settings.' },
        { name: 'Advanced Filters panel', description: 'A slide-out sheet opened by the Filters button. Contains: Customer (multi-select), Risk Level (multi-select), Currency (multi-select), Terms (multi-select), Credit Controller (multi-select), Account Manager (multi-select), Credit Limit Range (slider), Last Payment Date Range (from/to date inputs), Collection Strategy (multi-select), Days Overdue Range (slider), Outstanding Range (slider), and User Sort Key 1-4 (multi-select).' },
        { name: 'Customer Details drawer', description: 'Opens when you click a customer name. Shows the full account summary, aging band breakdown, and action buttons: Log Call, Send Email, Create Promise to Pay, and Assign Credit Controller.' },
        { name: 'Smart Actions panel', description: 'Appears automatically on the right when two or more customers are selected. Shows AI-generated bulk follow-up recommendations based on the selected accounts.' },
        { name: 'Pagination footer', description: 'Shows current page, total pages, and total customer count. Page size selector defaults to 25 rows; options are 10, 25, 50, and 100.' },
      ],
      useCases: [
        'Running a morning portfolio review to identify which aging band has grown overnight and deciding which customers to call first.',
        'Filtering by Credit Controller to review a team member\'s portfolio exposure before a one-to-one meeting.',
        'Using the Days Overdue Range filter to isolate customers with balances overdue by more than 90 days for immediate escalation.',
        'Switching to Base Currency and sorting the Outstanding column descending to find the single highest-value overdue account in the entire portfolio.',
        'Selecting multiple high-risk customers and using Smart Actions to generate a recommended bulk collection sequence.',
        'Exporting the filtered list to CSV to populate a monthly bad debt provision report in a spreadsheet.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Analytics & Reporting > Ageing Analysis. The page loads all customers with outstanding balances automatically. If the table is empty, your accounting system integration may not have synced -- contact your system administrator. No additional setup is required to read the report; however, aging band labels and intervals are configured in Settings > Company Settings and may differ from the defaults shown here.',
      steps: [
        {
          title: 'How do I read the aging band metric cards?',
          items: [
            { text: 'Look at the row of cards across the top of the page. Each card represents one aging band.' },
            { text: 'The figure on each card is the total outstanding across every customer in that band, shown in your base currency.' },
            { text: 'Hover the small info icon on any card to see a tooltip confirming the currency used and whether the figures reflect active filters.', note: 'The metric cards always show base currency totals and reflect the current search and filter state -- totals update as you apply or remove filters. The Currency Toggle affects table row amounts only, not the metric cards.' },
          ],
        },
        {
          title: 'How do I find a specific customer in the table?',
          items: [
            { text: 'Type the customer name, account number, or contact name in the Search bar at the top of the toolbar.' },
            { text: 'The table filters as you type -- no need to press Enter.' },
            { text: 'Click the x inside the search bar to clear it and return to the full list.', tip: 'If you know only the first few characters of an account number, start typing them -- the search matches partial strings anywhere in the account number field.' },
          ],
        },
        {
          title: 'How do I open a customer\'s full account details?',
          items: [
            { text: 'Click the customer\'s name in the Name column.' },
            { text: 'The Customer Details drawer opens on the right, showing the full account summary, aging breakdown, and available actions.' },
            { text: 'Use the Log Call, Send Email, Create Promise, or Assign Credit Controller buttons inside the drawer to act without leaving the page.' },
          ],
        },
        {
          title: 'How do I switch between customer currency and base currency?',
          items: [
            { text: 'Click the Currency Toggle in the top-right of the page header.' },
            { text: 'Select Customer Currency to see each row in the customer\'s own invoicing currency.' },
            { text: 'Select Base Currency to see all amounts converted to your company\'s home currency.', note: 'The metric cards at the top always show base currency regardless of this toggle. The toggle only changes the amounts displayed in the table.' },
          ],
        },
        {
          title: 'How do I filter the table by risk level, credit controller, or collection strategy?',
          items: [
            { text: 'Click the Filters button in the toolbar.' },
            { text: 'In the Advanced Filters panel, select one or more values in the Risk Level, Credit Controller, or Collection Strategy fields.' },
            { text: 'Click Apply Filters at the bottom of the panel.' },
            { text: 'The table updates and filter badges appear below the toolbar, one per active filter group.', note: 'The metric cards at the top also update when you apply filters -- they reflect the totals for the customers currently displayed, not the full portfolio. This lets you see the band distribution for your filtered segment at a glance.' },
            { text: 'To remove a single filter, click x on its badge. To clear all filters at once, click Clear All in the badge row.', tip: 'Use the Credit Controller filter at the start of each one-to-one meeting to scope the view to that person\'s portfolio only.' },
          ],
        },
        {
          title: 'How do I filter customers by outstanding balance or days overdue?',
          items: [
            { text: 'Click the Filters button to open the Advanced Filters panel.' },
            { text: 'Scroll to the Outstanding Range or Days Overdue Range section.' },
            { text: 'Drag the slider handles to set a minimum and maximum value.', note: 'The Outstanding Range slider uses your base currency. The Days Overdue Range applies a client-side filter based on the worst (longest) days overdue across all of the customer\'s open invoices.' },
            { text: 'Click Apply Filters.' },
          ],
        },
        {
          title: 'How do I configure which columns are visible?',
          items: [
            { text: 'Click the gear (settings) icon at the right end of the toolbar.' },
            { text: 'In the Column Configuration dialog, check or uncheck columns to show or hide them.' },
            { text: 'Drag rows up or down to reorder columns in the table.', note: 'Account # and Name are locked and cannot be hidden. Hidden by default: Last Payment, Contact Email, Contact Phone, Credit Controller, Account Manager, and User Sort Key 1-4.' },
            { text: 'Click Save to apply. Click Reset to Defaults to restore the original column set.' },
          ],
        },
        {
          title: 'How do I export the aging analysis?',
          items: [
            { text: 'Apply any search or filters you want to be reflected in the export.' },
            { text: 'Click the download icon in the toolbar.' },
            { text: 'Select Export as CSV from the dropdown.' },
            { text: 'The file downloads immediately and is named ageing-analysis-YYYY-MM-DD.csv.', note: 'The export includes all filtered rows, not just the current page. Only columns that are currently visible in the table are included in the CSV.', tip: 'Switch to Base Currency before exporting if you need a single-currency view for management reporting.' },
          ],
        },
      ],
      proTips: [
        'Sort the Outstanding column descending while in Base Currency mode to instantly surface the highest-value overdue accounts across the entire portfolio -- regardless of their risk rating.',
        'Use the Credit Limit Range filter alongside the Outstanding Range filter to find customers who are both highly overdue and close to or over their credit limit -- these accounts need both a collection call and a credit review.',
        'Enable the Credit Controller and Last Payment columns, then sort by Last Payment ascending, to find customers who have not paid recently and whose controller may not have followed up.',
        'The Days Overdue Range filter uses each customer\'s worst (longest) overdue invoice -- not their average. A customer with one invoice 95 days overdue and ten current invoices will still appear when you filter for 90+ days overdue.',
      ],
    },
    faq: [
      {
        question: 'What do the aging band column labels mean?',
        answer: 'Each column represents a time band based on days past the invoice due date. The band labels (e.g. "Current", "1-30 Days", "31-60 Days") and their day intervals are configured in Settings > Company Settings. Your labels may differ from these defaults -- contact your administrator if you are unsure.',
      },
      {
        question: 'How does the system calculate which band an invoice falls into?',
        answer: 'Ageing is calculated from the invoice due date (not the invoice issue date) to today. An invoice with a due date 45 days ago falls in the 31-60 Days band, regardless of when it was issued.',
      },
      {
        question: 'What does the Outstanding column show?',
        answer: 'The total open balance for the customer across all aging bands. It switches between the customer\'s own currency and your base currency when you toggle the Currency Toggle in the page header.',
      },
      {
        question: 'Do the metric card totals update when I apply filters?',
        answer: 'Yes -- the metric cards at the top reflect the current search and filter state. When you apply a filter or type in the search bar, the cards update to show totals for only the customers currently displayed in the table. This lets you see the aging band distribution for your filtered segment at a glance.',
      },
      {
        question: 'Can I customise the aging band intervals?',
        answer: 'Yes, but only a system administrator can do this. Ageing band labels, day ranges, and the number of bands are configured in Settings > Company Settings. Changes take effect for all users immediately.',
      },
      {
        question: 'How do I see the aging breakdown for a single customer?',
        answer: 'Click the customer\'s name in the Name column to open the Customer Details drawer, which shows the full aging band breakdown for that account alongside the contact details and available actions.',
      },
      {
        question: 'Does the aging analysis include disputed invoices?',
        answer: 'Yes -- disputed invoices are included in the aging bands by default. The aging analysis does not have a standalone Dispute filter. To exclude disputed invoices, use the All Disputes page to identify disputed amounts and reconcile them separately.',
      },
      {
        question: 'Why is a customer not appearing in the table even though they have an overdue invoice?',
        answer: 'There are a few common causes: (1) A search or filter is active that excludes them -- check the active filter badges and clear them. (2) The accounting integration has not synced the latest data -- check with your administrator. (3) The invoice is fully allocated and shows as zero balance.',
      },
      {
        question: 'How do I filter to just one credit controller\'s portfolio?',
        answer: 'Click Filters, select the credit controller\'s name in the Credit Controller field, and click Apply Filters. The table will show only that controller\'s accounts.',
        steps: [
          'Click the Filters button in the toolbar.',
          'Scroll to the Credit Controller field in the Advanced Filters panel.',
          'Select the controller\'s name from the dropdown.',
          'Click Apply Filters.',
        ],
      },
      {
        question: 'What are the User Sort Key columns?',
        answer: 'User Sort Key 1-4 are four configurable classification fields imported from your accounting system. Their labels and values are set by your administrator and typically represent internal customer groupings such as region, business unit, or segment. They are hidden by default -- enable them in column settings.',
      },
      {
        question: 'Can I log a call or create a promise to pay directly from this page?',
        answer: 'Yes. Click a customer\'s name to open the Customer Details drawer, then use the Log Call or Create Promise buttons inside the drawer. You do not need to navigate to the Collections Queue or Customer Accounts page.',
      },
    ],
    relatedPages: [
      { label: 'Collections Dashboard', path: '/collections/dashboard' },
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Customer Accounts', path: '/customers' },
      { label: 'Transactions', path: '/transactions' },
      { label: 'Dashboard', path: '/' },
    ],
  },

  'disputes-dashboard': {
    pageTitle: 'Dispute Dashboard',
    description:
      'A real-time summary of all open disputes across the team, showing active counts, total value in dispute, resolution rate, workflow progress, aging distribution, and priority breakdown.',
    functionality: {
      overview:
        'The Dispute Dashboard is the starting point for daily disputes management. It shows how many disputes are active, how much money is tied up, how quickly disputes are being resolved, and which disputes need immediate attention. Use the Team Member filter to scope the view to a single handler, or leave it on Everyone to see the full team picture. Click any metric card to navigate directly to the filtered All Disputes list.',
      keyFeatures: [
        {
          label: 'KPI Metric Cards',
          detail: 'Four cards show Active Disputes, Amount in Dispute, Average Resolution Time, and Resolution Rate -- each clickable to navigate to a filtered disputes list.',
        },
        {
          label: 'Team Member Filter',
          detail: 'Scope all panels to a specific handler using the filter in the top-right. Your selection persists across page refreshes via local storage.',
        },
        {
          label: 'Avg Workflow Progress Card',
          detail: 'Shows the mean percentage completion of all active disputes across their configured workflow levels.',
        },
        {
          label: 'Dispute Ageing Panel',
          detail: 'Breaks active disputes into four aging buckets -- 0-7, 8-14, 15-30, and 31+ days -- so you can see how many disputes are approaching or exceeding resolution targets.',
        },
        {
          label: 'Priority Disputes Panel',
          detail: 'Lists the highest-priority open disputes with their ID, customer, dispute type, priority badge, status badge, amount, days open, and a workflow progress bar. Click any row to open the dispute detail.',
        },
        {
          label: 'Disputes by Priority Panel',
          detail: 'Shows count and total value for each priority level -- Critical, High, Medium, Low -- so you can assess exposure at each tier.',
        },
        {
          label: 'Common Dispute Types Panel',
          detail: 'Ranks dispute categories by frequency with a proportional bar chart, helping to identify systemic issues worth escalating.',
        },
        {
          label: 'View All Disputes Button',
          detail: 'Opens the All Disputes page without any pre-set filter -- useful when you want to start from the full list.',
        },
        {
          label: 'Collapsible Panels',
          detail: 'Each lower panel can be collapsed using the chevron button in its header, letting you hide sections you do not need during a session.',
        },
      ],
      sectionsAndFields: [
        { name: 'Active Disputes card', description: 'Count of all open disputes currently assigned to the filtered scope. Shows "X overdue" below if any exist, or "X due soon" if overdue count is zero. Clicking navigates to All Disputes filtered to overdue if there are any, or the full list otherwise.' },
        { name: 'Amount in Dispute card', description: 'Sum of the disputed amounts across all active disputes in the filtered scope, shown in abbreviated form (e.g. GBP42.3K).' },
        { name: 'Avg Resolution Time card', description: 'Mean number of days from creation to resolution for disputes resolved within the selected scope. Shows N/A if no disputes have been resolved yet.' },
        { name: 'Resolution Rate card', description: 'Percentage of disputes that have been resolved compared to total disputes raised. Clicking navigates to All Disputes filtered to Resolved status.' },
        { name: 'Team Member filter', description: 'Dropdown in the page header. Defaults to Everyone (full team). Select an individual handler to scope all four KPI cards and all lower panels to that person.' },
        { name: 'Avg Workflow Progress card', description: 'Displays a percentage figure and a progress bar representing the average completion level across all active disputes\' configured workflow stages.' },
        { name: 'Dispute Ageing panel', description: 'Four rows -- 0-7 days, 8-14 days, 15-30 days, 31+ days -- each showing the count of active disputes in that age bracket. The 31+ row is highlighted in red.' },
        { name: 'Priority Disputes Requiring Attention panel', description: 'Scrollable list of the top priority open disputes. Each card shows: dispute ID, priority badge (Critical/High/Medium/Low), derived status badge (New/In Progress/Resolved/Closed), amount, customer name, dispute type, days open, and a workflow progress bar.' },
        { name: 'Dispute ID', description: 'Unique identifier for the dispute record. Click the row to open the dispute detail page.' },
        { name: 'Priority badge', description: 'Colour-coded badge: Critical (red), High (orange), Medium (blue), Low (grey). Drives the left border colour of each dispute card.' },
        { name: 'Derived Status badge', description: 'Calculated from the dispute\'s current workflow level, total levels, close reason, and status field. Reflects the actual stage in the resolution process.' },
        { name: 'Days Open', description: 'Number of calendar days since the dispute was created.' },
        { name: 'Workflow Progress bar', description: 'Shows Level X of Y for the dispute\'s configured workflow, with a proportional fill bar.' },
        { name: 'Disputes by Priority panel', description: 'Four rows -- Critical, High, Medium, Low -- each showing dispute count and total value for that priority tier.' },
        { name: 'Common Dispute Types panel', description: 'Ranks dispute categories by frequency. Each row shows the category name, count, and a proportional fill bar.' },
        { name: 'View All Disputes button', description: 'Fixed button in the page header. Navigates to All Disputes with no pre-applied filters.' },
        { name: 'Collapse / Expand chevron', description: 'Each lower panel has a chevron button in its header. Click to collapse the panel body and save screen space.' },
      ],
      useCases: [
        'Starting the working day by checking Active Disputes and the Dispute Ageing panel to identify any disputes that have crossed into the 31+ day bucket overnight.',
        'A disputes manager scoping the Team Member filter to an individual handler to review their workload before a one-to-one meeting.',
        'Clicking the Active Disputes card when it shows overdue disputes to jump directly into the filtered All Disputes list and begin working through them.',
        'Using the Common Dispute Types panel at month end to identify the most frequent dispute category and raise it with the operations or sales team.',
        'A finance director checking the Amount in Dispute KPI card at the end of a reporting period to quantify the total value at risk.',
        'Using the Disputes by Priority panel to assess whether any Critical disputes are currently open and need immediate escalation.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Disputes > Dashboard. The page loads automatically scoped to Everyone. If you only manage your own disputes, use the Team Member filter to scope it to yourself before reviewing any of the panels. No configuration is required before using the page -- disputes appear as soon as they are raised.',
      steps: [
        {
          title: 'How do I check the current dispute position?',
          items: [
            {
              text: 'Read the four KPI cards across the top of the page -- Active Disputes, Amount in Dispute, Avg Resolution Time, and Resolution Rate.',
              note: 'The KPI cards always reflect the current filtered scope. If the Team Member filter is set to an individual, the cards show only that person\'s disputes.',
            },
            {
              text: 'Check the Dispute Ageing panel to see how many disputes are in the 31+ day bucket.',
              tip: 'Any count in the 31+ row warrants immediate attention -- these disputes have likely exceeded their resolution target.',
            },
            { text: 'Review the Priority Disputes panel to see which open disputes are ranked Critical or High.' },
          ],
        },
        {
          title: 'How do I scope the dashboard to a specific team member?',
          items: [
            { text: 'Click the Team Member dropdown in the top-right area of the page header.' },
            {
              text: 'Select the handler\'s name from the list.',
              note: 'Your selection is saved in local storage and will still be active the next time you open the page. Click the dropdown again and select Everyone to reset.',
            },
            {
              text: 'All four KPI cards, the Ageing panel, and the Priority Disputes panel will refresh to show only that handler\'s disputes.',
              tip: 'This is useful during a one-to-one review with a team member -- scope the dashboard to them before the meeting so the numbers reflect their portfolio only.',
            },
          ],
        },
        {
          title: 'How do I navigate from a KPI card to the underlying disputes?',
          items: [
            { text: 'Click the Active Disputes card to open All Disputes. If any disputes are overdue, the page opens pre-filtered to overdue disputes only.' },
            { text: 'Click the Amount in Dispute card to open All Disputes with no pre-set filter.' },
            { text: 'Click the Avg Resolution Time or Resolution Rate cards to open All Disputes filtered to Resolved status.' },
            {
              text: 'Use the Back button in your browser to return to the dashboard with the same Team Member filter still active.',
              tip: 'The View All Disputes button in the top-right always opens All Disputes without any filter -- use it when you want to start from the full unfiltered list.',
            },
          ],
        },
        {
          title: 'How do I open a specific dispute from the Priority Disputes panel?',
          items: [
            { text: 'Scroll down to the Priority Disputes Requiring Attention panel.' },
            { text: 'Locate the dispute by its ID, customer name, priority badge, or amount.' },
            {
              text: 'Click anywhere on the dispute card to open the dispute detail page.',
              note: 'The panel shows the top disputes ranked by priority. If you need to find a dispute not in this list, use All Disputes and apply filters there.',
            },
          ],
        },
        {
          title: 'How do I read the Dispute Ageing panel?',
          items: [
            { text: 'Scroll to the Workflow & Ageing Insights section below the KPI cards.' },
            { text: 'Read each row -- 0-7 days, 8-14 days, 15-30 days, 31+ days -- and note the count beside each.' },
            {
              text: 'Pay particular attention to the 31+ row, shown in red.',
              note: 'Target resolution times for each dispute type are configured in Settings > Dispute Types. The aging panel does not cross-reference type-specific targets -- it shows calendar age only.',
            },
            {
              text: 'Click the Active Disputes KPI card to drill into the full list and sort by Days Open descending to identify the oldest disputes.',
              tip: 'If the 31+ count is consistently growing week-on-week, this indicates disputes are not being closed quickly enough -- review whether responses are being waited on from customers.',
            },
          ],
        },
        {
          title: 'How do I collapse a panel I do not need?',
          items: [
            { text: 'Click the chevron button (up/down arrow) in the top-right corner of any lower panel header.' },
            { text: 'The panel body collapses, leaving just the header visible.' },
            {
              text: 'Click the chevron again to expand the panel.',
              note: 'Collapsed state is not persisted -- panels return to their expanded state when you reload the page.',
            },
          ],
        },
      ],
      proTips: [
        'Set the Team Member filter to your own name at the start of each day to get a personal view of your disputes workload before switching to Everyone for the team overview.',
        'A Resolution Rate below 70% over a sustained period usually means disputes are not being closed after resolution -- check whether handlers are updating status to Closed once a dispute is settled.',
        'Use the Common Dispute Types panel weekly -- if one type is growing disproportionately, it is worth investigating whether a process or product issue is generating repeat disputes.',
        'The Avg Workflow Progress card is most useful when compared week-on-week: if the percentage is stagnant or falling while active count is stable, disputes may be stalling at a specific workflow stage.',
      ],
    },
    faq: [
      {
        question: 'What does the Active Disputes count include?',
        answer: 'It includes all disputes that are not in a Resolved or Closed status. New disputes and disputes In Progress are both counted.',
      },
      {
        question: 'What does the Resolution Rate percentage measure?',
        answer: 'Resolution Rate is the number of disputes marked Resolved or Closed divided by the total number of disputes ever raised, expressed as a percentage. A higher rate indicates a larger proportion of disputes have been successfully resolved.',
      },
      {
        question: 'What does Avg Workflow Progress mean?',
        answer: 'Each dispute type has a configured workflow with a set number of levels. Avg Workflow Progress is the mean of (current level / total levels x 100) across all active disputes. A figure close to 100% means most disputes are near the end of their resolution process.',
      },
      {
        question: 'Why does the Amount in Dispute card show a different figure than I expected?',
        answer: 'The figure reflects only active (open) disputes in the current Team Member filter scope. Resolved and Closed disputes are excluded. If you have just resolved several disputes, the figure will have dropped since the last time you checked.',
      },
      {
        question: 'What does the Derived Status badge on each dispute card mean?',
        answer: 'The derived status is calculated from the dispute\'s current workflow level, total levels, close reason, and status field -- it is not a manually set field. New means the dispute has just been raised. In Progress means it is being worked. Resolved means it has been settled. Closed means it has been finalised.',
      },
      {
        question: 'How do I filter the Priority Disputes panel to show only Critical disputes?',
        answer: 'The Priority Disputes panel cannot be filtered independently on this page. To filter by priority, click the Active Disputes KPI card to open All Disputes and apply the Priority filter there.',
        steps: [
          'Click the Active Disputes KPI card.',
          'On the All Disputes page, open the Filters panel.',
          'Select Critical from the Priority filter.',
          'The list updates to show only Critical disputes.',
        ],
      },
      {
        question: 'Why does the Avg Resolution Time show N/A?',
        answer: 'Avg Resolution Time is calculated from disputes that have been resolved. If no disputes have been resolved yet within the current filter scope, there is no data to calculate an average and the card shows N/A.',
      },
      {
        question: 'Can I export the Dispute Dashboard data?',
        answer: 'The dashboard itself does not have an export button. To export dispute data, navigate to All Disputes using the View All Disputes button and use the Export function there.',
      },
      {
        question: 'How do I find a dispute that is not appearing in the Priority Disputes panel?',
        answer: 'The Priority Disputes panel shows only the highest-priority open disputes. To find any specific dispute, click the View All Disputes button and use the search or filter options on the All Disputes page.',
      },
      {
        question: 'What does the left border colour on each dispute card mean?',
        answer: 'The left border colour matches the dispute\'s priority: red for Critical, orange for High, grey for Medium, and light grey for Low. This provides a quick visual indicator of urgency without reading the priority badge.',
      },
    ],
    relatedPages: [
      { label: 'All Disputes', path: '/disputes/all' },
      { label: 'Collections Dashboard', path: '/collections/dashboard' },
      { label: 'Ageing Analysis', path: '/analytics/ageing-analysis' },
      { label: 'Dashboard', path: '/' },
    ],
  },

  'disputes-all': {
    pageTitle: 'Dispute Management',
    description:
      'A searchable, filterable list of every dispute across all customer accounts, showing status, priority, workflow progress, assigned owner, and due date.',
    functionality: {
      overview:
        'This page is the primary working list for the disputes team. Each row represents a single dispute raised against a customer. You can search, filter, sort, open a dispute for full details, log calls and emails, reassign ownership, update priority, resolve, close, or reopen disputes from this page. Use it daily to manage your dispute workload and keep records up to date.',
      keyFeatures: [
        {
          label: 'KPI Summary Cards',
          detail: 'Three cards at the top show Total Disputes, Active Disputes (new this week), and Disputed Amount -- all reflecting the current filtered view.',
        },
        {
          label: 'Search Bar',
          detail: 'Type a dispute ID, customer name, or keyword to filter the list in real time using server-side search.',
        },
        {
          label: 'Filters Panel',
          detail: 'Filter by Status (multi-select: New, Under Review, In Progress, Escalated, Resolved, Closed), Priority (multi-select: Low, Medium, High, Critical), Assigned User, and Overdue Only toggle. Active filter count shown on the Filters button badge.',
        },
        {
          label: 'Column Configuration',
          detail: 'Show, hide, reorder, resize, and pin columns to suit your workflow. Preferences are saved per user.',
        },
        {
          label: 'Sortable Columns',
          detail: 'Click any column header with an arrow icon to sort ascending or descending. Click a third time to reset to default sort (Created Date, newest first).',
        },
        {
          label: 'Bulk Actions Toolbar',
          detail: 'Select one or more disputes using checkboxes to act on them. With a single dispute selected: Log Call, Send Email, Add Note, Reassign User, Update Priority, Resolve Dispute, and Close Dispute are available. With multiple disputes selected: Reassign User, Update Priority, and Reopen Closed disputes are available.',
        },
        {
          label: 'Export',
          detail: 'Export the current filtered view to CSV using "Export Current View" (visible columns) or "Export All Columns". Export reflects all applied filters.',
        },
        {
          label: 'Workflow Progress Bar',
          detail: 'Each row shows a progress bar and level counter (e.g. 2/4) indicating how far through the dispute type\'s resolution workflow the dispute has advanced.',
        },
      ],
      sectionsAndFields: [
        { name: 'Total Disputes card', description: 'Count of all disputes matching the current filters, with a month-on-month change label.' },
        { name: 'Active Disputes card', description: 'Count of disputes with a non-terminal status (not Resolved or Closed), with a count of new disputes raised this week.' },
        { name: 'Disputed Amount card', description: 'Total monetary value of all disputes in the current filtered view, shown in the company base currency.' },
        { name: 'Search field', description: 'Free-text search filtered server-side against Dispute ID, customer name, and description.' },
        { name: 'Filters button', description: 'Opens the Filters panel. A badge on the button shows how many filter groups are active.' },
        { name: 'Export button', description: 'Dropdown offering "Export Current View" (visible columns, current filters) or "Export All Columns".' },
        { name: 'Column Settings button', description: 'Opens the column configuration dialog to show, hide, reorder, resize, and pin columns.' },
        { name: 'Dispute ID column', description: 'Unique system reference for the dispute. Click to open the full Dispute Detail page.' },
        { name: 'Customer column', description: 'Name of the customer account the dispute is raised against.' },
        { name: 'Type column', description: 'The dispute type (e.g. Pricing Dispute, Delivery Issue) which also determines the resolution workflow.' },
        { name: 'Amount column', description: 'The monetary value being disputed, shown in the company base currency.' },
        { name: 'Status column', description: 'Current status badge: New (blue), Under Review (purple), In Progress (amber), Escalated (red), Resolved (green), Closed (grey).' },
        { name: 'Priority column', description: 'Priority badge: Low (grey), Medium (blue), High (amber), Critical (red).' },
        { name: 'Workflow Progress column', description: 'A progress bar and level counter showing current stage vs total stages for the dispute type\'s workflow.' },
        { name: 'Assigned To column', description: 'The user currently responsible for resolving the dispute.' },
        { name: 'Due Date column', description: 'The target resolution date. Shown in red when overdue, amber when due today.' },
        { name: 'Created Date column', description: 'Date the dispute was raised. Hidden by default -- enable via Column Settings.' },
        { name: 'Workflow column', description: 'The name of the resolution workflow assigned to this dispute type. Hidden by default.' },
        { name: 'Description column', description: 'The dispute description text entered at creation. Hidden by default.' },
        { name: 'Bulk Action toolbar', description: 'Appears above the table when one or more rows are selected. With a single row selected: Log Call, Send Email, Add Note, Reassign User, Update Priority, Resolve Dispute, and Close Dispute. With multiple rows selected: Reassign User, Update Priority, and Reopen (for Closed disputes).' },
        { name: 'Filters panel -- Status', description: 'Multi-select checkboxes: New, Under Review, In Progress, Escalated, Resolved, Closed.' },
        { name: 'Filters panel -- Priority', description: 'Multi-select checkboxes: Low, Medium, High, Critical.' },
        { name: 'Filters panel -- Assigned User', description: 'Single-select dropdown listing all active users. Defaults to All Users.' },
        { name: 'Filters panel -- Overdue Only', description: 'Toggle switch that limits the list to disputes whose Due Date is in the past.' },
        { name: 'Pagination footer', description: 'Shows total record count, current page, and page size selector (10, 25, 50, 100). Includes first, previous, numbered pages, next, and last buttons.' },
      ],
      useCases: [
        'Starting the day by filtering to your own assigned disputes with an In Progress or New status to plan your workload.',
        'Bulk-reassigning a set of New disputes to a colleague who has capacity, without opening each one individually.',
        'Filtering to Critical priority and sorting by Due Date ascending to identify which disputes need immediate action.',
        'Logging a call outcome directly from the Bulk Action toolbar after speaking to a customer, without navigating to the full detail page.',
        'Exporting all Escalated disputes to CSV for a weekly escalation review meeting.',
        'Reopening a batch of Closed disputes after discovering they were closed in error.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Disputes > All Disputes from the left navigation. The list loads showing all disputes newest first. Before you begin, apply the Assigned User filter to scope the list to your own disputes -- this is the most common starting point.',
      steps: [
        {
          title: 'How do I find my own open disputes?',
          items: [
            {
              text: 'Click the Filters button in the toolbar.',
            },
            {
              text: 'Under Assigned User, select your name from the dropdown.',
              tip: 'Also select New and In Progress under Status to exclude Resolved and Closed disputes from the view.',
            },
            {
              text: 'Click Apply Filters.',
              note: 'The filter badge on the Filters button will show the number of active filter groups so you can tell at a glance that filtering is active.',
            },
          ],
        },
        {
          title: 'How do I open a dispute to see the full details?',
          items: [
            {
              text: 'Click the Dispute ID link in the first column of the row.',
              note: 'This navigates to the full Dispute Detail page where you can view the workflow stages, activity timeline, linked transactions, notes, and communications.',
            },
            {
              text: 'To return to the list, use the browser Back button or the breadcrumb navigation.',
              tip: 'Your filters are preserved in the URL, so navigating back restores the same filtered view.',
            },
          ],
        },
        {
          title: 'How do I log a call or send an email from the list?',
          items: [
            {
              text: 'Tick the checkbox on the dispute row to select it.',
            },
            {
              text: 'In the Bulk Action toolbar that appears at the bottom of the screen, click Log Call to record a call outcome, or Send Email to compose and send an email to the dispute contact.',
              note: 'Send Email requires the dispute to have a linked customer contact with a valid email address. If no contacts are available, add them on the Customer page first.',
            },
            {
              text: 'Complete the form and click Save or Send.',
            },
          ],
        },
        {
          title: 'How do I reassign a dispute to a different user?',
          items: [
            {
              text: 'Tick the checkbox on one or more dispute rows to select them.',
              tip: 'Use the search bar to narrow the list to a specific customer or dispute type before selecting rows.',
            },
            {
              text: 'Click Reassign User in the Bulk Action toolbar that appears at the bottom of the screen.',
            },
            {
              text: 'Select the new user from the dropdown and click Confirm.',
            },
          ],
        },
        {
          title: 'How do I update the priority of a dispute?',
          items: [
            {
              text: 'Tick the checkbox on one or more dispute rows to select them.',
            },
            {
              text: 'Click Update Priority in the Bulk Action toolbar that appears at the bottom of the screen.',
            },
            {
              text: 'Select the new priority (Low, Medium, High, Critical) and click Update Priority.',
              note: 'Priority changes are reflected immediately in the Priority badge on the row and on the Disputes Dashboard.',
            },
          ],
        },
        {
          title: 'How do I resolve or close a dispute?',
          items: [
            {
              text: 'Tick the checkbox on the dispute row to select it.',
            },
            {
              text: 'In the Bulk Action toolbar at the bottom of the screen, click Resolve Dispute to record the resolution outcome (Upheld, Denied, or Partial) and enter resolution notes.',
              note: 'Resolve Dispute is only available for disputes that are not already Resolved or Closed. If you select a mix of Closed and non-Closed rows, bulk actions will be hidden.',
            },
            {
              text: 'Alternatively, click Close Dispute in the toolbar to close it without a formal resolution -- select a close reason (Duplicate, Customer Withdrew, Invalid Dispute, or Other) and add a note.',
              tip: 'Use Resolve Dispute when a decision has been reached. Use Close Dispute for administrative closures such as duplicates.',
            },
          ],
        },

        {
          title: 'How do I configure which columns are visible?',
          items: [
            {
              text: 'Click the Column Settings button (grid icon) in the toolbar.',
            },
            {
              text: 'Tick or untick columns to show or hide them.',
            },
            {
              text: 'Drag column rows in the dialog to reorder them.',
              tip: 'Pin frequently used columns to the left so they stay visible when scrolling horizontally on wide tables.',
            },
            {
              text: 'Click Save to apply your preferences. They are saved per user and persist across sessions.',
            },
          ],
        },
      ],
      proTips: [
        'Set Status = New and sort by Created Date ascending first thing each morning -- this surfaces disputes that have been waiting the longest without anyone picking them up.',
        'Use the Overdue Only toggle in the Filters panel combined with Priority = Critical to build an immediate action list for high-value overdue disputes.',
        'Pin the Due Date and Workflow Progress columns to the left so they are always visible regardless of how many other columns are shown.',
        'After a bulk reassign, remove the Assigned User filter and re-apply it with your own name to confirm the reassignment worked and your workload count is accurate.',
      ],
    },
    faq: [
      {
        question: 'What does the Workflow Progress bar show?',
        answer: 'It shows how far through the dispute type\'s resolution workflow the dispute has advanced. The counter (e.g. 2/4) shows the current stage number out of the total number of stages. Each dispute type has its own workflow with a different number of stages.',
      },
      {
        question: 'What does each Status mean?',
        answer: 'New -- just raised, not yet actioned. Under Review -- raised and under initial review before active investigation begins. In Progress -- actively being investigated. Escalated -- referred to a senior user or external party. Resolved -- a decision has been reached and recorded. Closed -- administratively closed without a formal resolution decision.',
      },
      {
        question: 'What does each Priority mean?',
        answer: 'Low -- no immediate action required. Medium -- standard handling within normal SLA. High -- action required within a shorter timeframe. Critical -- immediate action required, typically high-value or customer-threatening disputes.',
      },
      {
        question: 'Why is the Resolve Dispute option not showing in the Bulk Action toolbar?',
        answer: 'Resolve Dispute is hidden if the selected dispute is already Resolved or Closed, or if you have selected a mix of Closed and non-Closed disputes. Reopen the dispute first if it was closed in error.',
      },
      {
        question: 'How do I filter to see only overdue disputes?',
        answer: 'Click Filters and toggle on Overdue Only. This limits the list to disputes whose Due Date is before today. Combine with Status filters to see overdue disputes that are still active.',
      },
      {
        question: 'Can I filter by multiple statuses at once?',
        answer: 'Yes. In the Filters panel, tick as many Status checkboxes as you need. Each ticked status is included in the results -- the filter uses OR logic, not AND.',
      },
      {
        question: 'How do I find all disputes for a specific customer?',
        answer: 'Type the customer name into the Search bar. The list will filter server-side to show disputes where the customer name matches. You can also navigate to the customer\'s record on the Customers page and open their disputes from there.',
      },
      {
        question: 'What happens to a disputed invoice in the ageing analysis?',
        answer: 'Disputed invoices remain in the ageing analysis by default unless the Exclude Disputed filter is applied on the Ageing Analysis page.',
      },
      {
        question: 'Can I reopen a dispute that has been closed?',
        answer: 'Yes. Select the closed dispute row using the checkbox, then click the Reopen button in the Bulk Action toolbar. The dispute will be set back to New or In Progress depending on its workflow level.',
        steps: [
          'Tick the checkbox on the closed dispute row.',
          'In the Bulk Action toolbar above the table, click Reopen.',
          'Confirm the action in the dialog.',
        ],
      },
      {
        question: 'Why does the Disputed Amount KPI card not match what I see on the Disputes Dashboard?',
        answer: 'The KPI cards on this page reflect the current filtered view only. If you have active filters (status, priority, assigned user), the total will be lower than the unfiltered Dashboard figure. Clear all filters to see the full total.',
      },
      {
        question: 'Can I delete a dispute?',
        answer: 'Disputes cannot be deleted by standard users as they form part of the financial audit trail. Close the dispute instead using a close reason of Duplicate or Invalid Dispute. If a deletion is genuinely required, contact your system administrator.',
      },
    ],
    relatedPages: [],
  },

  'dispute-details': {
    pageTitle: 'Dispute Details',
    description:
      'The full record for a single dispute — view status, workflow progress, linked transactions, activity timeline, and complete audit history, with actions to advance the workflow, resolve, close, or reopen the dispute.',
    functionality: {
      overview:
        'The Dispute Details page opens when you click a Dispute ID from the All Disputes list. It provides a complete picture of one customer dispute across four tabs: Overview (customer info, dispute summary, next action, notes, and linked transactions), Workflow (visual stage-by-stage progress tracker), Activity (consolidated timeline of calls, emails, and notes), and Audit Log (a tamper-proof record of every change). A collapsible left sidebar lists all disputes so you can jump between records without returning to the list. Quick Action buttons in the header bar let you log a call, send an email, add a note, advance to the next workflow stage, resolve, close, or reopen the dispute — all without leaving the page. Click Edit Dispute to enter edit mode, where you can update the contact, priority, assigned user, and toggle which transactions are linked to the dispute.',
      keyFeatures: [
        {
          label: 'Collapsible Disputes Sidebar',
          detail:
            'A panel on the left edge lists all disputes in the system. Click any row to navigate to that dispute directly. Collapse it using the arrow button to gain more screen space; a teal DISPUTES handle on the left edge lets you reopen it.',
        },
        {
          label: 'Quick Actions Bar',
          detail:
            'A teal-highlighted card beneath the page header offers one-click access to: Call Customer (opens the Log Call modal), Send Email (opens the Send Email modal), Add Note (opens the Add Note modal), Next Stage (advances the workflow), Resolve Dispute (opens the resolution modal), Close Dispute (opens the close modal), and Reopen Dispute (for Closed disputes only). Actions are disabled for Resolved and Closed disputes.',
        },
        {
          label: 'Edit Dispute Mode',
          detail:
            'Click Edit Dispute in the top-right header to enter edit mode. In this mode the Contact Name, Priority, and Assigned User fields in the Overview tab become editable, and transaction checkboxes appear to link or unlink transactions. Click Save Changes to commit all edits or Cancel to discard them. The Edit Dispute button is disabled for Resolved and Closed disputes.',
        },
        {
          label: 'Overview Tab — Customer Information Card',
          detail:
            'Displays the customer account code and name, the contact name (editable in edit mode via a contact dropdown), contact email (with teal Send Email button), and contact phone (with teal Log Call button). One-click communication is available directly from this card.',
        },
        {
          label: 'Overview Tab — Dispute Summary Card',
          detail:
            'Shows Dispute ID, disputed Amount (shown in red), Dispute Type, Status badge, Priority badge (editable in edit mode), Assigned To (editable in edit mode), and a Workflow Level progress bar showing current stage vs total stages. A Closed Dispute information panel appears when a close reason is present. A Resolution Outcome card appears when the dispute is Resolved.',
        },
        {
          label: 'Overview Tab — Next Action Card',
          detail:
            'Shows the description of the next scheduled workflow action and its due date. The card reads "No further actions scheduled" with a n/a due date when the dispute is at the final stage.',
        },
        {
          label: 'Overview Tab — Dispute Notes Card',
          detail:
            'Shows a scrollable list of internal notes added to this dispute. Each note shows the text, author, and timestamp. A badge in the card header shows the total note count.',
        },
        {
          label: 'Overview Tab — Transactions Under Dispute',
          detail:
            'A searchable, sortable, paginated table of every transaction linked to this dispute. Supports column configuration (show/hide/reorder), CSV export (visible columns or all columns), and transaction detail drill-down by clicking the Reference Number. In edit mode, row checkboxes allow linking or unlinking of individual transactions.',
        },
        {
          label: 'Workflow Tab',
          detail:
            'A vertical timeline showing every stage in the dispute type\'s resolution workflow. Completed stages are shown with a filled primary circle and a tick icon. The current stage shows a clock icon. Future stages show the stage number. Each stage shows its level number, description, estimated days (where configured), and completion date for completed stages.',
        },
        {
          label: 'Activity Tab',
          detail:
            'A consolidated timeline of all calls logged, emails sent, and notes added against this dispute. Each item shows a type badge (Activity, Communication, Note), the description, outcome (for calls), the author, and timestamp. Load more older items using the Load more button at the foot of the timeline.',
        },
        {
          label: 'Audit Log Tab',
          detail:
            'A complete, ordered history of every modification to this dispute: status changes, priority changes, assignment changes, field updates, notes added, transaction links and unlinks, and workflow stage advances. Each entry shows a colour-coded action icon, description, old → new values (where applicable), the user responsible, and the timestamp. Load older entries using Load more.',
        },
        {
          label: 'Next Stage Action',
          detail:
            'Available from the Quick Actions bar for disputes that are not at the final workflow stage and not Resolved or Closed. Opens a modal prompting for notes describing the actions taken. On confirming, the dispute advances to the next level, the status is updated to In Progress, the due date is recalculated from the next stage\'s follow-up days, and the assigned user may be updated based on the next stage configuration.',
        },
        {
          label: 'Resolve Dispute Action',
          detail:
            'Opens a modal to record the formal resolution outcome: Upheld (customer won — transaction reversed), Denied (merchant won — transaction stands), or Partial (partial credit issued — enter a resolution amount). Resolution notes are required. On confirming, the dispute status is set to Resolved and the resolution outcome, amount, date, and notes are recorded.',
        },
        {
          label: 'Close Dispute Action',
          detail:
            'Opens a modal to administratively close the dispute without a formal resolution decision. Select a close reason (Duplicate Entry, Customer Withdrew Dispute, Invalid Dispute, Other) and optionally add a note. An additional note is required when the reason is Other. On confirming, the dispute status is set to Closed.',
        },
        {
          label: 'Reopen Dispute Action',
          detail:
            'Available only for Closed disputes. Opens a modal requiring a reason for reopening. On confirming, the dispute is set back to New (if at level 1) or In Progress, the close reason and note are cleared, and the reopen reason is recorded as a note in the activity timeline.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Disputes Sidebar',
          description:
            'Left-side panel listing all disputes. Each row shows the Dispute ID and customer name. Click any row to navigate to that dispute. The currently open dispute is highlighted. Collapse with the arrow button; reopen using the teal DISPUTES handle.',
        },
        {
          name: 'Breadcrumb navigation',
          description:
            'Shows the path: Home > All Disputes > [Dispute ID]. Click All Disputes to return to the list.',
        },
        {
          name: 'Page header — Dispute ID and title',
          description:
            'Shows "Dispute Details — [Dispute ID]" as the heading and "View and manage dispute information" as the subtitle. A back arrow returns to All Disputes (or the Disputes Dashboard if arrived from there).',
        },
        {
          name: 'Edit Dispute / Save Changes / Cancel buttons',
          description:
            'In view mode, a single Edit Dispute button (with pencil icon) is shown top-right. Clicking it enters edit mode and the button label changes to Cancel. A Save Changes button also appears. Both are disabled for Resolved and Closed disputes.',
        },
        {
          name: 'Quick Actions bar',
          description:
            'A teal-gradient card with a row of action buttons. Call Customer and Send Email are always available (but disabled when Resolved/Closed). Add Note is always available. Next Stage, Resolve Dispute, and Close Dispute are shown only when the dispute is not Resolved or Closed. Reopen Dispute is shown only when the dispute is Closed.',
        },
        {
          name: 'Customer Information — Customer',
          description:
            'The customer account code (in brackets) and name. Read-only on this page.',
        },
        {
          name: 'Customer Information — Contact Name',
          description:
            'The contact associated with this dispute. Read-only in view mode. In edit mode, a dropdown lists all contacts on the customer account.',
        },
        {
          name: 'Customer Information — Email',
          description:
            'The contact\'s email address. A teal envelope button next to it opens the Send Email modal. Clicking the address directly opens the default email client.',
        },
        {
          name: 'Customer Information — Phone',
          description:
            'The contact\'s phone number. A teal phone button next to it opens the Log Call modal. Clicking the number opens the default phone dialler.',
        },
        {
          name: 'Dispute Summary — Dispute ID',
          description: 'The unique system reference for this dispute (e.g. DSP-001). Read-only.',
        },
        {
          name: 'Dispute Summary — Amount',
          description:
            'The total monetary value being disputed, shown in red. Calculated from the linked transactions.',
        },
        {
          name: 'Dispute Summary — Dispute Type',
          description:
            'The category of the dispute (e.g. Billing Error, Delivery Issue). Determines the resolution workflow. Read-only.',
        },
        {
          name: 'Dispute Summary — Status',
          description:
            'Colour-coded badge: New (blue), Under Review (purple), In Progress (amber), Escalated (red), Resolved (green), Closed (grey). Derived from the current workflow level and any closed/resolved state. Read-only — updated automatically when actions are taken.',
        },
        {
          name: 'Dispute Summary — Priority',
          description:
            'Colour-coded badge: Low (grey), Medium (blue), High (amber), Critical (red). Read-only in view mode. In edit mode, a dropdown lets you change the priority.',
        },
        {
          name: 'Dispute Summary — Assigned To',
          description:
            'The user currently responsible for this dispute. Read-only in view mode. In edit mode, a dropdown lists all active users.',
        },
        {
          name: 'Dispute Summary — Workflow Level progress bar',
          description:
            'Shows the workflow name and "Level X of Y" alongside a proportional progress bar. Updates when Next Stage is used.',
        },
        {
          name: 'Dispute Summary — Closed Dispute panel',
          description:
            'A grey information box shown when the dispute has a close reason. Shows the close reason label, the date closed, and any close note.',
        },
        {
          name: 'Resolution Outcome card',
          description:
            'A green information card shown when the dispute has been Resolved. Shows the outcome (Upheld, Denied, Partial), the resolution amount (for Partial), the resolution date, who resolved it, and the resolution details notes.',
        },
        {
          name: 'Next Action card',
          description:
            'Shows the next scheduled workflow action description and its due date (formatted as e.g. "Jan 15, 2025"). When the dispute is at the final stage or resolved, shows "No further actions scheduled" and "n/a" for the due date.',
        },
        {
          name: 'Dispute Notes card',
          description:
            'Scrollable list of internal notes. Each note shows the note text, author name, and timestamp. A note count badge is shown in the card header.',
        },
        {
          name: 'Transactions Under Dispute — Search field',
          description:
            'Filters the transactions table in real time by reference number, description, currency, or status.',
        },
        {
          name: 'Transactions Under Dispute — Export button',
          description:
            'A dropdown offering Export Visible Columns (currently visible columns only) or Export All Columns. Downloads as a CSV file named dispute-[ID]-transactions-[date].csv.',
        },
        {
          name: 'Transactions Under Dispute — Column Settings button',
          description:
            'Opens the column configuration dialog. Default visible columns are: Reference Number, Transaction Date, Due Date, Type, Gross, Outstanding, Status, External Dispute. Hidden by default: Customer Name, Currency, Description, Created By, Modified By, Account, Net, Tax, Sub Ledger, Order Number, Actions.',
        },
        {
          name: 'Transactions Under Dispute — Reference Number column',
          description:
            'The transaction reference, shown as a clickable link. Clicking opens the Transaction Detail sheet to view full transaction information.',
        },
        {
          name: 'Transactions Under Dispute — Status column',
          description:
            'Shows Outstanding (default), Overdue (destructive/red, when due date is in the past), or Completed (default/solid, when the transaction is settled).',
        },
        {
          name: 'Transactions Under Dispute — External Dispute column',
          description:
            'Indicates whether this transaction is flagged as an external dispute in the accounting system. Shows a red "Yes" badge or a grey "No" label.',
        },
        {
          name: 'Transactions Under Dispute — row checkbox (edit mode)',
          description:
            'Only active in edit mode. Uncheck a transaction to unlink it from this dispute. The row is deselected and shown with reduced opacity. Saving will remove the link.',
        },
        {
          name: 'Transactions Under Dispute — Total Disputed Amount footer',
          description:
            'Always visible at the bottom of the card. Shows the sum of gross amounts across all transactions. In edit mode, updates in real time as transactions are checked or unchecked and shows how many transactions are selected.',
        },
        {
          name: 'Workflow tab — stage row',
          description:
            'Each stage shows a numbered circle (filled primary for completed stages, muted for future), the level number, a description, the estimated days (when configured), and the date the stage was completed (for completed stages).',
        },
        {
          name: 'Activity tab — Filter by Type toggles',
          description:
            'Three toggle buttons: Activity (blue), Communications (green), Notes (purple). Click to include or exclude categories from the timeline.',
        },
        {
          name: 'Audit Log tab — action icon colours',
          description:
            'Each entry\'s icon is colour-coded by action type: blue = status change, green = transaction added, red = transaction removed, amber = priority change, purple = assignment change, cyan = field update, teal = note added, grey = case created / other.',
        },
        {
          name: 'Next Stage modal — Notes field',
          description:
            'Required. Describe the actions taken and any context for advancing the stage. These notes are saved to the activity timeline.',
        },
        {
          name: 'Resolve Dispute modal — Resolution Outcome',
          description:
            'Required radio group: Upheld (customer won, transaction reversed), Denied (merchant won, transaction stands), Partial (partial credit — also requires a Resolution Amount).',
        },
        {
          name: 'Resolve Dispute modal — Resolution Amount',
          description:
            'Only visible when Outcome is Partial. Enter the monetary amount of the credit issued to the customer.',
        },
        {
          name: 'Resolve Dispute modal — Resolution Notes',
          description:
            'Required. Explain the reasoning behind the resolution decision.',
        },
        {
          name: 'Close Dispute modal — Reason for Closing',
          description:
            'Required dropdown: Duplicate Entry, Customer Withdrew Dispute, Invalid Dispute, or Other.',
        },
        {
          name: 'Close Dispute modal — Additional Notes',
          description:
            'Optional for most reasons. Required (marked with a red asterisk) when reason is Other.',
        },
        {
          name: 'Reopen Dispute modal — Reason for Reopening',
          description:
            'Required text area. The reason is saved as a note in the activity timeline when the dispute is reopened.',
        },
      ],
      useCases: [
        'Reviewing a dispute before a customer call — checking the current workflow level, the next action due date, and the linked transactions to prepare talking points.',
        'Advancing a dispute to the next workflow stage after completing the current level\'s actions, with a notes entry summarising what was done.',
        'Resolving a dispute after receiving written confirmation from the customer — recording the outcome (Upheld/Denied/Partial), the resolution amount for partial settlements, and the resolution notes.',
        'Closing a dispute administratively when a customer withdraws their claim — selecting "Customer Withdrew Dispute" as the close reason and adding context in the notes.',
        'Reopening a dispute that was closed in error — recording the reason so there is an audit trail explaining why the decision was reversed.',
        'Reassigning a dispute to a different credit controller — entering edit mode, selecting the new user from the Assigned To dropdown, and saving.',
        'Updating the priority to Critical when a dispute is escalated due to its value or time sensitivity.',
        'Reviewing the Audit Log to understand who changed the status and when — useful for manager queries or compliance investigations.',
        'Logging a call outcome and linking it to the dispute without leaving the detail page, keeping the activity timeline current.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Disputes > All Disputes, then click any Dispute ID to open its full detail page. The page loads on the Overview tab in view mode. Use the four tabs — Overview, Workflow, Activity, Audit Log — to navigate between sections. The left sidebar lists all disputes and lets you jump between records; collapse it with the arrow button to give the main content more space.',
      steps: [
        {
          title: 'How do I advance a dispute to the next workflow stage?',
          items: [
            {
              text: 'Confirm the dispute is not Resolved or Closed — the Next Stage button in the Quick Actions bar is only visible for active disputes that have not reached the final level.',
            },
            {
              text: 'Click Next Stage in the Quick Actions bar.',
            },
            {
              text: 'In the modal that appears, type notes describing the actions taken at this stage.',
              note: 'Notes are required — the Proceed button remains disabled until at least one character is entered. These notes are saved to the Activity timeline.',
            },
            {
              text: 'Click Proceed. The dispute advances to the next level, the status updates to In Progress, and the due date is recalculated from the next stage\'s follow-up days configuration.',
              tip: 'Check the Workflow tab after advancing to confirm the stage moved correctly and the progress bar has updated.',
            },
          ],
        },
        {
          title: 'How do I resolve a dispute?',
          items: [
            {
              text: 'Click Resolve Dispute in the Quick Actions bar. This button is only available for disputes that are not already Resolved or Closed.',
            },
            {
              text: 'In the Complete Resolution modal, select the Resolution Outcome: Upheld (customer won), Denied (merchant won), or Partial (partial credit).',
            },
            {
              text: 'If you selected Partial, enter the Resolution Amount — the monetary value of the credit being issued.',
            },
            {
              text: 'Enter Resolution Notes explaining the reasoning. This field is required.',
              note: 'If you are resolving before the final workflow stage, a yellow warning is shown: "Early Resolution — You are resolving at Level X of Y". You can still proceed, but it is recorded that the workflow was not completed.',
            },
            {
              text: 'Click Complete Resolution. The dispute status changes to Resolved and the resolution outcome, amount, date, and notes are recorded in the Dispute Summary card.',
            },
          ],
        },
        {
          title: 'How do I close a dispute without a formal resolution?',
          items: [
            {
              text: 'Click Close Dispute in the Quick Actions bar.',
            },
            {
              text: 'Select a Reason for Closing from the dropdown: Duplicate Entry, Customer Withdrew Dispute, Invalid Dispute, or Other.',
            },
            {
              text: 'Optionally add notes in the Additional Notes text area to provide context.',
              note: 'If you select "Other" as the reason, an additional note is required — you cannot close the dispute without entering explanatory text.',
            },
            {
              text: 'Click Close Dispute in the modal footer. The dispute status changes to Closed, and the close reason and date appear in a grey information panel in the Dispute Summary card.',
              tip: 'Use Close Dispute for administrative closures only (duplicates, withdrawn claims, invalid submissions). Use Resolve Dispute when a formal decision has been reached.',
            },
          ],
        },
        {
          title: 'How do I reopen a dispute that was closed?',
          items: [
            {
              text: 'Confirm the dispute status is Closed — the Reopen Dispute button only appears for Closed disputes.',
            },
            {
              text: 'Click Reopen Dispute in the Quick Actions bar.',
            },
            {
              text: 'In the modal, enter the reason for reopening. This is required — the Confirm Reopen button is disabled until text is entered.',
            },
            {
              text: 'Click Confirm Reopen. The dispute status is set back to New (if at level 1) or In Progress, the closed reason and note are cleared, and the reopen reason is saved as a note in the Activity timeline.',
            },
          ],
        },
        {
          title: 'How do I log a call or send an email from this page?',
          items: [
            {
              text: 'To log a call: click Call Customer in the Quick Actions bar, or click the teal phone button next to the phone number in the Customer Information card.',
            },
            {
              text: 'The Log Call modal opens. Complete the call outcome and any notes, then click Save.',
            },
            {
              text: 'To send an email: click Send Email in the Quick Actions bar, or click the teal envelope button next to the email address in the Customer Information card.',
            },
            {
              text: 'The Send Email modal opens. Select a template or compose a message, confirm the recipient, and click Send.',
              tip: 'Both calls and emails are recorded in the Activity timeline automatically after saving.',
            },
          ],
        },
        {
          title: 'How do I add an internal note?',
          items: [
            {
              text: 'Click Add Note in the Quick Actions bar.',
            },
            {
              text: 'The Add Note modal opens. Type the note content.',
            },
            {
              text: 'Click Save Note. The note appears immediately in the Dispute Notes card on the Overview tab and in the Activity timeline.',
              note: 'Notes are internal only and are never sent to the customer.',
            },
          ],
        },
        {
          title: 'How do I edit the dispute — change priority, assigned user, or contact?',
          items: [
            {
              text: 'Click Edit Dispute in the top-right of the page header.',
              note: 'The Edit Dispute button is disabled for Resolved and Closed disputes. If the button is greyed out, check the dispute status.',
            },
            {
              text: 'In the Dispute Summary card, use the Priority dropdown to update the urgency level.',
            },
            {
              text: 'Use the Assigned To dropdown to select a different credit controller.',
            },
            {
              text: 'In the Customer Information card, use the Contact Name dropdown to select a different contact person from the customer\'s contact list.',
              tip: 'Changing the contact also updates the email and phone number shown in the Customer Information card to match the selected contact\'s details.',
            },
            {
              text: 'Click Save Changes to commit all changes in one request.',
            },
          ],
        },
        {
          title: 'How do I link or unlink a transaction from this dispute?',
          items: [
            {
              text: 'Click Edit Dispute in the page header to enter edit mode.',
            },
            {
              text: 'In the Transactions Under Dispute card, a checkbox appears on the left of each transaction row.',
            },
            {
              text: 'To unlink a transaction, uncheck its checkbox. The row shows with reduced opacity and the Total Disputed Amount footer updates immediately.',
              note: 'Unchecking a row marks it for removal but does not delete the underlying transaction. The invoice remains open and can be linked to a different dispute.',
            },
            {
              text: 'Click Save Changes to commit. The unlinking is recorded in the Audit Log.',
            },
          ],
        },
        {
          title: 'How do I view the full workflow progression?',
          items: [
            {
              text: 'Click the Workflow tab.',
            },
            {
              text: 'Each stage is shown in a vertical timeline. Completed stages (levels at or below the current level) have a filled circle with a tick. The active stage shows a clock icon. Future stages show the stage number.',
            },
            {
              text: 'Read each stage\'s description to understand what actions are expected at that point in the workflow.',
              tip: 'The Workflow tab is read-only — use the Next Stage button in the Quick Actions bar to advance the dispute.',
            },
          ],
        },
        {
          title: 'How do I review the full change history for a dispute?',
          items: [
            {
              text: 'Click the Audit Log tab.',
            },
            {
              text: 'Each row shows a colour-coded action icon, a description of the change, old → new values (where applicable), the user who made the change, and the date and time.',
            },
            {
              text: 'If there are many entries, click Load more at the bottom to paginate through the full history.',
              tip: 'The Audit Log is tamper-proof — entries cannot be edited or deleted. Use it to resolve management queries about when a status was changed or which user took an action.',
            },
          ],
        },
        {
          title: 'How do I export the linked transactions to CSV?',
          items: [
            {
              text: 'On the Overview tab, scroll to the Transactions Under Dispute card.',
            },
            {
              text: 'Click the download icon button in the card header.',
            },
            {
              text: 'Select Export Visible Columns to export only the columns currently shown, or Export All Columns to include every available field.',
            },
            {
              text: 'The file downloads as dispute-[ID]-transactions-[date].csv.',
              note: 'If a search filter is active in the transactions table, only the filtered rows are exported.',
            },
          ],
        },
      ],
      proTips: [
        'Collapse the left sidebar when you need more horizontal space for the Transactions table — the teal DISPUTES handle on the left edge is always available to reopen it.',
        'Use the Add Note quick action rather than entering full edit mode just to leave an observation. It keeps the rest of the record in read-only view and is faster for a quick update after a call.',
        'Before resolving a dispute at an early stage, review the Workflow tab to check which stages have not yet been completed — the Early Resolution warning in the Resolve modal reminds you of this, but it is good practice to document why it is being resolved early in the Resolution Notes field.',
        'After using Next Stage, switch to the Workflow tab to confirm the progress bar moved to the correct level. This catches any misconfiguration in the workflow before the next scheduled action fires.',
        'Use the Audit Log tab at the start of any management review meeting to get a concise summary of what has happened to the dispute since the last check, rather than reading through the full Activity timeline.',
        'When closing a dispute as a Duplicate, add the Dispute ID of the original record in the Additional Notes field. This makes it easy to find the source dispute later from the closed one\'s record.',
      ],
    },
    faq: [
      {
        question: 'Why is the Edit Dispute button disabled?',
        answer:
          'The Edit Dispute button is disabled when the dispute status is Resolved or Closed. Resolved and Closed disputes are locked to preserve the integrity of the final decision. If the dispute was resolved or closed in error, use the Reopen Dispute button (for Closed disputes) to unlock it. Resolved disputes cannot be reopened — contact your system administrator if a Resolved dispute needs to be amended.',
      },
      {
        question: 'Why is the Next Stage button not showing?',
        answer:
          'The Next Stage button is hidden when: (1) the dispute is Resolved or Closed; (2) the dispute is already at the final workflow stage (use Resolve Dispute instead to complete it formally). If the dispute is active and at a non-final stage but the button is missing, check that your user role includes permission to advance dispute workflow stages.',
      },
      {
        question: 'What is the difference between Resolve Dispute and Close Dispute?',
        answer:
          'Resolve Dispute records a formal decision about the dispute — Upheld (customer won), Denied (merchant won), or Partial (partial credit). It is used when you have reached a decision on the merits of the claim. Close Dispute is an administrative action used for disputes that do not need a formal decision, such as duplicates, withdrawn claims, or invalid submissions. Closed disputes can be reopened; Resolved disputes cannot.',
      },
      {
        question: 'What does each status mean on a dispute?',
        answer:
          'New (blue) — dispute has been raised and not yet actioned. Under Review (purple) — initial review in progress before active investigation. In Progress (amber) — actively being investigated. Escalated (red) — referred to a senior user or external party for a decision. Resolved (green) — a formal decision has been reached and recorded. Closed (grey) — administratively closed without a formal decision.',
      },
      {
        question: 'Can I reopen a Resolved dispute?',
        answer:
          'No. Resolved disputes are permanently locked and cannot be reopened from this page. Only Closed disputes have a Reopen button. If a Resolved dispute needs to be revisited, contact your system administrator — a resolution reversal requires a manual process to preserve the audit trail.',
      },
      {
        question: 'What happens when I use Next Stage at the final workflow level?',
        answer:
          'If the dispute is at the penultimate level (one before the final), clicking Next Stage opens the Next Stage modal for notes. On confirming, the dispute advances to the final level and the status is automatically set to Resolved, completing the workflow. A success toast confirms "Dispute resolved — all workflow stages completed".',
      },
      {
        question: 'Why does the Resolve Dispute modal show an Early Resolution warning?',
        answer:
          'The warning appears when you initiate Resolve Dispute before the dispute has reached the final workflow stage. You can still resolve early — the warning is informational only. The early resolution level is recorded in the audit trail alongside the normal resolution fields.',
      },
      {
        question: 'What does the Total Disputed Amount footer show?',
        answer:
          'It shows the sum of the gross amounts of all linked transactions. In edit mode, it updates in real time as transactions are checked or unchecked, and also shows how many transactions are currently selected out of the total.',
      },
      {
        question: 'How do I see which transactions are linked to this dispute?',
        answer:
          'The Transactions Under Dispute card on the Overview tab lists all linked transactions. Click any Reference Number to open the Transaction Detail sheet for the full transaction record including invoice lines, payment history, and allocation status.',
      },
      {
        question: 'What does the External Dispute column in the Transactions table mean?',
        answer:
          'External Dispute (labelled "On Dispute" in the column configuration) indicates whether the transaction is flagged as disputed in the linked accounting system (e.g. Financials or DimensionsvNext). A red "Yes" badge means the accounting system also carries a dispute flag on that transaction.',
      },
      {
        question: 'Can I navigate between disputes without going back to the list?',
        answer:
          'Yes. The left sidebar lists all disputes. If the sidebar is collapsed, click the teal DISPUTES handle on the left edge to expand it. Then click any row to navigate directly to that dispute record.',
      },
      {
        question: 'What is recorded in the Audit Log compared to the Activity timeline?',
        answer:
          'The Activity tab shows human-facing interactions: calls logged, emails sent, and notes added by credit controllers. The Audit Log records system-level field changes: status changes, priority changes, assigned user changes, transaction links and unlinks, and workflow stage advances. Use the Activity tab to understand what was communicated; use the Audit Log to understand what changed in the record.',
      },
      {
        question: 'Why are my filter changes in the Transactions table not included in the export?',
        answer:
          'They are. The CSV export respects the current search filter applied to the transactions table — only the rows matching your search are exported. Clear the search field before exporting if you want all linked transactions included.',
      },
    ],
    relatedPages: [
      { label: 'All Disputes', path: '/disputes/all' },
      { label: 'Disputes Dashboard', path: '/disputes' },
      { label: 'Dispute Types', path: '/settings/dispute-types' },
      { label: 'Customer Detail', path: '/customers/:id' },
    ],
  },

  'credit-scoring': {
    pageTitle: 'Credit Scoring & Risk Assessment',
    description:
      'The Credit Scoring page lets you view customer credit scores, assess risk based on payment behaviour, and manage credit limit recommendations.',
    functionality: {
      overview:
        'This page calculates and displays creditworthiness scores for each customer based on payment history, outstanding balances, and external credit data where available. Use it to make informed decisions about credit limits before opening new accounts or reviewing existing ones.',
      keyFeatures: [
        { label: 'Credit Score Display', detail: 'A numeric or banded score per customer showing their current creditworthiness.' },
        { label: 'Risk Factor Breakdown', detail: 'Shows which factors are driving the score up or down -- payment history, utilisation, late payment frequency.' },
        { label: 'Credit Limit Recommendations', detail: 'Suggests a credit limit based on the calculated score and your business rules.' },
        { label: 'Score History', detail: 'Shows how the score has changed over time, indicating whether a customer\'s behaviour is improving or deteriorating.' },
        { label: 'Manual Override', detail: 'Allows an authorised user to override the calculated score or credit limit with a manual assessment.' },
      ],
      sectionsAndFields: [
        { name: 'Customer', description: 'The customer being scored.' },
        { name: 'Credit Score', description: 'The calculated score -- higher is better. Bands indicate risk level.' },
        { name: 'Risk Band', description: 'A category label (High / Medium / Low / Very Low) mapped from the numeric score.' },
        { name: 'Score Factors', description: 'The individual components contributing to the score: payment history, DSO, credit utilisation, dispute frequency.' },
        { name: 'Recommended Credit Limit', description: 'A suggested credit limit derived from the score and average invoice values.' },
        { name: 'Current Credit Limit', description: 'The limit currently applied to this customer.' },
        { name: 'Score History Chart', description: 'A line chart showing how the score has trended over the last 6-12 months.' },
        { name: 'Override Button', description: 'Allows authorised users to set a manual score or credit limit and record a reason.' },
      ],
      useCases: [
        'Reviewing a customer\'s credit score before approving a new order that would take them over their current credit limit.',
        'Annual credit review -- checking all customers\' scores and updating limits accordingly.',
        'Deciding whether to extend credit to a new customer before their first invoice.',
        'Identifying customers whose score has fallen significantly as early warning of potential bad debt.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Open Credit Scoring from the left navigation. Scores are calculated nightly based on data in the system. For new customers with no payment history, the score will be based on available data only.',
      steps: [
        {
          title: 'How do I view a customer\'s credit score?',
          items: [
            { text: 'Search for the customer by name or account number.' },
            { text: 'Click the customer row to open their credit score detail.' },
            { text: 'Review the score, risk band, and contributing factors.' },
          ],
        },
        {
          title: 'How do I update a customer\'s credit limit?',
          items: [
            { text: 'Open the customer\'s credit score detail.' },
            { text: 'Click Edit next to the Current Credit Limit field.' },
            { text: 'Enter the new limit and a reason for the change.' },
            { text: 'Click Save.' },
            { text: '', note: 'Credit limit changes are logged with your name and timestamp. They may also need to be updated in your accounting system separately.' },
          ],
        },
        {
          title: 'How do I override a calculated credit score?',
          items: [
            { text: 'Click Override Score.' },
            { text: 'Enter the manual score value and a written justification.' },
            { text: 'Click Save.' },
            { text: '', note: 'Override capability is restricted to users with the Credit Manager or Administrator role. The override is visible in the score history.' },
          ],
        },
      ],
      proTips: [
        'Review scores for all customers in the 91-120 aging bucket -- a declining score combined with aged debt is a strong indicator of bad debt risk.',
        'Use the score history chart to distinguish a temporary dip (customer had a cash flow problem one quarter) from a sustained decline (customer is structurally deteriorating).',
        'Set a calendar reminder for annual credit reviews -- use the score as the starting point and apply manual judgement for any override decisions.',
      ],
    },
    faq: [
      {
        question: 'How is the credit score calculated?',
        answer: 'Scores are calculated using payment history (frequency and severity of late payments), DSO, credit utilisation (balance versus credit limit), and dispute frequency. External credit bureau data is incorporated where your integration is configured.',
      },
      {
        question: 'How often is the credit score updated?',
        answer: 'Scores are recalculated overnight. Significant events (like a payment received or a case escalated) may trigger an intraday recalculation depending on your configuration.',
      },
      {
        question: 'Can I see why a score changed?',
        answer: 'Yes. The score history chart shows when the score changed, and clicking on a point in the chart shows the factors that drove the change at that date.',
      },
      {
        question: 'What does a score override mean for the customer?',
        answer: 'An override replaces the calculated score with a manual value. The credit limit recommendation is then based on the override score. The override is valid until it is removed or the next scheduled recalculation date you set.',
      },
    ],
    relatedPages: [
      { label: 'Customers', path: '/customers' },
      { label: 'Customer Detail', path: '/customers/:id' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
    ],
  },

  'settings-company': {
    pageTitle: 'Company Settings',
    description:
      'A management page where administrators create and configure the company profiles that the credit control system operates on, link each company to its accounting integration, and monitor integration sync status.',
    functionality: {
      overview:
        'Company Settings is the central place to register every legal entity or trading company whose accounts receivable data flows into Credit Control. Each company record holds its identity details (name, code, address, VAT/CRN), its currency, its integration link to the accounting system (Financials or DimensionsvNext), and optional bank details used on customer statements. The page also shows summary KPI cards for total companies, active integrations, active users, and document templates. You must have the Administrator role to access this page.',
      keyFeatures: [
        {
          label: 'Summary KPI Cards',
          detail:
            'Four cards at the top show Total Companies, Active Integrations, Active Users (click to navigate to Users), and Document Templates (click to navigate to Templates). These update live as you add or modify companies.',
        },
        {
          label: 'Company List Table',
          detail:
            'Lists every registered company with its Code, Name, Status, Currency, Integration badge, Linked Database, Linked Sub Ledger, User Count, Created Date, and Last Sync timestamp. Sub-companies are indented under their parent with a corner-arrow indicator.',
        },
        {
          label: 'Search and Filter',
          detail:
            'Search by company name or code. Filter the list to Active or Pending Setup companies. Sort by Name A-Z, Name Z-A, Newest, or Oldest.',
        },
        {
          label: 'Add Company',
          detail:
            'Opens a multi-step wizard to create a new company record. Step 1 captures the company details: required fields are Company ID (max 7 alphanumeric characters) and Company Name; optional fields include Address, VAT Registration No., Company Registration No., Phone, Email, and Logo upload. Subsequent steps allow you to configure the integration and bank details. Description can be added when editing an existing company.',
        },
        {
          label: 'Integration Setup',
          detail:
            'Each company can be linked to a Financials or DimensionsvNext instance and Sub Ledger. The integration drives the automatic import of invoices, receipts, and customer data. Integration status is shown as Connected, Partial, or Not Connected.',
        },
        {
          label: 'Bank Details',
          detail:
            'Each company profile supports multiple bank detail rows (one per currency) covering Sort Code, Account Number, Account Name, IBAN, and SWIFT/BIC. These appear on customer statements generated by the system.',
        },
        {
          label: 'Pending Setup Flow',
          detail:
            'Companies created via the integration that have not yet been fully configured appear in Pending Setup status. Click the row or select Complete Setup from the row menu to finish configuration.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Total Companies card',
          description: 'Count of all registered company records regardless of status.',
        },
        {
          name: 'Active Integrations card',
          description: 'Count of companies with a Connected integration status.',
        },
        {
          name: 'Active Users card',
          description: 'Count of users with Active status. Click the card to navigate to User Management.',
        },
        {
          name: 'Document Templates card',
          description: 'Count of configured document templates. Click the card to navigate to Document Templates.',
        },
        {
          name: 'Search bar',
          description: 'Filters the company list by partial match on company name or company code.',
        },
        {
          name: 'Status filter',
          description: 'Restricts the list to All Status, Active, or Pending Setup companies.',
        },
        {
          name: 'Sort dropdown',
          description: 'Orders the list by Name A-Z, Name Z-A, Newest (created date descending), or Oldest (created date ascending).',
        },
        {
          name: 'Add Company button',
          description: 'Opens the Create Company dialog. Only visible to administrators.',
        },
        {
          name: 'Company ID column',
          description: 'The short alphanumeric code (max 7 characters) that uniquely identifies the company within the system. Cannot be changed after creation.',
        },
        {
          name: 'Company Name column',
          description: 'Display name of the company. Sub-companies are indented with a corner-arrow icon. An External Company Name (from the integration) is shown in smaller text beneath if it differs.',
        },
        {
          name: 'Status column',
          description: 'Active (green badge) = fully configured and operational. Pending Setup (orange badge) = created but not yet completed.',
        },
        {
          name: 'Currency column',
          description: 'The base currency of the company, imported from the integration. Shown as an ISO currency code (e.g. GBP, USD, EUR).',
        },
        {
          name: 'Integration column',
          description: 'Shows the integration link status as a badge: Connected (solid), Partial (secondary/grey), or Not Connected (outline). Click the row to manage integration settings.',
        },
        {
          name: 'Linked Database column',
          description: 'The name of the accounting system instance (Financials or DimensionsvNext subscription) this company is linked to.',
        },
        {
          name: 'Linked Sub Ledger column',
          description: 'The Sub Ledger within the linked database that this company\'s accounts receivable data is drawn from. Shows a dash if not applicable.',
        },
        {
          name: 'Users column',
          description: 'The number of user accounts associated with this company.',
        },
        {
          name: 'Created Date column',
          description: 'The date the company record was created in the system.',
        },
        {
          name: 'Last Sync column',
          description: 'The date and time of the most recent successful data sync from the integration. Shows -- if no sync has occurred.',
        },
        {
          name: 'Row actions menu (...)',
          description: 'For Active companies: View (opens the company detail/edit dialog). For Pending Setup companies: Complete Setup.',
        },
        {
          name: 'Add/Edit Company dialog -- Company ID',
          description: 'Required. Max 7 alphanumeric characters. Used as the internal reference across the platform. Cannot be edited after creation.',
        },
        {
          name: 'Add/Edit Company dialog -- Company Name',
          description: 'Required. The display name shown in all company-related lists and reports.',
        },
        {
          name: 'Add/Edit Company dialog -- Currency',
          description: 'Read-only. Populated automatically from the integration. Cannot be set manually.',
        },
        {
          name: 'Add/Edit Company dialog -- Address',
          description: 'Optional. The registered business address used on statements and documents.',
        },
        {
          name: 'Add/Edit Company dialog -- VAT Registration No.',
          description: 'Optional. Validated against UK VAT format. Shown on customer statements where required.',
        },
        {
          name: 'Add/Edit Company dialog -- Company Registration No.',
          description: 'Optional. Validated against UK CRN format. Used for formal communications.',
        },
        {
          name: 'Add/Edit Company dialog -- Phone / Email',
          description: 'Optional. Contact details for the company. Email is validated for format. Used in statement footers and correspondence.',
        },
        {
          name: 'Add/Edit Company dialog -- Logo',
          description: 'Optional. Upload a PNG, JPG, GIF, or SVG (max 5 MB) to display on customer statements and email headers. Supports drag-and-drop.',
        },
        {
          name: 'Add/Edit Company dialog -- Integration tab',
          description: 'Select the accounting product (Financials or DimensionsvNext), the specific instance, and the Sub Ledger. The system fetches available instances when a product is selected.',
        },
        {
          name: 'Add/Edit Company dialog -- Bank Details tab',
          description: 'Add one bank detail row per currency. Fields: Currency (required), Sort Code, Account Number, Account Name, IBAN, SWIFT/BIC. IBAN is displayed with spaces for readability but stored without.',
        },
      ],
      useCases: [
        'Onboarding a new legal entity -- creating its company record, linking it to the integration, and confirming the first sync before going live.',
        'Completing the setup of a Pending Setup company that was auto-created by the integration before its details were configured.',
        'Updating bank details on a company record before the next statement run so customers see the correct payment information.',
        'Checking the integration status badge on a company row to identify any companies with a Partial or Not Connected status that need attention.',
        'Checking the Last Sync timestamp for a company to confirm that overnight data is up to date before the morning collections session.',
        'Adding a company logo so it appears correctly on PDF statements sent to customers.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings -> Company Settings. This page is only accessible to users with the Administrator role. If you cannot see it in the menu, ask your system administrator to grant you the adminManageCompaniesView permission.',
      steps: [
        {
          title: 'How do I add a new company?',
          items: [
            {
              text: 'Click the Add Company button in the top right of the page.',
            },
            {
              text: 'Enter a Company ID -- up to 7 alphanumeric characters with no spaces.',
              note: 'The Company ID cannot be changed after the company is saved. Choose a code that matches your internal reference convention.',
            },
            {
              text: 'Enter the Company Name.',
            },
            {
              text: 'Optionally complete Address, VAT Registration No., Company Registration No., Phone, and Email.',
              tip: 'VAT and CRN fields are validated against UK formats. If your company is not UK-registered, you may leave these blank or enter the local equivalent -- the field will warn if the format does not match but will still save.',
            },
            {
              text: 'Optionally upload a company logo by clicking the upload area or dragging a PNG, JPG, GIF, or SVG file onto it (max 5 MB).',
            },
            {
              text: 'Click Next to proceed through the integration and bank details steps. The company is created with Active status once the wizard completes.',
            },
          ],
        },
        {
          title: 'How do I link a company to an accounting integration?',
          items: [
            {
              text: 'Click the row for the company you want to configure, or select View from the row actions menu (...).',
            },
            {
              text: 'In the company dialog, select the Integration tab.',
            },
            {
              text: 'Select the accounting product from the dropdown: Financials or DimensionsvNext.',
            },
            {
              text: 'Wait for the system to fetch available instances, then select the correct instance from the Instance dropdown.',
              note: 'If no instances appear, the product may not be provisioned on your Evo account. Contact your system administrator.',
            },
            {
              text: 'If the product supports Sub Ledgers and your instance has them, select the correct Sub Ledger.',
            },
            {
              text: 'Click Save. The integration status on the company row updates to Connected once the first sync completes.',
            },
          ],
        },
        {
          title: 'How do I complete a Pending Setup company?',
          items: [
            {
              text: 'Identify companies with an orange Pending Setup badge in the Status column.',
            },
            {
              text: 'Click the company row, or open the row actions menu (...) and select Complete Setup.',
            },
            {
              text: 'The Add/Edit Company dialog opens pre-filled with any data already imported from the integration.',
            },
            {
              text: 'Review and complete the required fields, then click Save. The company status changes to Active.',
              note: 'Saving a Pending Setup company activates it immediately. All users with access to that company will see its data from this point forward.',
            },
          ],
        },
        {
          title: 'How do I add or update bank details for a company?',
          items: [
            {
              text: 'Open the company dialog by clicking the company row.',
            },
            {
              text: 'Select the Bank Details tab.',
            },
            {
              text: 'Click Add Bank Detail to add a new row.',
            },
            {
              text: 'Select the Currency, then enter Sort Code, Account Number, Account Name, IBAN, and SWIFT/BIC as applicable.',
              note: 'IBAN is validated for format and displayed with spaces for readability, but stored without spaces. Each currency can have only one bank detail row.',
            },
            {
              text: 'Click Save to persist the changes. Bank details appear on customer statements generated for that company.',
            },
          ],
        },
        {
          title: 'How do I trigger a manual data sync for a company?',
          items: [
            {
              text: 'Manual data sync is not available from this page.',
              note: 'Data synchronisation is handled automatically by scheduled background jobs. If you believe a sync is overdue or incorrect, check the Last Sync timestamp on the company row and contact your system administrator if the data appears stale.',
            },
          ],
        },
        {
          title: 'How do I upload or change a company logo?',
          items: [
            {
              text: 'Open the company dialog.',
            },
            {
              text: 'In the Logo section, click the upload area or drag a PNG, JPG, GIF, or SVG file onto it.',
            },
            {
              text: 'The file must be under 5 MB. A preview appears once the file is selected.',
            },
            {
              text: 'To remove an existing logo, click the x button on the current logo preview.',
            },
            {
              text: 'Click Save. The new logo appears on all statements and email headers generated after the save.',
            },
          ],
        },
      ],
      proTips: [
        'Use the Status filter to show only Pending Setup companies at the start of an onboarding project -- this gives you a clear checklist of companies still to configure.',
        'Check the Last Sync column each morning before collections work begins. If a company shows -- or a timestamp older than expected, contact your system administrator to investigate the scheduled sync.',
        'Keep Company IDs short and meaningful (e.g. "UK01", "IE02") -- they appear in reference fields across the platform and in exported data, so a consistent naming convention avoids confusion.',
        'Add bank details for every currency a company trades in before running the first statement batch -- statements without bank details may be returned by customers.',
      ],
    },
    faq: [
      {
        question: 'What does the Pending Setup status mean?',
        answer:
          'Pending Setup means the company record was created (usually automatically by an integration import) but has not yet been fully configured. The company is not fully operational until you complete its setup. Click the row or select Complete Setup from the row actions menu.',
      },
      {
        question: 'What does the Integration status badge show?',
        answer:
          'Connected = the company is linked to an accounting instance and syncing data. Partial = a link exists but is incomplete (e.g. a Sub Ledger is required but not selected). Not Connected = no integration has been configured for this company.',
      },
      {
        question: 'Why is the Currency field read-only in the company dialog?',
        answer:
          'The currency is imported automatically from the linked accounting system and cannot be set manually. If the wrong currency is showing, check the integration configuration and re-sync.',
      },
      {
        question: 'Can I change the Company ID after a company has been created?',
        answer:
          'No. The Company ID is used as an internal key throughout the system and cannot be modified after creation. If an incorrect ID was used, you will need to delete the company and recreate it with the correct ID.',
      },
      {
        question: 'How do I delete a company?',
        answer:
          'Company deletion is not available directly from this page. If a company needs to be removed, contact your system administrator -- deletion requires a full data review to ensure no linked transactions, cases, or customer records are orphaned.',
      },
      {
        question: 'Why is there no Sync option in the row actions menu?',
        answer:
          'Manual sync is not available from the row actions menu. Data synchronisation is handled automatically by scheduled background jobs. If data appears stale, check the Last Sync timestamp and contact your system administrator.',
      },
      {
        question: 'How do I see which users are assigned to a specific company?',
        answer: 'Click the Active Users KPI card at the top of the page to navigate to User Management, then filter or search for users associated with that company.',
      },
      {
        question: 'What file formats are supported for company logos?',
        answer: 'PNG, JPG, GIF, and SVG files are supported. The file must be under 5 MB. Very large images may be resized automatically when displayed on statements.',
      },
      {
        question: 'Why does the VAT Registration No. field show a warning?',
        answer:
          'The field validates against standard UK VAT number format. If your number is in a different format (e.g. EU or international), you will see a format warning but the value will still be saved.',
      },
      {
        question: 'Can I have more than one bank detail row per company?',
        answer:
          'Yes. You can add one bank detail row per currency. If your company uses GBP and USD, you can add a separate row for each with its own Sort Code, Account Number, IBAN, and SWIFT/BIC.',
      },
      {
        question: 'What happens when I click the Active Users or Document Templates KPI cards?',
        answer:
          'The Active Users card navigates to the User Management page. The Document Templates card navigates to the Document Templates settings page.',
      },
    ],
    relatedPages: [
      { label: 'User Management', path: '/settings/users' },
      { label: 'Document Templates', path: '/settings/templates' },
      { label: 'Multi-Company Dashboard', path: '/multi-company' },
    ],
  },

  'settings-user-management': {
    pageTitle: 'User Management',
    description:
      'A two-tab administration page where administrators view and manage user accounts, assign roles, and configure the permission sets that control what each role can access across the platform.',
    functionality: {
      overview:
        'The Users page has two tabs. The User Management tab lists every user account in the organisation -- you can search, filter by role or status, sort the list, view full user details, change a user\'s role inline, manage their integration mapping, manage their company access, and delete accounts. The Roles & Permissions tab lists the platform roles (Administrator and User). Clicking a role opens its full permission breakdown. This page is only accessible to users with the Administrator role.',
      keyFeatures: [
        {
          label: 'User Management tab',
          detail:
            'Lists all user accounts with Name, Email, Role badge, Status badge, Created date, and a row actions menu. Filterable by role and status, searchable by name or email, sortable by Name A-Z/Z-A, Most Recent, or Role.',
        },
        {
          label: 'Inline Role Change',
          detail:
            'Click the role badge on any user row to open a dropdown and change the user\'s role directly in the table. The role and its associated default permissions update immediately.',
        },
        {
          label: 'View User dialog',
          detail:
            'Opens a full detail panel for a user showing their profile, assigned role, permission breakdown, integration mapping (Evo user ID, external first/last name), and associated companies.',
        },
        {
          label: 'Manage Integrations dialog',
          detail:
            'Links a Credit Control user account to their corresponding Evo user identity. Required for users who need to sync activity between systems. Fields: Evo User ID, External First Name, External Last Name.',
        },
        {
          label: 'Manage Companies dialog',
          detail:
            'Assigns which company profiles a user has access to. A user with no companies assigned may not see data for any company until at least one is linked.',
        },
        {
          label: 'Delete User dialog',
          detail:
            'Permanently removes a user account. Optionally reassign their open tasks, collection cases, and promises to another user before deletion to avoid orphaned records.',
        },
        {
          label: 'Roles & Permissions tab',
          detail:
            'Lists all platform roles (Administrator, User) each shown with a Platform Role badge. Shows user count per role, status, and last modified date. Click any role row to view its full permission set.',
        },
        {
          label: 'Role Detail page',
          detail:
            'Accessible by clicking a role row. Shows every permission toggle for that role -- grouped by module. Administrators can see the full permission matrix.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'User Management tab -- Search bar',
          description: 'Filters the user list by partial match on first name, last name, or email address.',
        },
        {
          name: 'User Management tab -- Role filter',
          description: 'Restricts the list to All Roles, Administrator, or User.',
        },
        {
          name: 'User Management tab -- Status filter',
          description: 'Restricts the list to All Status, Active, or Inactive.',
        },
        {
          name: 'User Management tab -- Sort dropdown',
          description: 'Orders the list by Name A-Z, Name Z-A, Most Recent (created date descending), or Role.',
        },
        {
          name: 'User Management tab -- Results count',
          description: 'Shows "Showing X of Y users" below the toolbar, updating live as filters are applied.',
        },
        {
          name: 'Users table -- Name column',
          description: 'Full name of the user with their avatar initials.',
        },
        {
          name: 'Users table -- Email column',
          description: 'The user\'s login email address.',
        },
        {
          name: 'Users table -- Role column',
          description: 'Displays the user\'s current role as a clickable badge. Click to change the role inline.',
        },
        {
          name: 'Users table -- Status column',
          description: 'Active (green) = user can log in. Inactive (grey) = account is disabled.',
        },
        {
          name: 'Users table -- Created column',
          description: 'The date the user account was created.',
        },
        {
          name: 'Users table -- Row actions menu (...)',
          description: 'Contains: View (opens the View User dialog), Manage Integrations (opens the integration mapping dialog), Manage Companies (opens the company access dialog), Delete (opens the delete confirmation dialog).',
        },
        {
          name: 'View User dialog -- Profile section',
          description: 'Shows avatar, full name, email, role badge, status badge, and account creation/update timestamps.',
        },
        {
          name: 'View User dialog -- Permissions section',
          description: 'Lists every permission assigned to the user based on their role, shown as check marks.',
        },
        {
          name: 'Manage Integrations dialog -- Evo User ID',
          description: 'The unique identifier of the corresponding user in the Evo system. Required for integration sync.',
        },
        {
          name: 'Manage Integrations dialog -- External First / Last Name',
          description: 'The name as it appears in the external accounting system, if it differs from the Credit Control display name.',
        },
        {
          name: 'Delete User dialog -- Reassign To dropdown',
          description: 'Optional. Select another active user to reassign all open collection cases, promises, and tasks before the deletion is confirmed.',
        },
        {
          name: 'Roles & Permissions tab -- Role Name column',
          description: 'The name of the role. Click to open the Role Detail page.',
        },
        {
          name: 'Roles & Permissions tab -- Role Type column',
          description: 'Platform Role = system-defined, cannot be deleted.',
        },
        {
          name: 'Roles & Permissions tab -- Users Assigned column',
          description: 'The number of active user accounts currently assigned to this role.',
        },
        {
          name: 'Roles & Permissions tab -- Status column',
          description: 'Whether the role is Active. Platform roles are always active.',
        },
        {
          name: 'Roles & Permissions tab -- Last Modified column',
          description: 'The date the role configuration was last changed.',
        },
        {
          name: 'Roles & Permissions tab -- Actions column (->)',
          description: 'Click the chevron to navigate to the Role Detail page for that role.',
        },
      ],
      useCases: [
        'Checking which users are active before a system go-live to confirm all accounts have been provisioned correctly.',
        'Changing a user\'s role from User to Administrator when they take on a system admin responsibility.',
        'Mapping a user\'s Evo User ID before they start using automated collection strategies that depend on system identity.',
        'Assigning company access to a new credit controller so they can see their allocated portfolio.',
        'Reviewing the Administrator and User role permissions to understand what each access level can do.',
        'Deleting a user who has left the organisation and reassigning their open collection cases to a colleague.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings -> Users. This page is only accessible to users with the Administrator role. Users are created automatically when provisioned through the Evo system -- you cannot create users manually from this page. If a user is missing, ask your system administrator to provision them in Evo first.',
      steps: [
        {
          title: 'How do I find a specific user?',
          items: [
            {
              text: 'Type the user\'s name or email address into the Search bar at the top of the User Management tab.',
            },
            {
              text: 'Optionally apply the Role or Status filter to narrow the results further.',
            },
            {
              text: 'Click the row or select View from the row actions menu (...) to open the full user detail.',
            },
          ],
        },
        {
          title: 'How do I change a user\'s role?',
          items: [
            {
              text: 'Locate the user in the table.',
            },
            {
              text: 'Click the role badge in the Role column (e.g. "User").',
            },
            {
              text: 'Select the new role from the dropdown that appears.',
              note: 'Changing a user\'s role immediately resets their permissions to the default permission set for the new role. Any customisation at the individual user level is not preserved.',
            },
            {
              text: 'A success toast confirms the role has been updated.',
            },
          ],
        },
        {
          title: 'How do I manage a user\'s integration mapping?',
          items: [
            {
              text: 'Open the row actions menu (...) for the user and select Manage Integrations.',
            },
            {
              text: 'Enter the user\'s Evo User ID in the field provided.',
              note: 'The Evo User ID is found in the Evo platform under the user\'s account profile. Without this mapping, the user\'s activity may not sync correctly between systems.',
            },
            {
              text: 'Optionally enter External First Name and External Last Name if the user\'s name differs in the accounting system.',
            },
            {
              text: 'Click Save.',
            },
          ],
        },
        {
          title: 'How do I assign company access to a user?',
          items: [
            {
              text: 'Open the row actions menu (...) for the user and select Manage Companies.',
            },
            {
              text: 'In the dialog, enable the toggle next to each company the user should have access to.',
              note: 'A user with no companies assigned will not see any customer accounts, transactions, or collection cases until at least one company is linked.',
            },
            {
              text: 'Click Save.',
            },
          ],
        },
        {
          title: 'How do I delete a user?',
          items: [
            {
              text: 'Open the row actions menu (...) for the user and select Delete.',
            },
            {
              text: 'In the Delete User dialog, review the warning about open records associated with this user.',
            },
            {
              text: 'If the user has open collection cases, promises, or tasks, select a replacement user from the Reassign To dropdown.',
              note: 'If you do not reassign, those records will become unassigned. This can cause them to disappear from filtered views that show only the current user\'s work.',
            },
            {
              text: 'Click Delete to confirm. The action is permanent and cannot be undone.',
              tip: 'Before deleting, consider setting the user\'s status to Inactive first. This blocks login access while keeping their records intact for audit purposes.',
            },
          ],
        },
        {
          title: 'How do I view the permissions for a role?',
          items: [
            {
              text: 'Click the Roles & Permissions tab.',
            },
            {
              text: 'Click the role name or the chevron (->) at the end of the row.',
            },
            {
              text: 'The Role Detail page opens showing every permission toggle for that role, grouped by module.',
              note: 'Platform roles (Administrator, User) are system-defined and cannot be edited.',
            },
          ],
        },

      ],
      proTips: [
        'Use the Status filter to show only Active users when reviewing team capacity -- Inactive accounts inflate the total user count and can be misleading.',
        'Before deleting a user, export or note their Manage Integrations mapping (Evo User ID) in case the same person is re-provisioned later -- you will need to re-enter it.',
        'If a credit controller changes team, use Manage Companies to add their new company portfolio and remove the old one in the same session -- this avoids a window where they can see data they should not.',
        'Review the Users Assigned count on the Roles & Permissions tab after any role restructuring to confirm no users are left on a deprecated or misconfigured role.',
      ],
    },
    faq: [
      {
        question: 'Why can I not create a new user from this page?',
        answer:
          'User accounts in Credit Control are provisioned automatically through the Evo platform integration. To create a new user, they must first be set up in Evo. Once provisioned, they will appear here automatically.',
      },
      {
        question: 'What does the Inactive status mean?',
        answer:
          'Inactive means the user account exists in the system but the user cannot log in. Their historical records (cases, promises, tasks) are preserved. Inactive accounts do not count against active user limits.',
      },
      {
        question: 'What does changing a user\'s role do to their existing permissions?',
        answer:
          'Changing the role resets the user\'s permissions to the default permission set for the new role. The user will immediately gain or lose access to features based on the new role.',
      },
      {
        question: 'Can I assign a user to more than one role?',
        answer:
          'No. Each user has exactly one role at a time. The available roles are Administrator and User.',
      },
      {
        question: 'What happens to a user\'s collection cases and promises when they are deleted?',
        answer:
          'If you do not reassign before deletion, those records become unassigned. They will not appear in filtered views that show only the logged-in user\'s work. Use the Reassign To option in the Delete User dialog to transfer open records to a colleague.',
      },
      {
        question: 'What does the Evo User ID field in Manage Integrations do?',
        answer:
          'It links the Credit Control user account to the corresponding identity in the Evo platform. This is required for automated activity sync between systems. Without it, some automated processes may not attribute actions to the correct user.',
      },
      {
        question: 'Can I edit the Administrator or User platform roles directly?',
        answer:
          'No. Platform roles (Administrator and User) are system-defined and cannot be edited. Contact your system administrator if you need a permission change for a specific user.',
      },
      {
        question: 'How do I find all users assigned to a specific role?',
        answer: 'On the User Management tab, open the Role filter dropdown and select the role name. The list updates to show only users assigned to that role.',
      },
      {
        question: 'What does the Users Assigned count on the Roles & Permissions tab show?',
        answer:
          'It shows the number of currently active user accounts assigned to that role. This count updates live when you change a user\'s role from the User Management tab.',
      },
      {
        question: 'Why does the Roles & Permissions tab show 0 users for a role I know has users?',
        answer:
          'This can happen if all users on that role are currently filtered out on the User Management tab. The count on the Roles & Permissions tab reflects active users only -- inactive users are excluded from the count.',
      },
      {
        question: 'Can I see a log of changes made to user accounts?',
        answer:
          'A User Activity Log tab is planned but not yet enabled on this page. For current audit requirements, contact your system administrator who can access backend audit records.',
      },
    ],
    relatedPages: [
      { label: 'Company Settings', path: '/settings/company' },
      { label: 'Roles & Permissions', path: '/settings/users/roles-permissions' },
      { label: 'Document Templates', path: '/settings/templates' },
    ],
  },

  'settings-templates': {
    pageTitle: 'Document Templates',
    description:
      'The Document Templates page is where you create, manage, and organise the email and statement templates used for customer communications across the credit control process.',
    functionality: {
      overview:
        'This page lists all email and statement templates in your account. From here you can create new templates, edit existing ones, duplicate a template to use as a starting point, set a default template per type, and delete templates that are no longer needed. Templates are used by collection strategies to send automated emails and by the Customers page to generate statements.',
      keyFeatures: [
        {
          label: 'Template List with Type Tabs',
          detail: 'Switch between All Templates, Email, and Statement tabs to filter the list. Each tab shows a count badge so you can see how many templates exist per type at a glance.',
        },
        {
          label: 'Search Bar',
          detail: 'Type any part of a template name or subject to filter the list in real time. Search is server-side, so it searches across all pages -- not just the current page.',
        },
        {
          label: 'Sort Options',
          detail: 'Sort the list by Name A-Z, Name Z-A, Recently Modified, or Oldest using the Sort By dropdown.',
        },
        {
          label: 'Default Template Badge',
          detail: 'Mark one template per type as the default. The default is the template automatically selected when no specific template is specified by a strategy action.',
        },
        {
          label: 'Row Actions Menu',
          detail: 'Each template row has a three-dot menu with Edit, Duplicate, Set as Default, and Delete. Edit opens the email editor or PDF builder depending on template type. Duplicate creates a copy with "(COPY)" appended to the name.',
        },
        {
          label: 'Create Email Button',
          detail: 'Opens the email template editor to create a new email template from scratch.',
        },
        {
          label: 'Create Statement Button',
          detail: 'Opens the PDF builder to create a new statement template using the drag-and-drop layout builder.',
        },
        {
          label: 'Pagination',
          detail: 'Navigate through pages of templates with configurable page size (10, 20, or 50 rows per page).',
        },
      ],
      sectionsAndFields: [
        { name: 'All Templates tab', description: 'Shows every template regardless of type. The badge count reflects the total number of templates returned by the current search.' },
        { name: 'Email tab', description: 'Filters the list to email templates only.' },
        { name: 'Statement tab', description: 'Filters the list to statement (PDF) templates only.' },
        { name: 'Search field', description: 'Type to search by template name or subject line. Results update as you type.' },
        { name: 'Sort By dropdown', description: 'Controls the order of results: Name A-Z, Name Z-A, Recently Modified, or Oldest.' },
        { name: 'Name column', description: 'The internal name of the template. Clicking the name opens the editor. An icon indicates the type: envelope for email, document for statement.' },
        { name: 'Type column', description: 'A blue badge for email templates, a purple badge for statement templates.' },
        { name: 'Subject/Description column', description: 'Shows the email subject line for email templates. Truncated for long subjects.' },
        { name: 'Categories column', description: 'The template category (e.g. Customers, Collections, Promises) assigned in the editor. Up to two categories shown, with a +N badge if more.' },
        { name: 'Status column', description: 'Shows a yellow Default badge with a star icon if this template is the default for its type. Empty otherwise.' },
        { name: 'Last Modified column', description: 'The date the template was last saved.' },
        { name: 'Actions column', description: 'Three-dot menu on each row with: Edit, Duplicate, Set as Default (hidden if already default), and Delete.' },
        { name: 'Set as Default confirmation dialog', description: 'Confirms the action and warns if an existing default will be replaced.' },
        { name: 'Delete confirmation dialog', description: 'Warns if the template being deleted is the current default for its type, and confirms the action cannot be undone.' },
        { name: 'Rows per page selector', description: 'Controls how many templates appear per page: 10, 20, or 50.' },
        { name: 'Pagination controls', description: 'Previous and Next buttons with the current page and total page count.' },
      ],
      useCases: [
        'Building a set of three chaser email templates (Reminder 1, Reminder 2, Final Notice) to assign to the steps of a collection strategy.',
        'Duplicating an existing reminder email to create a variant with different wording for high-value accounts.',
        'Setting a standard monthly statement as the default so it is automatically used when a strategy sends a statement action.',
        'Searching for all templates containing "overdue" in the name to review and update collection wording after a policy change.',
        'Deleting a template created during testing before it gets assigned to a live strategy.',
        'Creating a promise confirmation email template using the <<promisedAmount>> and <<promiseDate>> variables to send when a promise to pay is recorded.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > Document Templates. You need Administrator access to create, edit, or delete templates. If the list is empty, start by creating your email templates before setting up collection strategies -- strategies reference templates, so templates must exist first.',
      steps: [
        {
          title: 'How do I create a new email template?',
          items: [
            { text: 'Click the Create Email button in the top right.' },
            { text: 'Enter a Template Name -- this is an internal label, not visible to the customer.' },
            { text: 'Select a Category from the dropdown (e.g. Collections, Customers, Promises). This controls which variables are available in the Variables Panel.', note: 'The category restricts the variables shown in the Variables Panel to those relevant for that template type. For example, a Promises category template shows <<promisedAmount>> and <<promiseDate>> variables.' },
            { text: 'Type the Subject line. Click any variable in the Variables Panel to insert it at the cursor position, or drag a variable chip directly into the subject field.' },
            { text: 'Write the email body in the rich text editor. Use the toolbar to format text, add lists, or adjust alignment.' },
            { text: 'Insert variables into the body by clicking them in the Variables Panel or dragging them into position.', tip: 'Click inside the body at the exact position where you want a variable before clicking it in the Variables Panel -- this ensures it inserts at the right place.' },
            { text: 'Tick Attach Invoice Copies if you want PDF copies of the relevant invoices attached to the email when it is sent.' },
            { text: 'Click the Preview button (eye icon) to see how the email will look with sample data substituted in.' },
            { text: 'Click Save.' },
          ],
        },
        {
          title: 'How do I create a new statement template?',
          items: [
            { text: 'Click the Create Statement button in the top right.' },
            { text: 'The PDF Builder opens. Use the drag-and-drop layout editor to design the statement.', note: 'Statement templates use the PDF Builder, not the rich text email editor. The two editors are separate tools for different template types.' },
            { text: 'Add blocks (header, customer details, transaction table, footer) from the left panel into the canvas.' },
            { text: 'Configure each block\'s content and styling in the right-hand properties panel.' },
            { text: 'Click Save when done.' },
          ],
        },
        {
          title: 'How do I edit an existing template?',
          items: [
            { text: 'Find the template in the list. Use the search bar or tab filter to narrow it down.' },
            { text: 'Click the template name, or click the three-dot menu on the row and select Edit.' },
            { text: 'Make your changes in the editor.' },
            { text: 'Click Save.', note: 'Editing a template updates it everywhere it is in use -- any strategy action that references this template will automatically send the updated version on its next trigger.' },
          ],
        },
        {
          title: 'How do I duplicate a template?',
          items: [
            { text: 'Click the three-dot menu on the template row.' },
            { text: 'Select Duplicate.' },
            { text: 'The editor opens with a copy of the template. The name will have "(COPY)" appended.' },
            { text: 'Rename the template and make any changes.' },
            { text: 'Click Save to create the new template.' },
          ],
        },
        {
          title: 'How do I set a template as the default?',
          items: [
            { text: 'Click the three-dot menu on the template row.' },
            { text: 'Select Set as Default.' },
            { text: 'A confirmation dialog appears. If another template of the same type is already the default, the dialog warns you it will be replaced.' },
            { text: 'Click Confirm.', note: 'You can only have one default per template type (one default email template, one default statement template).' },
          ],
        },
        {
          title: 'How do I delete a template?',
          items: [
            { text: 'Click the three-dot menu on the template row.' },
            { text: 'Select Delete.' },
            { text: 'Review the confirmation dialog. If the template is the current default for its type, a warning is shown.' },
            { text: 'Click Delete to confirm.', note: 'Deletion is permanent and cannot be undone. If the template is referenced by an active collection strategy, remove it from the strategy first.' },
          ],
        },
        {
          title: 'How do I insert a dynamic variable into a template?',
          items: [
            {
              text: 'In the email editor, click in the body or subject field at the position where you want the variable to appear.',
              tip: 'Variables can also be dragged from the Variables Panel and dropped into the body or subject field.',
            },
            { text: 'In the Variables Panel on the right, find the variable you want. Variables are grouped by category (Customer Information, Transaction Details, Company Information, Payment Information, System Information).' },
            { text: 'Click the variable name. It inserts at the cursor position as a coloured chip.' },
            { text: 'Check the preview to confirm the variable resolves correctly with sample data.', note: 'If a variable is greyed out or not visible, it may be restricted by the template\'s category setting. Change the category to one that allows that variable section.' },
          ],
        },
        {
          title: 'How do I find a template and assign it to a collection strategy?',
          items: [
            { text: 'Confirm the template exists and is saved on this page.' },
            { text: 'Navigate to Settings > Collection Strategies.' },
            { text: 'Open the strategy and find the action step where you want to use the template.' },
            { text: 'In the action step\'s template dropdown, type the first few characters of the template name to find it.' },
            { text: 'Select the template and save the strategy.' },
          ],
        },
      ],
      proTips: [
        'Use a consistent naming convention such as "CC_Reminder_1", "CC_Reminder_2", "CC_FinalNotice" -- this keeps templates sorted together alphabetically and makes them easy to identify in strategy dropdowns.',
        'Always preview before saving. Variable chips render correctly in the editor but the preview shows how the actual email will look -- especially important for <<overdueTransactions>> and <<outstandingTransactions>>, which render as formatted tables.',
        'Duplicate a master template with your company branding, footer, and standard legal disclaimers before creating each new template -- this avoids rebuilding the same base content from scratch each time.',
        'Set the correct category before writing the body. Changing the category after inserting variables can mark those variables as disallowed and block saving -- you will need to remove the offending variables first.',
      ],
    },
    faq: [
      {
        question: 'What variables can I use in a template?',
        answer: 'Variables are grouped into five sections. Customer Information includes: <<customerName>>, <<customerEmail>>, <<accountNumber>>, <<contactFirstName>>, <<contactSurname>>, <<contactEmail>>, <<accountBalance>>, <<overdueAmount>>, <<creditLimit>>. Transaction Details includes: <<overdueTransactions>> and <<outstandingTransactions>> (both render as formatted tables). Company Information includes: <<companyName>>, <<companyEmail>>, <<creditController>>, <<creditControllerEmail>>. Payment Information includes: <<promisedAmount>> and <<promiseDate>>. System Information includes: <<systemDate>> and <<systemTime>>. The Variables Panel in the editor shows only the variables allowed for the selected category.',
      },
      {
        question: 'Why are some variables greyed out or not showing in the Variables Panel?',
        answer: 'The Variables Panel filters variables based on the template category you have selected. For example, a template with the Transactions category does not show Payment Information variables (<<promisedAmount>>, <<promiseDate>>) because those are only relevant to promise-related templates. Change the category to one that includes the section you need, or use the General category for unrestricted access.',
      },
      {
        question: 'What does the Category field on a template do?',
        answer: 'The category serves two purposes. First, it controls which variable sections are available in the Variables Panel when editing the template and validates that no out-of-scope variables are used when saving. Second, it determines where the template appears when sending emails across the application. Each screen pre-filters the template dropdown to show only templates whose category matches that context -- for example, the Customers page shows templates with a Customers or General category, the Collections page shows Collections or General, the Disputes page shows Disputes or General, and the Promises page shows Promises or General. Templates with no category assigned are shown everywhere. If you want a template to be available on a specific screen, make sure its category matches that screen or is set to General.',
      },
      {
        question: 'Why is my template not showing in the template dropdown when sending an email?',
        answer: 'Each screen in the application pre-filters the template dropdown by category. For example, the Customers page only shows templates with a Customers or General category, the Collections page shows Collections or General, the Disputes page shows Disputes or General, and the Promises page shows Promises or General. If you created a template with a Disputes category, it will not appear in the dropdown on the Customers page. To fix this, either change the template\'s category to General (which makes it available everywhere) or to the category that matches the screen where you want to use it. Templates with no category assigned are always shown regardless of which screen you are on.',
      },
      {
        question: 'Can I delete a template that is being used by a collection strategy?',
        answer: 'You can attempt to delete it, but you should first remove the template from any strategy actions that reference it. Deleting a template that is actively referenced by a strategy may cause that strategy action to fail when it next triggers.',
      },
      {
        question: 'What does the Default badge mean on a template?',
        answer: 'The default template for a type is the one automatically selected when a strategy action does not specify a particular template. Only one email template and one statement template can be the default at any time. Setting a new default replaces the previous one.',
      },
      {
        question: 'How do I see what a template looks like before saving it?',
        answer: 'Click the Preview button (eye icon) in the template editor toolbar. The preview substitutes sample data for all variables so you can check formatting, layout, and variable placement before committing the save.',
        steps: [
          'Open the template in the editor.',
          'Click the eye icon in the top toolbar.',
          'Review the rendered preview in the dialog.',
          'Close the preview and make any adjustments.',
          'Click Save when satisfied.',
        ],
      },
      {
        question: 'Can I use the same template in more than one collection strategy?',
        answer: 'Yes. A template can be referenced by as many strategy actions as needed. Editing the template updates the content used by all of those actions.',
      },
      {
        question: 'What is the difference between an email template and a statement template?',
        answer: 'Email templates are created in the rich text editor and sent as emails to customers. They support variables, formatting, and optional invoice attachments. Statement templates are created in the PDF Builder and generate a PDF document showing the customer\'s account balance and transaction list. Statement templates are used by statement-type actions in collection strategies.',
      },
      {
        question: 'How do I add my company logo or branding to an email template?',
        answer: 'In the email editor, use the formatting toolbar to insert an image. Upload your logo file or paste a URL. Position it in the header area of the template body. For consistent branding, create a master template with the logo and footer already in place, then duplicate it for each new template.',
      },
      {
        question: 'Why can I not save my template?',
        answer: 'The most common reasons are: the Template Name field is empty; the Subject field is empty; the body contains variables that are not allowed for the selected category (the editor will highlight these). Check for any validation messages shown below the fields or in the editor toolbar.',
      },
      {
        question: 'What does Attach Invoice Copies do?',
        answer: 'When ticked, the system attaches PDF copies of the relevant outstanding or overdue invoices to the email when it is sent. This is typically used for reminder emails where you want the customer to see the invoice details without having to look them up.',
      },
    ],
    relatedPages: [],
  },

  'settings-strategies': {
    pageTitle: 'Collection Strategies',
    description:
      'Manage the automated workflows the system uses to chase overdue invoices -- each strategy defines which actions to take, when to take them, and which customers it applies to.',
    functionality: {
      overview:
        'The Collection Strategies page lists every strategy configured in the system. Each strategy is a sequence of timed collection actions -- emails, phone call tasks, SMS, payment link sends, escalations, or account stops -- that run automatically on a schedule you control. From this page you can see which strategies are active, how many open cases each one holds, how long cases have been running on average, and trigger an immediate run without waiting for the next scheduled execution. Click a strategy name or use the row menu to open the full Strategy Editor, where you configure all the details.',
      keyFeatures: [
        {
          label: 'Strategy List with Live Metrics',
          detail: 'Displays all strategies with their Open Cases count and Average Days Open, so you can see at a glance which strategies are carrying the most work.',
        },
        {
          label: 'Search Bar',
          detail: 'Filters the list by strategy name as you type -- useful when you have many strategies and need to find a specific one quickly.',
        },
        {
          label: 'Type Filter',
          detail: 'Narrows the list to a single strategy type (Standard, Intensive, VIP, High Risk) so you can review or compare strategies within the same segment.',
        },
        {
          label: 'Status Filter',
          detail: 'Shows only Active or Inactive strategies -- use this to audit which strategies are currently running or to find paused strategies that need reviewing.',
        },
        {
          label: 'Active / Inactive Toggle',
          detail: 'Enables or disables a strategy without deleting it. Inactive strategies stop being applied on the next scheduled run; existing cases are not affected.',
        },
        {
          label: 'Run Now',
          detail: 'Triggers an immediate strategy run outside the normal schedule. Available only for Active strategies via the row actions menu.',
        },
        {
          label: 'Row Actions Menu',
          detail: 'Access Edit Strategy, View Performance, Activate/Deactivate, Run Now, and Delete from a single three-dot menu on each row.',
        },
        {
          label: 'Strategy Editor (Settings tab)',
          detail: 'Full editor with four panels: Details (name, type, description, multi-stage handling), Triggers (trigger type and threshold), Stages (the ordered action sequence), and Automation Frequency (time and days of the week).',
        },
        {
          label: 'Performance Tab',
          detail: 'Shows live diagnostics for the selected strategy: status overview, open cases, last run time, emails sent in the last 14 days, next scheduled run, and a stage-by-stage funnel breakdown.',
        },
        {
          label: 'Audit Log Tab',
          detail: 'Records every change made to a strategy -- status changes, field edits, scheduled run completions, and manual Run Now triggers -- with timestamp, user, old value, and new value.',
        },
      ],
      sectionsAndFields: [
        { name: 'Search bar', description: 'Filters the strategy list by name in real time.' },
        { name: 'Type filter', description: 'Dropdown to show only strategies of a specific type: Standard, Intensive, VIP, or High Risk.' },
        { name: 'Status filter', description: 'Dropdown to show only Active or Inactive strategies.' },
        { name: 'Create Strategy button', description: 'Opens the Strategy Editor in create mode.' },
        { name: 'Name & Description column', description: 'Clickable strategy name that opens the editor; description shown beneath.' },
        { name: 'Type column', description: 'Colour-coded badge showing the strategy type (Standard = blue, Intensive = orange, VIP = purple, High Risk = red).' },
        { name: 'Status column', description: 'Toggle switch to activate or deactivate the strategy directly from the list.' },
        { name: 'Open Cases column', description: 'Count of collection cases currently running on this strategy.' },
        { name: 'Avg Days column', description: 'Average number of days cases have been open on this strategy -- a rising number may indicate the strategy is not resolving cases.' },
        { name: 'Actions column', description: 'Three-dot menu containing: Edit Strategy, View Performance, Activate/Deactivate, Run Now (Active only), Delete (Inactive with 0 open cases only).' },
        { name: 'Editor -- Strategy Name', description: 'Required. Up to 200 characters. Used as the display name throughout the application.' },
        { name: 'Editor -- Type', description: 'Strategy segment: Standard, Intensive, VIP, or High Risk. Controls which customers can be filtered into this strategy from the Customers page.' },
        { name: 'Editor -- Description', description: 'Required. Up to 1,000 characters. Explains when and how the strategy should be used.' },
        { name: 'Editor -- Multi-Stage Handling', description: 'Controls what happens when multiple stages become eligible in a single run: Run All in Order (all triggered stages run sequentially), Skip to Latest (only the most recent stage runs), or One Per Run (one stage per run, remainder picked up next time).' },
        { name: 'Editor -- Trigger Type', description: 'What causes the strategy to be applied to a customer: Days Overdue (invoice overdue by N days) or Amount Threshold (outstanding balance exceeds a value).' },
        { name: 'Editor -- Days Overdue Threshold', description: 'Visible when Trigger Type is Days Overdue. The number of days past due date before the strategy activates.' },
        { name: 'Editor -- Amount Threshold', description: 'Visible when Trigger Type is Amount Threshold. The outstanding balance (in base currency) that triggers the strategy.' },
        { name: 'Editor -- Stages panel', description: 'The ordered list of collection actions. Each stage has an Action Type (Email, Phone Call, SMS, Payment Link, Escalation, Place On Stop, Payment Plan), a Timing Type (Days from Trigger or Days after Previous Stage), a Days value, and -- for Email and SMS -- a Template selector. Up to 10 stages per strategy.' },
        { name: 'Editor -- Add Stage button', description: 'Appends a new stage to the sequence. Disabled when 10 stages have been added.' },
        { name: 'Editor -- Stage drag handle', description: 'Drag a stage card up or down to reorder it without changing the day values.' },
        { name: 'Editor -- Automation Time', description: 'The time of day (hour) at which the strategy\'s scheduled run executes.' },
        { name: 'Editor -- Days of the Week', description: 'Checkboxes for Monday-Sunday. At least one day must be selected. Default is Monday-Friday.' },
        { name: 'Performance tab -- Status Overview', description: 'Shows Active/Inactive status, Open Cases count, Last Run timestamp, Emails Sent (last 14 days), and Next Scheduled Run.' },
        { name: 'Performance tab -- Stage Breakdown', description: 'A funnel table showing how many cases are at each stage of the strategy.' },
        { name: 'Audit Log tab', description: 'Paginated table of all changes: Date & Time, Action type (colour-coded dot), Description, Field, Old Value, New Value, User.' },
      ],
      useCases: [
        'Building a standard 3-email reminder sequence (7, 14, 30 days overdue) for the majority of customers before a phone call task is generated.',
        'Setting up an Intensive strategy for high-value accounts that triggers at a lower days-overdue threshold and includes more frequent touchpoints.',
        'Configuring an escalation stage that places a customer On Stop after 90 days with no payment, then creates a legal-review task.',
        'Pausing a strategy mid-campaign by toggling it to Inactive without losing the stage configuration or historical case data.',
        'Using Run Now after editing a strategy to verify the updated stages execute correctly before the next scheduled run.',
        'Reviewing the Audit Log after a strategy produces unexpected results to identify which field was changed and by whom.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > Collection Strategies. Before creating a strategy, ensure the email templates you plan to use exist in Settings > Document Templates -- you cannot select a template that has not been created yet. You need Administrator access to create or edit strategies.',
      steps: [
        {
          title: 'How do I create a collection strategy?',
          items: [
            { text: 'Click Create Strategy in the top-right corner.' },
            { text: 'Enter a Strategy Name (required, up to 200 characters).' },
            { text: 'Select the strategy Type -- Standard, Intensive, VIP, or High Risk.' },
            { text: 'Enter a Description explaining when and how the strategy should be used (required).' },
            { text: 'Select the Multi-Stage Handling option. Use Run All in Order for most strategies.', tip: 'Use Skip to Latest if you want the strategy to jump straight to the most overdue action when a customer has fallen several stages behind.' },
            { text: 'Select the Trigger Type -- Days Overdue or Amount Threshold -- and set the threshold value.', note: 'The trigger determines when the system first applies this strategy to a customer. It does not control how individual stages fire -- that is controlled by the stage timing settings.' },
            { text: 'In the Stages panel, click Add Stage to add the first collection action.' },
            { text: 'Set the Action Type for stage 1 -- for example, Email.' },
            { text: 'Set the Timing Type to Days from Trigger and enter the number of days. Stage 1 must always use Days from Trigger.', note: 'Subsequent stages can use Days after Previous Stage to chain actions at intervals relative to the preceding stage.' },
            { text: 'For Email or SMS stages, select the template to use from the Template dropdown.', note: 'Only templates matching the stage\'s action type are shown. If the dropdown is empty, create the template first in Settings > Document Templates.' },
            { text: 'Repeat Add Stage for each action in the sequence. Drag the handle on a stage card to reorder.' },
            { text: 'In the Automation Frequency panel, select the Time and Days of the Week for the strategy to run.' },
            { text: 'Click Save.' },
          ],
        },
        {
          title: 'How do I activate or deactivate a strategy?',
          items: [
            { text: 'On the strategies list, find the strategy you want to change.' },
            { text: 'Click the toggle switch in the Status column to switch between Active and Inactive.' },
            { text: '', note: 'Deactivating a strategy stops it from running on the next scheduled execution. It does not close or remove existing open cases.' },
            { text: '', tip: 'You can also activate or deactivate from the row actions menu (three-dot icon) using the Activate / Deactivate option.' },
          ],
        },
        {
          title: 'How do I trigger a strategy run immediately?',
          items: [
            { text: 'Find the strategy in the list.' },
            { text: 'Click the three-dot actions menu on the right of the row.' },
            { text: 'Click Run Now.', note: 'Run Now is only available for Active strategies. If the option is greyed out or missing, check that the strategy is Active.' },
            { text: '', tip: 'Use Run Now after editing a strategy to test whether the updated stages execute as expected without waiting for the next scheduled run.' },
          ],
        },
        {
          title: 'How do I edit an existing strategy?',
          items: [
            { text: 'Click the strategy name in the list, or click the three-dot menu and select Edit Strategy.' },
            { text: 'Make your changes across the Details, Triggers, Stages, and Automation Frequency panels.' },
            { text: 'Click Save.', note: 'Changes take effect from the next strategy run. Cases currently mid-sequence will continue from where they are -- stages already completed are not re-run.' },
          ],
        },
        {
          title: 'How do I add a new stage to an existing strategy?',
          items: [
            { text: 'Open the strategy editor.' },
            { text: 'Scroll to the Stages panel.' },
            { text: 'Click Add Stage. A new stage card appears at the bottom of the sequence.' },
            { text: 'Set the Action Type, Timing Type, Days value, and Template (if applicable).' },
            { text: 'Drag the stage card to the correct position in the sequence if needed.' },
            { text: 'Click Save.', note: 'You can have a maximum of 10 stages per strategy. The Add Stage button is disabled when the limit is reached.' },
          ],
        },
        {
          title: 'How do I check a strategy\'s performance?',
          items: [
            { text: 'Open the strategy editor by clicking the strategy name.' },
            { text: 'Click the Performance tab.' },
            { text: 'Review the Status Overview card: Active/Inactive status, Open Cases, Last Run time, Emails Sent (last 14 days), and Next Scheduled Run.' },
            { text: 'Scroll down to the Stage Breakdown table to see how many cases are at each stage.', tip: 'If Emails Sent (14 days) shows 0 and the strategy has open cases, there may be a configuration issue -- check that all email stages have a template selected and that the strategy is Active.' },
            { text: '', note: 'Performance data only appears after the strategy has run at least once and cases have been processed.' },
          ],
        },
        {
          title: 'How do I delete a strategy?',
          items: [
            { text: 'Set the strategy to Inactive using the status toggle.' },
            { text: 'Wait until all open cases on the strategy have been closed or reassigned to another strategy.' },
            { text: 'Click the three-dot actions menu and select Delete.', note: 'The Delete option is only visible when the strategy is Inactive and has 0 open cases. If Delete is not showing, the strategy still has active cases.' },
          ],
        },
        {
          title: 'How do I review changes made to a strategy?',
          items: [
            { text: 'Open the strategy editor.' },
            { text: 'Click the Audit Log tab.' },
            { text: 'Review the table entries -- each row shows the date, action type, field changed, old value, new value, and the user who made the change.' },
            { text: 'Scroll down and click Load More to see older entries.', tip: 'Audit log entries are colour-coded by action type: blue = created, orange = status changed, yellow = field edited, purple = Run Now triggered, green = scheduled run completed, red = scheduled run failed.' },
          ],
        },
      ],
      proTips: [
        'Start with a 3-stage strategy (email at 7 days, email at 14 days, phone call task at 30 days) before building complex paths -- you can always add stages later without disrupting active cases.',
        'Use the Avg Days column on the list page as an early warning signal: a steadily rising average on an Active strategy suggests cases are not resolving and the stage timing or templates may need review.',
        'Set Automation Frequency to run at 07:00 Monday-Friday so tasks and emails are generated before your team starts work each morning.',
        'When editing a strategy that has many open cases, use Run Now immediately after saving to verify the changes work correctly on real data before the next scheduled run.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between the strategy Trigger and the stage Timing?',
        answer: 'The Trigger controls when the system first assigns a customer to a strategy -- for example, when an invoice is overdue by 5 days. The stage Timing controls when each individual action fires after assignment -- for example, send an email 7 days from the trigger, then a phone call task 14 days from the trigger. They are separate settings.',
      },
      {
        question: 'What does Multi-Stage Handling mean?',
        answer: 'When a strategy runs and multiple stages are eligible at the same time (because a customer has missed several intervals), Multi-Stage Handling controls what happens.',
        steps: [
          'Run All in Order -- every eligible stage runs sequentially in one run.',
          'Skip to Latest -- only the most recent eligible stage runs; earlier stages are skipped.',
          'One Per Run -- one stage runs per execution; the rest are picked up on subsequent runs.',
        ],
      },
      {
        question: 'Can I have multiple strategies active at the same time?',
        answer: 'Yes. You can have any number of Active strategies running simultaneously. Each customer is assigned to one strategy at a time, but different customers can be on different strategies.',
      },
      {
        question: 'What happens to open cases when I deactivate a strategy?',
        answer: 'Deactivating a strategy stops it from running on future scheduled executions. Existing open cases are not closed or removed -- they remain in the Collections Queue but no further automated actions are taken until the strategy is reactivated or the cases are reassigned.',
      },
      {
        question: 'Why is the Delete option not showing for a strategy?',
        answer: 'Delete is only available when a strategy is Inactive and has 0 open cases. If either condition is not met, the option is hidden. Set the strategy to Inactive first, then close or reassign all open cases before deleting.',
      },
      {
        question: 'Why does the Emails Sent (14 days) show 0 on the Performance tab?',
        answer: 'This can happen for three reasons: the strategy has no email stages; all email stages are missing a template; or the strategy has not had any cases reach an email stage in the last 14 days. Check the stage configuration and confirm the strategy is Active.',
      },
      {
        question: 'How do I change which days a strategy runs?',
        answer: 'Open the strategy editor, scroll to the Automation Frequency panel, and update the Days of the Week checkboxes. At least one day must remain selected. Click Save.',
      },
      {
        question: 'Can I use the same email template in more than one strategy?',
        answer: 'Yes. A template can be assigned to stages across multiple strategies. Editing the template will affect all strategies that use it.',
      },
      {
        question: 'What does the stage Action Type "Place On Stop" do?',
        answer: 'Place On Stop triggers an account action that flags the customer account. The exact behaviour (credit hold, order block) depends on how your ERP integration is configured. It does not automatically close the collection case.',
      },
      {
        question: 'How do I find out who changed a strategy setting?',
        answer: 'Open the strategy editor and click the Audit Log tab. Each row records the user, date, field changed, and the old and new values.',
      },
      {
        question: 'What happens if a strategy run fails?',
        answer: 'A Scheduled Run Failed entry appears in the Audit Log tab with a red indicator. The next scheduled run will attempt to execute normally. Use Run Now to trigger an immediate retry after investigating the cause.',
      },
    ],
    relatedPages: [
      { label: 'Document Templates', path: '/settings/templates' },
      { label: 'Customers', path: '/customers' },
      { label: 'Collections Queue', path: '/collections/queue' },
    ],
  },

  'settings-dispute-types': {
    pageTitle: 'Dispute Types',
    description:
      'Dispute Types is where administrators create and configure the categories used to classify customer disputes, including the multi-level escalation workflow assigned to each type.',
    functionality: {
      overview:
        'Each dispute type defines a named category and a workflow made up of escalation levels. When a dispute is raised against a customer, the user selects a type -- and the system uses the type\'s workflow to drive escalation, correspondence, and resolution. This page lists all types (System and Manual), shows their setup status, and lets administrators create, duplicate, activate, deactivate, and delete types. Clicking a type name opens the Workflow Editor where the escalation levels are configured.',
      keyFeatures: [
        { label: 'Dispute Type List', detail: 'View all dispute types in a table showing name, type badge (System or Manual), status, workflow level count, setup completeness, and last updated date.' },
        { label: 'Search Bar', detail: 'Filter the list by name or description as you type.' },
        { label: 'Status Filter', detail: 'Narrow the list to Active, Inactive, or All types using the filter dropdown.' },
        { label: 'Sort Options', detail: 'Sort the list by Name, Created Date, or Updated Date. Toggle ascending or descending order using the sort direction button.' },
        { label: 'Setup Status Badge', detail: 'Each row shows a Complete (green) or Incomplete (yellow) badge. A type cannot be activated until all workflow levels have an assigned user -- System types also require an email template on each level.' },
        { label: 'Active / Inactive Toggle', detail: 'Enable or disable a dispute type without deleting it. The toggle is locked if the type is Incomplete.' },
        { label: 'Row Actions Menu', detail: 'The three-dot menu on each row provides Edit Dispute Type, Duplicate, Activate/Deactivate, and Delete.' },
        { label: 'Create Dispute Type', detail: 'Opens a full creation page where you name the type, add a description, and configure one or more workflow levels before saving.' },
        { label: 'Workflow Editor', detail: 'Clicking a type name navigates to the Workflow Editor where you configure escalation levels, follow-up days, escalation days, assigned users, escalation users, email templates, and the Resolved stage.' },
        { label: 'Audit Log Tab', detail: 'The Workflow Editor includes an Audit Log tab showing a history of all changes made to the type, including who made the change and when.' },
      ],
      sectionsAndFields: [
        { name: 'Total Types card', description: 'Count of all dispute types in the system regardless of status.' },
        { name: 'Active Types card', description: 'Count of dispute types currently set to Active and available for use when raising disputes.' },
        { name: 'Search field', description: 'Filters the table in real time by matching the search term against the type name and description.' },
        { name: 'Status filter dropdown', description: 'Filters the table to show All, Active, or Inactive types.' },
        { name: 'Sort By dropdown', description: 'Sets the sort column: Name, Created Date, or Updated Date.' },
        { name: 'Sort direction button', description: 'Toggles between ascending and descending order for the selected sort column.' },
        { name: 'Name & Description column', description: 'The type name is a clickable link that opens the Workflow Editor. The description appears beneath it.' },
        { name: 'Type column', description: 'System badge (outline) = built-in type managed by the platform; Manual badge (secondary) = administrator-created type.' },
        { name: 'Status column', description: 'Shows the Active/Inactive toggle and an Active (green) or Inactive (grey) badge. The toggle is disabled if Setup Status is Incomplete.' },
        { name: 'Workflow Levels column', description: 'Count of escalation levels configured for this type, excluding the Resolved stage.' },
        { name: 'Setup Status column', description: 'Complete (green) = all required fields are set and the type can be activated. Incomplete (yellow) = one or more required fields are missing.' },
        { name: 'Last Updated column', description: 'Date the type was last modified.' },
        { name: 'Actions menu', description: 'Three-dot menu with: Edit Dispute Type (opens Workflow Editor), Duplicate (creates a copy), Activate/Deactivate (toggle status), Delete (disabled if type has active disputes or is a System type).' },
        { name: 'Workflow Editor - Settings tab', description: 'The primary configuration tab showing workflow name, description, and the list of escalation levels.' },
        { name: 'Workflow Editor - Level card', description: 'Each level has: Level number, Description, Follow-Up Days, Escalation Days, Assigned User, Escalation User, Document Template, Send Correspondence toggle.' },
        { name: 'Follow-Up Days field', description: 'Number of days after the previous level before a follow-up action is triggered for this level.' },
        { name: 'Escalation Days field', description: 'Number of days after the Follow-Up Days before the dispute escalates to the next level.' },
        { name: 'Assigned User dropdown', description: 'The user responsible for working this escalation level. Required -- cannot activate the type without setting this.' },
        { name: 'Escalation User dropdown', description: 'The user notified or escalated to when the Escalation Days threshold is reached. Optional if Escalation Days is zero.' },
        { name: 'Document Template dropdown', description: 'The email template sent at this level. Required for System types; optional for Manual types.' },
        { name: 'Send Correspondence toggle', description: 'When on, the system sends the Document Template email automatically at this level.' },
        { name: 'Resolved stage', description: 'The final stage appended automatically. Marks the dispute as resolved. It cannot be deleted.' },
        { name: 'Workflow Editor - Audit Log tab', description: 'Chronological list of all changes to this dispute type -- field changed, old value, new value, user, and timestamp.' },
      ],
      useCases: [
        'Creating dispute type categories before go-live so the team can classify disputes consistently from day one.',
        'Configuring a short 3-day escalation window on a Payment Dispute type to prioritise cash-flow-critical issues.',
        'Duplicating an existing Billing Error type to create a similar Contract Terms type without rebuilding the workflow from scratch.',
        'Deactivating a dispute type that was used for a temporary product line without losing the historical dispute records.',
        'Reviewing the Audit Log on a type to find out who changed the Assigned User on Level 1 and when.',
        'Setting the Assigned User on each level of an Incomplete type so the Active toggle becomes available.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > Dispute Types. You must have Administrator access to view or edit this page. Before creating types, make sure your users and email templates are configured in Settings -- the Workflow Editor dropdowns pull from those lists.',
      steps: [
        {
          title: 'How do I create a new dispute type?',
          items: [
            { text: 'Click Create Dispute Type in the top-right corner.' },
            { text: 'Enter a Name for the type -- this is the label users will see when raising a dispute.', note: 'The name must be unique. Duplicate names are not allowed.' },
            { text: 'Enter a Description to help users understand when to use this type.' },
            { text: 'In the Workflow Levels section, enter a Description for Level 1 -- a short label for what this escalation level represents (e.g., "Initial contact").' },
            { text: 'Set Follow-Up Days -- the number of days before a follow-up is triggered at this level.' },
            { text: 'Set Escalation Days -- the number of days before the dispute escalates to the next level. Set to 0 if you do not want automatic escalation from this level.' },
            { text: 'Select an Assigned User from the dropdown. This field is required -- the type cannot be activated without it.', note: 'Only active users appear in the dropdown. If the user you need is not listed, check their status in Settings > Users.' },
            { text: 'Optionally select an Escalation User and a Document Template.', note: 'If Escalation Days is 0, the Escalation User field must remain Unassigned.' },
            { text: 'Toggle Send Correspondence on if the system should automatically send the Document Template email at this level.' },
            { text: 'Click Add Level to add further escalation levels. Drag the grip handle to reorder levels.', tip: 'Most dispute workflows need 2-3 levels: initial contact, follow-up, and senior escalation.' },
            { text: 'Click Save when all required fields are complete.' },
          ],
        },
        {
          title: 'How do I edit an existing dispute type workflow?',
          items: [
            { text: 'Click the type name in the table -- this opens the Workflow Editor.' },
            { text: 'Select the Settings tab if it is not already active.' },
            { text: 'Edit the workflow name, description, or any level field.' },
            { text: 'Click Save Changes.', note: 'If the type has active disputes, a confirmation dialog warns you that saving will affect those disputes. Confirm to proceed.' },
          ],
        },
        {
          title: 'How do I activate a dispute type?',
          items: [
            { text: 'Check the Setup Status column. The type must show Complete (green) before it can be activated.', note: 'If the badge shows Incomplete (yellow), open the Workflow Editor and assign a user to every level. System types also require a Document Template on each level.' },
            { text: 'Click the toggle in the Status column to set the type to Active.' },
            { text: 'The Active (green) badge appears and the type is now available when raising disputes.' },
          ],
        },
        {
          title: 'How do I duplicate a dispute type?',
          items: [
            { text: 'Click the three-dot menu on the row you want to copy.' },
            { text: 'Select Duplicate.' },
            { text: 'A copy is created with the name "[Original Name] (Copy)" and a status of Inactive.', tip: 'Rename the duplicate immediately to avoid confusion -- open it, edit the workflow name field, and save.' },
            { text: 'Open the copy, complete any required fields, and activate when ready.' },
          ],
        },
        {
          title: 'How do I deactivate a dispute type?',
          items: [
            { text: 'Click the toggle in the Status column on the row you want to deactivate.' },
            { text: 'The type is set to Inactive and no longer appears in the dispute type dropdown when raising new disputes.', note: 'Existing disputes that use this type are not affected. Historical records remain unchanged.' },
          ],
        },
        {
          title: 'How do I delete a dispute type?',
          items: [
            { text: 'Click the three-dot menu on the row.' },
            { text: 'Select Delete.', note: 'The Delete option is disabled (greyed out) if the type has active disputes associated with it, or if it is a System type. System types cannot be deleted -- deactivate them instead.' },
            { text: 'Confirm the deletion in the dialog. This action cannot be undone.' },
          ],
        },
        {
          title: 'How do I review the change history for a dispute type?',
          items: [
            { text: 'Click the type name to open the Workflow Editor.' },
            { text: 'Select the Audit Log tab.' },
            { text: 'Review the list of changes -- each row shows the field changed, old value, new value, the user who made the change, and the timestamp.' },
            { text: '', tip: 'Use the Audit Log to investigate unexpected changes to assigned users or email templates, especially after a team restructure.' },
          ],
        },
      ],
      proTips: [
        'Keep the number of active dispute types to 8-12 categories. Too many types leads to inconsistent classification and makes the Common Dispute Types panel on the Disputes Dashboard less useful.',
        'Set a short Escalation Days value (3-5 days) on Level 1 of high-priority types like Payment Dispute or Overcharge. Longer windows are acceptable for low-priority types like Minor Delivery Issue.',
        'Always complete the workflow configuration before activating a type. An Incomplete type cannot be activated, and attempting to toggle it shows an error -- complete all Assigned User fields first.',
        'Use Duplicate as your primary creation method once you have one well-configured type. Copy it, rename it, adjust the escalation days and assigned users, and activate -- this is faster and less error-prone than creating from scratch.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between a System type and a Manual type?',
        answer: 'System types are built into the platform and represent standard dispute categories (e.g., Billing Error, Payment Dispute). They require both an Assigned User and a Document Template on each level before they can be activated. Manual types are created by administrators and only require an Assigned User -- the Document Template is optional.',
      },
      {
        question: 'Why is the Active toggle disabled on a dispute type?',
        answer: 'The toggle is disabled when the Setup Status is Incomplete. Open the Workflow Editor for that type and check each level -- every level must have an Assigned User. System types also require a Document Template. Once all required fields are set, the Setup Status changes to Complete and the toggle becomes available.',
      },
      {
        question: 'Can I delete a dispute type that has been used on a dispute?',
        answer: 'No. If a dispute type has one or more active disputes associated with it, the Delete option in the row actions menu is disabled. Deactivate the type instead to prevent it from being used on new disputes while preserving the historical records.',
      },
      {
        question: 'Can I delete a System dispute type?',
        answer: 'No. System types cannot be deleted. The Delete option is always disabled for System types. If you do not want a System type to appear when raising disputes, deactivate it using the Status toggle.',
      },
      {
        question: 'What does the Workflow Levels column count?',
        answer: 'It counts the number of escalation levels configured for the type, not including the Resolved stage. A type with 3 escalation levels and 1 resolved stage shows "3 levels" in this column.',
      },
      {
        question: 'What happens to existing disputes if I edit a dispute type workflow?',
        answer: 'A confirmation dialog warns you before saving changes to a type that has active disputes. If you confirm, the updated workflow settings apply to disputes that have not yet reached the affected level. Disputes that have already passed a level are not retroactively changed.',
      },
      {
        question: 'How do I add a Document Template to a workflow level?',
        answer: 'Open the Workflow Editor for the type, select the Settings tab, and use the Document Template dropdown on each level. Only email templates appear in this list. If the dropdown is empty, go to Settings > Templates and create an email template first, then return here.',
        steps: [
          'Click the type name to open the Workflow Editor.',
          'Select the Settings tab.',
          'Find the level you want to update.',
          'Click the Document Template dropdown and select the template.',
          'Click Save Changes.',
        ],
      },
      {
        question: 'What does the Send Correspondence toggle do on a workflow level?',
        answer: 'When on, the system automatically sends the Document Template email to the customer when the dispute reaches that level. When off, no email is sent automatically at that level -- the handler must send correspondence manually from the dispute record.',
      },
      {
        question: 'Can I reorder the escalation levels in a workflow?',
        answer: 'Yes. In the Workflow Editor, drag the grip handle (the six-dot icon on the left of each level card) to reorder the levels. The level numbers update automatically. Click Save Changes to persist the new order.',
      },
      {
        question: 'What does Escalation Days mean on a workflow level?',
        answer: 'Escalation Days is the number of days after the Follow-Up Days threshold before the dispute automatically escalates to the next level. For example, if Follow-Up Days is 3 and Escalation Days is 5, the follow-up action triggers at day 3 and escalation to the next level triggers at day 8. Set Escalation Days to 0 if you do not want automatic escalation from that level.',
      },
      {
        question: 'Why can I not see a user I want to assign in the Assigned User dropdown?',
        answer: 'Only active users appear in the Assigned User and Escalation User dropdowns. If the user you need is not listed, go to Settings > Users and check that their status is set to Active.',
      },
    ],
    relatedPages: [
      { label: 'All Disputes', path: '/disputes/all' },
      { label: 'Disputes Dashboard', path: '/disputes/dashboard' },
    ],
  },

  'settings-roles-permissions': {
    pageTitle: 'Roles & Permissions',
    description:
      'The Roles & Permissions page is where administrators define roles and configure what each role can view, create, edit, and delete within the system.',
    functionality: {
      overview:
        'Roles group a set of permissions that are applied to users. Instead of configuring permissions per user, you assign a role and the user inherits all permissions in that role. You can create custom roles for your organisation or modify the default roles provided.',
      keyFeatures: [
        { label: 'Role Creation', detail: 'Define a new role with a name, description, and permission set.' },
        { label: 'Granular Permissions', detail: 'Configure access at the page and action level -- for example, "View Customers" without "Edit Customers".' },
        { label: 'Default Roles', detail: 'Pre-built roles (Administrator, Manager, Collector, Read Only) are provided as a starting point.' },
        { label: 'Role Assignment Overview', detail: 'See which users are assigned to each role.' },
      ],
      sectionsAndFields: [
        { name: 'Role Name', description: 'The label assigned to the role -- displayed in user profiles and activity logs.' },
        { name: 'Description', description: 'A plain-language explanation of what this role is intended for.' },
        { name: 'Permissions Matrix', description: 'A table of modules and actions. Tick to grant permission, untick to deny.' },
        { name: 'Assigned Users', description: 'Count and list of users currently assigned to this role.' },
      ],
      useCases: [
        'Creating a "Senior Collector" role with additional permissions to approve write-offs.',
        'Restricting a "Viewer" role to read-only access on all pages for auditors or external consultants.',
        'Reviewing which users have Administrator access as part of a security audit.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > Roles & Permissions. Review the default roles before creating custom ones -- often a default role can be adapted rather than building from scratch.',
      steps: [
        {
          title: 'How do I create a new role?',
          items: [
            { text: 'Click Create Role.' },
            { text: 'Enter a Role Name and description.' },
            { text: 'Work through the permissions matrix and tick the actions this role should be able to perform.' },
            { text: 'Click Save.' },
            { text: '', tip: 'Duplicate an existing role as a starting point rather than building from scratch -- click the three-dot menu on an existing role and select Duplicate.' },
          ],
        },
        {
          title: 'How do I assign a role to a user?',
          items: [
            { text: 'Go to Settings > User Management.' },
            { text: 'Open the user\'s profile.' },
            { text: 'Click Edit.' },
            { text: 'Select the role from the Role dropdown.' },
            { text: 'Click Save.' },
          ],
        },
      ],
      proTips: [
        'Follow the principle of least privilege: give each role only the permissions it genuinely needs to do the job.',
        'Document the purpose of each custom role in the Description field so future administrators understand the intent.',
      ],
    },
    faq: [
      {
        question: 'What are the default roles?',
        answer: 'Default roles are: Administrator (full access), Manager (full access except system settings), Collector (operational pages only, no settings access), and Read Only (view access across all pages, no create/edit/delete).',
      },
      {
        question: 'Can I modify a default role?',
        answer: 'Yes. Default roles can be edited. However, modifying the Administrator role is not recommended -- ensure at least one user retains full Administrator access at all times.',
      },
    ],
    relatedPages: [
      { label: 'User Management', path: '/settings/users' },
      { label: 'Settings - Activity Log', path: '/settings/activity-log' },
    ],
  },

  'settings-activity-log': {
    pageTitle: 'User Activity Log',
    description:
      'The User Activity Log records every action performed by users in the system, providing an audit trail for security, compliance, and dispute resolution.',
    functionality: {
      overview:
        'This is a read-only log of system events. Every time a user creates, updates, or deletes a record, the action is logged with the user\'s name, the affected record, the timestamp, and the change made. Use it for compliance audits, investigating discrepancies, and monitoring unusual activity.',
      keyFeatures: [
        { label: 'Complete Audit Trail', detail: 'Every create, update, and delete action across all modules is logged.' },
        { label: 'User Filter', detail: 'Filter the log by user to see all actions performed by a specific person.' },
        { label: 'Module Filter', detail: 'Filter by module (Customers, Payments, Disputes, etc.) to investigate activity in a specific area.' },
        { label: 'Date Filter', detail: 'Restrict the log to a specific time period.' },
        { label: 'Export', detail: 'Download the filtered log as a CSV for external audit or compliance reporting.' },
      ],
      sectionsAndFields: [
        { name: 'Timestamp', description: 'The exact date and time the action was performed.' },
        { name: 'User', description: 'The user who performed the action.' },
        { name: 'Action', description: 'What was done: Created, Updated, Deleted, Logged In, Logged Out, etc.' },
        { name: 'Module', description: 'Which area of the system the action occurred in.' },
        { name: 'Record', description: 'The specific record that was affected -- e.g., "Customer: Acme Corp", "Invoice: INV-001".' },
        { name: 'Change Detail', description: 'What changed -- for example, "Credit Limit: GBP5,000 -> GBP10,000".' },
      ],
      useCases: [
        'Investigating who changed a customer\'s credit limit and when.',
        'Reviewing all activity by a specific user before they left the organisation.',
        'Providing an audit log to auditors or regulators as part of a compliance review.',
        'Investigating an unexpected change to a collection strategy.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > Activity Log. The log is read-only -- no actions can be changed or deleted from here. Use the filters to narrow the log to the activity you are investigating.',
      steps: [
        {
          title: 'How do I find a specific change?',
          items: [
            { text: 'Use the User filter to select the user you want to investigate.' },
            { text: 'Set a date range around when the change is believed to have occurred.' },
            { text: 'Use the Module filter to restrict to the relevant part of the system.' },
            { text: 'Scroll through the filtered results or search for a specific record name.' },
          ],
        },
        {
          title: 'How do I export the activity log?',
          items: [
            { text: 'Apply the relevant filters.' },
            { text: 'Click Export.' },
            { text: 'Select CSV.' },
            { text: 'Click Download.' },
          ],
        },
      ],
      proTips: [
        'Review the activity log periodically for unusual patterns -- for example, a user accessing and exporting customer data outside normal working hours.',
        'When resolving a dispute about a historical change, export the filtered log entry and attach it to the investigation notes.',
      ],
    },
    faq: [
      {
        question: 'How long are activity logs retained?',
        answer: 'Logs are retained according to your data retention policy, typically at least 12 months and up to 7 years for financial compliance. Contact your system administrator for the exact configuration.',
      },
      {
        question: 'Can activity log entries be deleted?',
        answer: 'No. Activity log entries cannot be deleted or altered. This is by design to maintain a trustworthy audit trail.',
      },
    ],
    relatedPages: [
      { label: 'User Management', path: '/settings/users' },
      { label: 'Roles & Permissions', path: '/settings/roles' },
    ],
  },

  'settings-ai-configuration': {
    pageTitle: 'AI Configuration',
    description:
      'The AI Configuration page controls which AI-powered features are enabled and how they are configured across the credit control module.',
    functionality: {
      overview:
        'The system includes optional AI features such as risk prediction, smart collection recommendations, and automated communication drafting. This page lets administrators enable or disable these features, configure their sensitivity and behaviour, and review AI-generated activity.',
      keyFeatures: [
        { label: 'Feature Enablement', detail: 'Turn individual AI features on or off without affecting the rest of the system.' },
        { label: 'Risk Prediction Configuration', detail: 'Set the thresholds and data inputs used by the AI risk scoring model.' },
        { label: 'Smart Action Recommendations', detail: 'Configure whether the AI suggests next actions to collectors in the Collections Queue.' },
        { label: 'Communication Drafting', detail: 'Enable the AI to draft collection emails based on case history for collector review before sending.' },
      ],
      sectionsAndFields: [
        { name: 'Feature Toggles', description: 'On/Off switches for each AI feature.' },
        { name: 'Risk Model Sensitivity', description: 'How aggressively the model scores risk -- higher sensitivity flags more customers as high risk.' },
        { name: 'Recommendation Confidence Threshold', description: 'Minimum confidence level before a smart action recommendation is shown to a collector.' },
        { name: 'AI Communication Drafting', description: 'Whether AI-drafted emails are enabled and whether they require manual approval before sending.' },
      ],
      useCases: [
        'Enabling smart action recommendations to support a new collections team during onboarding.',
        'Adjusting the risk model sensitivity to align with a change in credit policy.',
        'Disabling AI communication drafting temporarily while reviewing the templates it is using.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > AI Configuration. Only Administrator-role users can change these settings. Review which features are available under your subscription before enabling them.',
      steps: [
        {
          title: 'How do I enable an AI feature?',
          items: [
            { text: 'Find the feature in the feature toggles list.' },
            { text: 'Click the toggle to enable it.' },
            { text: 'Configure any associated settings that appear.' },
            { text: 'Click Save.' },
            { text: '', note: 'Some AI features require data to be available in the system before they are effective -- for example, risk prediction works best with at least 6 months of payment history.' },
          ],
        },
      ],
      proTips: [
        'Enable one AI feature at a time and monitor its impact before enabling the next -- this makes it easier to identify whether a feature is adding value.',
        'Review AI-generated recommendations against actual outcomes periodically to validate that the model is performing as expected for your customer base.',
      ],
    },
    faq: [
      {
        question: 'What AI features are available?',
        answer: 'Available features depend on your subscription. Typical features include: AI risk scoring, smart collection action recommendations, automated communication drafting, and payment prediction.',
      },
      {
        question: 'Can AI send communications automatically without human review?',
        answer: 'This depends on configuration. By default, AI-drafted communications require manual approval before sending. Auto-send can be enabled but is not recommended until the model has been validated on your customer base.',
      },
    ],
    relatedPages: [
      { label: 'Collection Strategies', path: '/settings/strategies' },
      { label: 'Credit Scoring', path: '/credit-scoring' },
      { label: 'Company Settings', path: '/settings/company' },
    ],
  },

  integrations: {
    pageTitle: 'Integrations',
    description:
      'The Integrations page shows all third-party system connections configured for the credit control module, including accounting systems, bank feeds, and payment gateways.',
    functionality: {
      overview:
        'The credit control module connects to your accounting system to import customers, invoices, and payments; to bank feeds for automatic receipt import; and to payment gateways for card processing. This page shows the status of each integration and allows administrators to configure connection settings.',
      keyFeatures: [
        { label: 'Integration Status', detail: 'Shows whether each integration is Active, Disconnected, or Error, and when it last successfully synced.' },
        { label: 'Accounting System Connection', detail: 'Connects to your ERP or accounting software to import customers, invoices, and payments.' },
        { label: 'Bank Feed', detail: 'Automatically imports daily bank receipts for allocation.' },
        { label: 'Payment Gateway', detail: 'Connects to card payment processors for online and phone card payment capability.' },
        { label: 'Sync Logs', detail: 'View the history of data sync events and any errors encountered.' },
      ],
      sectionsAndFields: [
        { name: 'Integration Name', description: 'The name of the third-party system -- e.g., Sage 200, Xero, Stripe.' },
        { name: 'Status', description: 'Active (syncing normally), Disconnected (connection lost), or Error (syncing but with failures).' },
        { name: 'Last Sync', description: 'When data was last successfully imported from this integration.' },
        { name: 'Configure Button', description: 'Opens the settings for this specific integration including API keys and sync schedule.' },
        { name: 'Sync Now Button', description: 'Triggers a manual sync outside the scheduled interval.' },
        { name: 'Sync Logs', description: 'History of sync events showing what was imported and any errors.' },
      ],
      useCases: [
        'Investigating why new invoices are not appearing in the system -- checking the accounting system sync status.',
        'Triggering a manual bank feed sync after a bank statement is available earlier than expected.',
        'Reconfiguring an API key after it is rotated in the accounting system.',
        'Reviewing sync error logs to understand why some invoices failed to import.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Integrations from the left navigation. Each integration tile shows its current status. Contact your system administrator if an integration shows as Disconnected or Error.',
      steps: [
        {
          title: 'How do I check an integration status?',
          items: [
            { text: 'Open the Integrations page.' },
            { text: 'Each integration tile shows its status badge: Active, Disconnected, or Error.' },
            { text: 'Click on the integration tile to see the last sync time and any recent errors.' },
          ],
        },
        {
          title: 'How do I trigger a manual sync?',
          items: [
            { text: 'Open the integration tile.' },
            { text: 'Click Sync Now.' },
            { text: 'Wait for the sync to complete -- the Last Sync timestamp updates when finished.' },
            { text: '', note: 'Manual syncs count towards API rate limits for some integrations. Do not trigger multiple syncs in quick succession.' },
          ],
        },
        {
          title: 'How do I investigate a sync error?',
          items: [
            { text: 'Open the integration tile showing an Error status.' },
            { text: 'Click Sync Logs.' },
            { text: 'Review the most recent error entries.' },
            {
              text: 'Common errors and resolutions:',
              subSteps: [
                'Authentication error: the API key may have expired -- reconfigure the connection with a new key.',
                'Timeout: the source system was slow to respond -- retry the sync or check the source system\'s status.',
                'Validation error: a record in the source system has missing required fields -- correct the record in the source system and resync.',
              ],
            },
          ],
        },
      ],
      proTips: [
        'Check the Integrations page first thing each morning to confirm all feeds synced successfully overnight before starting collections work.',
        'After rotating an API key in your accounting system, update the key in Integrations immediately to prevent a sync gap.',
        'Keep sync schedules aligned with your business day -- if your bank closes its API feed at midnight, schedule the bank feed sync for 06:00 to ensure receipts are available when the team starts.',
      ],
    },
    faq: [
      {
        question: 'Why are new invoices not appearing in the system?',
        answer: 'Check the accounting system integration status on the Integrations page. If the status shows Error or the last sync is more than a day old, the integration may have lost connection or encountered an error. Trigger a manual sync or reconfigure the connection.',
      },
      {
        question: 'Why are yesterday\'s bank receipts not showing in Receipts?',
        answer: 'Check the bank feed integration status. If it is Active and up to date, the bank may not have made the previous day\'s transactions available yet. If the integration shows an error, investigate the sync log for details.',
      },
      {
        question: 'Can I connect to more than one accounting system?',
        answer: 'This depends on your configuration and subscription. Contact your system administrator or support team for multi-system connection options.',
      },
    ],
    relatedPages: [
      { label: 'Receipts', path: '/payments/receipts' },
      { label: 'Company Settings', path: '/settings/company' },
      { label: 'Payment History', path: '/payments/history' },
    ],
  },

  tasks: {
    pageTitle: 'My Tasks',
    description:
      'Your personal action list of every collection activity assigned to you -- follow-up calls, overdue cases, promises due today, and disputes awaiting a response.',
    functionality: {
      overview:
        'My Tasks aggregates all task types in one list so you always know what requires your attention. Tasks arrive here automatically from collection strategies, promise due dates, and dispute escalations, as well as tasks created manually by you or a manager. Use the quick filters to focus by urgency, the advanced filters to narrow by type or priority, and the search bar to locate a specific customer or task. Click any row to open the task detail and take action.',
      keyFeatures: [
        {
          label: 'Unified Task List',
          detail:
            'Combines all task categories -- collection follow-ups, promises due, dispute responses, and manual tasks -- into a single list so nothing is missed.',
        },
        {
          label: 'Quick Filter Bar',
          detail:
            'One-click toggle badges for My Tasks, Overdue, Due Today, and Due This Week let you instantly narrow the list to the most time-sensitive items without opening the advanced filters.',
        },
        {
          label: 'Advanced Filters',
          detail:
            'Multi-select dropdowns for Task Type (Collections, Promises, Disputes, Receipts, Task) and Priority (Critical, High, Medium, Low), plus a free-text search bar, let you build a precise working list for any situation.',
        },
        {
          label: 'Priority Badges',
          detail:
            'Every task row shows a colour-coded priority badge -- Critical (red), High (orange), Medium (blue), Low (grey) -- so you can sequence your work before opening individual tasks.',
        },
        {
          label: 'Create Task',
          detail:
            'Manually create a task, assign it to yourself or a colleague, set a due date, and optionally link it to a customer account -- useful for ad hoc follow-ups not generated by a strategy.',
        },
        {
          label: 'Grouped Open / Completed View',
          detail:
            'Tasks are split into an Open group (expanded by default) and a Completed group (collapsed by default), keeping completed work out of the way without discarding it.',
        },
        {
          label: 'Refresh',
          detail:
            'Reload the task list at any point to pick up tasks generated since the page was last loaded -- collection strategies can create tasks throughout the day.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Quick Filter Bar',
          description:
            'Row of toggle badges at the top of the page: My Tasks, Overdue, Due Today, Due This Week. Multiple badges can be active simultaneously; the list shows tasks matching any active filter.',
        },
        {
          name: 'My Tasks badge',
          description:
            'Limits the list to tasks assigned to the currently logged-in user. Active by default when the page loads.',
        },
        {
          name: 'Overdue badge',
          description:
            'Shows open tasks whose due date has already passed.',
        },
        {
          name: 'Due Today badge',
          description:
            'Shows tasks due by the end of today, regardless of type or priority.',
        },
        {
          name: 'Due This Week badge',
          description:
            'Shows tasks due within the next 7 calendar days from today.',
        },
        {
          name: 'Search field',
          description:
            'Free-text input that filters across task titles, descriptions, and linked customer names in real time.',
        },
        {
          name: 'Task Type dropdown',
          description:
            'Multi-select filter for task category: Collections, Promises, Disputes, Receipts, or Task. Select one or more to narrow the list.',
        },
        {
          name: 'Priority dropdown',
          description:
            'Multi-select filter for urgency level: Critical, High, Medium, or Low.',
        },
        {
          name: 'Create Task button',
          description:
            'Opens the Create Task drawer where you enter a title, description, due date, assigned user, and optional customer link.',
        },
        {
          name: 'Clear Filters button',
          description:
            'Removes all active quick filters and advanced filters, returning the list to the full unfiltered view.',
        },
        {
          name: 'Open Tasks group',
          description:
            'Collapsible section containing all tasks with status Open. Expanded by default. Shows a count of open tasks in the group header.',
        },
        {
          name: 'Completed Tasks group',
          description:
            'Collapsible section containing tasks marked as complete. Collapsed by default.',
        },
        {
          name: 'Task row',
          description:
            'Each row shows: Type badge, task title, source reference, Priority badge, due date, assigned user avatar and name, and linked amount (where applicable). Click anywhere on the row to open the task detail drawer.',
        },
        {
          name: 'Type badge',
          description:
            'Identifies the task category -- Collections, Promise, Dispute, Receipt, or Task -- shown as an outlined badge in the first column.',
        },
        {
          name: 'Priority badge',
          description:
            'Colour-coded label on each task row: Critical (red), High (orange), Medium (blue), Low (grey).',
        },
        {
          name: 'Due Date',
          description:
            'The date by which the task should be completed, displayed in day-month-year format.',
        },
        {
          name: 'Assigned To',
          description:
            'The user responsible for completing the task, shown as an avatar and name.',
        },
        {
          name: 'Refresh button',
          description:
            'Located in the top-right header. Reloads the task list from the server.',
        },
      ],
      useCases: [
        'Starting the day with the My Tasks and Overdue quick filters both active to identify personal tasks that are already past their due date.',
        'A team manager removing the My Tasks filter and searching by a collector\'s name to review that person\'s full workload.',
        'Filtering by Task Type = "Promises" and the Due Today badge to call every customer who committed to pay today before end of business.',
        'Creating a manual follow-up task after a customer requests a callback at a specific time, with a note in the description.',
        'Using the Due This Week filter on a Friday to plan and balance workload across the coming week.',
        'Filtering by Priority = "Critical" before a management review to confirm the highest-risk accounts have been actioned.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Select My Tasks from the left navigation. The page loads with the My Tasks quick filter already active, so you see only tasks assigned to you. No prerequisite setup is needed -- tasks are created automatically by collection strategies and promise due dates. If this is your first time and the list is empty, your organisation may not yet have run a collection strategy; contact your administrator to confirm.',
      steps: [
        {
          title: 'How do I see only my tasks?',
          items: [
            {
              text: 'The My Tasks badge is active by default when the page loads -- your personal tasks are shown immediately.',
              tip: 'Click the My Tasks badge a second time to deactivate it and see tasks across the whole team.',
            },
          ],
        },
        {
          title: 'How do I filter tasks by urgency?',
          items: [
            {
              text: 'Click the Overdue badge to see tasks whose due date has already passed.',
            },
            {
              text: 'Click the Due Today badge to see tasks due by end of today.',
            },
            {
              text: 'Click the Due This Week badge to see tasks due in the next 7 days.',
              note: 'Multiple quick filter badges can be active at the same time. The list shows tasks matching any active badge -- not only those matching all of them.',
            },
            {
              text: 'Click an active badge again to deactivate it and remove it from the filter.',
            },
          ],
        },
        {
          title: 'How do I filter by task type or priority?',
          items: [
            {
              text: 'Locate the Advanced Filters panel directly below the quick filter bar.',
            },
            {
              text: 'Click the Task Type dropdown and select one or more categories -- Collections, Promises, Disputes, Receipts, or Task.',
              tip: 'Select "Promises" and activate the Due Today badge at the same time to get a focused call list of customers who committed to pay today.',
            },
            {
              text: 'Click the Priority dropdown and select one or more levels -- Critical, High, Medium, or Low.',
            },
            {
              text: 'The list updates immediately with each selection. Active filter counts are shown on the filter button.',
            },
          ],
        },
        {
          title: 'How do I open a task and take action?',
          items: [
            {
              text: 'Click on any task row in the list.',
            },
            {
              text: 'The task detail drawer opens on the right side of the screen.',
            },
            {
              text: 'Review the task title, type, linked customer, due date, and any notes.',
            },
            {
              text: 'For task types Promise to Pay, Dispute, and Collection Case, the drawer is view-only. To amend the underlying record, click the View & Edit button at the bottom of the drawer. This navigates you to the relevant module where you can make changes.',
              note: 'The View & Edit button is available for Promise to Pay, Dispute, and Collection Case task types only. Tasks of type Task can be managed directly within the drawer.',
            },
            {
              text: 'For tasks of type Task, you can manage the task directly in the drawer.',
            },
            {
              text: 'Click Mark as Complete to move the task to the Completed group and remove it from your open list.',
            },
          ],
        },
        {
          title: 'How do I create a manual task?',
          items: [
            {
              text: 'Click the Create Task button in the Advanced Filters panel.',
            },
            {
              text: 'The Create Task drawer opens on the right side.',
            },
            {
              text: 'Type a title that describes the action required -- for example, "Call Acme Corp re: invoice INV-1234".',
            },
            {
              text: 'Optionally add a description with more detail.',
            },
            {
              text: 'Select the due date using the date picker.',
            },
            {
              text: 'Select the user to assign the task to.',
              note: 'Only active users with access to the current company are shown in the assignee list.',
            },
            {
              text: 'Optionally type the first few characters of a customer name in the Customer field to link the task to an account.',
            },
            {
              text: 'Click Save. The task appears in the Open Tasks group immediately.',
              tip: 'Manual tasks created here appear in the assigned user\'s My Tasks list as soon as you save them.',
            },
          ],
        },
        {
          title: 'How do I clear all filters?',
          items: [
            {
              text: 'Click the Clear Filters button in the Advanced Filters panel.',
            },
            {
              text: 'All active quick filter badges and advanced filter selections are removed, and the full unfiltered task list is shown.',
              note: 'Clear Filters also deactivates the My Tasks badge, so the list will show tasks for all users after clearing.',
            },
          ],
        },
        {
          title: 'How do I refresh the task list?',
          items: [
            {
              text: 'Click the Refresh button in the top-right corner of the page header.',
              note: 'Collection strategies can generate new tasks throughout the day, not just overnight. If a colleague tells you a task has been assigned to you and you cannot see it, click Refresh.',
            },
          ],
        },
      ],
      proTips: [
        'Start each morning by activating both the My Tasks and Overdue badges. This shows only your personal tasks that are already late -- action these before moving to anything else.',
        'Combine the Task Type = "Promises" filter with the Due Today badge to build a focused morning call list. Calling promise customers before 11 AM gives you time to escalate unpaid promises before end of business.',
        'After completing a block of tasks, click Refresh to pick up any new tasks assigned to you since you loaded the page -- strategies run continuously and new tasks can appear at any time.',
        'On Fridays, switch to Due This Week to preview next week\'s pipeline. If one day looks very heavy, create manual tasks now to distribute work more evenly across the team.',
      ],
    },
    faq: [
      {
        question: 'How do I know where a task came from?',
        answer:
          'Each task row shows a Type badge (Collections, Promises, Disputes, Receipts, or Task) and a source reference. Open the task detail to see the linked collection case, promise, or dispute record.',
      },
      {
        question: 'Why do I have no tasks even though I manage overdue accounts?',
        answer:
          'Tasks are generated by collection strategies, not created automatically for every overdue account. If no strategy has run for those accounts, no tasks exist yet. Try the following:',
        steps: [
          'Click Clear Filters to remove all filters and see every task in the system.',
          'Check whether the accounts appear in the Collections Queue page instead.',
          'Contact your administrator to confirm whether a collection strategy is active for those account segments.',
        ],
      },
      {
        question: 'What does the Priority badge on each task mean?',
        answer:
          'Priority reflects the urgency of the task: Critical -- very high-value account or escalated case, action today. High -- overdue task or significant balance. Medium -- within terms, action within a few days. Low -- general follow-up with no immediate urgency.',
      },
      {
        question: 'Can I assign a task to a colleague instead of myself?',
        answer:
          'Yes. When creating a manual task, select any active user from the Assigned To dropdown. For system-generated tasks, open the task detail and update the Assigned To field to reassign it.',
      },
      {
        question: 'Can I complete multiple tasks at once?',
        answer:
          'No. Bulk task completion is not currently available. Each task must be opened individually and marked as complete from the task detail drawer.',
      },
      {
        question: 'What does completing a task do to the collection case?',
        answer:
          'Marking a task as complete changes only the status of that task. The linked collection case remains open and will continue to generate new tasks according to its strategy. To close a collection case, go to the Collections Queue and close it from there.',
      },
      {
        question: 'How do I find all tasks for a specific customer?',
        answer:
          'Type the first few characters of the customer name in the Search field in the Advanced Filters panel. The list filters in real time to show only tasks linked to that customer.',
      },
      {
        question: 'How do I see all tasks for my team, not just mine?',
        answer:
          'Click the My Tasks badge to deactivate it. The list then shows tasks assigned to all users. You can type a colleague\'s name in the Search field to focus on their tasks specifically.',
      },
      {
        question: 'Why do some tasks show an amount and others do not?',
        answer:
          'The amount is populated only when the task is linked to a financial record -- such as the overdue balance on a collection case, the value of a promise to pay, or the amount of an unallocated receipt. Manual tasks and some informational tasks have no linked amount.',
      },
      {
        question: 'Can I delete a task I created by mistake?',
        answer:
          'Manual tasks can be deleted from the task detail drawer. System-generated tasks cannot be deleted directly -- they are closed automatically when the underlying record (promise, case, or dispute) is resolved or the task is marked complete.',
      },
      {
        question: 'How do I see tasks that were completed earlier today?',
        answer:
          'Expand the Completed Tasks group at the bottom of the task list by clicking the group header. Completed tasks are kept here throughout the day and are visible until the list is refreshed or the page is reloaded.',
      },
    ],
    relatedPages: [
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Promises to Pay', path: '/collections/promises' },
      { label: 'Dashboard', path: '/' },
      { label: 'Customer Detail', path: '/customers/:id' },
      { label: 'Disputes', path: '/disputes/all' },
    ],
  },

  'multi-company-dashboard': {
    pageTitle: 'Multi-Company Dashboard',
    description:
      'A consolidated performance view across all companies in your portfolio, showing total AR, overdue balances, DSO, and collection efficiency for each entity side by side.',
    functionality: {
      overview:
        'The Multi-Company Dashboard loads every company in your portfolio into a single comparison table so you can assess receivables health across entities without switching between individual company accounts. Metric summary cards at the top show portfolio-level totals when a single currency is selected. The Company Performance Comparison table lists every company with key AR metrics in the Latest view, or trend indicators for 7, 30, 60, and 90-day periods. Use the search bar, company multi-select, and currency filter to narrow the table. Click any company name to open its full Company Detail page. Export the filtered table to CSV for external reporting.',
      keyFeatures: [
        {
          label: 'Portfolio Metric Cards',
          detail: 'Four summary cards show Total Outstanding, Total Overdue, Most At-Risk Company, and Average DSO across the filtered portfolio -- visible only when a single currency is selected.',
        },
        {
          label: 'Multi-Currency Warning Banner',
          detail: 'When All Currencies is selected, a yellow banner replaces the metric cards to prevent misleading cross-currency aggregations and prompts you to select a specific currency.',
        },
        {
          label: 'Company Performance Comparison Table',
          detail: 'Lists every company with sortable columns for Total AR, Overdue, Overdue %, DSO, CEI, and Avg Days to Pay in Latest view, or AR Trend, Overdue Trend, DSO Trend, CEI Trend, and Payment Speed in trend views.',
        },
        {
          label: 'View Mode Selector',
          detail: 'Switch between Latest (point-in-time snapshot) and four trend periods -- 7 Days, 30 Days, 60 Days, 90 Days -- to see whether each company\'s metrics are improving or deteriorating.',
        },
        {
          label: 'Currency Filter',
          detail: 'Select a specific currency to aggregate metrics into the summary cards and filter the table to companies invoiced in that currency. Defaults to All Currencies.',
        },
        {
          label: 'Search Bar',
          detail: 'Type a company name to filter the table in real time. Useful when the portfolio contains many entities.',
        },
        {
          label: 'Company Multi-Select Filter',
          detail: 'Select one or more specific companies from the dropdown to compare a subset of entities side by side.',
        },
        {
          label: 'Sortable Columns',
          detail: 'Click any column header to sort the table ascending or descending. Click again to reverse. Useful for surfacing the highest-risk or highest-value entities instantly.',
        },
        {
          label: 'Company Detail Drill-Down',
          detail: 'Click any company name in the table to open its full Company Detail page, which includes AR trend charts, a customer list, open cases, disputes, and recent activities for that entity.',
        },
        {
          label: 'Export to CSV',
          detail: 'Download all filtered companies and their current view columns -- including both Latest and trend data -- as a CSV file named company-performance-YYYY-MM-DD.csv.',
        },
      ],
      sectionsAndFields: [
        { name: 'Page header', description: 'Shows the page title "Multi-Company Dashboard" and a subtitle. Contains the Help button and the Currency filter on the right side.' },
        { name: 'Currency filter (header)', description: 'A dropdown in the top-right of the header. Select a specific currency (e.g. GBP, USD, EUR) to enable the metric summary cards and filter the table. Defaults to All Currencies.' },
        { name: 'Multi-Currency Warning Banner', description: 'A yellow alert card that replaces the metric cards when All Currencies is selected. Explains that aggregated totals cannot be shown across different currencies and prompts you to pick one.' },
        { name: 'Total Outstanding card', description: 'The sum of Total AR across all companies in the filtered currency. Shown only when a single currency is selected.' },
        { name: 'Total Overdue card', description: 'The sum of overdue balances across all companies in the filtered currency. Shown only when a single currency is selected.' },
        { name: 'Most At-Risk Company card', description: 'The company with the highest Overdue % in the filtered set. Clicking the card navigates to that company\'s detail page. Shown only when a single currency is selected.' },
        { name: 'Avg DSO card', description: 'The mean Days Sales Outstanding across all companies in the filtered currency. Shown only when a single currency is selected.' },
        { name: 'Company Performance Comparison table', description: 'The main data table. Switches columns depending on whether Latest or a trend period is selected.' },
        { name: 'Search bar (table toolbar)', description: 'Filters the table rows by company name in real time. Clears automatically when you remove text.' },
        { name: 'Filter by companies dropdown', description: 'A multi-select dropdown to include only specific companies in the table. Shows a count badge when values are selected.' },
        { name: 'View Mode selector', description: 'A dropdown to switch the table between Latest (point-in-time) and 7 Days, 30 Days, 60 Days, 90 Days (trend) views.' },
        { name: 'Export button (spreadsheet icon)', description: 'Opens a small menu with "Export Current View". Downloads a CSV of all filtered rows and all column data (both Latest and trend fields). Disabled when the table is empty.' },
        { name: 'Company column (Latest & Trend)', description: 'The company name. Displayed as a clickable link that navigates to the Company Detail page for that entity.' },
        { name: 'Currency column (Latest & Trend)', description: 'The currency the company is invoiced in, shown as a monospace badge (e.g. GBP).' },
        { name: 'Total AR column (Latest view)', description: 'The company\'s total open accounts receivable balance in its own currency.' },
        { name: 'Overdue column (Latest view)', description: 'The portion of Total AR that is past due date, in the company\'s currency.' },
        { name: 'Overdue % column (Latest view)', description: 'Overdue as a percentage of Total AR. Colour-coded: green below 20%, orange 20-29%, red 30%+.' },
        { name: 'DSO column (Latest view)', description: 'Days Sales Outstanding -- how many days on average it takes customers to pay. Colour-coded: green below 45 days, orange 45-59 days, red 60+ days.' },
        { name: 'CEI column (Latest view)', description: 'Collections Effectiveness Index -- a percentage measuring how much of the collectable debt was collected in the period. Colour-coded: green 90%+, orange 75-89%, red below 75%.' },
        { name: 'Avg Days to Pay column (Latest view)', description: 'The average number of days between invoice date and payment date for the company\'s customers.' },
        { name: 'AR Trend column (Trend view)', description: 'The change in Total AR over the selected period, shown as a percentage with a direction indicator.' },
        { name: 'Overdue Trend column (Trend view)', description: 'The change in Overdue balance over the selected period.' },
        { name: 'DSO Trend column (Trend view)', description: 'The change in DSO over the selected period.' },
        { name: 'CEI Trend column (Trend view)', description: 'The change in CEI over the selected period.' },
        { name: 'Payment Speed column (Trend view)', description: 'A badge indicating whether customers are paying faster, at the same speed, or slower than the previous period. Values: Improving, Stable, Declining.' },
        { name: 'Pagination footer', description: 'Shows the current page, total pages, and total company count. Page size options are 10, 25, 50, and 100.' },
      ],
      useCases: [
        'Running a weekly portfolio health check to compare Total AR and Overdue % across all entities and identify which companies have deteriorated since the previous review.',
        'Selecting a single currency (e.g. USD) to aggregate the metric cards and find the total overdue exposure across all USD-invoiced subsidiaries before a treasury meeting.',
        'Switching to the 30-day trend view and sorting the Overdue Trend column descending to find which companies\' overdue balances have grown the most in the past month.',
        'Using the Most At-Risk Company card to navigate directly to the highest-risk entity and review its open cases and top overdue customers.',
        'Filtering to a subset of companies using the multi-select dropdown and exporting the result to CSV for inclusion in a board-level receivables report.',
        'Comparing CEI values across subsidiaries to identify entities where collection activity is underperforming relative to the rest of the group.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Analytics & Reporting > Multi-Company Dashboard. The page loads automatically with all companies. Access to this page requires the "View Multi-Company Dashboard" permission in your role -- contact your system administrator if you do not see it in the menu. If the table is empty, no company data has been synced yet.',
      steps: [
        {
          title: 'How do I view aggregated totals across the portfolio?',
          items: [
            { text: 'Click the Currency filter dropdown in the top-right of the page header.' },
            { text: 'Select a specific currency (e.g. GBP) from the list.' },
            { text: 'The yellow multi-currency banner disappears and four metric cards appear -- Total Outstanding, Total Overdue, Most At-Risk Company, and Avg DSO.', note: 'Metric cards are hidden when All Currencies is selected because adding amounts in different currencies would produce a meaningless total. You must select a single currency to see portfolio aggregates.' },
          ],
        },
        {
          title: 'How do I compare companies side by side in Latest view?',
          items: [
            { text: 'Ensure the View Mode dropdown shows Latest.' },
            { text: 'The table shows Total AR, Overdue, Overdue %, DSO, CEI, and Avg Days to Pay for each company.' },
            { text: 'Click any column header to sort the table by that metric.', tip: 'Sort by Overdue % descending to immediately see which company has the highest proportion of overdue receivables.' },
            { text: 'Click a company name to open its full Company Detail page.' },
          ],
        },
        {
          title: 'How do I see whether a company\'s metrics are improving or worsening?',
          items: [
            { text: 'Click the View Mode dropdown in the table toolbar.' },
            { text: 'Select a trend period -- 7 Days, 30 Days, 60 Days, or 90 Days.' },
            { text: 'The table switches to show AR Trend, Overdue Trend, DSO Trend, CEI Trend, and Payment Speed for the selected period.', note: 'Trend data is based on historical snapshots. If a company was added recently, trend columns may show a dash (--) until sufficient history has been captured.' },
            { text: 'Sort the Overdue Trend column descending to surface the companies with the fastest-growing overdue balances.' },
          ],
        },
        {
          title: 'How do I filter the table to specific companies?',
          items: [
            { text: 'Type a company name in the Search bar to filter by name in real time.' },
            { text: 'Use the Filter by companies dropdown to select one or more specific companies to compare.', tip: 'Combine the company multi-select with a currency filter to compare only the USD-invoiced subsidiaries, for example.' },
            { text: 'To clear company selection, open the dropdown and deselect all companies, or clear the search bar.' },
          ],
        },
        {
          title: 'How do I open a company\'s full detail view?',
          items: [
            { text: 'Click the company name (shown as a blue underlined link) in the Company column of the table.' },
            { text: 'The Company Detail page opens, showing AR trend charts, customer list, open collection cases, disputes, and recent activities for that entity.' },
            { text: 'Click the Back arrow at the top of the Company Detail page to return to the Multi-Company Dashboard.', note: 'The Company Detail page is a separate page -- your Multi-Company Dashboard filters are not preserved when you navigate back using the browser back button. Use the Back arrow on the Company Detail page to return.' },
          ],
        },
        {
          title: 'How do I navigate to the most at-risk company quickly?',
          items: [
            { text: 'Select a specific currency using the Currency filter to make the metric cards appear.' },
            { text: 'Locate the Most At-Risk Company card, which shows the company name and its Overdue %.' },
            { text: 'Click the View Details button on the card to navigate directly to that company\'s detail page.' },
          ],
        },
        {
          title: 'How do I export the company performance data?',
          items: [
            { text: 'Apply any search or company filters you want to be reflected in the export.' },
            { text: 'Click the spreadsheet icon (Export button) at the right end of the table toolbar.' },
            { text: 'Select Export Current View from the dropdown.' },
            { text: 'The file downloads as company-performance-YYYY-MM-DD.csv.', note: 'The export includes all filtered rows (not just the current page) and both Latest and trend data columns, regardless of which View Mode is currently displayed.' },
            { text: '', tip: 'Apply the company multi-select filter before exporting to produce a targeted report for a specific subset of entities.' },
          ],
        },
      ],
      proTips: [
        'Switch to 30-day trend view and sort Overdue Trend descending at the start of each month to identify which subsidiaries need a priority review before the month-end close.',
        'Use the CEI column in Latest view as your primary performance indicator -- a CEI below 75% for any company suggests collection activity is not keeping pace with new invoicing.',
        'Select a single currency before exporting so the metric card totals match the figures in your CSV -- this prevents reconciliation questions from finance.',
        'Pin your highest-volume currency as a bookmark habit: open the page, select your main currency, and bookmark the URL with the currency pre-selected if your browser preserves query state.',
      ],
    },
    faq: [
      {
        question: 'Why are the metric cards not showing?',
        answer: 'The metric cards (Total Outstanding, Total Overdue, Most At-Risk Company, Avg DSO) only appear when you select a specific currency using the Currency filter in the page header. When All Currencies is selected, the cards are replaced by a warning banner because summing amounts in different currencies would produce a meaningless total.',
      },
      {
        question: 'What does DSO mean?',
        answer: 'DSO stands for Days Sales Outstanding. It measures the average number of days it takes customers of a company to pay their invoices. A lower number means customers are paying faster. The colour coding is: green below 45 days, orange 45-59 days, red 60 days or more.',
      },
      {
        question: 'What does CEI mean?',
        answer: 'CEI stands for Collections Effectiveness Index. It is a percentage that measures how much of the collectable receivables were actually collected in the period. A CEI of 100% means all collectible debt was recovered. The colour coding is: green 90%+, orange 75-89%, red below 75%.',
      },
      {
        question: 'What does the Payment Speed badge in trend view mean?',
        answer: 'Payment Speed compares the average days to pay in the selected trend period against the previous equivalent period. Improving means customers are paying faster on average. Stable means no significant change. Declining means customers are taking longer to pay.',
      },
      {
        question: 'What does the Most At-Risk Company card show?',
        answer: 'It shows the company in the currently filtered currency set with the highest Overdue % -- that is, the largest proportion of its Total AR that is past due. Click the View Details button on the card to go directly to that company\'s detail page.',
      },
      {
        question: 'Why does a trend column show a dash (--) for some companies?',
        answer: 'A dash means there is insufficient historical data to calculate a trend for that company over the selected period. This typically happens for recently onboarded companies or entities added to the portfolio within the selected trend window.',
      },
      {
        question: 'How do I see the customers and open cases for a specific company?',
        answer: 'Click the company name in the Company column to open the Company Detail page. It contains a full customer list, open collection cases, open disputes, and a recent activities feed for that entity.',
      },
      {
        question: 'Can I filter by multiple currencies at once?',
        answer: 'No -- the Currency filter is a single-select control. You can select one specific currency or All Currencies. To compare companies across two currencies, use All Currencies and rely on the table rows to compare individual company figures.',
      },
      {
        question: 'How does the search bar interact with the company multi-select filter?',
        answer: 'Both filters apply simultaneously. The table shows only companies that match both the name search text and the selected company IDs. Clear either filter independently -- delete the search text or remove selections from the multi-select dropdown.',
      },
      {
        question: 'What data is included in the CSV export?',
        answer: 'The export includes all filtered rows (not just the current page) and all column data -- both Latest metrics (Total AR, Overdue, Overdue %, DSO, CEI, Avg Days to Pay) and trend metrics (AR Trend, Overdue Trend, DSO Trend, CEI Trend, Payment Speed) -- regardless of which view mode is currently selected in the table.',
      },
      {
        question: 'How do I get access to the Multi-Company Dashboard if I cannot see it in the menu?',
        answer: 'Access requires the "View Multi-Company Dashboard" permission in your user role. Contact your system administrator to have this permission added to your role.',
      },
    ],
    relatedPages: [
      { label: 'Dashboard', path: '/' },
      { label: 'Ageing Analysis', path: '/analytics-reporting/ageing-analysis' },
      { label: 'Collections Dashboard', path: '/collections/dashboard' },
      { label: 'Customer Accounts', path: '/customers' },
      { label: 'Collections Queue', path: '/collections/queue' },
    ],
  },

  emailing: {
    pageTitle: 'Email History',
    description:
      'A searchable, filterable log of every email sent by the system -- from collection chasers and statement runs to promise confirmations and dispute notifications.',
    functionality: {
      overview:
        'Email History gives you a complete audit trail of all outbound emails generated across the platform. You can find any email by subject, recipient, or customer; narrow the list by status, source, or date range; open an email to read its full body and metadata; and export the log for compliance or investigation purposes. Nothing is deleted from this log -- every sent, accepted, pending, and failed message is retained.',
      keyFeatures: [
        {
          label: 'Search Bar',
          detail:
            'Type any part of the subject line, recipient address, or customer name to instantly narrow the list to matching emails.',
        },
        {
          label: 'Status Filter',
          detail:
            'Restrict the list to emails in a specific delivery state: Pending (queued but not yet dispatched), Accepted (confirmed received by the provider), Delivered (confirmed accepted by the recipient\'s mail server), or Failed (delivery error).',
        },
        {
          label: 'Source Filter',
          detail:
            'Filter by the module that triggered the email: Customer (manual sends from a customer record), Collection (automated chasers from a collection case), Promise (promise-to-pay confirmations), or Dispute (dispute notification emails).',
        },
        {
          label: 'Template Type Filter',
          detail:
            'Filter by the type of template used to compose the email: Email (standard email templates), Statement (statement/PDF templates), or No Template (emails composed without a template).',
        },
        {
          label: 'Date Range Filter',
          detail:
            'Set a From and To date to restrict the list to emails created within that window. Useful for period-specific compliance reviews.',
        },
        {
          label: 'Column Configuration',
          detail:
            'Show or hide any of the 12 available columns. CC Recipients, Created At, and Error Message are hidden by default and can be enabled for detailed investigation.',
        },
        {
          label: 'Sortable Columns',
          detail:
            'Click any column header to sort ascending or descending. The default sort is Created At descending (most recent first).',
        },
        {
          label: 'Email Detail Dialog',
          detail:
            'Click the eye icon on any row to open a full-detail panel showing recipient addresses, template used, source reference, sent-by user, timestamps, error message (if any), and an inline preview of the email body HTML.',
        },
        {
          label: 'Export',
          detail:
            'Download the filtered list as a CSV -- either visible columns only or all available columns. The export includes every matching row, not just the current page.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Search bar',
          description:
            'Searches across email subject, recipient (To) address, and customer name. The search is debounced -- results update after you stop typing.',
        },
        {
          name: 'Status dropdown',
          description:
            'Filters the list to a single delivery status. Options: All Status, Pending, Accepted, Delivered, Failed.',
        },
        {
          name: 'Source dropdown',
          description:
            'Filters by the module that generated the email. Options: All Sources, Customer, Collection, Promise, Dispute.',
        },
        {
          name: 'Template Type dropdown',
          description:
            'Filters by the type of template used. Options: All Template Types, Email, Statement, No Template. Emails composed without selecting a template are shown under No Template.',
        },
        {
          name: 'From date input',
          description:
            'Restricts the list to emails whose Created At date is on or after this date (filters by Created At, not Sent At). Leave blank to include all dates from the beginning of history.',
        },
        {
          name: 'To date input',
          description:
            'Restricts the list to emails whose Created At date is before this date (filters by Created At, not Sent At). Leave blank to include all dates up to today.',
        },
        {
          name: 'Export button ()',
          description:
            'Opens a dropdown with two options: Export Current View (visible columns only) and Export All Columns (all 12 fields including hidden ones).',
        },
        {
          name: 'Column Settings button',
          description:
            'Opens the column configuration dialog to show, hide, or reorder columns in the table.',
        },
        {
          name: 'Subject column',
          description:
            'The email subject line. Displayed in bold. Truncated if long -- open the detail dialog to see the full subject.',
        },
        {
          name: 'Customer column',
          description: 'The customer account the email is associated with.',
        },
        {
          name: 'To column',
          description:
            'The primary recipient address(es). Truncated at 200px -- open the detail dialog to see all recipients.',
        },
        {
          name: 'CC column (hidden by default)',
          description:
            'Carbon-copy recipient addresses, if any were included. Enable via column settings.',
        },
        {
          name: 'Source column',
          description:
            'The module that triggered the email (Customer, Collection, Promise, Dispute), displayed as an outline badge.',
        },
        {
          name: 'Template column',
          description:
            'The name of the email template used to generate the message. Shows a dash if no template was applied. Use the Template Type filter to narrow by Email or Statement template type.',
        },
        {
          name: 'Status column',
          description:
            'Current delivery state shown as a colour-coded badge: Accepted/Delivered = solid (green); Pending = secondary (grey); Failed = destructive (red).',
        },
        {
          name: 'Sent By column',
          description:
            'The user or system process that triggered the email. Automated emails sent by strategies show the system account name.',
        },
        {
          name: 'Sent At column',
          description:
            'The date and time the email was dispatched. Displayed in dd MMM yyyy HH:mm format.',
        },
        {
          name: 'Created At column (hidden by default)',
          description:
            'The date and time the email record was created in the system. May differ from Sent At if the email was queued before dispatch. Enable via column settings.',
        },
        {
          name: 'Error column (hidden by default)',
          description:
            'The error message returned by the mail provider when delivery failed. Only populated for Failed status emails. Enable via column settings to investigate delivery failures.',
        },
        {
          name: 'Eye icon (actions column)',
          description:
            'Opens the Email Detail dialog for that row, showing all metadata and an inline HTML preview of the email body.',
        },
        {
          name: 'Email Detail dialog -- Status & Source',
          description:
            'Shows the delivery status badge and the source module badge, including the source record ID (e.g. Collection case #123).',
        },
        {
          name: 'Email Detail dialog -- To / CC',
          description: 'Full recipient and CC address lists without truncation.',
        },
        {
          name: 'Email Detail dialog -- Customer',
          description:
            'Customer name and account number for the associated account.',
        },
        {
          name: 'Email Detail dialog -- Sent By',
          description:
            'Full name and email address of the user or system account that triggered the send.',
        },
        {
          name: 'Email Detail dialog -- Error Message',
          description:
            'Shown only when Status is Failed. Displays the provider error in a red highlighted box to aid diagnosis.',
        },
        {
          name: 'Email Detail dialog -- Email Body',
          description:
            'An inline sandboxed iframe rendering the full HTML email body exactly as the recipient received it.',
        },
        {
          name: 'Pagination footer',
          description:
            'Shows the current page, total rows, and page-size selector (10 / 25 / 50 / 100). Navigate between pages using the Previous and Next buttons or page number links.',
        },
      ],
      useCases: [
        'Checking whether a specific customer received their overdue reminder email -- search by customer name and filter to Collection source.',
        'Investigating why an email was not delivered -- filter to Failed status, then enable the Error column to read the provider error message.',
        'Producing a compliance export of all system emails sent during a specific month -- set From and To dates and export all columns.',
        'Confirming that a promise-to-pay confirmation email was sent after a call -- search by customer name and filter to Promise source.',
        'Checking which template was used for a chaser email before editing it in Settings -- open the email detail and read the Template field.',
        'Auditing all emails sent by a specific credit controller -- search by their name in the Sent By column and apply a date range.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Email History is available to all users who have access to the Analytics & Reporting section. No setup is required -- emails are logged automatically whenever the system or a user sends a message. If the list appears empty, check that no filters are active and that your account has the correct permissions to view email history.',
      steps: [
        {
          title: 'How do I find an email sent to a specific customer?',
          items: [
            {
              text: 'Type the customer name into the Search bar at the top left of the toolbar.',
              tip: 'You can also search by the recipient email address or part of the subject line if you do not know the exact customer name.',
            },
            {
              text: 'The list filters as you type. Wait a moment after finishing -- the search is debounced to avoid excessive requests while you are still typing.',
            },
            {
              text: 'If you want to narrow further, apply the Source filter to the module that generated the email (e.g. Collection for chaser emails).',
            },
          ],
        },
        {
          title: 'How do I investigate a failed email?',
          items: [
            {
              text: 'Open the Status dropdown and select Failed.',
            },
            {
              text: 'Click the Column Settings button (grid icon, top right of toolbar) and enable the Error column.',
              note: 'The Error column is hidden by default. Enable it via Column Settings and click Apply -- your column preferences are saved persistently, so the column will remain visible on your next visit.',
            },
            {
              text: 'Read the Error column in the table for a summary, or click the eye icon on the row to open the Email Detail dialog and see the full error message in a highlighted red box.',
            },
            {
              text: 'Note the Source field in the detail dialog -- this tells you which module sent the email and the source record ID, so you can navigate to the originating case, promise, or dispute to retry.',
              tip: 'Common failure reasons include invalid recipient address, mailbox full, or mail server rejection. Share the error message with your system administrator if the cause is not clear.',
            },
          ],
        },
        {
          title: 'How do I view the full email body?',
          items: [
            {
              text: 'Click the eye icon on any row in the table to open the Email Detail dialog.',
            },
            {
              text: 'Scroll down to the Email Body section at the bottom of the dialog.',
            },
            {
              text: 'The email renders in a sandboxed inline preview exactly as the recipient would have seen it.',
              note: 'The preview is sandboxed -- links inside the email body are disabled for security. To follow a link, copy and paste it manually.',
            },
          ],
        },
        {
          title: 'How do I filter emails by date range?',
          items: [
            {
              text: 'Click inside the From date field in the toolbar and select the start date.',
            },
            {
              text: 'Click inside the To date field and select the end date.',
              note: 'The date filter applies to the Created At timestamp (when the email record was created), not the Sent At timestamp. For emails that were queued and sent later, these dates may differ slightly.',
            },
            {
              text: 'The list updates automatically when both or either date is set. You can set only From, only To, or both.',
              tip: 'For a monthly compliance export, set From to the 1st and To to the last day of the month, then click Export All Columns.',
            },
          ],
        },
        {
          title: 'How do I filter by email source?',
          items: [
            {
              text: 'Open the Source dropdown in the toolbar and select one of: Customer, Collection, Promise, or Dispute.',
              subSteps: [
                'Customer -- emails sent manually from a customer record',
                'Collection -- automated chaser emails triggered by a collection strategy',
                'Promise -- confirmation emails sent when a promise to pay is recorded',
                'Dispute -- notification emails sent when a dispute is created or updated',
              ],
            },
            {
              text: 'You can combine the Source filter with the Status filter and a date range for precise results.',
            },
          ],
        },
        {
          title: 'How do I configure which columns are visible?',
          items: [
            {
              text: 'Click the Column Settings button (grid icon) at the right end of the toolbar.',
            },
            {
              text: 'In the dialog, toggle columns on or off and drag to reorder them.',
              note: 'Columns hidden by default include CC Recipients, Created At, and Error Message. Enable Error Message when investigating Failed emails.',
            },
            {
              text: 'Click Apply to save your preferences. Column settings are persisted and will be remembered on your next visit.',
            },
          ],
        },
        {
          title: 'How do I export the email log?',
          items: [
            {
              text: 'Apply any filters and search terms you need to scope the export.',
            },
            {
              text: 'Click the Export button ( arrow icon) at the right of the toolbar.',
            },
            {
              text: 'Select Export Current View to include only the currently visible columns, or Export All Columns to include all 12 fields.',
              note: 'The export is server-side -- it includes every row matching your current filters, not just the rows visible on the current page.',
              tip: 'For a full audit trail, select Export All Columns and apply a date range rather than leaving it open-ended.',
            },
            {
              text: 'A toast notification confirms the export has started. The file downloads automatically when ready.',
            },
          ],
        },
      ],
      proTips: [
        'Combine the Source filter (Collection) with the Status filter (Failed) and a date range to quickly identify all failed chaser emails from a strategy run -- then share the Error column export with your IT team.',
        'Use the Created At column (enable via Column Settings) alongside Sent At to identify emails that were queued and delayed. A large gap between the two timestamps may indicate a mail server backlog.',
        'Before editing an email template in Settings, use Email History to find a recent real example of that template (filter by Source and search the template name in Subject) -- open the detail dialog to verify the current output before making changes.',
        'For customer disputes over whether they received a chaser, open the Email Detail dialog and check Status: Delivered means the mail server confirmed receipt; Accepted means the provider acknowledged the send request. Screenshot the detail dialog as an audit record.',
      ],
    },
    faq: [
      {
        question: 'What does the Accepted status mean?',
        answer:
          'Accepted means the email was successfully handed to the outbound mail provider (e.g. SendGrid). The provider has acknowledged receipt and queued it for delivery. This is the initial success state after sending.',
      },
      {
        question: 'What does the Delivered status mean?',
        answer:
          'Delivered means the mail provider has confirmed that the recipient\'s mail server accepted the message. This is the strongest confirmation available in the system. Not all providers support delivery confirmation -- if yours does not, emails will remain in Accepted status.',
      },
      {
        question: 'What does the Pending status mean?',
        answer:
          'Pending means the email has been created in the system but has not yet been handed to the mail provider. This is normal for emails queued by an automated strategy run that has not yet been processed. If an email stays Pending for more than a few minutes, contact your system administrator.',
      },
      {
        question: 'What does the Failed status mean?',
        answer:
          'Failed means the mail provider rejected or could not deliver the email. Enable the Error column via Column Settings, or open the Email Detail dialog, to read the specific error message returned by the provider. Common causes include an invalid email address, a full mailbox, or a mail server rejection.',
      },
      {
        question: 'What does the Source badge on each row mean?',
        answer:
          'Source identifies which part of the system generated the email. Customer = sent manually from a customer record. Collection = sent automatically by a collection strategy chaser. Promise = sent as a promise-to-pay confirmation. Dispute = sent as a dispute notification.',
      },
      {
        question: 'How do I find all emails sent to a specific email address?',
        answer:
          'Type the email address into the Search bar. The search matches against the To recipient field as well as subject and customer name.',
      },
      {
        question: 'Can I resend a failed email from this page?',
        answer:
          'No. Email History is a read-only audit log. To resend a failed email, navigate to the originating record -- open the Email Detail dialog, note the Source and source record ID, then go to that case, promise, or customer record and trigger a new send from there.',
      },
      {
        question: 'Why does the Email Body section show a blank preview?',
        answer:
          'The email body preview will be blank if the email was sent as plain text only (no HTML), or if the HTML content was not stored (this can happen with very old records). The subject, recipients, and metadata will still be available.',
      },
      {
        question: 'How do I find all emails sent by automated collection strategies?',
        answer: 'Filter the Source dropdown to Collection. All emails generated by strategy chaser steps will appear. Combine with a date range to scope to a specific period or strategy run.',
      },
      {
        question: 'Why is an email I sent not appearing in the list?',
        answer: 'Check the following:',
        steps: [
          'Clear all filters and search terms to confirm the email is not being filtered out.',
          'Check the date range - if a From date is set, emails before that date will not appear.',
          'Confirm the email was sent from within this application -- emails sent from an external mail client will not be logged here.',
          'If you believe the email was sent via the system and it is still missing, contact your system administrator -- it may have failed silently before a log record was created.',
        ],
      },
      {
        question: 'Can I see which collection case or promise an email was sent from?',
        answer:
          'Yes. Click the eye icon on the email row to open the Email Detail dialog. The Source field shows both the module (e.g. Collection) and the source record ID (e.g. #142). Use that ID to navigate directly to the case or promise.',
      },
      {
        question: 'What is the difference between the Template column and the Template Type filter?',
        answer:
          'The Template column shows the specific template name (e.g. "Payment Reminder"). The Template Type filter groups templates by their type: Email templates are used for standard email communications, Statement templates are used for account statement PDFs. Select "No Template" to see emails that were composed without using any template.',
      },
    ],
    relatedPages: [
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Promise to Pay', path: '/collections/promises' },
      { label: 'Dispute Management', path: '/disputes/all' },
      { label: 'Customer Accounts', path: '/customers' },
      { label: 'Settings -- Templates', path: '/settings/templates' },
    ],
  },

  'settings-general': {
    pageTitle: 'General Settings',
    description:
      'The General Settings page is where administrators configure system-wide defaults that control how ageing analysis bands are displayed, how DSO is measured, how credit limit warnings behave, how customer category keys are labelled, and what display name appears on outgoing emails.',
    functionality: {
      overview:
        'General Settings contains four tabs -- Metrics, Customers, Risk & Credit, and Email -- each governing a different area of system behaviour. Changes saved here apply globally to all users and all company data. Only users with the Administrator role can access this page.',
      keyFeatures: [
        {
          label: 'Metrics tab -- Ageing Bands Configuration',
          detail:
            'Define between 1 and 10 custom ageing bands. Each band has a name, a start and end day range, a colour, and an optional feed notification alert. Bands are drag-reorderable and are validated in real time to prevent gaps between consecutive ranges.',
        },
        {
          label: 'Metrics tab -- DSO Target',
          detail:
            'Set the global Days Sales Outstanding target (in days) that is displayed in the Performance Snapshot section on the main Dashboard. The default is 40 days.',
        },
        {
          label: 'Metrics tab -- Ageing By toggle',
          detail:
            'Switch the basis for ageing calculations between Due Date (the invoice payment due date) and Transaction Date (the date the invoice was raised). This affects the Ageing Analysis page and all ageing band columns across the application.',
        },
        {
          label: 'Metrics tab -- Band Alerts',
          detail:
            'Enable a feed notification alert on any ageing band to automatically notify a user or the assigned credit controller when an invoice moves into that band.',
        },
        {
          label: 'Customers tab -- On Stop Notes',
          detail:
            'When enabled, users are prompted to enter a note when placing a customer on stop. The note is automatically saved to the customer\'s Notes tab, creating an audit trail.',
        },
        {
          label: 'Customers tab -- Category Keys',
          detail:
            'Rename the four User Sort Key fields (User Sort Key 1-4) to organisation-specific labels. These custom labels appear on the Customer Accounts page, customer detail pages, and in the More Filters panel.',
        },
        {
          label: 'Risk & Credit tab -- Credit Limit Warnings',
          detail:
            'Enable notifications when a customer exceeds their credit limit. Choose whether to notify the assigned credit controller, specific named users, or both. Also set a warning threshold percentage at which an early-warning notification is sent.',
        },
        {
          label: 'Email tab -- Sender Display Name',
          detail:
            'Set the display name that appears in the "From" field of all outgoing system emails (collection chasers, statements, promise confirmations). The sender email address is fixed and cannot be changed here.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Metrics tab',
          description: 'Contains DSO target configuration, the Ageing By toggle, ageing band configuration, and band-level alert settings.',
        },
        {
          name: 'Configured Bands card',
          description: 'Shows how many ageing bands are currently configured (maximum 10).',
        },
        {
          name: 'Active Alerts card',
          description: 'Shows how many of the configured bands have feed notification alerts enabled.',
        },
        {
          name: 'Target DSO card',
          description: 'Shows the current DSO target value in days as set in the DSO Target Configuration section.',
        },
        {
          name: 'Target DSO (Days) field',
          description: 'A number input where administrators enter the organisation\'s DSO target. Must be a positive integer. Default is 40.',
        },
        {
          name: 'Ageing By toggle',
          description: 'Switches ageing calculations between Due Date and Transaction Date. Due Date ages invoices from their payment due date; Transaction Date ages invoices from the date they were raised.',
        },
        {
          name: 'Visual Preview bar',
          description: 'A colour-coded bar showing all configured bands proportionally. Hover over a segment to see the band name and day range.',
        },
        {
          name: 'Add Band button',
          description: 'Adds a new band after the last existing band. Disabled when 10 bands are already configured.',
        },
        {
          name: 'Band Name field',
          description: 'The label for this ageing band as it will appear in charts, tables, and column headers across the application.',
        },
        {
          name: 'Start Day field',
          description: 'The first day number in this band\'s range. Must be exactly one greater than the End Day of the previous band to avoid gaps.',
        },
        {
          name: 'End Day field',
          description: 'The last day number in this band\'s range. Must be greater than Start Day.',
        },
        {
          name: 'Color picker',
          description: 'A native colour input for selecting the band\'s display colour used in charts and the visual preview bar.',
        },
        {
          name: 'Enable Feed Notification Alert toggle',
          description: 'When switched on, the system sends a feed notification when any invoice moves into this ageing band.',
        },
        {
          name: 'Assigned Credit Controller checkbox',
          description: 'When alert is enabled, notifies the credit controller assigned to the customer whose invoice has entered this band.',
        },
        {
          name: 'Alert Recipient field',
          description: 'When alert is enabled and Assigned Credit Controller is not ticked, a specific user must be selected to receive the alert. Required when alerts are on and the checkbox is off.',
        },
        {
          name: 'Drag handle (grip icon)',
          description: 'Drag a band row up or down to reorder bands. Also supports keyboard reordering with the Arrow Up and Arrow Down keys.',
        },
        {
          name: 'Remove band button (trash icon)',
          description: 'Deletes this band. Disabled when only one band remains -- at least one band must always exist.',
        },
        {
          name: 'Customers tab',
          description: 'Contains On Stop Notes configuration and Category Keys (User Sort Key label customisation).',
        },
        {
          name: 'Require Note When Placing Customer On Stop toggle',
          description: 'When enabled, a note dialog appears each time a user places a customer on stop. The note is saved to the customer\'s Notes tab.',
        },
        {
          name: 'User Sort Key 1-4 Label fields',
          description: 'Four text inputs to rename the User Sort Key columns to organisation-specific labels (e.g., "Region", "Territory", "Segment"). Labels appear across the application wherever User Sort Key fields are shown.',
        },
        {
          name: 'Risk & Credit tab',
          description: 'Contains credit limit exceeded notification settings and the credit warning threshold percentage.',
        },
        {
          name: 'Credit Limit Exceeded Notification toggle',
          description: 'When enabled, the system sends a feed notification whenever a customer\'s outstanding balance exceeds their credit limit.',
        },
        {
          name: 'Notification Recipients -- Assigned Credit Controller checkbox',
          description: 'Sends the credit limit exceeded notification to the credit controller assigned to that specific customer.',
        },
        {
          name: 'Notification Recipients -- Specific Recipients field',
          description: 'A user search combobox for adding named users who will receive all credit limit exceeded notifications, regardless of which customer triggered the alert.',
        },
        {
          name: 'Credit Warning Threshold field',
          description: 'A percentage value (1-100). When a customer\'s credit utilisation reaches this threshold, an early-warning notification is sent to the same recipients. Default is 80%.',
        },
        {
          name: 'Email tab',
          description: 'Contains the sender display name setting for outgoing emails.',
        },
        {
          name: 'Sender Email Address field (read-only)',
          description: 'The fixed email address used as the system sender. Cannot be changed from this page -- contact your system administrator to change the sender address.',
        },
        {
          name: 'Sender Display Name field',
          description: 'The name that appears in the "From" field of all outgoing system emails. For example, "Accounts Receivable" or your company name.',
        },
        {
          name: 'Save Changes button',
          description: 'Saves all changes made on the currently active tab. Each tab saves independently -- switching tabs without saving will discard unsaved changes on the previous tab.',
        },
        {
          name: 'Cancel button',
          description: 'Discards all unsaved changes on the current tab and resets fields to the last saved values.',
        },
      ],
      useCases: [
        'Restructuring the ageing bands to match your organisation\'s overdue categorisation -- for example, adding a 121-150 days band and a 151+ days band instead of a single Over 120 Days band.',
        'Enabling a feed notification alert on the 91-120 days band so the assigned credit controller is automatically notified when an invoice becomes seriously overdue.',
        'Renaming User Sort Key 1 to "Region" and User Sort Key 2 to "Territory" so that field names in the customer list match your organisation\'s reporting structure.',
        'Enabling On Stop Notes to ensure every credit hold decision has a recorded reason, providing an audit trail for compliance or dispute purposes.',
        'Setting the credit warning threshold to 90% so the credit team receives an early warning before a customer formally exceeds their limit, allowing proactive contact.',
        'Updating the sender display name from the default to your company\'s trading name so collection emails appear professional and are less likely to be marked as spam.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings > General Settings. You must have the Administrator role to access this page -- standard Users will see an access restriction message. Each tab saves independently: make your changes on one tab, click Save Changes, then move to the next tab.',
      steps: [
        {
          title: 'How do I add or edit an ageing band?',
          items: [
            { text: 'Click the Metrics tab.' },
            { text: 'To add a new band, click Add Band. A new band is appended after the last existing band with a default day range continuing from the previous band\'s end day.' },
            { text: 'To edit an existing band, update the Band Name, Start Day, End Day, and Color fields directly in the band row.' },
            {
              text: 'Ensure there are no gaps between bands -- Start Day of each band must equal End Day of the previous band plus 1.',
              note: 'The system validates bands in real time and shows an error message below the page title if any gaps, overlaps, or missing names are detected. The Save Changes button will not save until all errors are resolved.',
            },
            { text: 'Click Save Changes.' },
          ],
        },
        {
          title: 'How do I reorder ageing bands?',
          items: [
            { text: 'Click the Metrics tab.' },
            { text: 'Drag a band row by its grip handle (the vertical dots icon on the left) and drop it in the desired position.' },
            {
              text: 'Alternatively, focus the grip handle using the keyboard and press Arrow Up or Arrow Down to move the band one position.',
              tip: 'After reordering, check that Start Day and End Day values still connect consecutively -- reordering does not automatically renumber the day ranges.',
            },
            { text: 'Click Save Changes.' },
          ],
        },
        {
          title: 'How do I enable an alert on an ageing band?',
          items: [
            { text: 'Click the Metrics tab.' },
            { text: 'Find the band you want to alert on and switch on the Enable Feed Notification Alert toggle.' },
            { text: 'Choose a recipient: tick Assigned Credit Controller to notify the customer\'s own controller, or leave it unticked and select a specific user from the Alert Recipient dropdown.' },
            {
              text: 'Both Assigned Credit Controller and a specific recipient can be selected simultaneously.',
              note: 'If Assigned Credit Controller is unticked and no specific recipient is selected, the Save Changes button will not save and an error message is shown on the band.',
            },
            { text: 'Click Save Changes.' },
          ],
        },
        {
          title: 'How do I change the DSO target?',
          items: [
            { text: 'Click the Metrics tab.' },
            { text: 'Find the DSO Target Configuration card.' },
            { text: 'Enter the new target value in the Target DSO (Days) field. Must be a positive integer.' },
            {
              text: 'Click Save Changes.',
              tip: 'The new target is reflected immediately on the main Dashboard Performance Snapshot for all users.',
            },
          ],
        },
        {
          title: 'How do I switch ageing calculations from Due Date to Transaction Date?',
          items: [
            { text: 'Click the Metrics tab.' },
            { text: 'Find the Ageing By toggle inside the Ageing Analysis Bands Configuration card.' },
            { text: 'Switch the toggle from Due Date to Transaction Date.' },
            {
              text: 'Click Save Changes.',
              note: 'This change affects the Ageing Analysis page and all ageing band columns throughout the application. All users will see recalculated band values based on transaction dates after the next data refresh.',
            },
          ],
        },
        {
          title: 'How do I rename the User Sort Key fields?',
          items: [
            { text: 'Click the Customers tab.' },
            { text: 'Find the Category Keys card.' },
            { text: 'Update the labels in the User Sort Key 1-4 Label fields. Each field accepts any text up to the field\'s character limit.' },
            {
              text: 'Click Save Changes.',
              tip: 'Use short, clear labels (e.g., "Region", "Territory") that will display clearly in table column headers and filter dropdowns.',
            },
          ],
        },
        {
          title: 'How do I enable On Stop Notes?',
          items: [
            { text: 'Click the Customers tab.' },
            { text: 'Find the On Stop Notes card.' },
            { text: 'Switch on the Require Note When Placing Customer On Stop toggle.' },
            {
              text: 'Click Save Changes.',
              note: 'Once enabled, any user who places a customer on stop from the Customer Accounts or Customer Detail page will see a note prompt. The note is mandatory -- users cannot complete the on-stop action without entering text.',
            },
          ],
        },
        {
          title: 'How do I set up credit limit exceeded notifications?',
          items: [
            { text: 'Click the Risk & Credit tab.' },
            { text: 'Switch on the Credit Limit Exceeded Notification toggle.' },
            { text: 'Tick Assigned Credit Controller if you want the customer\'s own controller notified.' },
            { text: 'Use the Specific Recipients field to search for and add any additional named users who should always receive the alert.' },
            {
              text: 'Set the Credit Warning Threshold percentage -- for example, 80 means an early warning is sent when a customer reaches 80% of their credit limit.',
              note: 'At least one of Assigned Credit Controller or a Specific Recipient must be selected when the notification is enabled. Saving with neither selected will show an error.',
            },
            { text: 'Click Save Changes.' },
          ],
        },
        {
          title: 'How do I change the email sender display name?',
          items: [
            { text: 'Click the Email tab.' },
            { text: 'Update the Sender Display Name field with the name you want recipients to see in the From field of emails.' },
            {
              text: 'Click Save Changes.',
              note: 'The Sender Email Address field is read-only. To change the actual sender email address, contact your system administrator.',
            },
          ],
        },
      ],
      proTips: [
        'When configuring ageing bands, keep band names short -- they appear as column headers in the Ageing Analysis table and in chart legends. Names longer than 12 characters may be truncated.',
        'Use the 80% credit warning threshold to give the credit team a window to contact a customer before they formally breach their limit, rather than only finding out after the fact.',
        'After renaming User Sort Key labels, communicate the new names to your team -- existing data in those fields is not changed, only the display label, so users need to know what values to expect.',
        'Each tab saves independently. If you need to change both the DSO target and the sender display name, save the Metrics tab first, then navigate to the Email tab and save again.',
      ],
    },
    faq: [
      {
        question: 'What happens to existing ageing analysis data if I change the band ranges?',
        answer:
          'The data itself is not changed -- invoices and their ages are recalculated against the new band definitions on the next page load. Historical reports may look different if band ranges have changed, but no underlying transaction data is altered.',
      },
      {
        question: 'What does the Ageing By toggle actually change?',
        answer:
          'It changes the date used to calculate how many days overdue an invoice is. Due Date ages from the payment due date on the invoice. Transaction Date ages from the date the invoice was raised. Most organisations use Due Date, but some prefer Transaction Date for internal reporting consistency.',
      },
      {
        question: 'Can I have more than 10 ageing bands?',
        answer:
          'No. The system supports a maximum of 10 ageing bands. The Add Band button is disabled once 10 bands are configured.',
      },
      {
        question: 'Why is the Save Changes button not working?',
        answer:
          'The Save Changes button is disabled if there are no unsaved changes, or if the Metrics tab has validation errors. Check the validation error list below the page title for any band configuration issues such as gaps between band ranges, missing names, or alert recipients not selected.',
      },
      {
        question: 'What does the Assigned Credit Controller checkbox on an alert band do?',
        answer:
          'When ticked, the system looks up the credit controller assigned to the customer whose invoice has moved into that band, and sends the feed alert to that specific user. This means alerts are automatically routed to the right person without having to select a fixed recipient.',
      },
      {
        question: 'Can I set a different alert recipient for each ageing band?',
        answer:
          'Yes. Each band has its own independent alert configuration. You can notify different users for different bands -- for example, a junior collector for the 31-60 days band and a senior controller for the 91+ days band.',
      },
      {
        question: 'What does Credit Warning Threshold mean?',
        answer:
          'It is the percentage of a customer\'s credit limit at which an early-warning notification is triggered. For example, with a threshold of 80%, a customer with a GBP10,000 credit limit will trigger a warning when their outstanding balance reaches GBP8,000.',
      },
      {
        question: 'How do I remove a specific user from the credit limit notification recipients?',
        answer: 'Click the trash icon on the right side of the user row in the Specific Recipients list on the Risk & Credit tab, then click Save Changes.',
      },
      {
        question: 'Why can I not see the General Settings page?',
        answer:
          'Only users with the Administrator role can access General Settings. Standard User accounts see an access restriction message. Contact your system administrator to request Administrator access.',
      },
      {
        question: 'What are User Sort Keys used for?',
        answer:
          'User Sort Keys (1-4) are organisation-defined classification fields imported from your accounting system or set manually on customer records. They can be used to filter and sort customers by segment, region, territory, or any other category your organisation uses. The General Settings page lets you rename these fields so the labels are meaningful to your team.',
      },
      {
        question: 'Does changing the sender display name affect emails already sent?',
        answer:
          'No. The display name change only applies to emails sent after the change is saved. Previously sent emails are not affected.',
      },
    ],
    relatedPages: [
      { label: 'Ageing Analysis', path: '/ageing-analysis' },
      { label: 'Credit Control Dashboard', path: '/dashboard' },
      { label: 'Customer Accounts', path: '/customers' },
      { label: 'User Management', path: '/settings/users' },
      { label: 'Company Settings', path: '/settings/company' },
    ],
  },

  'promise-details': {
    pageTitle: 'Promise Details',
    description:
      'The full record for a single payment commitment — view and edit the promise amount, date, status, and confirmation method, manage the linked transactions, review the activity timeline, and inspect the complete audit trail.',
    functionality: {
      overview:
        'The Promise Details page opens when you click a Promise ID from the Promises to Pay list. It shows everything about one customer payment commitment in a three-tab layout: Overview (customer information, promise summary, notes, and linked transactions), Activity (a chronological timeline of calls, emails, and notes with the ability to add new notes), and Audit Log (a tamper-proof history of every change ever made to this promise record). A collapsible left sidebar lists all promises so you can jump between records without returning to the list. In view mode the header shows an Edit button; clicking it unlocks all editable fields and exposes an Add Transaction button in the Transactions card. Click Save Changes to commit edits or Cancel to discard them. From the header you can also Log a Call or send an Email directly without entering edit mode.',
      keyFeatures: [
        {
          label: 'Collapsible Promises Sidebar',
          detail:
            'A panel on the left edge lists all promises. Click any promise to navigate directly to its detail record. Collapse the sidebar using the arrow button to give the main content more space; a teal PROMISES handle on the left edge lets you reopen it.',
        },
        {
          label: 'Overview Tab — Customer Information Card',
          detail:
            'Displays the customer account number and name, the contact name, email address (with a teal Send Email action button), and phone number (with a teal Log Call action button) for one-click communication directly from the page.',
        },
        {
          label: 'Overview Tab — Promise Summary Card',
          detail:
            'Shows Promise Date (editable in edit mode), Promised Amount (calculated from the linked transactions), Assigned User (reassignable in edit mode), Status badge (editable in edit mode), Confirmation Method badge (editable in edit mode), Case Number (clickable link to the linked case), Created By, Created At, and Days Overdue badge (shown only when the promise is past its due date).',
        },
        {
          label: 'Overview Tab — Promise Notes Card',
          detail:
            'Displays the current promise notes in view mode with a "View Full Activity History" button. In edit mode the notes field becomes an editable text area. Full note history, including notes added by other users, is available on the Activity tab.',
        },
        {
          label: 'Overview Tab — Transactions Card',
          detail:
            'Lists every invoice or transaction linked to this promise in a searchable, sortable, paginated table. Each row shows reference number, transaction date, due date, type, gross amount, currency, outstanding amount, promised amount (editable in edit mode), status, and a flags indicator. Export to Excel (current view or all columns) and configure visible columns using the column settings button. In edit mode, select or deselect transactions and edit each row\'s promised amount individually. Click Add Transaction to link additional transactions. An inline validation error appears if a promised amount exceeds the transaction\'s outstanding balance.',
        },
        {
          label: 'Promised Amount Validation',
          detail:
            'When editing, the system validates that each individual promised amount does not exceed the transaction\'s outstanding balance, that the sign of the allocation matches the sign of the outstanding amount, and that the combined total promised amount does not exceed the combined outstanding across all selected transactions. Validation errors are shown inline in the Transactions card and as a blocking alert before saving.',
        },
        {
          label: 'Activity Tab — Timeline',
          detail:
            'A chronological feed showing every call, email, note, and system event associated with this promise. Filter the timeline by Activity, Notes, or Communications using the toggle buttons. Items are colour-coded: blue for Activity, purple for Notes, and green for Communications. Each item shows the description, outcome (for calls), the creating user, and a timestamp.',
        },
        {
          label: 'Activity Tab — Add New Note',
          detail:
            'A dedicated panel on the Activity tab lets you type and post an internal note directly against this promise without entering edit mode. The note appears immediately in the timeline and is also visible in the Promise Notes section on the Overview tab.',
        },
        {
          label: 'Audit Log Tab',
          detail:
            'A complete, ordered history of every modification to this promise record: status changes, amount changes, date changes, field updates, transaction links and unlinks, assigned user updates, and phone calls logged. Each entry shows the action type (colour-coded icon), a description, the old and new values (shown as before → after badges where applicable), the user who made the change, and the date and time.',
        },
        {
          label: 'Edit Mode',
          detail:
            'Click the Edit button in the top-right header to enter edit mode. In this mode: Promise Date, Assigned User, Status, Confirmation Method, and Notes become editable. Promised amounts on each transaction row are also editable. The Add Transaction button appears. Click Save Changes to commit all edits in one request, or Cancel to discard all changes and return to view mode.',
        },
        {
          label: 'Send Email and Log Call',
          detail:
            'Available in view mode from the teal action buttons next to the customer email and phone in the Customer Information card. Send Email opens an email composition modal pre-addressed to the customer. Log Call opens a call logging modal to record the call outcome and any notes.',
        },
        {
          label: 'Export Transactions',
          detail:
            'The download icon button in the Transactions card header offers two export options: Export Current View (visible columns only) or Export All Columns. Both export to Excel and apply the current transaction search and sort state.',
        },
      ],
      sectionsAndFields: [
        {
          name: 'Page header — Promise Details title',
          description:
            'Shows the HandCoins icon, the "Promise Details" heading, and a sub-line with the customer name and a Promise ID badge. The Back button on the left returns to the Promises to Pay list.',
        },
        {
          name: 'Page header — Edit / Save Changes / Cancel buttons',
          description:
            'In view mode a single Edit button is shown. Clicking it enters edit mode, which replaces the Edit button with a Cancel button and a Save Changes button. Save commits all pending edits; Cancel discards them.',
        },
        {
          name: 'Tabs — Overview / Activity / Audit Log',
          description:
            'The three main tabs. Overview is the default view on page load. Activity shows the communication and note timeline. Audit Log shows the change history.',
        },
        {
          name: 'Customer Information — Customer',
          description:
            'The customer account number (in square brackets) followed by the customer name. Read-only on this page.',
        },
        {
          name: 'Customer Information — Contact Name',
          description: 'The primary contact name for this customer. Read-only.',
        },
        {
          name: 'Customer Information — Email Address',
          description:
            'The customer\'s email address with a teal Send Email button. Clicking the button opens the Send Email modal. Clicking the address opens the default email client.',
        },
        {
          name: 'Customer Information — Phone Number',
          description:
            'The customer\'s phone number with a teal Log Call button. Clicking the button opens the Log Call modal. Clicking the number opens the default phone dialler.',
        },
        {
          name: 'Promise Summary — Promise Date',
          description:
            'The date the customer committed to paying by. Shown as a formatted date in view mode. In edit mode, a date picker input replaces the label.',
        },
        {
          name: 'Promise Summary — Promised Amount',
          description:
            'The total value of the promise, calculated as the sum of all promised amounts across the linked transactions. Always read-only in this field — edit the individual transaction promised amounts in the Transactions card to change this value.',
        },
        {
          name: 'Promise Summary — Assigned User',
          description:
            'The credit controller responsible for monitoring this promise. Read-only in view mode. In edit mode, a dropdown with avatar lets you reassign to any active user.',
        },
        {
          name: 'Promise Summary — Status',
          description:
            'Current status badge: Active (green), Broken (red), Paid (blue), Partially Paid (amber), or Closed (grey). Read-only in view mode. In edit mode a dropdown lets you change the status.',
        },
        {
          name: 'Promise Summary — Confirmation Method',
          description:
            'How the customer confirmed the payment commitment: Phone (phone icon), Email (mail icon), or Portal (trending icon). Read-only in view mode. In edit mode a dropdown lets you change the method.',
        },
        {
          name: 'Promise Summary — Case Number',
          description:
            'The collection case this promise is linked to. Shown as a clickable link that navigates to the Case Details page for that case. Shown as a dash if no case is linked.',
        },
        {
          name: 'Promise Summary — Created By',
          description: 'The name of the user who originally recorded this promise. Read-only.',
        },
        {
          name: 'Promise Summary — Created At',
          description: 'The date and time this promise was first recorded in the system. Read-only.',
        },
        {
          name: 'Promise Summary — Days Overdue badge',
          description:
            'A red destructive badge showing how many days past the Promise Date the promise currently is. Only shown when the days overdue value is greater than zero.',
        },
        {
          name: 'Promise Notes — notes text',
          description:
            'The most recent promise notes shown as plain text in view mode. In edit mode, replaced by a resizable text area. The full history of all notes, including notes from other users, is available on the Activity tab.',
        },
        {
          name: 'Promise Notes — View Full Activity History button',
          description:
            'A ghost button that switches the active tab to Activity so you can see the complete note and communication timeline without scrolling.',
        },
        {
          name: 'Transactions card — badge count',
          description:
            'A secondary badge next to the Transactions heading showing the total number of transaction rows (server-side page total plus any unsaved pending additions).',
        },
        {
          name: 'Transactions card — Add Transaction button',
          description:
            'Only visible in edit mode. Opens the Select Transactions modal to find and link additional invoices to this promise.',
        },
        {
          name: 'Transactions card — Quick search',
          description: 'A search field that filters the transactions table in real time by reference number or description.',
        },
        {
          name: 'Transactions card — Export button',
          description:
            'A download icon button with a dropdown: Export Current View (visible columns only) or Export All Columns — both export to Excel and respect the current search and sort state.',
        },
        {
          name: 'Transactions card — Column Settings button',
          description: 'Opens the Column Configuration dialog to show, hide, or reorder transaction columns. Preferences are saved per user.',
        },
        {
          name: 'Transactions card — Promised Amount column',
          description:
            'In view mode shows the amount allocated to this transaction within the promise. In edit mode, replaces with an editable currency input. Inline validation prevents amounts that exceed the outstanding balance or have the wrong sign.',
        },
        {
          name: 'Transactions card — row checkbox',
          description:
            'Only active in edit mode. Uncheck a row to unlink that transaction from the promise. The Total Promised Amount in the footer updates immediately.',
        },
        {
          name: 'Transactions card — Total Promised Amount footer',
          description:
            'Always visible at the bottom of the Transactions card. Shows the sum of all promised amounts across selected transactions alongside the Total Outstanding for those same transactions.',
        },
        {
          name: 'Activity tab — Filter by Type controls',
          description:
            'Three toggle buttons: Activity (blue), Notes (purple), Communications (green). Click to include or exclude each type from the timeline. A Clear Filters button appears when any filter is active.',
        },
        {
          name: 'Activity tab — Add New Note panel',
          description:
            'A purple-tinted panel above the timeline with a multi-line text area and an Add Note button. The button is disabled until at least one character is typed. Saved notes are immediately prepended to the timeline.',
        },
        {
          name: 'Activity tab — Timeline item',
          description:
            'Each item shows a colour-coded rounded icon (matching the tag colour), a tag badge (Activity / Note / Communication), the description text, an optional outcome line (for calls), the creating user badge, and a timestamp.',
        },
        {
          name: 'Activity tab — Load more button',
          description:
            'Appears at the bottom of the timeline when there are more items to load. Click to fetch the next page of activities without refreshing the page.',
        },
        {
          name: 'Audit Log tab — log entry',
          description:
            'Each entry shows a colour-coded action icon (e.g. blue arrows for status change, green £ for amount change, amber calendar for date change, purple person for reassignment), the description text, old → new value badges (for changes that have both values), the user who made the change, and the timestamp.',
        },
        {
          name: 'Audit Log tab — Load more button',
          description:
            'Appears when there are more audit entries to load. Click to paginate through the full audit history.',
        },
      ],
      useCases: [
        'Reviewing a customer\'s promise before a follow-up call — checking the agreed amount, due date, confirmation method, and linked invoices to prepare talking points.',
        'Updating the promise status to Broken after a customer misses their payment date, and adding a note explaining the follow-up action taken.',
        'Editing the promise date when a customer calls to request an extension — entering edit mode, updating the Promise Date, and saving the change.',
        'Reassigning a promise to a different credit controller when a colleague is absent — entering edit mode and selecting the new user from the Assigned User dropdown.',
        'Adding an inline note after a call — switching to the Activity tab and posting a note directly without entering edit mode.',
        'Reviewing the Audit Log to understand who changed the promise status and when — useful when a manager queries why a promise is Closed.',
        'Linking an additional invoice to an existing promise — entering edit mode and using Add Transaction to open the Select Transactions modal.',
        'Unlinking a transaction that was added in error — entering edit mode and unchecking the row checkbox on that transaction.',
        'Navigating from the promise to the linked collection case — clicking the Case Number link in the Promise Summary card.',
        'Exporting the linked transactions to Excel for inclusion in a customer payment plan spreadsheet.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Collections > Promises to Pay and click any Promise ID to open its detail record. The page opens on the Overview tab in view mode. All information is read-only until you click Edit in the top-right header. Use the tabs — Overview, Activity, Audit Log — to move between sections. The left sidebar lists all promises and lets you navigate between them without returning to the list; collapse it with the arrow button to give yourself more working space.',
      steps: [
        {
          title: 'How do I edit a promise — change the date, status, or assigned user?',
          items: [
            {
              text: 'Click the Edit button in the top-right of the page header.',
            },
            {
              text: 'The Promise Summary card fields become editable. Change any combination of Promise Date (date picker), Assigned User (user dropdown with avatar), Status (dropdown), or Confirmation Method (dropdown).',
              tip: 'You can edit the promise notes at the same time — a text area replaces the notes section in the Promise Notes card while edit mode is active.',
            },
            {
              text: 'Click Save Changes in the header. All edits are committed in a single request.',
              note: 'Click Cancel instead to discard all changes and return to view mode without saving anything.',
            },
          ],
        },
        {
          title: 'How do I update the promised amount on a linked transaction?',
          items: [
            {
              text: 'Click the Edit button in the page header to enter edit mode.',
            },
            {
              text: 'Scroll to the Transactions card. The Promised Amount column becomes an editable currency input for each row.',
            },
            {
              text: 'Click into a Promised Amount cell and type the new amount.',
              note: 'An inline validation message appears if you enter an amount that exceeds the transaction\'s outstanding balance or has the wrong sign. The Total Promised Amount in the footer updates immediately as you type.',
            },
            {
              text: 'Click Save Changes in the header once you are happy with all the amounts.',
              tip: 'If you need to reduce the promised amount to zero for a specific invoice, enter 0 in that row\'s Promised Amount field and leave the row checked.',
            },
          ],
        },
        {
          title: 'How do I add a note to a promise?',
          items: [
            {
              text: 'Click the Activity tab.',
            },
            {
              text: 'Find the purple "Add New Note" panel near the top of the tab.',
            },
            {
              text: 'Type your note in the text area.',
            },
            {
              text: 'Click Add Note. The note appears at the top of the timeline immediately.',
              note: 'Notes added here are internal only and are never sent to the customer. They are also visible in the Promise Notes section on the Overview tab.',
            },
          ],
        },
        {
          title: 'How do I log a call from this page?',
          items: [
            {
              text: 'On the Overview tab, find the Customer Information card.',
            },
            {
              text: 'Click the teal telephone button next to the customer\'s phone number.',
            },
            {
              text: 'The Log Call modal opens. Enter the call outcome and any notes.',
            },
            {
              text: 'Click Save. The call is recorded in the Activity timeline.',
              tip: 'You do not need to enter edit mode to log a call — the teal action buttons work in view mode.',
            },
          ],
        },
        {
          title: 'How do I send an email from this page?',
          items: [
            {
              text: 'On the Overview tab, find the Customer Information card.',
            },
            {
              text: 'Click the teal envelope button next to the customer\'s email address.',
            },
            {
              text: 'The Send Email modal opens. Select a template or write a custom message, then confirm the recipient.',
            },
            {
              text: 'Click Send. The email is recorded in the Activity timeline under Communications.',
            },
          ],
        },
        {
          title: 'How do I link an additional transaction to this promise?',
          items: [
            {
              text: 'Click the Edit button in the page header to enter edit mode.',
            },
            {
              text: 'Scroll to the Transactions card. The Add Transaction button appears in the card header.',
            },
            {
              text: 'Click Add Transaction. The Select Transactions modal opens, showing all outstanding transactions for this customer.',
            },
            {
              text: 'Select the transaction(s) you want to link and click Confirm.',
            },
            {
              text: 'The new transactions appear in the Transactions card. Enter a Promised Amount for each new row.',
            },
            {
              text: 'Click Save Changes to commit.',
              note: 'Transactions added in edit mode are not saved until you click Save Changes. If you click Cancel, no new transactions are linked.',
            },
          ],
        },
        {
          title: 'How do I unlink a transaction from this promise?',
          items: [
            {
              text: 'Click the Edit button in the page header to enter edit mode.',
            },
            {
              text: 'In the Transactions card, uncheck the checkbox on the left of the row you want to remove.',
            },
            {
              text: 'The Total Promised Amount in the footer updates immediately.',
            },
            {
              text: 'Click Save Changes to commit the removal.',
              note: 'Unchecking a row in edit mode marks it for removal but does not delete the underlying transaction — the invoice remains open and can be linked to a different promise.',
            },
          ],
        },
        {
          title: 'How do I navigate to the linked collection case?',
          items: [
            {
              text: 'On the Overview tab, find the Promise Summary card.',
            },
            {
              text: 'Click the Case Number value (shown in blue as a link).',
            },
            {
              text: 'The Case Details page opens for that case. Use the browser back button or the Back to Promises navigation path to return.',
              note: 'If the Case Number shows a dash, this promise is not linked to a collection case.',
            },
          ],
        },
        {
          title: 'How do I review who changed this promise and when?',
          items: [
            {
              text: 'Click the Audit Log tab.',
            },
            {
              text: 'Each row shows the action type (colour-coded icon), a description of what changed, the old and new values (shown side-by-side for changes with before/after values), the user who made the change, and the date and time.',
            },
            {
              text: 'If the list is long, click Load more at the bottom to paginate through the full history.',
              tip: 'The Audit Log is tamper-proof — entries cannot be edited or deleted. Use it to resolve disputes about when a status was changed or by whom.',
            },
          ],
        },
        {
          title: 'How do I filter the activity timeline?',
          items: [
            {
              text: 'Click the Activity tab.',
            },
            {
              text: 'In the "Filter by Type" section, click any combination of Activity (blue), Notes (purple), and Communications (green) toggle buttons to include or exclude those categories.',
            },
            {
              text: 'The timeline updates immediately to show only the selected types.',
            },
            {
              text: 'To remove all filters and show everything, click the Clear Filters button that appears when any filter is active.',
            },
          ],
        },
        {
          title: 'How do I export the linked transactions to Excel?',
          items: [
            {
              text: 'On the Overview tab, scroll to the Transactions card.',
            },
            {
              text: 'Click the download icon button in the Transactions card header.',
            },
            {
              text: 'Select Export Current View (visible columns only) or Export All Columns.',
            },
            {
              text: 'The file downloads as an Excel spreadsheet.',
              note: 'The export reflects the current search and sort state of the transactions table.',
            },
          ],
        },
      ],
      proTips: [
        'Collapse the left sidebar when working on a wide monitor — it gives the Transactions card more space to show more columns without horizontal scrolling.',
        'Use the Activity tab\'s Add New Note panel rather than entering full edit mode just to leave a note. It is a single click and keeps the rest of the record in view mode.',
        'After marking a promise as Broken, immediately add a note on the Activity tab explaining the reason and the planned next action. This saves time when a manager reviews the record later.',
        'If a customer asks you to split a promise across two dates, create a second promise for the remaining amount rather than editing the existing one — this preserves a clean audit trail showing each commitment separately.',
        'Before a customer call, glance at the Audit Log tab first. If a colleague updated the status or added a note recently, you will know the current situation without needing to read through the full Activity timeline.',
        'When editing promised amounts, use the Total Promised Amount footer at the bottom of the Transactions card as a running total. It updates as you type so you can confirm the grand total matches what the customer agreed before saving.',
      ],
    },
    faq: [
      {
        question: 'How do I change the promised amount?',
        answer:
          'Click Edit in the page header to enter edit mode. In the Transactions card, the Promised Amount column becomes editable — click into each cell to type the new amount. The Total Promised Amount footer updates as you type. Click Save Changes when done. Note: you cannot edit the Promised Amount field in the Promise Summary card directly; that figure is always calculated from the individual transaction rows.',
      },
      {
        question: 'What does "Days Overdue" mean on a promise?',
        answer:
          'Days Overdue is the number of days that have elapsed since the Promise Date without a matching payment being recorded. A promise is overdue when today\'s date is past the Promise Date and the status is still Active. The red badge only appears when the value is greater than zero. If a customer pays late, update the status to Paid and the badge will no longer be shown.',
      },
      {
        question: 'Can I change which customer or case a promise is linked to?',
        answer:
          'No. The customer and linked collection case are set when the promise is created and cannot be changed afterwards. If the promise was recorded against the wrong customer or case, close it with a note explaining the reason and create a new promise on the correct record.',
      },
      {
        question: 'Why is the Promised Amount field in the Promise Summary read-only?',
        answer:
          'The Promised Amount shown in the Promise Summary card is always a calculated total — the sum of all individual transaction promised amounts in the Transactions card. To change the total, edit the per-transaction amounts in the Transactions card while in edit mode.',
      },
      {
        question: 'What is the difference between the Activity tab and the Audit Log tab?',
        answer:
          'The Activity tab shows human-facing interactions: calls logged, emails sent, and notes added by credit controllers. The Audit Log tab shows system-level field changes: who changed the status, what the amount was before and after, when the assignment was updated, and which transactions were linked or unlinked. Use the Activity tab to understand what was communicated; use the Audit Log to understand what changed in the record.',
      },
      {
        question: 'Can I delete a note I added on the Activity tab?',
        answer:
          'No. Notes are part of the activity audit trail and cannot be deleted. If you added a note in error, you can add a follow-up note on the Activity tab to clarify.',
      },
      {
        question: 'Why does Save Changes show a validation error?',
        answer:
          'There are three possible validation errors that block saving: (1) at least one transaction must be selected — uncheck all rows to trigger this; (2) an individual transaction\'s promised amount exceeds its outstanding balance — reduce the amount for that row; (3) the total promised amount exceeds the combined outstanding across all selected transactions — reduce one or more amounts until the total is within the outstanding. Inline error messages in the Transactions card identify which rows have issues.',
      },
      {
        question: 'How do I mark a promise as paid?',
        answer:
          'Click Edit in the page header. In the Promise Summary card, change the Status dropdown to Paid. Click Save Changes. Note: marking a promise as Paid here does not automatically allocate any receipt in the system — go to the Receipts page to match the payment to the specific invoices.',
      },
      {
        question: 'What confirmation methods are available?',
        answer:
          'Three methods are available: Phone (the customer confirmed verbally over the phone), Email (the customer confirmed in writing via email), and Portal (the customer confirmed through the customer-facing portal). Select the method that accurately reflects how the commitment was made — this information is visible in the Promises list and used in reporting.',
      },
      {
        question: 'What happens when I unlink a transaction from a promise?',
        answer:
          'Unlinking a transaction removes it from this promise record and reduces the Total Promised Amount accordingly. The underlying invoice is not changed — it remains open and can be linked to a different promise. Unlinking is recorded in the Audit Log with the transaction reference.',
      },
      {
        question: 'How do I navigate between promises without going back to the list?',
        answer:
          'Use the left sidebar, which lists all promises. If the sidebar is collapsed (showing only the teal PROMISES handle on the left edge), click the handle to expand it. Then click any promise in the list to navigate directly to that record.',
      },
      {
        question: 'Can I see all the changes made to this promise?',
        answer:
          'Yes — click the Audit Log tab. Every change to the promise record is recorded there, including status changes, amount adjustments, date changes, user reassignments, transaction links and unlinks, and call logs. Each entry shows the action, old and new values, the user responsible, and the exact date and time.',
      },
    ],
    relatedPages: [
      { label: 'Promises to Pay', path: '/collections/promises' },
      { label: 'Case Details', path: '/collections/queue' },
      { label: 'Collections Queue', path: '/collections/queue' },
      { label: 'Customer Detail', path: '/customers/:id' },
      { label: 'Receipts', path: '/receipts' },
    ],
  },
};
